import React, { useMemo, useState, useRef, useEffect, useCallback, useContext, Suspense } from 'react';
import { useSelector, useDispatch, useStore } from 'react-redux';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { OrbitControls, PerspectiveCamera, Html, Environment, useGLTF, Line } from '@react-three/drei';
import SafeEnvironment from '../SafeEnvironment';
import { EffectComposer, Bloom, SSAO } from '@react-three/postprocessing';
import * as THREE from 'three';
import { FLOOR_MATERIALS, WALL_MATERIALS, CEILING_MATERIALS, FURNITURE_TYPES, LIGHTING_TYPES, ROOM_TYPES, selectRoom, moveFurniture, recolorFurniture, resetFurnitureColor, rotateFurniture, placeFurniture, removeFurniture, resizeFurniture, changeFurnitureStyle, addOpening, removeOpening, resizeOpening, recolorOpening, resetOpeningColor, moveOpening, moveAutoWallOpening, updateAutoWallOpening, removeAutoWallOpening, addWallSegOpening, removeWallSegOpening, updateWallSegOpening, addFreeWallOpening, removeFreeWallOpening, updateFreeWallOpening, computeRoomAutoWalls, WALL_DECOR_TYPES, isWallDecorType, WALL_DECOR_META, builderHistoryBack, builderHistoryNext, jumpToBuilderHistory, commitBuilderHistory, OPENING_STYLES, placeLighting, moveLighting, rotateLighting, removeLighting, resizeLighting, changeLightingStyle, recolorLighting, resetLightingColor } from '../../store/houseSlice';

// Default size/elevation per opening type (mirrors houseSlice's OPENING_TYPE_DEFAULTS) —
// used by the 3D panels for width/height/sill fallbacks and slider ranges.
const OPENING_3D_DEFAULTS = {
  door:     { width: 0.88, height: 2.06 },
  archway:  { width: 1.2,  height: 2.4 },
  window:   { width: 0.98, height: 0.88, sill: 0.9 },
  painting: { width: 0.80, height: 0.60, sill: 1.30 },
  ac:       { width: 0.85, height: 0.30, sill: 2.15 },
  clock:    { width: 0.35, height: 0.35, sill: 1.55 },
  outlet:   { width: 0.10, height: 0.10, sill: 0.30 },
  radiator: { width: 1.00, height: 0.50, sill: 0.12 },
};
function opening3DDefaults(type) {
  return OPENING_3D_DEFAULTS[type] || OPENING_3D_DEFAULTS.window;
}
import { makeFloorTexture, makeWallTexture, makeCeilingTexture, makePaintingArtTexture, finishToPBR } from '../../utils/textures';
import { edgeControlPoint, edgePointAt, edgeLength, loopSignedArea } from '../../utils/freeWallRoom';
import { saveOrShareImage, watermarkCanvasToDataUrl } from '../../utils/screenshot';
import { shadowMapSize, useMSAA, postFxSamples, isLowPowerGPU } from '../../utils/gpuProfile';
import { useQuality, setQuality, qualitySettings, QUALITY_LEVELS } from '../../utils/quality';
import DownloadModal from '../DownloadModal';
import { useGuestMode } from '../../context/GuestModeContext';
import { useT } from '../../i18n';
import Loader3D, { SceneReadySignal } from '../Loader3D';
import { ItemIcon } from './ItemIcons';
import { getModelThumbnail } from './modelThumbnails';
import './RoomEditor.css';

const CELL = 26;           // px per grid cell (must match Builder.jsx)
const PX_TO_M = 1 / CELL;  // 1 pixel = 1/26 metres

// Translate an item's display name dynamically: t('<prefix><type>') with a
// fallback to the meta object's own English label if no translation key exists.
// Keeps this driven by the FURNITURE_TYPES / LIGHTING_TYPES lists — adding a new
// item type only needs a `furn_<type>` / `light_<type>` key, never a code edit.
function itemLabel(t, prefix, meta) {
  if (!meta) return '';
  const key = prefix + meta.type;
  const tr = t(key);
  return (tr && tr !== key) ? tr : (meta.label || meta.type);
}

function resolveMat(val, matList) {
  if (!val) return null;
  if (typeof val === 'object') return val;
  return matList.find(m => m.id === val) || null;
}

// ─── REAL MEDIA ASSETS (the only non-procedural textures in this file) ────────
// Served from /public so they're plain static files, no bundler asset-loader
// involved — a wall painting photo and a short looping TV clip.
const PAINTING_IMAGE_URL = '/media/wall_painting_1.png';
const TV_CLIP_URL = '/media/tv_clip_1.mp4';

// Loads a static image into a THREE.Texture. No Suspense involved (this file
// has no Suspense boundary set up) — plain imperative TextureLoader + state,
// same non-Suspense approach as everything else here. Pass a falsy `url` to
// skip loading entirely (used so only painting-type openings actually fetch
// the image, not every door/window/outlet that shares this component).
function useImageTexture(url) {
  const [texture, setTexture] = useState(null);
  useEffect(() => {
    if (!url) { setTexture(null); return; }
    let cancelled = false;
    const loader = new THREE.TextureLoader();
    loader.load(url, (tex) => {
      if (cancelled) return;
      tex.colorSpace = THREE.SRGBColorSpace;
      setTexture(tex);
    });
    return () => { cancelled = true; };
  }, [url]);
  return texture;
}

// Loads a short looping muted video into a THREE.VideoTexture and plays/pauses
// it as `active` changes. Muted + playsInline so browser autoplay policy never
// blocks it. Pass a falsy `url` to skip creating any video element at all.
function useVideoTexture(url, active) {
  const texture = useMemo(() => {
    if (!url) return null;
    const video = document.createElement('video');
    video.src = url;
    video.loop = true;
    video.muted = true;
    video.playsInline = true;
    video.crossOrigin = 'anonymous';
    const tex = new THREE.VideoTexture(video);
    tex.colorSpace = THREE.SRGBColorSpace;
    return tex;
  }, [url]);
  useEffect(() => {
    const video = texture?.image;
    if (!video) return;
    if (active) video.play().catch(() => {});
    else video.pause();
  }, [texture, active]);
  useEffect(() => () => { texture?.image?.pause?.(); texture?.dispose?.(); }, [texture]);
  return texture;
}

function shadeColor(hex, amount) {
  const n = parseInt((hex || '#888888').replace('#', ''), 16);
  const r = Math.min(255, Math.max(0, (n >> 16) + Math.round(255 * amount)));
  const g = Math.min(255, Math.max(0, ((n >> 8) & 0xff) + Math.round(255 * amount)));
  const b = Math.min(255, Math.max(0, (n & 0xff) + Math.round(255 * amount)));
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
}

// ─── OPENING HELPERS ─────────────────────────────────────────────────────────

const EPS = 0.01; // tolerance for floating-point grid comparisons

// Returns all rects for a room (handles both simple and compound rooms)
function getRoomRects(room) {
  return (room.rects && room.rects.length > 0)
    ? room.rects
    : [{ x: room.x, y: room.y, w: room.w, h: room.h }];
}

// Resolves a naive { wall, pos } choice (pos = fraction of the room's full
// bounding box) from the "Add Opening" modal into a precise placement. For a
// simple (single-rect) room the naive fraction is already exact — nothing to
// do. For a compound room, the bounding-box edge often doesn't correspond to
// any real wall (it may fall on a step/notch), so this snaps the position
// onto the nearest of the room's *actual* boundary segments and returns
// absX/absY — the same precise-position format the Builder's step-wall
// placement tool already produces, which every view (Builder, Plan, 3D)
// knows how to render and match adjacency against.
function resolveOpeningPlacement(room, wall, pos) {
  const rects = getRoomRects(room);
  if (rects.length <= 1) return { wall, pos };

  const isH = wall === 'top' || wall === 'bottom';
  const naive = isH
    ? { x: room.x + pos * room.w, y: wall === 'top' ? room.y : room.y + room.h }
    : { y: room.y + pos * room.h, x: wall === 'left' ? room.x : room.x + room.w };

  const ownWalls = computeRoomAutoWalls([room]); // this room's own real boundary segments
  if (!ownWalls.length) return { wall, pos };

  const overlapsX = (r, w) => w.x1 < r.x + r.w && w.x2 > r.x;
  const overlapsY = (r, w) => w.y1 < r.y + r.h && w.y2 > r.y;

  // Segments that actually face the requested side (top/bottom/left/right).
  const sideCandidates = ownWalls.filter(w => {
    if (isH) {
      if (w.y1 !== w.y2) return false;
      return wall === 'top'
        ? rects.some(r => r.y === w.y1 && overlapsX(r, w))
        : rects.some(r => r.y + r.h === w.y1 && overlapsX(r, w));
    }
    if (w.x1 !== w.x2) return false;
    return wall === 'left'
      ? rects.some(r => r.x === w.x1 && overlapsY(r, w))
      : rects.some(r => r.x + r.w === w.x1 && overlapsY(r, w));
  });

  // Fall back to every wall the room has if none face the requested side —
  // e.g. the notch itself replaced that whole side with a wall on another
  // axis. Always landing on *some* real wall beats silently keeping a
  // bounding-box position that may not correspond to any wall at all.
  const pool = sideCandidates.length ? sideCandidates : ownWalls;

  let target = pool.find(w => isH
    ? naive.x >= w.x1 - EPS && naive.x <= w.x2 + EPS
    : naive.y >= w.y1 - EPS && naive.y <= w.y2 + EPS);

  if (!target) {
    // Naive point falls in a notch with no wall on that line — snap to the
    // closest segment in the pool (perpendicular distance from its line plus
    // any overshoot past its ends).
    target = pool.reduce((best, w) => {
      const wIsH    = w.y1 === w.y2;
      const along   = wIsH ? naive.x : naive.y;
      const lo      = wIsH ? w.x1 : w.y1;
      const hi      = wIsH ? w.x2 : w.y2;
      const overshoot = along < lo ? lo - along : along > hi ? along - hi : 0;
      const perpVal  = wIsH ? naive.y : naive.x;
      const perpWall = wIsH ? w.y1 : w.x1;
      const dist = Math.hypot(overshoot, perpVal - perpWall);
      return (!best || dist < best.dist) ? { w, dist } : best;
    }, null).w;
  }

  const targetIsH = target.y1 === target.y2;
  const clamp = (v, lo, hi) => Math.max(lo + 0.05, Math.min(hi - 0.05, v));
  // The target segment's own line, not the naive point's — when the naive
  // point fell in a notch and snapped to a *different* segment, that segment
  // may sit on a different line entirely (e.g. an inner step wall), and may
  // even be a different axis than what was requested.
  const x = targetIsH ? clamp(naive.x, target.x1, target.x2) : target.x1;
  const y = targetIsH ? target.y1 : clamp(naive.y, target.y1, target.y2);
  const resolvedWall = targetIsH
    ? (rects.some(r => r.y === target.y1) ? 'top' : 'bottom')
    : (rects.some(r => r.x === target.x1) ? 'left' : 'right');

  return { wall: resolvedWall, pos, absX: (x - room.x) * CELL, absY: (y - room.y) * CELL };
}

// Returns the adjacent room that this opening connects to, or null.
// Uses rect-based checks so compound rooms are found even when the adjacency
// is through a step wall (not the BB edge).
function findAdjacentRoom(rooms, room, opening) {
  const { wall, pos } = opening;
  for (const other of rooms) {
    if (other.id === room.id) continue;
    const otherRects = getRoomRects(other);
    if (opening.absX !== undefined) {
      // Step-wall opening: absolute grid position from BB-relative pixel offsets
      const gx = room.x + opening.absX / CELL;
      const gy = room.y + opening.absY / CELL;
      for (const r of otherRects) {
        if (wall === 'top'    && Math.abs(r.y + r.h - gy) < EPS && gx >= r.x - EPS && gx <= r.x + r.w + EPS) return other;
        if (wall === 'bottom' && Math.abs(r.y - gy)       < EPS && gx >= r.x - EPS && gx <= r.x + r.w + EPS) return other;
        if (wall === 'left'   && Math.abs(r.x + r.w - gx) < EPS && gy >= r.y - EPS && gy <= r.y + r.h + EPS) return other;
        if (wall === 'right'  && Math.abs(r.x - gx)       < EPS && gy >= r.y - EPS && gy <= r.y + r.h + EPS) return other;
      }
    } else {
      // BB-edge opening: check each rect of other so compound step-wall adjacency works
      const gx = room.x + pos * room.w;
      const gy = room.y + pos * room.h;
      for (const r of otherRects) {
        if (wall === 'top'    && Math.abs(r.y + r.h - room.y)         < EPS && gx >= r.x - EPS && gx <= r.x + r.w + EPS) return other;
        if (wall === 'bottom' && Math.abs(r.y - (room.y + room.h))    < EPS && gx >= r.x - EPS && gx <= r.x + r.w + EPS) return other;
        if (wall === 'left'   && Math.abs(r.x + r.w - room.x)         < EPS && gy >= r.y - EPS && gy <= r.y + r.h + EPS) return other;
        if (wall === 'right'  && Math.abs(r.x - (room.x + room.w))    < EPS && gy >= r.y - EPS && gy <= r.y + r.h + EPS) return other;
      }
    }
  }
  return null;
}

// Returns door/arch openings from adjacent rooms that open INTO currentRoom,
// re-expressed in currentRoom's local coordinate system.
// Always stores absX/absY on the mirrored opening so it renders at the correct
// wall position even when the shared wall is a step wall inside a compound BB.
function getMirroredDoors(rooms, currentRoom) {
  const MIRROR = { top: 'bottom', bottom: 'top', left: 'right', right: 'left' };
  const result = [];
  const currentRects = getRoomRects(currentRoom);

  for (const other of rooms) {
    if (other.id === currentRoom.id) continue;
    for (const op of (other.openings || [])) {
      if (op.type !== 'door' && op.type !== 'archway') continue;
      const { wall, pos } = op;

      let newPos, mirrorAbsX, mirrorAbsY;

      if (op.absX !== undefined) {
        // Step-wall opening in `other`
        const gx = other.x + op.absX / CELL;
        const gy = other.y + op.absY / CELL;
        let matchedCr = null;
        for (const cr of currentRects) {
          if (wall === 'top'    && Math.abs(cr.y + cr.h - gy) < EPS && gx >= cr.x - EPS && gx <= cr.x + cr.w + EPS) { newPos = (gx - currentRoom.x) / currentRoom.w; matchedCr = cr; break; }
          if (wall === 'bottom' && Math.abs(cr.y - gy)        < EPS && gx >= cr.x - EPS && gx <= cr.x + cr.w + EPS) { newPos = (gx - currentRoom.x) / currentRoom.w; matchedCr = cr; break; }
          if (wall === 'left'   && Math.abs(cr.x + cr.w - gx) < EPS && gy >= cr.y - EPS && gy <= cr.y + cr.h + EPS) { newPos = (gy - currentRoom.y) / currentRoom.h; matchedCr = cr; break; }
          if (wall === 'right'  && Math.abs(cr.x - gx)        < EPS && gy >= cr.y - EPS && gy <= cr.y + cr.h + EPS) { newPos = (gy - currentRoom.y) / currentRoom.h; matchedCr = cr; break; }
        }
        if (!matchedCr) continue;
        // Compute absX/absY in currentRoom's BB coordinate system
        if (wall === 'top' || wall === 'bottom') {
          mirrorAbsX = (gx - currentRoom.x) * CELL;
          mirrorAbsY = (wall === 'top' ? matchedCr.y + matchedCr.h : matchedCr.y) - currentRoom.y;
          mirrorAbsY *= CELL;
        } else {
          mirrorAbsY = (gy - currentRoom.y) * CELL;
          mirrorAbsX = (wall === 'right' ? matchedCr.x : matchedCr.x + matchedCr.w) - currentRoom.x;
          mirrorAbsX *= CELL;
        }
      } else {
        // BB-edge opening in `other`: check each rect of currentRoom
        const gx = other.x + pos * other.w;
        const gy = other.y + pos * other.h;
        let matchedCr = null;
        for (const cr of currentRects) {
          if (wall === 'top'    && Math.abs(cr.y + cr.h - other.y)         < EPS && gx >= cr.x - EPS && gx <= cr.x + cr.w + EPS) { newPos = (gx - currentRoom.x) / currentRoom.w; matchedCr = cr; break; }
          if (wall === 'bottom' && Math.abs(cr.y - (other.y + other.h))    < EPS && gx >= cr.x - EPS && gx <= cr.x + cr.w + EPS) { newPos = (gx - currentRoom.x) / currentRoom.w; matchedCr = cr; break; }
          if (wall === 'left'   && Math.abs(cr.x + cr.w - other.x)         < EPS && gy >= cr.y - EPS && gy <= cr.y + cr.h + EPS) { newPos = (gy - currentRoom.y) / currentRoom.h; matchedCr = cr; break; }
          if (wall === 'right'  && Math.abs(cr.x - (other.x + other.w))    < EPS && gy >= cr.y - EPS && gy <= cr.y + cr.h + EPS) { newPos = (gy - currentRoom.y) / currentRoom.h; matchedCr = cr; break; }
        }
        if (!matchedCr) continue;
        // Compute absX/absY for correct rendering at step-wall position
        if (wall === 'top' || wall === 'bottom') {
          mirrorAbsX = (gx - currentRoom.x) * CELL;
          mirrorAbsY = (wall === 'top' ? matchedCr.y + matchedCr.h : matchedCr.y) - currentRoom.y;
          mirrorAbsY *= CELL;
        } else {
          mirrorAbsY = (gy - currentRoom.y) * CELL;
          mirrorAbsX = (wall === 'left' ? matchedCr.x + matchedCr.w : matchedCr.x) - currentRoom.x;
          mirrorAbsX *= CELL;
        }
      }

      // Strip the source room's absX/absY (they're relative to `other`'s BB, not ours).
      // Add newly computed absX/absY relative to currentRoom's BB.
      const { absX: _ax, absY: _ay, ...opBase } = op;
      result.push({
        ...opBase,
        wall: MIRROR[wall],
        pos: newPos,
        id: `mirror-${op.id}`,
        _sourceRoom: other,
        absX: mirrorAbsX,
        absY: mirrorAbsY,
      });
    }
  }
  return result;
}

// ─── GLB (external model) FURNITURE ──────────────────────────────────────────

// Externally hosted GLB catalogue, grouped by category on the CDN. Keyed by
// furniture STYLE id, so a GLB is just another style of an existing type —
// that's what buys full interaction parity for free (see GLBFurniture below).
//
// Filenames are CASE-SENSITIVE on the CDN: the sofas are all lowercase except
// Sofa1.glb, which is capital-S. Generated rather than hand-listed so the two
// categories can't drift; all 54 URLs verified reachable.
// ── MODELS ON FIREBASE STORAGE ───────────────────────────────────────────────
// Previously raw.githubusercontent, then jsDelivr. Both were somebody else's
// CDN serving our assets: raw throttles (it started answering 429 once designs
// opened inside the feed) and jsDelivr caches @main for ~12h, so a replaced
// model kept serving the stale file. Storage is ours — no third-party rate
// limit, no branch cache, and the same origin as everything else.
//
// A BUILDER, NOT A PREFIX, and that is forced by the URL shape. Storage
// addresses an object by its FULL path in a single segment, so every '/' inside
// it has to be percent-encoded:
//
//   .../o/3d-models%2Fdesks%2Fdesk17.glb?alt=media
//
// String-concatenating a base and 'desks/desk17.glb' would leave real slashes
// and Storage would 404. `?alt=media` is what returns the GLB bytes; without
// it the endpoint answers with the object's JSON metadata instead.
const GLB_BASE = 'https://firebasestorage.googleapis.com/v0/b/tunify-40a72.firebasestorage.app/o/';

/** Absolute URL for a model, given its path under 3d-models/. */
const glb = (path) => `${GLB_BASE}${encodeURIComponent(`3d-models/${path}`)}?alt=media`;
const GLB_COUNT = { sofa: 27, armchair: 27, table: 27, chair: 15, bed: 9, desk: 17, shelf: 6, wardrobe: 16, bathtub: 9, toilet: 5, sink: 15, lamp: 12, plant: 9 };
// (The jsDelivr 20 MB cap that desk17 exceeded no longer applies — Storage has
// no such limit, so every model comes from the same place.)

const glbRange = (n) => Array.from({ length: n }, (_, i) => i + 1);

const GLB_SOFAS = Object.fromEntries(
  glbRange(GLB_COUNT.sofa).map((n) => [
    `sofa${n}`,
    glb(`sofas/${n === 1 ? 'Sofa1' : `sofa${n}`}.glb`),
  ])
);
const GLB_ARMCHAIRS = Object.fromEntries(
  glbRange(GLB_COUNT.armchair).map((n) => [
    `armchair${n}`,
    glb(`armchairs/armchair${n}.glb`),
  ])
);
// Dining tables. The style ids are `table1`… (unique — no procedural table
// style uses those), so they slot into the same flat lookup. Note the folder
// name is spelled "dinning-tables" on the CDN.
const GLB_TABLES = Object.fromEntries(
  glbRange(GLB_COUNT.table).map((n) => [
    `table${n}`,
    glb(`dinning-tables/table${n}.glb`),
  ])
);
// Chairs. Style ids `chair1`… — unique, so they slot into the same flat lookup.
const GLB_CHAIRS = Object.fromEntries(
  glbRange(GLB_COUNT.chair).map((n) => [
    `chair${n}`,
    glb(`chairs/chair${n}.glb`),
  ])
);
// Beds. Style ids `bed1`… — unique, so they slot into the same flat lookup.
const GLB_BEDS = Object.fromEntries(
  glbRange(GLB_COUNT.bed).map((n) => [
    `bed${n}`,
    glb(`beds/bed${n}.glb`),
  ])
);
// Desks. Style ids `desk1`… — unique, so they slot into the same flat lookup.
const GLB_DESKS = Object.fromEntries(
  glbRange(GLB_COUNT.desk).map((n) => [
    `desk${n}`,
    glb(`desks/desk${n}.glb`),
  ])
);
// Bookshelves — furniture type is `shelf`, style ids `bookshelf1`… (unique).
// Note the CDN folder is spelled "bookshelfves".
const GLB_SHELVES = Object.fromEntries(
  glbRange(GLB_COUNT.shelf).map((n) => [
    `bookshelf${n}`,
    glb(`bookshelfves/bookshelf${n}.glb`),
  ])
);
// Storage (the furniture type is still `wardrobe` internally — only the display
// label changed to "Storage"). Style ids `storage1`… (unique).
const GLB_STORAGE = Object.fromEntries(
  glbRange(GLB_COUNT.wardrobe).map((n) => [
    `storage${n}`,
    glb(`storage/storage${n}.glb`),
  ])
);
// Bathtubs. Style ids `bath1`… (unique). Note the CDN folder is spelled
// "bathubs" and the files are "bath1.glb"…
const GLB_BATHS = Object.fromEntries(
  glbRange(GLB_COUNT.bathtub).map((n) => [
    `bath${n}`,
    glb(`bathubs/bath${n}.glb`),
  ])
);
// Toilets. Style ids `toilet1`… (unique).
const GLB_TOILETS = Object.fromEntries(
  glbRange(GLB_COUNT.toilet).map((n) => [`toilet${n}`, glb(`toilets/toilet${n}.glb`)])
);
// Sinks. Style ids `sink1`… (unique).
const GLB_SINKS = Object.fromEntries(
  glbRange(GLB_COUNT.sink).map((n) => [`sink${n}`, glb(`sinks/sink${n}.glb`)])
);
// Floor lamps — furniture type is `lamp`, style ids `floorlamp1`… (unique).
// Note the CDN folder is camelCase "floorLamps", files lowercase "floorlamp1".
const GLB_LAMPS = Object.fromEntries(
  glbRange(GLB_COUNT.lamp).map((n) => [`floorlamp${n}`, glb(`floorLamps/floorlamp${n}.glb`)])
);
// Plants. Style ids `plant1`… (unique).
const GLB_PLANTS = Object.fromEntries(
  glbRange(GLB_COUNT.plant).map((n) => [`plant${n}`, glb(`plants/plant${n}.glb`)])
);

// Doors are NOT furniture — they're wall openings, styled via OPENING_STYLES and
// rendered by Opening3D (room view) / HouseOpening3D (house view). So the door
// GLBs live in their OWN lookup (keyed by opening style id `door1`…), separate
// from GLB_MODELS. A GLB door replaces just the procedural door leaf/frame; the
// wall-hole cutting, placement, drag and navigation all stay as-is.
const GLB_DOORS = Object.fromEntries(
  glbRange(21).map((n) => [`door${n}`, glb(`doors/door${n}.glb`)])
);
const GLB_DOOR_FALLBACK = 'door1';
// Windows — same idea as doors (wall openings), but they sit at a sill height
// rather than on the floor. Style ids `window1`…
const GLB_WINDOWS = Object.fromEntries(
  glbRange(15).map((n) => [`window${n}`, glb(`windows/window${n}.glb`)])
);
const GLB_WINDOW_FALLBACK = 'window1';
const isGlbWindow = (style) => !!GLB_WINDOWS[style];
// Some door models are detailed on only ONE face and are authored with that
// face pointing the wrong way, so from inside the room you see the plain/hollow
// back. Flip these 180° so the solid front faces into the room.
const GLB_DOOR_FLIP = new Set(['door1', 'door7', 'door12', 'door14', 'door16']);

// Clocks are wall-decor (openings list, type 'clock') — GLB models keyed by the
// opening style id `clock1`… Like doors/windows they live in their own lookup
// (not GLB_MODELS) and replace just the procedural clock mesh; the wall stays
// solid behind them (decor never cuts a void).
const GLB_CLOCKS = Object.fromEntries(
  glbRange(13).map((n) => [`clock${n}`, glb(`clocks/clock${n}.glb`)])
);
const GLB_CLOCK_FALLBACK = 'clock1';
const isGlbClock = (style) => !!GLB_CLOCKS[style];

// Ceiling fixtures (lighting list) are GLB now too. Chandeliers `chandelier1`…
// hang from the ceiling; ceiling lamps `ceilingLamp1`… sit closer to it. Each
// keeps a real pointLight so the room is still lit at night (see
// GLBCeilingFixture). Note the CDN folder is camelCase "ceilingLamps".
const GLB_CHANDELIERS = Object.fromEntries(
  glbRange(18).map((n) => [`chandelier${n}`, glb(`chandeliers/chandelier${n}.glb`)])
);
const GLB_CHANDELIER_FALLBACK = 'chandelier1';
// Folder is camelCase "ceilingLamps" but the files are lowercase
// "ceilinglamp1.glb" (same as floorLamps/floorlamp1). Style ids stay camelCase.
const GLB_CEILING_LAMPS = Object.fromEntries(
  glbRange(12).map((n) => [`ceilingLamp${n}`, glb(`ceilingLamps/ceilinglamp${n}.glb`)])
);
const GLB_CEILING_LAMP_FALLBACK = 'ceilingLamp1';

// One flat lookup — style ids are unique across categories, and the furniture
// switch simply asks GLB_MODELS[style].
const GLB_MODELS = {
  ...GLB_SOFAS, ...GLB_ARMCHAIRS, ...GLB_TABLES, ...GLB_CHAIRS, ...GLB_BEDS,
  ...GLB_DESKS, ...GLB_SHELVES, ...GLB_STORAGE, ...GLB_BATHS,
  ...GLB_TOILETS, ...GLB_SINKS, ...GLB_LAMPS, ...GLB_PLANTS,
};

// Which GLB stands in for items saved under the old procedural style ids.
const GLB_SOFA_FALLBACK = 'sofa1';
const GLB_ARMCHAIR_FALLBACK = 'armchair1';
const GLB_TABLE_FALLBACK = 'table1';
const GLB_CHAIR_FALLBACK = 'chair1';
const GLB_BED_FALLBACK = 'bed1';
const GLB_DESK_FALLBACK = 'desk1';
const GLB_SHELF_FALLBACK = 'bookshelf1';
const GLB_STORAGE_FALLBACK = 'storage1';
const GLB_BATH_FALLBACK = 'bath1';
const GLB_TOILET_FALLBACK = 'toilet1';
const GLB_SINK_FALLBACK = 'sink1';
const GLB_LAMP_FALLBACK = 'floorlamp1';
const GLB_PLANT_FALLBACK = 'plant1';

// Availability probe, cached once per URL.
//
// Why this exists: useGLTF SUSPENDS, and on a failed fetch it THROWS from the
// suspense path. An error boundary catches the render throw, but the loader's
// async rejection still escapes as an uncaught error — which trips CRA's
// full-screen dev error overlay and hides the whole editor. So instead of
// catching the failure after the fact, we simply never mount useGLTF against a
// URL we know we can't fetch. Preload is likewise deferred until a URL checks
// out, so a dead asset never spams the console at import time.
const _glbProbe = new Map();     // original url -> Promise<boolean>
const _glbResolved = new Map();  // original url -> url actually worth loading

// ─── MOBILE MODEL VARIANTS ───────────────────────────────────────────────────
//
// The full-detail models are big — table1.glb is 17.8 MB, bed1.glb 17.0 MB, and
// a furnished room pulls 30–40 MB of them. Measured over a short session on an
// emulated phone, a 4-room project fetched 128 MB across 35 model loads. That is
// what exhausts a mobile browser's budget and gets the tab killed, which the
// user experiences as the page reloading itself.
//
// scripts/compress-models.mjs writes a compressed mirror of the model tree under
// /mobile (Draco geometry, WebP textures, textures capped at 1024px), typically
// 10–20× smaller. Phones load from there; desktops are untouched and keep the
// full-detail originals exactly as before.
//
// Nothing here depends on the mirror existing. If a compressed file is missing —
// the mirror not generated yet, or one model skipped — the probe simply falls
// through to the original URL. So this is safe to ship before the models are
// converted, and each model starts benefiting the moment its variant appears.
const glbMobileVariant = (url) => url.replace('/3d-models/', '/3d-models/mobile/');

const glbHead = (url) => fetch(url, { method: 'HEAD', mode: 'cors' })
  .then((r) => r.ok)
  .catch(() => false);   // network/CORS failure → treat as unavailable

async function resolveGLB(url) {
  if (isLowPowerGPU()) {
    const light = glbMobileVariant(url);
    if (light !== url && await glbHead(light)) {
      _glbResolved.set(url, light);
      try { useGLTF.preload(light); } catch { /* ignore */ }
      return true;
    }
  }
  if (await glbHead(url)) {
    _glbResolved.set(url, url);
    try { useGLTF.preload(url); } catch { /* ignore */ }
    return true;
  }
  return false;
}

function probeGLB(url) {
  if (!_glbProbe.has(url)) _glbProbe.set(url, resolveGLB(url));
  return _glbProbe.get(url);
}

// The URL to actually load. Only meaningful once probeGLB has resolved, which is
// exactly when the components below mount their model.
const glbSrc = (url) => _glbResolved.get(url) || url;

// Which materials the colour picker should tint, per style id. Values are
// matched case-insensitively as substrings of the material name. Leave a model
// out to fall back to the automatic heuristic below.
//   e.g.  sofa1: ['fabric', 'cushion'],
// The material names found in each model are logged to the console once on
// first load, so you can read them off and pin them here if the heuristic
// tints something it shouldn't (a chrome leg, say).
const GLB_TINT_PARTS = {
  // This model names its upholstery "Couch_Blue" and its legs "Black" —
  // without pinning, the colour picker would paint the legs too.
  sofa6: ['couch'],
};

// Heuristic: tint upholstery, leave obvious hardware alone. Name-based only —
// do NOT filter on metalness: real-world GLB exports frequently ship every
// material at metalness 1.0, which silently excluded the entire model.
// Asset packs are authored all over the world, so the hardware names are not
// reliably English — armchair5, for instance, calls its legs "gambe_vanessa".
// A few common non-English terms are included; anything this misses can be
// pinned precisely per-model via GLB_TINT_PARTS.
const GLB_NO_TINT = /leg|foot|feet|chrome|steel|screw|bolt|shadow|gambe?|pied|bein|pata|legno|metall|glas|vetr|verre|cristal/i;
function isTintable(mat, allow) {
  if (!mat || !mat.color) return false;
  if (allow && allow.length) {
    return allow.some(n => (mat.name || '').toLowerCase().includes(String(n).toLowerCase()));
  }
  return !GLB_NO_TINT.test(mat.name || '');
}

const _glbLogged = new Set();   // log each model's material names only once

// Rebuilds an albedo map as normalised greyscale "detail only".
//
// THE PROBLEM: three multiplies the material colour by the albedo texel —
//   final = pickedColour x texel
// Multiplication can only darken. A black/dark albedo (texel ~0.05) stays
// black no matter what colour is picked, so the picker appears dead on dark
// models. A strongly-coloured albedo has the same issue in hue: a deep blue
// texture tinted red just goes muddy.
//
// THE FIX: strip the albedo's own colour and brightness, keeping only its
// relative light/dark detail (folds, seams, stitching, wear), rescaled so the
// average sits near white. Then `colour x detail` reproduces the picked colour
// faithfully while the fabric detail survives. Cached per source texture, so
// the cost is paid once per model, not per instance or per colour change.
const _tintMapCache = new Map();
function neutralisedAlbedo(tex) {
  if (!tex || !tex.image) return null;
  if (_tintMapCache.has(tex.uuid)) return _tintMapCache.get(tex.uuid);
  let out = null;
  try {
    const img = tex.image;
    const w = img.width | 0, h = img.height | 0;
    if (!w || !h) return null;
    const cv = document.createElement('canvas');
    cv.width = w; cv.height = h;
    const ctx = cv.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(img, 0, 0, w, h);
    const data = ctx.getImageData(0, 0, w, h);   // throws if the canvas is tainted
    const p = data.data;

    let sum = 0;
    for (let i = 0; i < p.length; i += 4) sum += 0.2126 * p[i] + 0.7152 * p[i + 1] + 0.0722 * p[i + 2];
    const mean = Math.max(1, sum / (p.length / 4));
    // Aim the average at ~0.80 so the tint reads bright without clipping the
    // highlights into flat white.
    const k = 204 / mean;
    for (let i = 0; i < p.length; i += 4) {
      const L = 0.2126 * p[i] + 0.7152 * p[i + 1] + 0.0722 * p[i + 2];
      const v = Math.min(255, L * k);
      p[i] = p[i + 1] = p[i + 2] = v;            // greyscale: hue now comes from .color
    }
    ctx.putImageData(data, 0, 0);

    out = new THREE.CanvasTexture(cv);
    // Carry over the sampling setup or the map lands rotated/offset/upside-down:
    // glTF textures use flipY=false, which CanvasTexture does NOT default to.
    out.flipY = tex.flipY;
    out.wrapS = tex.wrapS; out.wrapT = tex.wrapT;
    out.repeat.copy(tex.repeat); out.offset.copy(tex.offset);
    out.center.copy(tex.center); out.rotation = tex.rotation;
    out.colorSpace = tex.colorSpace;
    out.anisotropy = tex.anisotropy;
    out.needsUpdate = true;
  } catch (e) {
    out = null;   // tainted canvas / undrawable image → leave the original map
  }
  _tintMapCache.set(tex.uuid, out);
  return out;
}

// Renders one .glb, auto-fitted to the item's grid footprint, tinted to `color`.
//
// Three things are essential here and easy to get wrong:
//  1. clone(true) per instance — useGLTF caches ONE scene per URL, so placing
//     two of the same sofa would otherwise move/parent the same nodes twice.
//  2. Materials must be cloned TOO. clone(true) deep-copies nodes but keeps
//     material references SHARED, so recolouring one sofa would recolour every
//     other instance and poison the cached original.
//  3. Auto-fit — a .glb arrives in whatever scale it was authored in, while
//     every procedural item here is already built to the room's metre scale.
//     Without normalising, the sofa lands either microscopic or building-sized.
// Tracks how many GLB assets (furniture + doors) in the scene are still loading,
// so the room's full-screen loader can stay up until EVERYTHING has arrived (and
// re-show when switching rooms). Provided INSIDE the Canvas by ThreeDRoom — R3F's
// renderer doesn't forward outer React context across the Canvas boundary. The
// default no-op keeps GLB components safe when rendered without a provider (e.g.
// the whole-house view, which has its own loader).
export const GlbLoadingContext = React.createContext(null);

// Shared by the door/window/clock/ceiling-fixture GLB models (GLBModel above
// inlines its own copy). Clones each mesh's materials — so tinting one instance
// never bleeds into another or the cached original — optionally forces double
// -sided, and collects the tintable materials with the same heuristic furniture
// uses. Returns [{ mat, origColor, origMap, neutMap }] for applyGlbTint.
function prepGlbMaterials(clone, { styleId, doubleSide = false, tintAll = false, castShadow = true } = {}) {
  const allow = GLB_TINT_PARTS[styleId];
  const tintable = [];
  clone.traverse((o) => {
    if (!o.isMesh) return;
    o.castShadow = castShadow; o.receiveShadow = true;
    o.material = Array.isArray(o.material) ? o.material.map((m) => m.clone()) : o.material.clone();
    const mats = Array.isArray(o.material) ? o.material : [o.material];
    mats.forEach((m) => {
      if (doubleSide) m.side = THREE.DoubleSide;
      // `tintAll` (lighting fixtures) recolours the whole body — the metal IS
      // the fixture — so it bypasses the hardware/metal exclusion and keeps the
      // material's metalness (a brass frame recolours to a metallic black, not a
      // flat matte). It still skips glass and glowing/emissive bulbs. The normal
      // (furniture) path uses the heuristic and de-metals upholstery.
      if (tintAll) {
        const glassy = /glas|vetr|verre|cristal|bulb|light|lamp_?glass|shade/i.test(m.name || '');
        const lit = m.emissive && (m.emissive.r + m.emissive.g + m.emissive.b) > 0.15;
        if (!m.color || glassy || lit) return;
      } else {
        if (!isTintable(m, allow)) return;
        if (!m.metalnessMap && typeof m.metalness === 'number' && m.metalness > 0.1) m.metalness = 0.0;
        if (!m.roughnessMap && typeof m.roughness === 'number' && m.roughness < 0.35) m.roughness = 0.55;
      }
      tintable.push({ mat: m, origColor: m.color.clone(), origMap: m.map || null, neutMap: m.map ? neutralisedAlbedo(m.map) : null });
    });
  });
  return tintable;
}

// Apply a tint, or restore the authored default when `tint` is falsy. Call from
// a useEffect keyed on [tintable, tint] (same swap logic as GLBModel).
function applyGlbTint(tintable, tint) {
  tintable.forEach(({ mat, origColor, origMap, neutMap }) => {
    if (tint) { if (neutMap) mat.map = neutMap; mat.color.set(tint); }
    else { mat.map = origMap; mat.color.copy(origColor); }
    mat.needsUpdate = true;
  });
}

function GLBModel({ url, mw, md, mh, posX, posZ, yRot, color, styleId, onLoaded }) {
  const { scene } = useGLTF(url);
  // Fires once the model is actually mounted (useGLTF has resolved) — the signal
  // that this asset finished loading.
  useEffect(() => { onLoaded && onLoaded(); }, [onLoaded]);

  const { object, fit, tintable } = useMemo(() => {
    // clone(true) = deep clone, so each placed instance owns its nodes.
    // (For rigged/skinned assets you'd want SkeletonUtils.clone instead —
    // static furniture is fine with this.)
    const clone = scene.clone(true);
    const allow = GLB_TINT_PARTS[styleId];
    const tintable = [];
    const seen = [];
    clone.traverse((o) => {
      if (!o.isMesh) return;
      o.castShadow = true;
      o.receiveShadow = true;
      // Per-instance material copies (see note 2 above).
      o.material = Array.isArray(o.material)
        ? o.material.map(m => m.clone())
        : o.material.clone();
      const mats = Array.isArray(o.material) ? o.material : [o.material];
      mats.forEach((m) => {
        seen.push(`${m.name || '(unnamed)'}[metal=${m.metalness ?? '-'}${m.metalnessMap ? '*map' : ''} rough=${m.roughness ?? '-'}${m.roughnessMap ? '*map' : ''} map=${m.map ? 'y' : 'n'}]`);
        if (!isTintable(m, allow)) return;
        // Upholstery is a dielectric, but ONLY correct the scalar when no
        // metalnessMap is present. three multiplies scalar × map, so a model
        // that ships metalness:1 alongside a proper metalnessMap is already
        // fine — forcing 0 there would wipe out legitimately metallic parts.
        // The scalar-only case is the broken-export one worth fixing.
        if (!m.metalnessMap && typeof m.metalness === 'number' && m.metalness > 0.1) m.metalness = 0.0;
        if (!m.roughnessMap && typeof m.roughness === 'number' && m.roughness < 0.35) m.roughness = 0.55;
        // Remember the ORIGINAL colour + albedo, and prepare a neutralised
        // (greyscale-detail) albedo. The effect below swaps between them:
        //   • no colour picked → original map + original colour = the true
        //     default look (this is what "Default" restores to);
        //   • colour picked → neutralised map + tint, so even dark/strongly
        //     coloured models take the colour faithfully (see neutralisedAlbedo).
        tintable.push({
          mat: m,
          origColor: m.color.clone(),
          origMap: m.map || null,
          neutMap: m.map ? neutralisedAlbedo(m.map) : null,
        });
      });
    });
    if (!_glbLogged.has(url)) {
      _glbLogged.add(url);
      // eslint-disable-next-line no-console
      console.info(`[GLB] ${styleId} materials:`, seen.join(', '),
        `| tinting ${tintable.length}/${seen.length}`);
    }

    // Normalise: uniform-scale to fit the footprint, centre on X/Z, sit on y=0.
    const box = new THREE.Box3().setFromObject(clone);
    const size = new THREE.Vector3();
    const centre = new THREE.Vector3();
    box.getSize(size);
    box.getCenter(centre);
    let s = Math.min(
      mw / Math.max(size.x, 1e-6),
      md / Math.max(size.z, 1e-6),
    );
    // Optional height cap: for TALL furniture (bookshelves) the footprint-only
    // fit can scale the model so its height towers metres above the walls (a
    // narrow 2m-tall shelf placed in a 3m-wide grid footprint would balloon to
    // ~6m). Capping the rendered height to the item's intended height (h3)
    // keeps it realistic. Only passed for tall types, so wide/low furniture
    // (sofas, tables, …) are unaffected.
    if (mh && size.y * s > mh) s = mh / Math.max(size.y, 1e-6);
    return {
      object: clone,
      tintable,
      fit: { s, offset: [-centre.x * s, -box.min.y * s, -centre.z * s] },
    };
  }, [scene, mw, md, mh, styleId, url]);

  // Apply tint, or restore the default. Runs on colour change AND on reset —
  // the reset path (color falsy) is what puts the map/colour back so the item
  // returns to its original look instead of keeping the last tint.
  useEffect(() => {
    tintable.forEach(({ mat, origColor, origMap, neutMap }) => {
      if (color) {
        if (neutMap) mat.map = neutMap;   // greyscale detail → tint reads true
        mat.color.set(color);
      } else {
        mat.map = origMap;                // back to the authored albedo…
        mat.color.copy(origColor);        // …and the authored colour
      }
      mat.needsUpdate = true;
    });
  }, [tintable, color]);

  // These material copies are ours, so we own their disposal. Textures are NOT
  // disposed — origMap belongs to useGLTF's cache and neutMap is module-cached
  // & shared across instances.
  useEffect(() => () => { tintable.forEach(({ mat }) => mat.dispose()); }, [tintable]);

  return (
    <group position={[posX, 0, posZ]} rotation={[0, yRot, 0]}>
      <group scale={fit.s} position={fit.offset}>
        <primitive object={object} />
      </group>
    </group>
  );
}

// Suspense catches the LOAD (pending); an error boundary is still required for
// the FAILED case — a 404/CORS error inside useGLTF throws, and an uncaught
// throw inside <Canvas> tears down the whole 3D view.
class GLBErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { failed: false }; }
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch(err) {
    // eslint-disable-next-line no-console
    console.warn('[GLB] model failed to load:', err?.message || err);
    // Let the load counter know this asset is finished (failed), so the room
    // loader doesn't wait forever on a model that errored after its probe.
    this.props.onError && this.props.onError();
  }
  render() { return this.state.failed ? this.props.fallback : this.props.children; }
}

// Stand-in shown while a .glb downloads (and if one fails).
//
// Deliberately NOT a procedural sofa: substituting a different, fully-detailed
// piece of furniture reads as "the wrong model loaded" rather than "still
// loading" — you can't tell a slow download from a broken one. A neutral ghost
// volume + spinner makes the pending state unambiguous.
function GLBPlaceholder({ mw, md, posX, posZ, yRot, failed }) {
  const h = 0.55;
  return (
    <group position={[posX, 0, posZ]} rotation={[0, yRot, 0]}>
      {/* faint volume so the slot still reads as occupied at the right size */}
      <mesh position={[0, h / 2, 0]}>
        <boxGeometry args={[mw * 0.92, h, md * 0.92]} />
        <meshBasicMaterial
          color={failed ? '#b0685a' : '#8d97a3'}
          transparent opacity={0.12} depthWrite={false}
        />
      </mesh>
      <Html center position={[0, h + 0.42, 0]} distanceFactor={7} zIndexRange={[20, 0]}>
        <div style={{
          background: failed ? 'rgba(120,40,32,0.92)' : 'rgba(28,26,23,0.92)',
          color: '#f0e8d8', padding: '4px 12px', borderRadius: 20,
          fontSize: 12, fontWeight: 600, fontFamily: 'system-ui,sans-serif',
          whiteSpace: 'nowrap', pointerEvents: 'none',
          border: '1.5px solid rgba(255,255,255,0.18)',
        }}>
          {failed ? '⚠ Model unavailable' : 'Loading…'}
        </div>
      </Html>
    </group>
  );
}

// Floating "Loading…" pill — the same badge the furniture placeholder shows,
// pulled out so the other GLB items (clock / window / door / ceiling fixture)
// can float the identical indicator while their model streams in (or "⚠ Model
// unavailable" if it fails). `position` is in the item's local space.
function GLBLoadingLabel({ position = [0, 0, 0], failed = false }) {
  return (
    <Html center position={position} distanceFactor={7} zIndexRange={[20, 0]}>
      <div style={{
        background: failed ? 'rgba(120,40,32,0.92)' : 'rgba(28,26,23,0.92)',
        color: '#f0e8d8', padding: '4px 12px', borderRadius: 20,
        fontSize: 12, fontWeight: 600, fontFamily: 'system-ui,sans-serif',
        whiteSpace: 'nowrap', pointerEvents: 'none',
        border: '1.5px solid rgba(255,255,255,0.18)',
      }}>
        {failed ? '⚠ Model unavailable' : 'Loading…'}
      </div>
    </Html>
  );
}

// Public wrapper used by the furniture switch.
// Shows the loading placeholder until the asset is confirmed fetchable, then
// swaps in the GLB. Suspense covers the download, the boundary covers a late
// failure (e.g. a valid HEAD but a corrupt/blocked body).
function GLBFurniture({ styleId, mw, md, mh, posX, posZ, yRot, color }) {
  const url = GLB_MODELS[styleId];
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);
  // Count this item as "loading" from mount until its model is rendered (or it
  // fails/unmounts), so the room loader waits for it. done() is idempotent.
  const glbLoad = useContext(GlbLoadingContext);
  const doneRef = useRef(false);
  const finish = () => { if (!doneRef.current) { doneRef.current = true; glbLoad && glbLoad.done(); } };

  useEffect(() => {
    if (!url) return;
    doneRef.current = false;
    glbLoad && glbLoad.start();
    let live = true;
    setReady(false); setFailed(false);
    // Last-resort net: never let one slow/hung asset pin the room loader open.
    const safety = setTimeout(finish, 25000);
    probeGLB(url).then((ok) => {
      if (!live) return;
      if (ok) setReady(true); else { setFailed(true); finish(); }
    });
    return () => { live = false; clearTimeout(safety); finish(); };
  }, [url]);

  const loading = <GLBPlaceholder mw={mw} md={md} posX={posX} posZ={posZ} yRot={yRot} />;
  const broken  = <GLBPlaceholder mw={mw} md={md} posX={posX} posZ={posZ} yRot={yRot} failed />;

  if (!url || failed) return broken;
  if (!ready) return loading;
  return (
    <GLBErrorBoundary fallback={broken} onError={finish}>
      <Suspense fallback={loading}>
        <GLBModel url={glbSrc(url)} styleId={styleId} mw={mw} md={md} mh={mh}
                  posX={posX} posZ={posZ} yRot={yRot} color={color} onLoaded={finish} />
      </Suspense>
    </GLBErrorBoundary>
  );
}

// ─── GLB DOOR ────────────────────────────────────────────────────────────────
// A door GLB, fitted into a wall opening. Unlike furniture (fit to a footprint),
// a door is stretched to exactly fill the opening's width (dW) × height (dH) so
// there's never a see-through gap around it; depth is scaled proportionally so
// the leaf/frame keeps its thickness. Rendered at local origin — the caller
// (Opening3D / HouseOpening3D) wraps it in the group that positions/rotates the
// opening on its wall, so the door inherits the right place and facing.
function GLBDoorModel({ url, dW, dH, styleId, tint, depthMin, onLoaded }) {
  const { scene } = useGLTF(url);
  useEffect(() => { onLoaded && onLoaded(); }, [onLoaded]);
  const { object, scale, offset, tintable } = useMemo(() => {
    const clone = scene.clone(true);
    // DoubleSide: a door is seen from inside the room AND from the adjacent
    // side, so a single-sided leaf would look hollow from one side. Also collect
    // the tintable materials so the colour picker can recolour the leaf.
    // castShadow off: a door set into a wall would otherwise throw an odd strip
    // of shadow into the room (the sun isn't really coming through it).
    const tintable = prepGlbMaterials(clone, { styleId, doubleSide: true, castShadow: false });
    // A door's thinnest horizontal axis is its thickness; the wide face (the
    // leaf) should end up facing the doorway (±Z here). Most GLBs are authored
    // with the leaf facing ±X (width along Z), so rotate 90° when that's the
    // case; a door already authored facing ±Z sits right without rotation.
    let box = new THREE.Box3().setFromObject(clone);
    const size = new THREE.Vector3(); box.getSize(size);
    let rotY = size.x < size.z ? Math.PI / 2 : 0;
    // Flip 180° for models whose detailed front is authored facing away.
    if (GLB_DOOR_FLIP.has(styleId)) rotY += Math.PI;
    if (rotY) {
      clone.rotation.y = rotY;
      clone.updateMatrixWorld(true);
      box = new THREE.Box3().setFromObject(clone);
      box.getSize(size);
    }
    const centre = new THREE.Vector3(); box.getCenter(centre);
    // Fill width (X) and height (Y) exactly; scale thickness (Z) proportionally.
    const sx = dW / Math.max(size.x, 1e-6);
    const sy = dH / Math.max(size.y, 1e-6);
    const sz = Math.min(sx, sy);
    // Seat the door on the floor. Both 3D views put the floor plane at ~y=0.
    // Anchoring the door's bottom exactly at 0 makes its underside coplanar with
    // the floor → z-fighting (a flickering white strip). Lifting it above the
    // floor instead leaves a visible gap under the door. So SINK it a few mm:
    // the flat underside drops just beneath the opaque floor plane (hidden), the
    // door visually meets the floor with no gap, and nothing is coplanar so
    // there's no z-fighting.
    const FLOOR_SINK = -0.006;
    // depthMin (house view) thickens the leaf to fill the wall and centres it in
    // Z, so a door recessed in a thick dollhouse wall is proud of the wall paint
    // on BOTH sides instead of one. Undefined (room view) keeps the original
    // thin, +0.05-proud placement untouched.
    const szZ  = depthMin ? depthMin / Math.max(size.z, 1e-6) : sz;
    const zOff = depthMin
      ? -centre.z * szZ
      : (url.includes('door18') ? -centre.z * sz + 0.1 : -centre.z * sz + 0.05);
    return {
      object: clone,
      tintable,
      scale: [sx, sy, szZ],
      offset: [-centre.x * sx, -box.min.y * sy + FLOOR_SINK, zOff],
    };
  }, [scene, dW, dH, styleId, url, depthMin]);
  useEffect(() => { applyGlbTint(tintable, tint); }, [tintable, tint]);
  useEffect(() => () => { tintable.forEach(({ mat }) => mat.dispose()); }, [tintable]);
  return (
    <group scale={scale} position={offset}>
      <primitive object={object} />
    </group>
  );
}

// Plain door slab — shown while the GLB streams in (or if it fails), so the
// doorway is never a see-through hole.
function GLBDoorSlab({ dW, dH, color = '#c9b79a' }) {
  return (
    <mesh position={[0, dH / 2, 0]} castShadow receiveShadow>
      <boxGeometry args={[dW, dH, 0.05]} />
      <meshStandardMaterial color={color} roughness={0.62} />
    </mesh>
  );
}

// Probe + Suspense + error boundary wrapper (mirrors GLBFurniture). Exported so
// the whole-house view (HouseOpening3D) renders doors identically to the room
// view. Falls back to a plain slab while loading / on failure.
export function GLBDoor({ styleId, dW, dH, color, tint, depthMin }) {
  const url = GLB_DOORS[styleId] || GLB_DOORS[GLB_DOOR_FALLBACK];
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);
  // Count the door toward the room loader too (same idempotent start/done).
  const glbLoad = useContext(GlbLoadingContext);
  const doneRef = useRef(false);
  const finish = () => { if (!doneRef.current) { doneRef.current = true; glbLoad && glbLoad.done(); } };
  useEffect(() => {
    if (!url) return;
    doneRef.current = false;
    glbLoad && glbLoad.start();
    let live = true;
    setReady(false); setFailed(false);
    const safety = setTimeout(finish, 25000);
    probeGLB(url).then((ok) => { if (!live) return; if (ok) setReady(true); else { setFailed(true); finish(); } });
    return () => { live = false; clearTimeout(safety); finish(); };
  }, [url]);
  const slab = <GLBDoorSlab dW={dW} dH={dH} color={color} />;
  const labelPos = [0, dH + 0.2, 0.05];
  const loadingFb = <>{slab}<GLBLoadingLabel position={labelPos} /></>;
  const failedFb  = <>{slab}<GLBLoadingLabel position={labelPos} failed /></>;
  if (!url) return slab;
  if (failed) return failedFb;
  if (!ready) return loadingFb;
  return (
    <GLBErrorBoundary fallback={failedFb} onError={finish}>
      <Suspense fallback={loadingFb}>
        <GLBDoorModel url={glbSrc(url)} dW={dW} dH={dH} styleId={styleId} tint={tint} depthMin={depthMin} onLoaded={finish} />
      </Suspense>
    </GLBErrorBoundary>
  );
}

// Style ids that are GLB doors (vs the legacy procedural styles like 'panel4').
const isGlbDoor = (style) => !!GLB_DOORS[style];

// ─── GLB WINDOW ──────────────────────────────────────────────────────────────
// Like GLBDoorModel, but the window fills wW × wH and its bottom sits at the
// SILL height (wSill), not the floor. Same orientation handling (thin axis is
// the frame thickness; rotate so the wide face fills the doorway plane), double
// -sided so the glass/frame reads from both rooms, and a matching onLoaded ping.
function GLBWindowModel({ url, wW, wH, wSill, styleId, tint, depthMin, onLoaded }) {
  const { scene } = useGLTF(url);
  useEffect(() => { onLoaded && onLoaded(); }, [onLoaded]);
  const { object, scale, offset, tintable } = useMemo(() => {
    const clone = scene.clone(true);
    // DoubleSide (glass/frame read from both rooms) + collect tintable materials
    // — the heuristic skips glass, so the colour picker recolours the frame.
    // castShadow off: a window shouldn't cast its frame's shadow into the room.
    const tintable = prepGlbMaterials(clone, { styleId, doubleSide: true, castShadow: false });
    let box = new THREE.Box3().setFromObject(clone);
    const size = new THREE.Vector3(); box.getSize(size);
    if (size.x < size.z) {
      clone.rotation.y = Math.PI / 2;
      clone.updateMatrixWorld(true);
      box = new THREE.Box3().setFromObject(clone);
      box.getSize(size);
    }
    const centre = new THREE.Vector3(); box.getCenter(centre);
    const sx = wW / Math.max(size.x, 1e-6);
    const sy = wH / Math.max(size.y, 1e-6);
    const sz = Math.min(sx, sy);
    // depthMin (house view) thickens the window to fill the wall + centres it in
    // Z so it's proud of the wall paint on both sides; undefined (room view) keeps
    // the thin, +0.05-proud placement.
    const szZ  = depthMin ? depthMin / Math.max(size.z, 1e-6) : sz;
    const zOff = depthMin ? -centre.z * szZ : -centre.z * sz + 0.05;
    return {
      object: clone,
      tintable,
      scale: [sx, sy, szZ],
      // X centred, bottom raised to the sill, Z per the depth rule above.
      offset: [-centre.x * sx, -box.min.y * sy + wSill, zOff],
    };
  }, [scene, wW, wH, wSill, styleId, depthMin]);
  useEffect(() => { applyGlbTint(tintable, tint); }, [tintable, tint]);
  useEffect(() => () => { tintable.forEach(({ mat }) => mat.dispose()); }, [tintable]);
  return (
    <group scale={scale} position={offset}>
      <primitive object={object} />
    </group>
  );
}

// Fallback while a window GLB streams in (a faint glass pane, so the opening
// isn't a see-through hole).
function GLBWindowPane({ wW, wH, wSill }) {
  return (
    <mesh position={[0, wSill + wH / 2, 0]}>
      <boxGeometry args={[wW, wH, 0.03]} />
      <meshStandardMaterial color="#c8ddf4" transparent opacity={0.45} roughness={0.1} metalness={0.1} side={THREE.DoubleSide} />
    </mesh>
  );
}

export function GLBWindow({ styleId, wW, wH, wSill, tint, depthMin }) {
  const url = GLB_WINDOWS[styleId] || GLB_WINDOWS[GLB_WINDOW_FALLBACK];
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);
  const glbLoad = useContext(GlbLoadingContext);
  const doneRef = useRef(false);
  const finish = () => { if (!doneRef.current) { doneRef.current = true; glbLoad && glbLoad.done(); } };
  useEffect(() => {
    if (!url) return;
    doneRef.current = false;
    glbLoad && glbLoad.start();
    let live = true;
    setReady(false); setFailed(false);
    const safety = setTimeout(finish, 25000);
    probeGLB(url).then((ok) => { if (!live) return; if (ok) setReady(true); else { setFailed(true); finish(); } });
    return () => { live = false; clearTimeout(safety); finish(); };
  }, [url]);
  const pane = <GLBWindowPane wW={wW} wH={wH} wSill={wSill} />;
  const labelPos = [0, wSill + wH + 0.2, 0.05];
  const loadingFb = <>{pane}<GLBLoadingLabel position={labelPos} /></>;
  const failedFb  = <>{pane}<GLBLoadingLabel position={labelPos} failed /></>;
  if (!url) return pane;
  if (failed) return failedFb;
  if (!ready) return loadingFb;
  return (
    <>
      {/* Light-blue "sky glass" backing filling the glass area, so from the
          outside every window reads as light-blue glass rather than a dark
          see-through hole. Sits at the window's glass depth, inset to stay
          inside the frame; double-sided so it shows from either side. */}
      <mesh position={[0, wSill + wH / 2, 0.05]}>
        <planeGeometry args={[wW * 0.86, wH * 0.86]} />
        <meshStandardMaterial color="#aed4ee" roughness={0.12} metalness={0.05} transparent opacity={0.9} side={THREE.DoubleSide} />
      </mesh>
      <GLBErrorBoundary fallback={failedFb} onError={finish}>
        <Suspense fallback={loadingFb}>
          <GLBWindowModel url={glbSrc(url)} wW={wW} wH={wH} wSill={wSill} styleId={styleId} tint={tint} depthMin={depthMin} onLoaded={finish} />
        </Suspense>
      </GLBErrorBoundary>
    </>
  );
}

// ─── GLB WALL CLOCK ──────────────────────────────────────────────────────────
// A clock is wall-decor: it doesn't cut a hole, it mounts flat on the interior
// wall face. The GLB fills a cSize × cSize square, is centred vertically on
// midY, and its back seats at zBase (the wall face) so it protrudes into the
// room. Uniform scale keeps the round face undistorted; double-sided materials
// so it reads even if the model is single-sided.
function GLBClockModel({ url, cSize, midY, zBase, styleId, tint, onLoaded }) {
  const { scene } = useGLTF(url);
  useEffect(() => { onLoaded && onLoaded(); }, [onLoaded]);
  const { object, scale, offset, tintable } = useMemo(() => {
    const clone = scene.clone(true);
    // DoubleSide (reads even if single-sided) + collect tintable materials so
    // the colour picker can recolour the clock body.
    const tintable = prepGlbMaterials(clone, { styleId, doubleSide: true });
    let box = new THREE.Box3().setFromObject(clone);
    const size = new THREE.Vector3(); box.getSize(size);
    // The face lies in the X-Y plane with the thin axis = depth (Z). If the
    // model's smallest axis is X instead (authored facing sideways), rotate 90°
    // so the face points along Z toward the room.
    if (size.x < size.z && size.x < size.y) {
      clone.rotation.y = Math.PI / 2;
      clone.updateMatrixWorld(true);
      box = new THREE.Box3().setFromObject(clone);
      box.getSize(size);
    }
    const centre = new THREE.Vector3(); box.getCenter(centre);
    const s = Math.min(cSize / Math.max(size.x, 1e-6), cSize / Math.max(size.y, 1e-6));
    return {
      object: clone,
      tintable,
      scale: [s, s, s],
      // X centred, vertical centre at midY, back seated on the wall face.
      offset: [-centre.x * s, -centre.y * s + midY, -box.min.z * s + zBase],
    };
  }, [scene, cSize, midY, zBase, styleId]);
  useEffect(() => { applyGlbTint(tintable, tint); }, [tintable, tint]);
  useEffect(() => () => { tintable.forEach(({ mat }) => mat.dispose()); }, [tintable]);
  return (
    <group scale={scale} position={offset}>
      <primitive object={object} />
    </group>
  );
}

// Fallback disc while the clock GLB streams in (so the wall isn't bare).
function GLBClockFace({ cSize, midY, zBase, color = '#f2efe6' }) {
  return (
    <group position={[0, midY, zBase + 0.02]}>
      <mesh rotation={[Math.PI / 2, 0, 0]}>
        <cylinderGeometry args={[cSize / 2, cSize / 2, 0.04, 24]} />
        <meshStandardMaterial color={color} roughness={0.6} />
      </mesh>
    </group>
  );
}

// Probe + Suspense + counter wrapper (mirrors GLBDoor/GLBWindow). Exported so
// the whole-house view renders clocks identically to the room view.
export function GLBClock({ styleId, cSize, midY, zBase = 0, color, tint }) {
  const url = GLB_CLOCKS[styleId] || GLB_CLOCKS[GLB_CLOCK_FALLBACK];
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);
  const glbLoad = useContext(GlbLoadingContext);
  const doneRef = useRef(false);
  const finish = () => { if (!doneRef.current) { doneRef.current = true; glbLoad && glbLoad.done(); } };
  useEffect(() => {
    if (!url) return;
    doneRef.current = false;
    glbLoad && glbLoad.start();
    let live = true;
    setReady(false); setFailed(false);
    const safety = setTimeout(finish, 25000);
    probeGLB(url).then((ok) => { if (!live) return; if (ok) setReady(true); else { setFailed(true); finish(); } });
    return () => { live = false; clearTimeout(safety); finish(); };
  }, [url]);
  const face = <GLBClockFace cSize={cSize} midY={midY} zBase={zBase} color={color} />;
  const labelPos = [0, midY + cSize / 2 + 0.18, zBase];
  const loadingFb = <>{face}<GLBLoadingLabel position={labelPos} /></>;
  const failedFb  = <>{face}<GLBLoadingLabel position={labelPos} failed /></>;
  if (!url) return face;
  if (failed) return failedFb;
  if (!ready) return loadingFb;
  return (
    <GLBErrorBoundary fallback={failedFb} onError={finish}>
      <Suspense fallback={loadingFb}>
        <GLBClockModel url={glbSrc(url)} cSize={cSize} midY={midY} zBase={zBase} styleId={styleId} tint={tint} onLoaded={finish} />
      </Suspense>
    </GLBErrorBoundary>
  );
}

// ─── FURNITURE 3D MESHES ─────────────────────────────────────────────────────

// Exported so the whole-house view (House3DView) can render furniture with the
// exact same meshes — GLB models from GitHub, procedural chairs, beds, etc. —
// giving it 1:1 parity with this per-room view. The house view passes absolute
// world positions via posXOverride/posZOverride and a huge roomW/roomD so the
// in-room culling below never drops a valid item.
export function FurnitureMesh3D({ item, room, roomW, roomD, posXOverride, posZOverride, timeOfDay }) {
  const ft = FURNITURE_TYPES.find(f => f.type === item.type);
  const color = item.color || ft?.color || '#a89070';
  // Procedural furniture always uses `color`. GLB models instead get a tint
  // only once the user has explicitly picked one (item.colored) — until then
  // they show their own authored materials (the default). null = "don't tint".
  const glbTint = item.colored ? (item.color || null) : null;
  const matRoughness = 0.85;
  const matMetalness = 0;

  // TV plays the uploaded clip in the morning/evening (off at night, like a
  // room nobody's in front of, and the room's early-morning bounds-skip below
  // must never gate this — it's a hook, so it has to run unconditionally on
  // every render regardless of item.type or the off-screen early return).
  const isTV = item.type === 'tv';
  const tvActive = isTV && (timeOfDay === 'morning' || timeOfDay === 'evening');
  const tvTexture = useVideoTexture(isTV ? TV_CLIP_URL : null, tvActive);

  const rot = item.rotation || 0;
  // displayW/H = what's visible on the 2D grid (swapped when rotated 90/270)
  const displayW = rot % 180 === 0 ? item.w : item.h;
  const displayH = rot % 180 === 0 ? item.h : item.w;

  // Centre of furniture in pixels, relative to room top-left corner
  const relPxX = (item.x + displayW / 2) - room.x * CELL;
  const relPxZ = (item.y + displayH / 2) - room.y * CELL;

  // 3D position (room is centred at origin); overrides supplied while dragging
  const posX = posXOverride !== undefined ? posXOverride : relPxX * PX_TO_M - roomW / 2;
  const posZ = posZOverride !== undefined ? posZOverride : relPxZ * PX_TO_M - roomD / 2;

  // Skip anything outside the room bounds (with small tolerance)
  if (posX < -roomW / 2 - 0.3 || posX > roomW / 2 + 0.3 ||
      posZ < -roomD / 2 - 0.3 || posZ > roomD / 2 + 0.3) return null;

  // Stored dimensions in metres (before rotation is applied in 3D)
  const fw = item.w * PX_TO_M;
  const fd = item.h * PX_TO_M;
  // Visual mesh slightly smaller than grid footprint
  const mw = fw * 0.86;
  const md = fd * 0.86;

  const yRot = -rot * Math.PI / 180;

  const st = item.style || '';

  // Every furniture shape below is hand-modeled with fixed internal
  // proportions (seat height, cushion thickness, etc.) driven only by mw/md —
  // none of them take a height parameter. Rather than threading a height prop
  // through 40+ shape functions, the whole rendered mesh is scaled vertically
  // as one group afterwards, using item.h3 (metres) against the furniture
  // type's reference height (FURNITURE_TYPES[].h3).
  const defaultH3 = ft?.h3 ?? 1;
  const heightScale = (item.h3 ?? defaultH3) / defaultH3;
  // Intended real-world height (metres) — used to cap tall GLB models (shelves).
  const glbH = item.h3 ?? defaultH3;

  const mesh = (() => {
  switch (item.type) {

    case 'sofa': {
      // GLB-backed styles. Returning them from here (rather than as a separate
      // render path) is what gives them parity: DraggableFurniture already
      // wraps FurnitureMesh3D with the raycast hitbox, selection outline,
      // drag handling and height scaling, so select / move / rotate / resize /
      // copy / delete all work with no extra wiring.
      if (GLB_MODELS[st]) {
        return <GLBFurniture styleId={st} mw={mw} md={md} posX={posX} posZ={posZ} yRot={yRot} color={glbTint} />;
      }

      // The procedural sofa styles (modern/classic/sectional/loveseat/
      // chesterfield/daybed) have been removed in favour of the GLB catalogue.
      // Rooms saved before that still carry those style ids, so rather than
      // rendering nothing we fall those legacy items back to a real GLB sofa —
      // an old project opens with its sofas intact instead of empty gaps.
      return <GLBFurniture styleId={GLB_SOFA_FALLBACK} mw={mw} md={md} posX={posX} posZ={posZ} yRot={yRot} color={glbTint} />;
    }

    case 'armchair': {
      // Armchairs are GLB-only now — the procedural styles (modern/wingback/
      // barrel/club/ottoman/tulip/beanbag) were removed. Rooms saved earlier
      // still carry those style ids, so they fall back to a real GLB armchair
      // rather than rendering an empty gap.
      if (GLB_MODELS[st]) {
        return <GLBFurniture styleId={st} mw={mw} md={md} posX={posX} posZ={posZ} yRot={yRot} color={glbTint} />;
      }
      return <GLBFurniture styleId={GLB_ARMCHAIR_FALLBACK} mw={mw} md={md} posX={posX} posZ={posZ} yRot={yRot} color={glbTint} />;
    }

    case 'table': {
      // Tables are GLB-only now — the procedural styles (rectangular/round/
      // industrial/oval/farmhouse/marble) were removed. Rooms saved earlier
      // still carry those style ids, so they fall back to a real GLB table
      // rather than rendering an empty gap.
      if (GLB_MODELS[st]) {
        return <GLBFurniture styleId={st} mw={mw} md={md} posX={posX} posZ={posZ} yRot={yRot} color={glbTint} />;
      }
      return <GLBFurniture styleId={GLB_TABLE_FALLBACK} mw={mw} md={md} posX={posX} posZ={posZ} yRot={yRot} color={glbTint} />;
    }

    case 'chair': {
      // Chairs are GLB-only now — the procedural styles (dining/eames/windsor/
      // tulip/thonet/ghost) were removed in favour of the GLB catalogue. Rooms
      // saved earlier still carry those style ids, so they fall back to a real
      // GLB chair rather than rendering an empty gap.
      if (GLB_MODELS[st]) {
        return <GLBFurniture styleId={st} mw={mw} md={md} posX={posX} posZ={posZ} yRot={yRot} color={glbTint} />;
      }
      return <GLBFurniture styleId={GLB_CHAIR_FALLBACK} mw={mw} md={md} posX={posX} posZ={posZ} yRot={yRot} color={glbTint} />;
    }

    case 'bed': {
      // Beds are GLB-only now — the procedural styles (classic/platform/canopy/
      // sleigh/storage/metalframe) were removed in favour of the GLB catalogue.
      // Rooms saved earlier still carry those style ids, so they fall back to a
      // real GLB bed rather than rendering an empty gap.
      if (GLB_MODELS[st]) {
        return <GLBFurniture styleId={st} mw={mw} md={md} posX={posX} posZ={posZ} yRot={yRot} color={glbTint} />;
      }
      return <GLBFurniture styleId={GLB_BED_FALLBACK} mw={mw} md={md} posX={posX} posZ={posZ} yRot={yRot} color={glbTint} />;
    }

    case 'desk': {
      // Desks are GLB-only now — the procedural styles (simple/executive/
      // standing/secretary/pipe/glasstop) were removed in favour of the GLB
      // catalogue. Rooms saved earlier still carry those style ids, so they
      // fall back to a real GLB desk rather than rendering an empty gap.
      if (GLB_MODELS[st]) {
        return <GLBFurniture styleId={st} mw={mw} md={md} posX={posX} posZ={posZ} yRot={yRot} color={glbTint} />;
      }
      return <GLBFurniture styleId={GLB_DESK_FALLBACK} mw={mw} md={md} posX={posX} posZ={posZ} yRot={yRot} color={glbTint} />;
    }

    case 'tv': {
      const screenW = mw, screenH = mw * 0.56;
      if (st === 'wallmount') {
        const screenY = 1.10 + screenH / 2;
        return (
          <group position={[posX, 0, posZ]} rotation={[0, yRot, 0]}>
            {/* Wall mount bracket */}
            <mesh position={[0, screenY, -md*0.44]}>
              <boxGeometry args={[mw*0.36, screenH*0.38, 0.028]} />
              <meshStandardMaterial color="#606068" roughness={0.22} metalness={0.74} />
            </mesh>
            {/* Screen housing flat to wall */}
            <mesh position={[0, screenY, -md*0.46]} castShadow>
              <boxGeometry args={[screenW, screenH, 0.038]} />
              <meshStandardMaterial color="#0a0a10" roughness={0.18} metalness={0.38} />
            </mesh>
            <mesh position={[0, screenY, -md*0.44+0.022]}>
              <boxGeometry args={[screenW*0.97, screenH*0.97, 0.006]} />
              {tvActive && tvTexture
                ? <meshBasicMaterial map={tvTexture} toneMapped={false} />
                : <meshStandardMaterial color="#02020a" roughness={0.04} emissive="#04041a" emissiveIntensity={0.36} />}
            </mesh>
          </group>
        );
      }
      if (st === 'unit') {
        const unitH = 0.52, screenY = unitH + screenH/2 + 0.04;
        return (
          <group position={[posX, 0, posZ]} rotation={[0, yRot, 0]}>
            {/* TV unit cabinet */}
            <mesh position={[0, unitH/2, 0]} castShadow receiveShadow>
              <boxGeometry args={[mw, unitH, md*0.82]} />
              <meshStandardMaterial color={color} roughness={0.52} />
            </mesh>
            {/* Unit legs */}
            {[[-mw/2+0.06,0],[mw/2-0.06,0]].map(([lx,lz],i) => (
              <mesh key={i} position={[lx, 0.036, lz]}>
                <boxGeometry args={[0.06, 0.072, md*0.72]} />
                <meshStandardMaterial color={shadeColor(color,-0.18)} roughness={0.32} metalness={0.08} />
              </mesh>
            ))}
            {/* Door panels */}
            {[-mw*0.27, mw*0.27].map((dx,i) => (
              <mesh key={i} position={[dx, unitH*0.52, md*0.42]}>
                <boxGeometry args={[mw*0.42, unitH*0.80, 0.018]} />
                <meshStandardMaterial color={shadeColor(color,-0.10)} roughness={0.42} />
              </mesh>
            ))}
            {/* Center open display section */}
            <mesh position={[0, unitH*0.30, md*0.42]}>
              <boxGeometry args={[mw*0.14, unitH*0.44, 0.014]} />
              <meshStandardMaterial color={shadeColor(color,-0.22)} roughness={0.55} />
            </mesh>
            {/* Screen on top */}
            <mesh position={[0, screenY, -md*0.06]} castShadow>
              <boxGeometry args={[screenW, screenH, 0.044]} />
              <meshStandardMaterial color="#0c0c12" roughness={0.2} metalness={0.35} />
            </mesh>
            <mesh position={[0, screenY, -md*0.06+0.024]}>
              <boxGeometry args={[screenW*0.97, screenH*0.97, 0.006]} />
              {tvActive && tvTexture
                ? <meshBasicMaterial map={tvTexture} toneMapped={false} />
                : <meshStandardMaterial color="#04040a" roughness={0.04} emissive="#06061a" emissiveIntensity={0.32} />}
            </mesh>
          </group>
        );
      }
      if (st === 'projector') {
        const screenY = 1.60, projH = 2.30;
        return (
          <group position={[posX, 0, posZ]} rotation={[0, yRot, 0]}>
            {/* Ceiling-mounted projector body */}
            <mesh position={[0, projH, 0]} castShadow>
              <boxGeometry args={[mw*0.24, 0.12, md*0.36]} />
              <meshStandardMaterial color="#202028" roughness={0.30} metalness={0.44} />
            </mesh>
            {/* Projector lens cone */}
            <mesh position={[0, projH-0.04, md*0.22]}>
              <cylinderGeometry args={[0.048, 0.038, 0.12, 12]} rotation={[Math.PI/2,0,0]} />
              <meshStandardMaterial color="#101014" roughness={0.20} metalness={0.60} />
            </mesh>
            {/* Lens glass */}
            <mesh position={[0, projH-0.04, md*0.26]}>
              <cylinderGeometry args={[0.038, 0.038, 0.014, 12]} rotation={[Math.PI/2,0,0]} />
              <meshStandardMaterial color="#2040a0" roughness={0.06} metalness={0.12} transparent opacity={0.7} emissive="#1020a0" emissiveIntensity={0.8} />
            </mesh>
            {/* Projector mount bracket */}
            <mesh position={[0, projH+0.07, 0]}>
              <boxGeometry args={[mw*0.14, 0.14, 0.06]} />
              <meshStandardMaterial color="#a0a0a8" roughness={0.20} metalness={0.72} />
            </mesh>
            {/* Projection screen — roll-down on wall */}
            <mesh position={[0, screenY, -md*0.48]} castShadow receiveShadow>
              <boxGeometry args={[screenW*1.1, screenH*1.0, 0.018]} />
              {tvActive && tvTexture
                ? <meshBasicMaterial map={tvTexture} toneMapped={false} />
                : <meshStandardMaterial color="#f2f2f0" roughness={0.92} />}
            </mesh>
            {/* Screen border */}
            <mesh position={[0, screenY, -md*0.47]}>
              <boxGeometry args={[screenW*1.12, screenH*1.02, 0.010]} />
              <meshStandardMaterial color="#1a1a1a" roughness={0.60} />
            </mesh>
            {/* Screen roller housing */}
            <mesh position={[0, screenY+screenH*0.50+0.04, -md*0.46]}>
              <cylinderGeometry args={[0.034, 0.034, screenW*1.08, 10]} rotation={[0,0,Math.PI/2]} />
              <meshStandardMaterial color="#303030" roughness={0.44} metalness={0.30} />
            </mesh>
          </group>
        );
      }
      if (st === 'crt') {
        const screenY = 0.64 + screenH*0.6;
        return (
          <group position={[posX, 0, posZ]} rotation={[0, yRot, 0]}>
            {/* Chunky deep CRT housing */}
            <mesh position={[0, screenY, 0]} castShadow receiveShadow>
              <boxGeometry args={[screenW*0.94, screenH*1.1, md*0.82]} />
              <meshStandardMaterial color={color || '#c8c4b8'} roughness={0.62} metalness={0.04} />
            </mesh>
            {/* Beveled screen face (recessed) */}
            <mesh position={[0, screenY+screenH*0.05, md*0.42]}>
              <boxGeometry args={[screenW*0.74, screenH*0.72, 0.026]} />
              <meshStandardMaterial color="#101010" roughness={0.28} metalness={0.18} />
            </mesh>
            {/* Actual screen (glowing) */}
            <mesh position={[0, screenY+screenH*0.05, md*0.43]}>
              <boxGeometry args={[screenW*0.68, screenH*0.66, 0.008]} />
              {tvActive && tvTexture
                ? <meshBasicMaterial map={tvTexture} toneMapped={false} />
                : <meshStandardMaterial color="#000a06" roughness={0.04} emissive="#004418" emissiveIntensity={1.2} />}
            </mesh>
            {/* Control panel strip */}
            <mesh position={[mw*0.28, screenY-screenH*0.25, md*0.43]}>
              <boxGeometry args={[mw*0.16, screenH*0.22, 0.022]} />
              <meshStandardMaterial color={shadeColor(color || '#c8c4b8', -0.14)} roughness={0.64} />
            </mesh>
            {/* Power knob */}
            <mesh position={[mw*0.30, screenY-screenH*0.18, md*0.45]}>
              <cylinderGeometry args={[0.018, 0.018, 0.028, 8]} rotation={[Math.PI/2,0,0]} />
              <meshStandardMaterial color="#282828" roughness={0.40} metalness={0.20} />
            </mesh>
            {/* Stand legs */}
            {[-mw*0.28, mw*0.28].map((lx,i) => (
              <mesh key={i} position={[lx, 0.08, 0]}>
                <boxGeometry args={[0.06, 0.16, md*0.52]} />
                <meshStandardMaterial color={shadeColor(color || '#c8c4b8', -0.18)} roughness={0.56} />
              </mesh>
            ))}
          </group>
        );
      }
      if (st === 'ultrawide') {
        const uwW = mw*1.05, uwH = mw*0.38, screenY = 0.80 + uwH/2;
        return (
          <group position={[posX, 0, posZ]} rotation={[0, yRot, 0]}>
            {/* Ultrawide curved screen housing */}
            <mesh position={[0, screenY, -md*0.04]} castShadow>
              <boxGeometry args={[uwW, uwH, 0.052]} />
              <meshStandardMaterial color="#0c0c10" roughness={0.20} metalness={0.38} />
            </mesh>
            {/* Emissive screen area */}
            <mesh position={[0, screenY, -md*0.04+0.028]}>
              <boxGeometry args={[uwW*0.98, uwH*0.97, 0.006]} />
              {tvActive && tvTexture
                ? <meshBasicMaterial map={tvTexture} toneMapped={false} />
                : <meshStandardMaterial color="#020208" roughness={0.04} emissive="#060612" emissiveIntensity={0.40} />}
            </mesh>
            {/* Curved bottom bezel highlight */}
            <mesh position={[0, screenY-uwH*0.48, -md*0.04]}>
              <boxGeometry args={[uwW*0.26, 0.022, 0.054]} />
              <meshStandardMaterial color="#a0a0b0" roughness={0.18} metalness={0.82} />
            </mesh>
            {/* Center single stand leg */}
            <mesh position={[0, 0.40, -md*0.04]}>
              <boxGeometry args={[0.06, 0.80, 0.06]} />
              <meshStandardMaterial color="#888898" roughness={0.22} metalness={0.76} />
            </mesh>
            {/* Stand foot */}
            <mesh position={[0, 0.032, -md*0.04]}>
              <boxGeometry args={[mw*0.56, 0.064, md*0.52]} />
              <meshStandardMaterial color="#888898" roughness={0.24} metalness={0.74} />
            </mesh>
          </group>
        );
      }
      const screenY = 0.74 + screenH / 2;
      return (
        <group position={[posX, 0, posZ]} rotation={[0, yRot, 0]}>
          <mesh position={[0, 0.038, 0]} receiveShadow>
            <boxGeometry args={[mw * 0.44, 0.076, md * 0.72]} />
            <meshStandardMaterial color="#141418" roughness={0.32} metalness={0.18} />
          </mesh>
          <mesh position={[0, 0.42, -md*0.04]}>
            <boxGeometry args={[0.044, 0.66, 0.036]} />
            <meshStandardMaterial color="#1e1e26" roughness={0.26} metalness={0.58} />
          </mesh>
          <mesh position={[0, screenY, -md*0.04]} castShadow>
            <boxGeometry args={[screenW, screenH, 0.048]} />
            <meshStandardMaterial color="#0c0c12" roughness={0.2} metalness={0.35} />
          </mesh>
          <mesh position={[0, screenY, -md*0.04 + 0.026]}>
            <boxGeometry args={[screenW * 0.97, screenH * 0.97, 0.006]} />
            {tvActive && tvTexture
              ? <meshBasicMaterial map={tvTexture} toneMapped={false} />
              : <meshStandardMaterial color="#04040a" roughness={0.04} emissive="#06061a" emissiveIntensity={0.32} />}
          </mesh>
        </group>
      );
    }

    case 'shelf': {
      // Bookshelves are GLB-only now — the procedural styles (bookcase/floating/
      // ladder/kallax/cornerl/industrialpipe) were removed in favour of the GLB
      // catalogue. Rooms saved earlier still carry those style ids, so they fall
      // back to a real GLB bookshelf rather than rendering an empty gap.
      // mh caps the height (bookshelves are tall — see GLBModel's fit) so they
      // don't tower above the walls when placed in a wide grid footprint.
      if (GLB_MODELS[st]) {
        return <GLBFurniture styleId={st} mw={mw} md={md} mh={glbH} posX={posX} posZ={posZ} yRot={yRot} color={glbTint} />;
      }
      return <GLBFurniture styleId={GLB_SHELF_FALLBACK} mw={mw} md={md} mh={glbH} posX={posX} posZ={posZ} yRot={yRot} color={glbTint} />;
    }

    case 'wardrobe': {
      // Storage units (type stays 'wardrobe') are GLB-only now — the procedural
      // styles (hinged/sliding/modern/armoire/mirrored/openrail) were removed in
      // favour of the GLB catalogue. Rooms saved earlier still carry those style
      // ids, so they fall back to a real GLB storage unit rather than an empty
      // gap. mh caps the height (these are tall) so they don't tower — see the
      // bookshelf note in GLBModel's fit.
      if (GLB_MODELS[st]) {
        return <GLBFurniture styleId={st} mw={mw} md={md} mh={glbH} posX={posX} posZ={posZ} yRot={yRot} color={glbTint} />;
      }
      return <GLBFurniture styleId={GLB_STORAGE_FALLBACK} mw={mw} md={md} mh={glbH} posX={posX} posZ={posZ} yRot={yRot} color={glbTint} />;
    }

    case 'bathtub': {
      // Bathtubs are GLB-only now — the procedural styles (builtin/freestanding/
      // corner/japanese/copper/extrawide) were removed in favour of the GLB
      // catalogue. Rooms saved earlier still carry those style ids, so they fall
      // back to a real GLB bathtub rather than rendering an empty gap.
      if (GLB_MODELS[st]) {
        return <GLBFurniture styleId={st} mw={mw} md={md} posX={posX} posZ={posZ} yRot={yRot} color={glbTint} />;
      }
      return <GLBFurniture styleId={GLB_BATH_FALLBACK} mw={mw} md={md} posX={posX} posZ={posZ} yRot={yRot} color={glbTint} />;
    }

    case 'toilet': {
      // Toilets are GLB-only now — procedural styles removed. Legacy style ids
      // fall back to a real GLB toilet.
      if (GLB_MODELS[st]) {
        return <GLBFurniture styleId={st} mw={mw} md={md} posX={posX} posZ={posZ} yRot={yRot} color={glbTint} />;
      }
      return <GLBFurniture styleId={GLB_TOILET_FALLBACK} mw={mw} md={md} posX={posX} posZ={posZ} yRot={yRot} color={glbTint} />;
    }

    case 'sink': {
      // Sinks are GLB-only now — procedural styles removed. Legacy style ids
      // fall back to a real GLB sink.
      if (GLB_MODELS[st]) {
        return <GLBFurniture styleId={st} mw={mw} md={md} posX={posX} posZ={posZ} yRot={yRot} color={glbTint} />;
      }
      return <GLBFurniture styleId={GLB_SINK_FALLBACK} mw={mw} md={md} posX={posX} posZ={posZ} yRot={yRot} color={glbTint} />;
    }

    case 'lamp': {
      // Floor lamps are GLB-only now. Legacy style ids fall back to a real GLB
      // floor lamp. mh caps the height (lamps are tall/narrow) so a lamp dropped
      // in a wide grid slot doesn't tower. A warm point light at the top keeps
      // the lamp lighting the room like the old procedural one did.
      const lampStyle = GLB_MODELS[st] ? st : GLB_LAMP_FALLBACK;
      return (
        <>
          <GLBFurniture styleId={lampStyle} mw={mw} md={md} mh={glbH} posX={posX} posZ={posZ} yRot={yRot} color={glbTint} />
          <pointLight position={[posX, glbH * 0.88, posZ]} intensity={2.2} color="#fff0c0" distance={3.5} decay={2} />
        </>
      );
    }

    case 'plant': {
      // Plants are GLB-only now. Legacy style ids fall back to a real GLB plant.
      // mh is a generous cap (2.4 m) — plants vary in height, so this keeps tall
      // plants tall while stopping a narrow-tall model from towering through the
      // ceiling when placed in a wide grid slot.
      if (GLB_MODELS[st]) {
        return <GLBFurniture styleId={st} mw={mw} md={md} mh={2.4} posX={posX} posZ={posZ} yRot={yRot} color={glbTint} />;
      }
      return <GLBFurniture styleId={GLB_PLANT_FALLBACK} mw={mw} md={md} mh={2.4} posX={posX} posZ={posZ} yRot={yRot} color={glbTint} />;
    }

    case 'rug': {
      if (st === 'round') {
        const r = Math.min(mw,md)*0.5;
        return (
          <group position={[posX, 0, posZ]} rotation={[0, yRot, 0]}>
            <mesh position={[0, 0.011, 0]} receiveShadow rotation={[Math.PI/2,0,0]}>
              <circleGeometry args={[r, 32]} />
              <meshStandardMaterial color={color} roughness={0.97} />
            </mesh>
            <mesh position={[0, 0.021, 0]} receiveShadow rotation={[Math.PI/2,0,0]}>
              <ringGeometry args={[r*0.72, r*0.90, 32]} />
              <meshStandardMaterial color={shadeColor(color,-0.20)} roughness={0.97} />
            </mesh>
            <mesh position={[0, 0.022, 0]} receiveShadow rotation={[Math.PI/2,0,0]}>
              <circleGeometry args={[r*0.60, 28]} />
              <meshStandardMaterial color={shadeColor(color,0.04)} roughness={0.97} />
            </mesh>
          </group>
        );
      }
      if (st === 'persian') {
        const dark = shadeColor(color,-0.28), accent = shadeColor(color,0.14);
        return (
          <group position={[posX, 0, posZ]} rotation={[0, yRot, 0]}>
            <mesh position={[0, 0.010, 0]} receiveShadow>
              <boxGeometry args={[mw, 0.020, md]} />
              <meshStandardMaterial color={color} roughness={0.97} />
            </mesh>
            {/* Outer border */}
            <mesh position={[0, 0.021, 0]}>
              <boxGeometry args={[mw*0.93, 0.012, md*0.93]} />
              <meshStandardMaterial color={dark} roughness={0.97} />
            </mesh>
            {/* Inner border */}
            <mesh position={[0, 0.022, 0]}>
              <boxGeometry args={[mw*0.83, 0.012, md*0.83]} />
              <meshStandardMaterial color={shadeColor(color,-0.08)} roughness={0.97} />
            </mesh>
            {/* Field */}
            <mesh position={[0, 0.023, 0]}>
              <boxGeometry args={[mw*0.73, 0.012, md*0.73]} />
              <meshStandardMaterial color={accent} roughness={0.97} />
            </mesh>
            {/* Medallion centre */}
            <mesh position={[0, 0.024, 0]} rotation={[Math.PI/2,0,0]}>
              <circleGeometry args={[Math.min(mw,md)*0.14, 8]} />
              <meshStandardMaterial color={dark} roughness={0.97} />
            </mesh>
          </group>
        );
      }
      if (st === 'shaggy') {
        const dark = shadeColor(color, -0.18), lite = shadeColor(color, 0.10);
        return (
          <group position={[posX, 0, posZ]} rotation={[0, yRot, 0]}>
            {/* Thick shaggy pile base */}
            <mesh position={[0, 0.022, 0]} receiveShadow>
              <boxGeometry args={[mw, 0.044, md]} />
              <meshStandardMaterial color={color} roughness={0.99} />
            </mesh>
            {/* Textured strands — 5×5 raised loops */}
            {Array.from({length:5},(_,i) => Array.from({length:5},(_,j) => (
              <mesh key={`s${i}${j}`} position={[
                -mw*0.36 + i*mw*0.18,
                0.056,
                -md*0.36 + j*md*0.18
              ]}>
                <cylinderGeometry args={[mw*0.028, mw*0.022, 0.030, 5]} />
                <meshStandardMaterial color={j%2===0?color:lite} roughness={0.99} />
              </mesh>
            )))}
            {/* Border (pile slightly denser) */}
            <mesh position={[0, 0.044, 0]}>
              <boxGeometry args={[mw*0.96, 0.010, md*0.96]} />
              <meshStandardMaterial color={dark} roughness={0.99} />
            </mesh>
          </group>
        );
      }
      if (st === 'kilim') {
        const c1 = color, c2 = shadeColor(color,-0.22), c3 = shadeColor(color,0.16);
        return (
          <group position={[posX, 0, posZ]} rotation={[0, yRot, 0]}>
            {/* Base weave */}
            <mesh position={[0, 0.010, 0]} receiveShadow>
              <boxGeometry args={[mw, 0.020, md]} />
              <meshStandardMaterial color={c1} roughness={0.96} />
            </mesh>
            {/* Geometric stripe bands (5 alternating) */}
            {[0.14,0.28,0.42,0.56,0.70,0.84].map((t,i) => (
              <mesh key={i} position={[0, 0.021, md*(t-0.5)]}>
                <boxGeometry args={[mw*0.94, 0.012, md*0.08]} />
                <meshStandardMaterial color={i%3===0?c2:i%3===1?c3:c1} roughness={0.96} />
              </mesh>
            ))}
            {/* Diamond medallions (3 across) */}
            {[-mw*0.28, 0, mw*0.28].map((dx,i) => (
              <mesh key={i} position={[dx, 0.022, 0]} rotation={[0, Math.PI/4, 0]}>
                <boxGeometry args={[md*0.18, 0.014, md*0.18]} />
                <meshStandardMaterial color={c2} roughness={0.96} />
              </mesh>
            ))}
            {/* Border stripe */}
            <mesh position={[0, 0.023, 0]}>
              <boxGeometry args={[mw*0.98, 0.012, md*0.98]} />
              <meshStandardMaterial color={c2} roughness={0.96} />
            </mesh>
            <mesh position={[0, 0.024, 0]}>
              <boxGeometry args={[mw*0.92, 0.010, md*0.92]} />
              <meshStandardMaterial color={c1} roughness={0.96} />
            </mesh>
          </group>
        );
      }
      if (st === 'runner') {
        const dark = shadeColor(color,-0.20), accent = shadeColor(color,0.12);
        return (
          <group position={[posX, 0, posZ]} rotation={[0, yRot, 0]}>
            {/* Long narrow runner body */}
            <mesh position={[0, 0.011, 0]} receiveShadow>
              <boxGeometry args={[mw, 0.022, md]} />
              <meshStandardMaterial color={color} roughness={0.96} />
            </mesh>
            {/* Outer border */}
            <mesh position={[0, 0.022, 0]}>
              <boxGeometry args={[mw*0.94, 0.012, md*0.94]} />
              <meshStandardMaterial color={dark} roughness={0.96} />
            </mesh>
            {/* Inner field */}
            <mesh position={[0, 0.023, 0]}>
              <boxGeometry args={[mw*0.82, 0.012, md*0.82]} />
              <meshStandardMaterial color={accent} roughness={0.96} />
            </mesh>
            {/* Repeating motif strips (4 diamonds along length) */}
            {[0.22,0.38,0.55,0.72,0.88].map((t,i) => (
              <mesh key={i} position={[0, 0.025, md*(t-0.55)]}>
                <boxGeometry args={[mw*0.48, 0.012, md*0.10]} />
                <meshStandardMaterial color={dark} roughness={0.96} />
              </mesh>
            ))}
            {/* Fringe tassels at ends (left and right) */}
            {[-1,1].map((side,si) => Array.from({length:6},(_,i) => (
              <mesh key={`fr${si}${i}`} position={[side*(mw/2+0.016), 0.008, md*(-0.40+i*0.16)]}>
                <cylinderGeometry args={[0.006, 0.004, 0.044, 4]} />
                <meshStandardMaterial color={shadeColor(color,-0.10)} roughness={0.94} />
              </mesh>
            )))}
          </group>
        );
      }
      return (
        <group position={[posX, 0, posZ]} rotation={[0, yRot, 0]}>
          <mesh position={[0, 0.011, 0]} receiveShadow>
            <boxGeometry args={[mw, 0.022, md]} />
            <meshStandardMaterial color={color} roughness={0.97} />
          </mesh>
          <mesh position={[0, 0.024, 0]}>
            <boxGeometry args={[mw*0.9, 0.014, md*0.9]} />
            <meshStandardMaterial color={shadeColor(color, -0.22)} roughness={0.97} />
          </mesh>
          <mesh position={[0, 0.025, 0]}>
            <boxGeometry args={[mw*0.76, 0.014, md*0.76]} />
            <meshStandardMaterial color={color} roughness={0.97} />
          </mesh>
        </group>
      );
    }

    default: {
      return (
        <group position={[posX, 0, posZ]} rotation={[0, yRot, 0]}>
          <mesh position={[0, 0.28, 0]} castShadow receiveShadow>
            <boxGeometry args={[mw, 0.55, md]} />
            <meshStandardMaterial color={color} roughness={0.8} />
          </mesh>
        </group>
      );
    }
  }
  })();

  if (!mesh) return null;
  if (heightScale === 1) return mesh;
  // Scaling happens around each shape's own local y=0 (the floor), since
  // every case above builds its geometry upward from there — so a plain
  // Y-only scale on the whole subtree stretches/shrinks height in place
  // without shifting the item off the floor or touching its footprint.
  return <group scale={[1, heightScale, 1]}>{mesh}</group>;
}

// ─── LIGHTING 3D MESHES (ceiling-mounted fixtures) ───────────────────────────
// Free-placed like furniture but the group's Y is pinned to the ceiling (see
// LightingMesh3D/DraggableLighting below) — every design hangs its geometry
// downward from local y=0 (the ceiling attachment point) via negative y offsets.

const LIGHTING_COLOR_PRESETS = [
  { label: 'Brushed Brass',   color: '#c9a227' },
  { label: 'Matte Black',     color: '#232323' },
  { label: 'Polished Chrome', color: '#c8ccd0' },
  { label: 'Warm Bronze',     color: '#7a5230' },
  { label: 'Copper',          color: '#b5652f' },
  { label: 'White',           color: '#f2ede2' },
  { label: 'Graphite',        color: '#3a3632' },
  { label: 'Rose Gold',       color: '#c99a86' },
  { label: 'Antique Gold',    color: '#9c7a2e' },
  { label: 'Gunmetal',        color: '#4a4e54' },
  { label: 'Ivory Glass',     color: '#efe9da' },
  { label: 'Smoked Glass',    color: '#565048' },
];

// Ceiling fixtures are GLB models now. Chandeliers `chandelier1`…chandelier18,
// ceiling lamps `ceilingLamp1`…ceilingLamp12. The old procedural fixtures were
// removed; a legacy/unknown style id just falls back to the first GLB model.
// (Pendant lights were removed entirely.)
export const LIGHTING_STYLES = {
  chandelier: Array.from({ length: 18 }, (_, i) => ({ id: `chandelier${i + 1}`, label: `Chandelier ${i + 1}` })),
  ceiling_lamp: Array.from({ length: 12 }, (_, i) => ({ id: `ceilingLamp${i + 1}`, label: `Ceiling Lamp ${i + 1}` })),
};

const BULB_GLOW  = '#fff2c8';
const WARM_LIGHT = '#ffd9a0';

function GlowBulb({ position = [0, 0, 0], radius = 0.026, litBoost = 1 }) {
  return (
    <mesh position={position}>
      <sphereGeometry args={[radius, 10, 8]} />
      <meshStandardMaterial color={BULB_GLOW} emissive="#ffcc55" emissiveIntensity={4.2 * litBoost} roughness={1} />
    </mesh>
  );
}

// (The procedural chandelier/ceiling-lamp fixtures and the DownSpotLight
// helper were removed — these are GLB models now; see GLBCeilingFixture below.)

// ─── GLB CEILING FIXTURE ─────────────────────────────────────────────────────
// Chandeliers and ceiling lamps are GLB models now. The fixture hangs from the
// ceiling: its TOP seats at the ceiling plane (the group sits at posY) and the
// body drops into -y. Footprint (X,Z) fits mw × md; the vertical drop is capped
// at dropMax so a tall model can't spear the floor. A real pointLight rides with
// it so the room is still lit — litBoost/litShadow follow the time of day
// exactly like the old procedural fixtures — and it keeps lighting even while
// the model streams in or if it fails, so a room is never left dark.
function GLBCeilingFixtureModel({ url, mw, md, dropMax, styleId, tint, onLoaded }) {
  const { scene } = useGLTF(url);
  useEffect(() => { onLoaded && onLoaded(); }, [onLoaded]);
  const { object, scale, offset, tintable } = useMemo(() => {
    const clone = scene.clone(true);
    // DoubleSide + tintAll: the whole fixture body recolours (the metal is the
    // fixture), keeping its metalness; glass and glowing bulbs are left alone.
    const tintable = prepGlbMaterials(clone, { styleId, doubleSide: true, tintAll: true });
    const box = new THREE.Box3().setFromObject(clone);
    const size = new THREE.Vector3(); box.getSize(size);
    const centre = new THREE.Vector3(); box.getCenter(centre);
    let s = Math.min(mw / Math.max(size.x, 1e-6), md / Math.max(size.z, 1e-6));
    if (size.y * s > dropMax) s = dropMax / Math.max(size.y, 1e-6);
    return {
      object: clone,
      tintable,
      scale: [s, s, s],
      // Top seated at the ceiling (y=0), body hanging down; X/Z centred.
      offset: [-centre.x * s, -box.max.y * s, -centre.z * s],
    };
  }, [scene, mw, md, dropMax, styleId]);
  useEffect(() => { applyGlbTint(tintable, tint); }, [tintable, tint]);
  useEffect(() => () => { tintable.forEach(({ mat }) => mat.dispose()); }, [tintable]);
  return (
    <group scale={scale} position={offset}>
      <primitive object={object} />
    </group>
  );
}

function GLBCeilingFixture({ styleId, catalogue, fallbackId, dropMax, mw, md, posX, posY, posZ, yRot, litBoost, tint }) {
  const url = catalogue[styleId] || catalogue[fallbackId];
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);
  const glbLoad = useContext(GlbLoadingContext);
  const doneRef = useRef(false);
  const finish = () => { if (!doneRef.current) { doneRef.current = true; glbLoad && glbLoad.done(); } };
  useEffect(() => {
    if (!url) return;
    doneRef.current = false;
    glbLoad && glbLoad.start();
    let live = true;
    setReady(false); setFailed(false);
    const safety = setTimeout(finish, 25000);
    probeGLB(url).then((ok) => { if (!live) return; if (ok) setReady(true); else { setFailed(true); finish(); } });
    return () => { live = false; clearTimeout(safety); finish(); };
  }, [url]);
  const glow = <GlowBulb position={[0, -Math.min(dropMax, 0.4), 0]} radius={0.03} litBoost={litBoost} />;
  const labelPos = [0, -Math.min(dropMax, 0.4) - 0.18, 0];
  const loadingFb = <>{glow}<GLBLoadingLabel position={labelPos} /></>;
  const failedFb  = <>{glow}<GLBLoadingLabel position={labelPos} failed /></>;
  let body;
  if (!url) body = glow;
  else if (failed) body = failedFb;
  else if (!ready) body = loadingFb;
  else body = (
    <GLBErrorBoundary fallback={failedFb} onError={finish}>
      <Suspense fallback={loadingFb}>
        <GLBCeilingFixtureModel url={glbSrc(url)} mw={mw} md={md} dropMax={dropMax} styleId={styleId} tint={tint} onLoaded={finish} />
      </Suspense>
    </GLBErrorBoundary>
  );
  return (
    <group position={[posX, posY, posZ]} rotation={[0, yRot, 0]}>
      {/* Room light rides with the fixture and stays on regardless of model
          load state, so a room is never dark. Shadow casting is intentionally
          OFF: a point light this close to furniture self-shadows (acne) with a
          512 cube map + no normal-bias, which showed up as hard round black
          blobs on the sofa/floor at night. The light still illuminates. */}
      <pointLight position={[0, -Math.min(dropMax, 0.42), 0]} intensity={3.6 * litBoost} color={WARM_LIGHT}
        distance={6.2} decay={2} />
      {body}
    </group>
  );
}

const LIGHTING_CEILING_OFFSET = 0.03; // metres below the ceiling surface, avoids z-fighting with crown moulding

function LightingMesh3D({ item, room, roomW, roomD, roomH, posXOverride, posZOverride, timeOfDay }) {
  const lt = LIGHTING_TYPES.find(l => l.type === item.type);
  const color = item.color || lt?.color || '#c9a227';

  const rot = item.rotation || 0;
  const displayW = rot % 180 === 0 ? item.w : item.h;
  const displayH = rot % 180 === 0 ? item.h : item.w;

  const relPxX = (item.x + displayW / 2) - room.x * CELL;
  const relPxZ = (item.y + displayH / 2) - room.y * CELL;

  const posX = posXOverride !== undefined ? posXOverride : relPxX * PX_TO_M - roomW / 2;
  const posZ = posZOverride !== undefined ? posZOverride : relPxZ * PX_TO_M - roomD / 2;

  if (posX < -roomW / 2 - 0.3 || posX > roomW / 2 + 0.3 ||
      posZ < -roomD / 2 - 0.3 || posZ > roomD / 2 + 0.3) return null;

  const mw = item.w * PX_TO_M * 0.9;
  const md = item.h * PX_TO_M * 0.9;
  const posY = roomH - LIGHTING_CEILING_OFFSET;
  const yRot = -rot * Math.PI / 180;
  const st = item.style || (LIGHTING_STYLES[item.type]?.[0]?.id) || '';

  // At night the fixtures are the primary light source in the room, so they
  // cast real shadows and burn a little brighter/more visible; by day the sun
  // dominates and fixture shadows would be redundant clutter, so they're off.
  const isNight = timeOfDay === 'night';
  const isMorning = timeOfDay === 'morning';
  const litShadow = isNight;
  // Fixtures are switched off in bright morning daylight (matches how nobody
  // leaves ceiling lights on with the sun up), normal brightness in the
  // evening, and boosted so they read as the room's dominant light at night.
  const litBoost = isMorning ? 0 : (isNight ? 1.45 : 1);

  const p = { mw, md, posX, posY, posZ, yRot, color, litShadow, litBoost };

  // Both fixture types are GLB now. GLBCeilingFixture falls back to the first
  // model in its catalogue if `st` is a legacy/unknown style id, so nothing
  // ever renders blank.
  switch (item.type) {
    case 'chandelier':
      return <GLBCeilingFixture styleId={st} catalogue={GLB_CHANDELIERS} fallbackId={GLB_CHANDELIER_FALLBACK} dropMax={0.95} {...p} tint={item.colored ? (item.color || null) : null} />;
    case 'ceiling_lamp':
      return <GLBCeilingFixture styleId={st} catalogue={GLB_CEILING_LAMPS} fallbackId={GLB_CEILING_LAMP_FALLBACK} dropMax={0.5} {...p} tint={item.colored ? (item.color || null) : null} />;
    default:
      return null;
  }
}

// ─── FURNITURE ↔ WALL DISTANCE DIMENSIONS (3D) ───────────────────────────────
// Floor-plane measurement lines from a selected/dragging furniture piece to
// each surrounding wall, with a metre label — the 3D counterpart of the 2D
// Builder's dimension guides. Distances update live while dragging.
const DIM3D_COLOR = '#2f7de1';
const DIM3D_Y = 0.03; // sit just above the floor so the lines aren't z-fought

// One dimension line (with end ticks) + its label. `from`/`to` are [x, z] on
// the floor; `axis` = 'x' (line runs along X) or 'z' — ticks go perpendicular.
function Dim3D({ from, to, axis }) {
  const dist = Math.hypot(to[0] - from[0], to[1] - from[1]);
  if (dist < 0.05) return null;
  const T = 0.09;
  const main = [[from[0], DIM3D_Y, from[1]], [to[0], DIM3D_Y, to[1]]];
  const tick = (p) => axis === 'x'
    ? [[p[0], DIM3D_Y, p[1] - T], [p[0], DIM3D_Y, p[1] + T]]
    : [[p[0] - T, DIM3D_Y, p[1]], [p[0] + T, DIM3D_Y, p[1]]];
  const mid = [(from[0] + to[0]) / 2, DIM3D_Y + 0.02, (from[1] + to[1]) / 2];
  return (
    <>
      <Line points={main} color={DIM3D_COLOR} lineWidth={1.5} dashed dashSize={0.14} gapSize={0.09} />
      <Line points={tick(from)} color={DIM3D_COLOR} lineWidth={1.5} />
      <Line points={tick(to)} color={DIM3D_COLOR} lineWidth={1.5} />
      <Html center position={mid} distanceFactor={7} zIndexRange={[15, 0]}>
        <div style={{
          background: 'rgba(255,255,255,0.94)', color: DIM3D_COLOR,
          border: `1px solid ${DIM3D_COLOR}`, padding: '1px 6px', borderRadius: 5,
          fontSize: 11, fontWeight: 700, fontFamily: 'system-ui, sans-serif',
          whiteSpace: 'nowrap', pointerEvents: 'none',
        }}>{Math.round(dist * 100) / 100}m</div>
      </Html>
    </>
  );
}

// World-space walls a furniture piece can measure against: the room's own
// boundary (real walls — handles compound/L-shaped rooms, not just a bounding
// box) plus any freestanding interior walls drawn through it. Each entry is
// { vertical, coord, lo, hi } — a vertical wall sits at X=coord spanning z
// [lo,hi]; a horizontal wall at Z=coord spanning x [lo,hi].
function buildDimWalls(room, roomW, roomD, wallSegments) {
  if (!room) return [];
  const out = [];
  const toWorld = (x1, y1, x2, y2) => {
    if (x1 === x2) { // vertical
      const coord = (x1 - room.x) * CELL_M - roomW / 2;
      const zA = (Math.min(y1, y2) - room.y) * CELL_M - roomD / 2;
      const zB = (Math.max(y1, y2) - room.y) * CELL_M - roomD / 2;
      out.push({ vertical: true, coord, lo: zA, hi: zB });
    } else if (y1 === y2) { // horizontal
      const coord = (y1 - room.y) * CELL_M - roomD / 2;
      const xA = (Math.min(x1, x2) - room.x) * CELL_M - roomW / 2;
      const xB = (Math.max(x1, x2) - room.x) * CELL_M - roomW / 2;
      out.push({ vertical: false, coord, lo: xA, hi: xB });
    }
  };
  // Room's real perimeter walls (compound-aware).
  computeRoomAutoWalls([room]).forEach(w => toWorld(w.x1, w.y1, w.x2, w.y2));
  // Interior freestanding walls that pass through the room.
  const rx1 = room.x, rx2 = room.x + room.w, ry1 = room.y, ry2 = room.y + room.h;
  (wallSegments || []).forEach(seg => {
    const sx1 = Math.min(seg.x1, seg.x2), sx2 = Math.max(seg.x1, seg.x2);
    const sy1 = Math.min(seg.y1, seg.y2), sy2 = Math.max(seg.y1, seg.y2);
    if (sx1 < rx2 && sx2 > rx1 && sy1 < ry2 && sy2 > ry1) toWorld(seg.x1, seg.y1, seg.x2, seg.y2);
  });
  return out;
}

// Nearest wall coordinate from point (cx,cz) travelling one cardinal direction,
// considering only walls whose span actually crosses the ray. null if none.
function nearestDimWall(cx, cz, dir, walls) {
  const E = 1e-6;
  let best = null;
  for (const w of walls) {
    if (dir === 'left' || dir === 'right') {
      if (!w.vertical || cz < w.lo - E || cz > w.hi + E) continue;
      if (dir === 'right' && w.coord > cx + E && (best === null || w.coord < best)) best = w.coord;
      if (dir === 'left'  && w.coord < cx - E && (best === null || w.coord > best)) best = w.coord;
    } else {
      if (w.vertical || cx < w.lo - E || cx > w.hi + E) continue;
      if (dir === 'front' && w.coord > cz + E && (best === null || w.coord < best)) best = w.coord;
      if (dir === 'back'  && w.coord < cz - E && (best === null || w.coord > best)) best = w.coord;
    }
  }
  return best;
}

// Four gaps (left/right/back/front) from the piece footprint to the NEAREST
// wall on each side — an interior wall if one lies between the piece and the
// surrounding wall, otherwise the room's own perimeter wall. cx/cz = piece
// centre, halfW/halfD = half footprint; `walls` from buildDimWalls().
function FurnitureDimensions3D({ cx, cz, halfW, halfD, walls }) {
  const left = cx - halfW, right = cx + halfW, back = cz - halfD, front = cz + halfD;
  const wl = nearestDimWall(cx, cz, 'left', walls);
  const wr = nearestDimWall(cx, cz, 'right', walls);
  const wb = nearestDimWall(cx, cz, 'back', walls);
  const wf = nearestDimWall(cx, cz, 'front', walls);
  return (
    <group>
      {wl !== null && left - wl > 0.05  && <Dim3D from={[wl, cz]}    to={[left, cz]}  axis="x" />}
      {wr !== null && wr - right > 0.05 && <Dim3D from={[right, cz]} to={[wr, cz]}   axis="x" />}
      {wb !== null && back - wb > 0.05  && <Dim3D from={[cx, wb]}     to={[cx, back]} axis="z" />}
      {wf !== null && wf - front > 0.05 && <Dim3D from={[cx, front]} to={[cx, wf]}   axis="z" />}
    </group>
  );
}

// ─── DRAGGABLE FURNITURE WRAPPER ─────────────────────────────────────────────

const DRAG_THRESHOLD = 8;

// Item types that, when DRAGGED onto another item, come to rest ON TOP of it
// (lifted to that item's surface) instead of sitting on the floor. Only the
// dragged item's type needs to be listed — it can stack on anything. Add more
// types here as they're enabled.
const STACK_ON_DROP_TYPES = ['plant'];

// Elevation (metres) a stackable piece should have at a given floor position:
// the top-surface height of whatever item its footprint centre lands on, or 0
// on the open floor / for non-stackable types. `ctx` = the drag ctxRef.current.
function stackElevationAt(worldX, worldZ, ctx) {
  const { item: it, displayW: dw, displayH: dh, room: r, roomW: rw, roomD: rd, itemId, store: st, heightsRef: hr } = ctx;
  if (!STACK_ON_DROP_TYPES.includes(it.type) || !st) return 0;
  const newX = Math.round((worldX + rw / 2) / PX_TO_M - dw / 2 + r.x * CELL);
  const newY = Math.round((worldZ + rd / 2) / PX_TO_M - dh / 2 + r.y * CELL);
  const cx = newX + dw / 2, cy = newY + dh / 2;
  const target = st.getState().house.furniture.find(f => {
    if (f.id === itemId) return false;
    const fr = f.rotation || 0;
    const fw = fr % 180 === 0 ? f.w : f.h;
    const fh = fr % 180 === 0 ? f.h : f.w;
    return cx >= f.x && cx <= f.x + fw && cy >= f.y && cy <= f.y + fh;
  });
  if (!target) return 0;
  const tft = FURNITURE_TYPES.find(f => f.type === target.type);
  return (hr && hr.current[target.id]) || target.h3 || tft?.h3 || 1;
}

// Unit-box edge geometry shared across all instances
const _unitEdgeGeo = (() => {
  const box = new THREE.BoxGeometry(1, 1, 1);
  const geo = new THREE.EdgesGeometry(box);
  box.dispose();
  return geo;
})();

function DraggableFurniture({ item, room, roomW, roomD, onFurnitureClick, isSelected, isHovered, timeOfDay, dimWalls, heightsRef }) {
  const three = useThree();
  const dispatch = useDispatch();
  const { t } = useT();
  const store = useStore(); // read the live furniture list at drop time (for stacking)
  const [dragPos, setDragPos] = useState(null);
  const elevation = item.elevation || 0; // metres — lifted when stacked on another item

  // A GLB is uniformly scaled to fit its FOOTPRINT, so its real rendered height
  // depends on the model and rarely equals item.h3 — which is why the fixed
  // item.h3 selection box floated taller than the model. Measure the actual
  // rendered mesh so the box/hitbox hug the model's true height (and since
  // editing H scales the model, the measured box follows it). Throttled, and
  // only sets state when the height actually changes, so it's cheap and settles
  // once the GLB has loaded.
  const measureRef  = useRef();
  const measuredRef = useRef(0);
  const measureBox  = useMemo(() => new THREE.Box3(), []);
  const frameRef    = useRef(0);
  const [measuredH, setMeasuredH] = useState(null);
  useFrame(() => {
    const g = measureRef.current;
    if (!g) return;
    frameRef.current = (frameRef.current + 1) % 20;
    if (frameRef.current !== 0) return; // ~3×/sec is plenty
    measureBox.setFromObject(g);
    const h = measureBox.max.y - measureBox.min.y;
    if (h > 0.02 && Math.abs(h - measuredRef.current) > 0.01) {
      measuredRef.current = h;
      setMeasuredH(h);
    }
    // Publish this item's top-surface height (metres above the floor) so a
    // stackable item dropped on it knows how high to sit.
    if (heightsRef) heightsRef.current[item.id] = (item.elevation || 0) + (measuredRef.current || 0);
  });

  // Drop the registry entry when the piece is removed.
  useEffect(() => () => { if (heightsRef) delete heightsRef.current[item.id]; }, [item.id, heightsRef]);

  const rot = item.rotation || 0;
  const displayW = rot % 180 === 0 ? item.w : item.h;
  const displayH = rot % 180 === 0 ? item.h : item.w;

  // World-space centre of the furniture (updated when dragging)
  const relPxX = (item.x + displayW / 2) - room.x * CELL;
  const relPxZ = (item.y + displayH / 2) - room.y * CELL;
  const baseX  = relPxX * PX_TO_M - roomW / 2;
  const baseZ  = relPxZ * PX_TO_M - roomD / 2;
  const outX   = dragPos ? dragPos.x : baseX;
  const outZ   = dragPos ? dragPos.z : baseZ;
  const outW   = displayW * PX_TO_M;
  const outD   = displayH * PX_TO_M;

  // Furniture label (for hover tooltip)
  const ft = FURNITURE_TYPES.find(f => f.type === item.type);

  // Updated every render so DOM listeners always see fresh values
  const ctxRef = useRef({});
  ctxRef.current = {
    camera: three.camera, gl: three.gl, controls: three.controls,
    dispatch, room, roomW, roomD, displayW, displayH,
    itemId: item.id, onFurnitureClick, item, store, heightsRef,
  };

  const plane  = useMemo(() => new THREE.Plane(new THREE.Vector3(0, 1, 0), 0), []);
  const ptVec  = useMemo(() => new THREE.Vector3(), []);
  const rc     = useMemo(() => new THREE.Raycaster(), []);
  const ndcVec = useMemo(() => new THREE.Vector2(), []);

  const { onMove, onUp, dragState } = useMemo(() => {
    const dragState = { pos: null, moved: false, startX: 0, startY: 0 };

    function onMove(e) {
      if (!dragState.moved) {
        const dx = e.clientX - dragState.startX;
        const dy = e.clientY - dragState.startY;
        if (Math.sqrt(dx * dx + dy * dy) < DRAG_THRESHOLD) return;
        dragState.moved = true;
      }
      const { camera, gl } = ctxRef.current;
      const rect = gl.domElement.getBoundingClientRect();
      ndcVec.set(
        ((e.clientX - rect.left) / rect.width) * 2 - 1,
        -((e.clientY - rect.top) / rect.height) * 2 + 1
      );
      rc.setFromCamera(ndcVec, camera);
      if (rc.ray.intersectPlane(plane, ptVec)) {
        // Live elevation: if the piece is currently over another item it can
        // stack on, lift it onto that item's surface *while dragging* so you
        // can position it on top; back over the open floor it rides at 0.
        const elevation = stackElevationAt(ptVec.x, ptVec.z, ctxRef.current);
        dragState.pos = { x: ptVec.x, z: ptVec.z, elevation };
        setDragPos({ x: ptVec.x, z: ptVec.z, elevation });
      }
    }

    function onUp() {
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
      document.body.style.cursor = '';
      const {
        controls, dispatch: d, room: r, roomW: rw, roomD: rd,
        displayW: dw, displayH: dh, itemId, onFurnitureClick: onClick, item: it,
        store: st, heightsRef: hr,
      } = ctxRef.current;
      if (controls) controls.enabled = true;

      if (!dragState.moved) {
        dragState.pos = null;
        setDragPos(null);
        if (onClick) onClick(it);
        return;
      }

      const pos = dragState.pos;
      dragState.pos = null;
      dragState.moved = false;
      setDragPos(null);
      if (pos) {
        const newX = Math.round((pos.x + rw / 2) / PX_TO_M - dw / 2 + r.x * CELL);
        const newY = Math.round((pos.z + rd / 2) / PX_TO_M - dh / 2 + r.y * CELL);

        // Commit the same elevation that was shown live during the drag: on top
        // of the item it's over (stackable types), or 0 on the open floor.
        if (STACK_ON_DROP_TYPES.includes(it.type)) {
          d(moveFurniture({ id: itemId, x: newX, y: newY, elevation: stackElevationAt(pos.x, pos.z, ctxRef.current) }));
        } else {
          d(moveFurniture({ id: itemId, x: newX, y: newY }));
        }
      }
    }

    return { onMove, onUp, dragState };
  }, []);

  useEffect(() => () => {
    document.removeEventListener('pointermove', onMove);
    document.removeEventListener('pointerup', onUp);
  }, [onMove, onUp]);

  function onPointerDown(e) {
    if (!ctxRef.current.onFurnitureClick) return;
    e.stopPropagation();
    dragState.startX = e.nativeEvent.clientX;
    dragState.startY = e.nativeEvent.clientY;
    dragState.moved = false;
    dragState.pos = null;
    const { controls } = ctxRef.current;
    if (controls) controls.enabled = false;
    document.body.style.cursor = 'grabbing';
    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
  }

  // Was a flat 1.1 for every item regardless of its actual height — the
  // visual mesh scales correctly with item.h3 (see FurnitureMesh3D), but
  // this hitbox/outline never read it, so a taller item's click target and
  // selection outline stayed stuck at the old fixed size instead of growing
  // (or shrinking) along with it.
  const outH = item.h3 ?? ft?.h3 ?? 1;
  // Prefer the model's measured height so the box hugs it; fall back to item.h3
  // until the first measurement lands (e.g. before the GLB has loaded).
  const boxH = measuredH ?? outH;
  const showOutline  = isHovered || isSelected || !!dragPos;
  const outlineColor = dragPos ? '#44aaff' : isSelected ? '#ffaa20' : '#ffffff';
  // Live elevation: while dragging, the move handler computes whether the piece
  // is currently over a stackable item and carries that height on dragPos, so
  // it rides on top of the item mid-drag (and back to 0 over the floor). When
  // not dragging, use its committed elevation.
  const renderElev = dragPos ? (dragPos.elevation || 0) : elevation;

  // Stable ref-callback: disables raycasting on the outline so it never
  // steals the pointer from the hitbox or keeps hover alive after mouseout.
  const noRaycast = useRef(node => {
    if (node) node.traverse(obj => { obj.raycast = () => {}; });
  }).current;

  return (
    <>
      {/* ① Transparent hitbox — THE ONLY raycasted object per furniture piece.
             A simple BoxGeometry gives perfectly reliable onPointerOver/Out. */}
      <mesh
        position={[outX, 0.01 + boxH / 2 + renderElev, outZ]}
        onPointerDown={onPointerDown}
      >
        <boxGeometry args={[outW, boxH, outD]} />
        <meshBasicMaterial transparent opacity={0} depthWrite={false} />
      </mesh>

      {/* ② Visual mesh — purely decorative, no pointer handlers. Wrapped so its
             true rendered height can be measured for the box/hitbox above. The
             group's Y lifts the piece when it's stacked on top of another item. */}
      <group ref={measureRef} position={[0, renderElev, 0]}>
        <FurnitureMesh3D
          item={item}
          room={room}
          roomW={roomW}
          roomD={roomD}
          posXOverride={dragPos?.x}
          posZOverride={dragPos?.z}
          timeOfDay={timeOfDay}
        />
      </group>

      {/* ③ Outline — raycasting disabled via ref so it cannot steal hover */}
      {showOutline && (
        <group
          ref={noRaycast}
          position={[outX, 0.015 + boxH / 2 + renderElev, outZ]}
          scale={[outW, boxH, outD]}
        >
          <lineSegments geometry={_unitEdgeGeo}>
            <lineBasicMaterial color={outlineColor} />
          </lineSegments>
        </group>
      )}

      {/* ④ Hover label */}
      {isHovered && !isSelected && (
        <Html center position={[outX, boxH + 0.35 + renderElev, outZ]} distanceFactor={7} zIndexRange={[20, 0]}>
          <div style={{
            background: 'rgba(28,26,23,0.92)', color: '#f0e8d8',
            padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600,
            fontFamily: 'system-ui,sans-serif', whiteSpace: 'nowrap',
            pointerEvents: 'none', border: '1.5px solid rgba(255,255,255,0.18)',
          }}>
            {ft?.icon} {itemLabel(t, 'furn_', ft)}
          </div>
        </Html>
      )}

      {/* ⑤ Distance-to-wall dimensions — shown while selected or dragging */}
      {(isSelected || dragPos) && (
        <FurnitureDimensions3D
          cx={outX} cz={outZ}
          halfW={outW / 2} halfD={outD / 2}
          walls={dimWalls || []}
        />
      )}
    </>
  );
}

// ─── DRAGGABLE LIGHTING WRAPPER ──────────────────────────────────────────────
// Mirrors DraggableFurniture's drag mechanics exactly, but the raycast plane
// sits at ceiling height instead of the floor, and the resulting X/Z is
// clamped to the room footprint (furniture never clamps — a light fixture
// dragged off the ceiling should stop at the edge, not just fade from view).

function DraggableLighting({ item, room, roomW, roomD, roomH, onLightingClick, isSelected, timeOfDay }) {
  const three = useThree();
  const dispatch = useDispatch();
  const { t } = useT();
  const [dragPos, setDragPos] = useState(null);

  const rot = item.rotation || 0;
  const displayW = rot % 180 === 0 ? item.w : item.h;
  const displayH = rot % 180 === 0 ? item.h : item.w;

  const relPxX = (item.x + displayW / 2) - room.x * CELL;
  const relPxZ = (item.y + displayH / 2) - room.y * CELL;
  const baseX  = relPxX * PX_TO_M - roomW / 2;
  const baseZ  = relPxZ * PX_TO_M - roomD / 2;
  const outX   = dragPos ? dragPos.x : baseX;
  const outZ   = dragPos ? dragPos.z : baseZ;
  const outW   = displayW * PX_TO_M;
  const outD   = displayH * PX_TO_M;
  const ceilY  = roomH - LIGHTING_CEILING_OFFSET;

  const lt = LIGHTING_TYPES.find(l => l.type === item.type);

  const ctxRef = useRef({});
  ctxRef.current = {
    camera: three.camera, gl: three.gl, controls: three.controls,
    dispatch, room, roomW, roomD, displayW, displayH,
    itemId: item.id, onLightingClick, item,
  };

  const plane  = useMemo(() => new THREE.Plane(new THREE.Vector3(0, 1, 0), -ceilY), [ceilY]);
  const ptVec  = useMemo(() => new THREE.Vector3(), []);
  const rc     = useMemo(() => new THREE.Raycaster(), []);
  const ndcVec = useMemo(() => new THREE.Vector2(), []);

  const { onMove, onUp, dragState } = useMemo(() => {
    const dragState = { pos: null, moved: false, startX: 0, startY: 0 };

    function onMove(e) {
      if (!dragState.moved) {
        const dx = e.clientX - dragState.startX;
        const dy = e.clientY - dragState.startY;
        if (Math.sqrt(dx * dx + dy * dy) < DRAG_THRESHOLD) return;
        dragState.moved = true;
      }
      const { camera, gl, roomW: rw, roomD: rd } = ctxRef.current;
      const rect = gl.domElement.getBoundingClientRect();
      ndcVec.set(
        ((e.clientX - rect.left) / rect.width) * 2 - 1,
        -((e.clientY - rect.top) / rect.height) * 2 + 1
      );
      rc.setFromCamera(ndcVec, camera);
      if (rc.ray.intersectPlane(plane, ptVec)) {
        // Clamp to the ceiling/room footprint — "move only on ceiling".
        const cx = Math.max(-rw / 2, Math.min(rw / 2, ptVec.x));
        const cz = Math.max(-rd / 2, Math.min(rd / 2, ptVec.z));
        dragState.pos = { x: cx, z: cz };
        setDragPos({ x: cx, z: cz });
      }
    }

    function onUp() {
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
      document.body.style.cursor = '';
      const {
        controls, dispatch: d, room: r, roomW: rw, roomD: rd,
        displayW: dw, displayH: dh, itemId, onLightingClick: onClick, item: it,
      } = ctxRef.current;
      if (controls) controls.enabled = true;

      if (!dragState.moved) {
        dragState.pos = null;
        setDragPos(null);
        if (onClick) onClick(it);
        return;
      }

      const pos = dragState.pos;
      dragState.pos = null;
      dragState.moved = false;
      setDragPos(null);
      if (pos) {
        const newRelPxX = (pos.x + rw / 2) / PX_TO_M;
        const newRelPxZ = (pos.z + rd / 2) / PX_TO_M;
        d(moveLighting({
          id: itemId,
          x: Math.round(newRelPxX - dw / 2 + r.x * CELL),
          y: Math.round(newRelPxZ - dh / 2 + r.y * CELL),
        }));
      }
    }

    return { onMove, onUp, dragState };
  }, [plane]);

  useEffect(() => () => {
    document.removeEventListener('pointermove', onMove);
    document.removeEventListener('pointerup', onUp);
  }, [onMove, onUp]);

  function onPointerDown(e) {
    if (!ctxRef.current.onLightingClick) return;
    e.stopPropagation();
    dragState.startX = e.nativeEvent.clientX;
    dragState.startY = e.nativeEvent.clientY;
    dragState.moved = false;
    dragState.pos = null;
    const { controls } = ctxRef.current;
    if (controls) controls.enabled = false;
    document.body.style.cursor = 'grabbing';
    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
  }

  const outH = 0.5;
  const showOutline  = isSelected || !!dragPos;
  const outlineColor = dragPos ? '#44aaff' : '#ffaa20';

  const noRaycast = useRef(node => {
    if (node) node.traverse(obj => { obj.raycast = () => {}; });
  }).current;

  return (
    <>
      {/* ① Transparent hitbox at the ceiling attachment point */}
      <mesh
        position={[outX, ceilY - outH / 2, outZ]}
        onPointerDown={onPointerDown}
      >
        <boxGeometry args={[outW, outH, outD]} />
        <meshBasicMaterial transparent opacity={0} depthWrite={false} />
      </mesh>

      {/* ② Visual fixture — purely decorative, no pointer handlers */}
      <LightingMesh3D
        item={item}
        room={room}
        roomW={roomW}
        roomD={roomD}
        roomH={roomH}
        posXOverride={dragPos?.x}
        posZOverride={dragPos?.z}
        timeOfDay={timeOfDay}
      />

      {/* ③ Outline */}
      {showOutline && (
        <group
          ref={noRaycast}
          position={[outX, ceilY - outH / 2, outZ]}
          scale={[outW, outH, outD]}
        >
          <lineSegments geometry={_unitEdgeGeo}>
            <lineBasicMaterial color={outlineColor} />
          </lineSegments>
        </group>
      )}

      {/* ④ Label while selected */}
      {isSelected && (
        <Html center position={[outX, ceilY - 0.5, outZ]} distanceFactor={7} zIndexRange={[20, 0]}>
          <div style={{
            background: 'rgba(28,26,23,0.92)', color: '#f0e8d8',
            padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600,
            fontFamily: 'system-ui,sans-serif', whiteSpace: 'nowrap',
            pointerEvents: 'none', border: '1.5px solid rgba(255,255,255,0.18)',
          }}>
            {lt?.icon} {itemLabel(t, 'light_', lt)}
          </div>
        </Html>
      )}
    </>
  );
}

// ─── FURNITURE PANEL (floating — no backdrop, 3D stays visible) ──────────────

const FURNITURE_COLOR_PRESETS = [
  { label: 'Beige Linen',  color: '#ded7cc' },
  { label: 'Warm Cream',   color: '#c8b898' },
  { label: 'Camel',        color: '#a89070' },
  { label: 'Cognac',       color: '#8b5030' },
  { label: 'Walnut',       color: '#2d1a0e' },
  { label: 'Charcoal',     color: '#4a4a4a' },
  { label: 'Light Grey',   color: '#d0cfc9' },
  { label: 'Off White',    color: '#f0ece6' },
  { label: 'Sage Green',   color: '#6a8a7a' },
  { label: 'Steel Blue',   color: '#5c7a9a' },
  { label: 'Terracotta',   color: '#c08070' },
  // 11 presets + the "Default" swatch = 12 = two even rows of 6.
];

const FURNITURE_STYLES = {
  // Sofas are GLB-only now — the six procedural styles were removed. Derived
  // straight from GLB_MODELS so the picker and the asset map can't drift apart:
  // adding a URL there is all it takes to expose a new sofa here.
  // Sofas are GLB-only. Derived from GLB_SOFAS (NOT GLB_MODELS — that also
  // holds the armchairs now) so the picker can't drift from the asset map.
  sofa:     Object.keys(GLB_SOFAS).map(id => ({ id, label: `Sofa ${id.replace(/^sofa/, '')}` })),
  // Armchairs are GLB-only too, derived from GLB_ARMCHAIRS.
  armchair: Object.keys(GLB_ARMCHAIRS).map(id => ({ id, label: `Armchair ${id.replace(/^armchair/, '')}` })),
  // Tables are GLB-only too, derived from GLB_TABLES.
  table:    Object.keys(GLB_TABLES).map(id => ({ id, label: `Table ${id.replace(/^table/, '')}` })),
  // Chairs are GLB-only too, derived from GLB_CHAIRS.
  chair:    Object.keys(GLB_CHAIRS).map(id => ({ id, label: `Chair ${id.replace(/^chair/, '')}` })),
  // Beds are GLB-only too, derived from GLB_BEDS.
  bed:      Object.keys(GLB_BEDS).map(id => ({ id, label: `Bed ${id.replace(/^bed/, '')}` })),
  // Desks are GLB-only too, derived from GLB_DESKS.
  desk:     Object.keys(GLB_DESKS).map(id => ({ id, label: `Desk ${id.replace(/^desk/, '')}` })),
  // Bookshelves are GLB-only too, derived from GLB_SHELVES.
  shelf:    Object.keys(GLB_SHELVES).map(id => ({ id, label: `Bookshelf ${id.replace(/^bookshelf/, '')}` })),
  // Storage units are GLB-only too, derived from GLB_STORAGE (type is still 'wardrobe').
  wardrobe: Object.keys(GLB_STORAGE).map(id => ({ id, label: `Storage ${id.replace(/^storage/, '')}` })),
  // Bathtubs are GLB-only too, derived from GLB_BATHS.
  bathtub:  Object.keys(GLB_BATHS).map(id => ({ id, label: `Bathtub ${id.replace(/^bath/, '')}` })),
  // All GLB-only now, derived from their catalogues.
  toilet:   Object.keys(GLB_TOILETS).map(id => ({ id, label: `Toilet ${id.replace(/^toilet/, '')}` })),
  sink:     Object.keys(GLB_SINKS).map(id => ({ id, label: `Sink ${id.replace(/^sink/, '')}` })),
  lamp:     Object.keys(GLB_LAMPS).map(id => ({ id, label: `Floor Lamp ${id.replace(/^floorlamp/, '')}` })),
  plant:    Object.keys(GLB_PLANTS).map(id => ({ id, label: `Plant ${id.replace(/^plant/, '')}` })),
  rug:      [{ id: 'rectangular',  label: 'Rectangular'  }, { id: 'round',       label: 'Round'        }, { id: 'persian',     label: 'Persian'      }, { id: 'shaggy',      label: 'Shaggy'       }, { id: 'kilim',       label: 'Kilim'        }, { id: 'runner',      label: 'Runner'       }],
  tv:       [{ id: 'pedestal',     label: 'Pedestal'     }, { id: 'wallmount',   label: 'Wall Mount'   }, { id: 'unit',        label: 'TV Unit'      }, { id: 'projector',   label: 'Projector'    }, { id: 'crt',         label: 'Retro CRT'    }, { id: 'ultrawide',   label: 'Ultrawide'    }],
};

// A −/+ stepper whose value is also a click-to-type text field — shared by
// FurniturePanel/LightingPanel/OpeningPanel's size (and opening sill) rows.
// Always operates in the DISPLAY unit (metres); callers pass/receive metres
// and do their own conversion to whatever internal unit (pixels, for
// furniture/lighting) the resize dispatch actually needs.
function SizeField({ label, valueM, min = 0, max = 999, decimals = 2, stepM = 0.1, onChangeM, stepBtnStyle }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const displayVal = valueM.toFixed(decimals);

  function commit() {
    const parsed = parseFloat(draft.replace(',', '.'));
    if (!isNaN(parsed)) onChangeM(Math.max(min, Math.min(max, parsed)));
    setEditing(false);
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 6 }}>
      {label && <span style={{ color: '#888', fontSize: 11, width: 24, fontWeight: 600 }}>{label}</span>}
      <button style={stepBtnStyle} onClick={() => onChangeM(Math.max(min, +(valueM - stepM).toFixed(decimals)))}>−</button>
      {editing ? (
        <input
          autoFocus
          type="text"
          inputMode="decimal"
          defaultValue={displayVal}
          onChange={e => setDraft(e.target.value)}
          onFocus={e => e.target.select()}
          onBlur={commit}
          onKeyDown={e => {
            if (e.key === 'Enter') { e.currentTarget.blur(); }
            if (e.key === 'Escape') setEditing(false);
          }}
          style={{
            color: '#ffcc60', fontSize: 12, flex: 1, textAlign: 'center', fontVariantNumeric: 'tabular-nums',
            background: 'rgba(255,200,80,0.12)', border: '1px solid rgba(255,200,80,0.5)', borderRadius: 5,
            padding: '2px 0', outline: 'none', fontFamily: 'inherit', width: 0, minWidth: 0,
          }}
        />
      ) : (
        <span
          onClick={() => { setDraft(displayVal); setEditing(true); }}
          title="Click to type a value"
          style={{
            color: '#ddd', fontSize: 12, flex: 1, textAlign: 'center', fontVariantNumeric: 'tabular-nums',
            cursor: 'text', borderBottom: '1px dashed rgba(255,255,255,0.25)',
          }}
        >
          {displayVal} m
        </span>
      )}
      <button style={stepBtnStyle} onClick={() => onChangeM(Math.min(max, +(valueM + stepM).toFixed(decimals)))}>+</button>
    </div>
  );
}

function FurniturePanel({ item, onClose, onColorChange, onResetColor, onRotate, onDelete, onResize, onStyleChange, onCopy }) {
  const { t } = useT();
  const isGuest = useGuestMode();
  const ft = FURNITURE_TYPES.find(f => f.type === item.type);
  const [color, setColor] = useState(item.color || ft?.color || '#a89070');
  // Whether the user has picked a colour. false = "Default" (the item's own
  // authored look for GLB models, or the type colour for procedural ones).
  const [colored, setColored] = useState(!!item.colored);

  useEffect(() => {
    setColor(item.color || ft?.color || '#a89070');
    setColored(!!item.colored);
  }, [item.id]);

  function apply(c) { if (isGuest) return; setColor(c); setColored(true); onColorChange(c); }
  function applyDefault() { if (isGuest) return; setColored(false); setColor(ft?.color || '#a89070'); onResetColor && onResetColor(); }

  const STEP = 13; // 0.5 m in pixels

  const iconBtn = {
    background: 'rgba(255,255,255,0.08)', border: 'none', borderRadius: 7,
    color: '#ddd', fontSize: 16, cursor: 'pointer',
    width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center',
  };
  const stepBtn = {
    background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.15)',
    borderRadius: 6, color: '#ccc', fontSize: 16, cursor: 'pointer',
    width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontWeight: 700, lineHeight: 1,
  };

  return (
    <div className="room3d-item-panel" style={{
      position: 'absolute', bottom: 72, right: 16,
      background: 'rgba(16,16,22,0.94)',
      backdropFilter: 'blur(14px)',
      borderRadius: 14, padding: '14px 16px', width: 256,
      boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
      border: '1px solid rgba(255,255,255,0.12)',
      zIndex: 30, userSelect: 'none',
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 }}>
        <span style={{ fontSize: 20 }}>{ft?.icon ?? '🪑'}</span>
        <span style={{ color: '#fff', fontWeight: 700, fontSize: 14, flex: 1 }}>{itemLabel(t, 'furn_', ft) || item.type}</span>
        <button onClick={onRotate} title={t('rotate90_tooltip')} style={iconBtn}>↻</button>
        <button onClick={onCopy}  title={t('copy_ctrlc_tooltip')} style={iconBtn}>⧉</button>
        <button onClick={onDelete} title={t('delete_tooltip')} style={{ ...iconBtn, color: '#ff7070' }}>🗑</button>
        <button onClick={onClose}  title={t('close_tooltip')}  style={iconBtn}>✕</button>
      </div>

      {/* Color picker row + preset swatches — locked for guests */}
      <div style={{ position: 'relative' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, opacity: isGuest ? 0.35 : 1 }}>
          <input
            type="color" value={color} onChange={e => apply(e.target.value)} disabled={isGuest}
            style={{
              width: 38, height: 30, padding: 2, cursor: isGuest ? 'default' : 'pointer', background: 'none',
              border: '1.5px solid rgba(255,255,255,0.2)', borderRadius: 7,
            }}
          />
          <span style={{ color: '#666', fontSize: 11, fontFamily: 'monospace', flex: 1 }}>{color}</span>
          <span style={{ color: '#555', fontSize: 11 }}>{t('drag_to_move_hint')}</span>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 5, opacity: isGuest ? 0.35 : 1 }}>
          {/* "Default" — clears any tint so the item shows its original look
              (the model as loaded from the catalogue). Active when no colour
              is chosen, i.e. right after the item is added. */}
          <button
            title={t('default_color')} onClick={applyDefault} disabled={isGuest}
            style={{
              width: 30, height: 30, borderRadius: 6, cursor: isGuest ? 'default' : 'pointer',
              // neutral checker-ish fill so it reads as "no colour / auto"
              background: 'linear-gradient(135deg, #6b6f76 0 48%, #d8dade 52% 100%)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#fff', fontSize: 14, lineHeight: 1,
              border: !colored ? '2px solid #fff' : '2px solid rgba(255,255,255,0.12)',
              boxShadow: !colored ? '0 0 0 2px rgba(255,200,80,0.5)' : 'none',
            }}
          >↺</button>
          {FURNITURE_COLOR_PRESETS.map(({ color: c, label }) => (
            <button
              key={c} title={label} onClick={() => apply(c)} disabled={isGuest}
              style={{
                width: 30, height: 30, borderRadius: 6, cursor: isGuest ? 'default' : 'pointer', background: c,
                border: (colored && color === c) ? '2px solid #fff' : '2px solid rgba(255,255,255,0.12)',
                boxShadow: (colored && color === c) ? '0 0 0 2px rgba(255,200,80,0.5)' : 'none',
              }}
            />
          ))}
        </div>
        {isGuest && (
          <div style={{
            position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
            gap: 5, fontSize: 10, fontWeight: 700, color: '#ffcc60', textAlign: 'center', padding: '0 10px',
          }}>
            🔒 {t('guest_customize_locked')}
          </div>
        )}
      </div>

      {/* Size controls — Width/Length are the floor footprint (pixels,
          converted to metres); Height is the 3D vertical scale (item.h3,
          already stored in metres — see FurnitureMesh3D). */}
      <div style={{ marginTop: 12, borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: 10 }}>
        <div style={{ color: '#666', fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', marginBottom: 7 }}>{t('size').toUpperCase()}</div>
        {[['W', item.w, 'w'], ['L', item.h, 'l']].map(([label, val, axis]) => (
          <SizeField
            key={axis}
            label={label}
            valueM={val * PX_TO_M}
            min={0.1}
            decimals={1}
            stepM={STEP * PX_TO_M}
            stepBtnStyle={stepBtn}
            onChangeM={m => {
              const px = Math.max(2.6, Math.round(m / PX_TO_M));
              onResize(axis === 'w' ? px : item.w, axis === 'l' ? px : item.h, item.h3);
            }}
          />
        ))}
        <SizeField
          label="H"
          valueM={item.h3 ?? ft?.h3 ?? 1}
          min={0.1}
          max={4}
          decimals={2}
          stepM={0.1}
          stepBtnStyle={stepBtn}
          onChangeM={m => onResize(item.w, item.h, m)}
        />
      </div>

      {/* Style picker */}
      {FURNITURE_STYLES[item.type] && (
        <div style={{ marginTop: 12, borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: 10, position: 'relative' }}>
          <div style={{ color: '#666', fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', marginBottom: 7 }}>{t('style_label').toUpperCase()}</div>
          {/* ~5 rows tall, then scroll — the GLB catalogues have 27 styles, which
              would otherwise make the panel taller than the viewport. */}
          <div className="fp-style-scroll" style={{
            display: 'flex', gap: 5, flexWrap: 'wrap', opacity: isGuest ? 0.35 : 1,
            maxHeight: 152, overflowY: 'auto', paddingRight: 4,
            scrollbarWidth: 'thin', scrollbarColor: 'rgba(255,255,255,0.28) transparent',
          }}>
            {FURNITURE_STYLES[item.type].map(s => {
              const active = (item.style || FURNITURE_STYLES[item.type][0].id) === s.id;
              return (
                <button key={s.id} onClick={() => { if (!isGuest) onStyleChange(s.id); }} disabled={isGuest} style={{
                  flex: '1 1 28%', minWidth: 56, padding: '4px 5px', borderRadius: 7, cursor: isGuest ? 'default' : 'pointer',
                  border: active ? '1.5px solid rgba(255,200,80,0.8)' : '1.5px solid rgba(255,255,255,0.12)',
                  background: active ? 'rgba(255,200,80,0.15)' : 'rgba(255,255,255,0.05)',
                  color: active ? '#ffcc50' : '#aaa',
                  fontSize: 10, fontWeight: active ? 700 : 400, transition: 'all 0.15s',
                }}>{s.label}</button>
              );
            })}
          </div>
          {isGuest && (
            <div style={{
              position: 'absolute', inset: '17px 0 0 0', display: 'flex', alignItems: 'center', justifyContent: 'center',
              gap: 5, fontSize: 10, fontWeight: 700, color: '#ffcc60', textAlign: 'center', padding: '0 10px',
            }}>
              🔒 {t('guest_customize_locked')}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── LIGHTING PANEL (floating — mirrors FurniturePanel) ──────────────────────

function LightingPanel({ item, onClose, onColorChange, onResetColor, onRotate, onDelete, onResize, onStyleChange, onCopy }) {
  const { t } = useT();
  const isGuest = useGuestMode();
  const lt = LIGHTING_TYPES.find(l => l.type === item.type);
  const [color, setColor] = useState(item.color || lt?.color || '#c9a227');
  // Whether the user has picked a colour. false = "Default" (the fixture's own
  // authored GLB materials).
  const [colored, setColored] = useState(!!item.colored);

  useEffect(() => { setColor(item.color || lt?.color || '#c9a227'); setColored(!!item.colored); }, [item.id]);

  function apply(c) { if (isGuest) return; setColor(c); setColored(true); onColorChange(c); }
  function applyDefault() { if (isGuest) return; setColored(false); setColor(lt?.color || '#c9a227'); onResetColor && onResetColor(); }

  const STEP = 8; // ~0.3 m in pixels

  const iconBtn = {
    background: 'rgba(255,255,255,0.08)', border: 'none', borderRadius: 7,
    color: '#ddd', fontSize: 16, cursor: 'pointer',
    width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center',
  };
  const stepBtn = {
    background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.15)',
    borderRadius: 6, color: '#ccc', fontSize: 16, cursor: 'pointer',
    width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontWeight: 700, lineHeight: 1,
  };

  return (
    <div className="room3d-item-panel" style={{
      position: 'absolute', bottom: 72, right: 16,
      background: 'rgba(16,16,22,0.94)',
      backdropFilter: 'blur(14px)',
      borderRadius: 14, padding: '14px 16px', width: 256,
      boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
      border: '1px solid rgba(255,255,255,0.12)',
      zIndex: 30, userSelect: 'none',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 }}>
        <span style={{ fontSize: 20 }}>{lt?.icon ?? '💡'}</span>
        <span style={{ color: '#fff', fontWeight: 700, fontSize: 14, flex: 1 }}>{itemLabel(t, 'light_', lt) || item.type}</span>
        <button onClick={onRotate} title={t('rotate90_tooltip')} style={iconBtn}>↻</button>
        <button onClick={onCopy}  title={t('copy_ctrlc_tooltip')} style={iconBtn}>⧉</button>
        <button onClick={onDelete} title={t('delete_tooltip')} style={{ ...iconBtn, color: '#ff7070' }}>🗑</button>
        <button onClick={onClose}  title={t('close_tooltip')}  style={iconBtn}>✕</button>
      </div>

      <div style={{ position: 'relative' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, opacity: isGuest ? 0.35 : 1 }}>
          <input
            type="color" value={color} onChange={e => apply(e.target.value)} disabled={isGuest}
            style={{
              width: 38, height: 30, padding: 2, cursor: isGuest ? 'default' : 'pointer', background: 'none',
              border: '1.5px solid rgba(255,255,255,0.2)', borderRadius: 7,
            }}
          />
          <span style={{ color: '#666', fontSize: 11, fontFamily: 'monospace', flex: 1 }}>{color}</span>
          <span style={{ color: '#555', fontSize: 11 }}>{t('drag_to_move_hint')}</span>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 5, opacity: isGuest ? 0.35 : 1 }}>
          {/* "Default" — clears the tint so the fixture shows its authored GLB
              materials. Active right after the fixture is added. */}
          <button
            title={t('default_color')} onClick={applyDefault} disabled={isGuest}
            style={{
              width: 30, height: 30, borderRadius: 6, cursor: isGuest ? 'default' : 'pointer',
              background: 'linear-gradient(135deg, #6b6f76 0 48%, #d8dade 52% 100%)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#fff', fontSize: 14, lineHeight: 1,
              border: !colored ? '2px solid #fff' : '2px solid rgba(255,255,255,0.12)',
              boxShadow: !colored ? '0 0 0 2px rgba(255,200,80,0.5)' : 'none',
            }}
          >↺</button>
          {LIGHTING_COLOR_PRESETS.map(({ color: c, label }) => (
            <button
              key={c} title={label} onClick={() => apply(c)} disabled={isGuest}
              style={{
                width: 30, height: 30, borderRadius: 6, cursor: isGuest ? 'default' : 'pointer', background: c,
                border: (colored && color === c) ? '2px solid #fff' : '2px solid rgba(255,255,255,0.12)',
                boxShadow: (colored && color === c) ? '0 0 0 2px rgba(255,200,80,0.5)' : 'none',
              }}
            />
          ))}
        </div>
        {isGuest && (
          <div style={{
            position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
            gap: 5, fontSize: 10, fontWeight: 700, color: '#ffcc60', textAlign: 'center', padding: '0 10px',
          }}>
            🔒 {t('guest_customize_locked')}
          </div>
        )}
      </div>

      <div style={{ marginTop: 12, borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: 10 }}>
        <div style={{ color: '#666', fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', marginBottom: 7 }}>{t('size').toUpperCase()}</div>
        <SizeField
          valueM={item.w * PX_TO_M}
          min={0.1}
          decimals={2}
          stepM={STEP * PX_TO_M}
          stepBtnStyle={stepBtn}
          onChangeM={m => {
            const px = Math.max(2.6, Math.round(m / PX_TO_M));
            onResize(px, px);
          }}
        />
      </div>

      {LIGHTING_STYLES[item.type] && (
        <div style={{ marginTop: 12, borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: 10, position: 'relative' }}>
          <div style={{ color: '#666', fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', marginBottom: 7 }}>{t('style_label').toUpperCase()}</div>
          <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', opacity: isGuest ? 0.35 : 1 }}>
            {LIGHTING_STYLES[item.type].map(s => {
              const active = (item.style || LIGHTING_STYLES[item.type][0].id) === s.id;
              return (
                <button key={s.id} onClick={() => { if (!isGuest) onStyleChange(s.id); }} disabled={isGuest} style={{
                  flex: '1 1 28%', minWidth: 56, padding: '4px 5px', borderRadius: 7, cursor: isGuest ? 'default' : 'pointer',
                  border: active ? '1.5px solid rgba(255,200,80,0.8)' : '1.5px solid rgba(255,255,255,0.12)',
                  background: active ? 'rgba(255,200,80,0.15)' : 'rgba(255,255,255,0.05)',
                  color: active ? '#ffcc50' : '#aaa',
                  fontSize: 10, fontWeight: active ? 700 : 400, transition: 'all 0.15s',
                }}>{s.label}</button>
              );
            })}
          </div>
          {isGuest && (
            <div style={{
              position: 'absolute', inset: '17px 0 0 0', display: 'flex', alignItems: 'center', justifyContent: 'center',
              gap: 5, fontSize: 10, fontWeight: 700, color: '#ffcc60', textAlign: 'center', padding: '0 10px',
            }}>
              🔒 {t('guest_customize_locked')}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── DOORS & WINDOWS ─────────────────────────────────────────────────────────

const DRAG_THRESHOLD_OP = 6;

function Opening3D({ opening, room, roomW, roomD, roomH, adjacentRoom, onNavigate, onOpeningClick, isSelected, roomId, editable, roomRects, wallSegments, freeWalls, timeOfDay }) {
  const [hovered, setHovered]   = useState(false);
  const [dragSnap, setDragSnap] = useState(null); // { wall, pos } live during drag

  const { t } = useT();
  const dispatch     = useDispatch();
  const { camera, gl, controls: orbitControls } = useThree();

  const rc      = useMemo(() => new THREE.Raycaster(), []);
  const ndcVec  = useMemo(() => new THREE.Vector2(), []);
  const projVec = useMemo(() => new THREE.Vector3(), []);

  // Precomputed interior-wall-segment geometry (fixed plane + physical
  // extent, in the same room-local metre space as the room boundary) so a
  // drag can be tested against every hand-drawn wall in this room, not just
  // the room's own 4 sides — lets an opening move freely between "around
  // the room" and "inside the room" walls instead of being stuck on
  // whichever one it started on.
  const wallSegGeoms = useMemo(() => {
    if (!room || !wallSegments || !wallSegments.length) return [];
    const rx1 = room.x, rx2 = room.x + room.w, ry1 = room.y, ry2 = room.y + room.h;
    const out = [];
    for (const seg of wallSegments) {
      const sx1 = Math.min(seg.x1, seg.x2), sx2 = Math.max(seg.x1, seg.x2);
      const sy1 = Math.min(seg.y1, seg.y2), sy2 = Math.max(seg.y1, seg.y2);
      if (!(sx1 < rx2 && sx2 > rx1 && sy1 < ry2 && sy2 > ry1)) continue;
      const isH = seg.y1 === seg.y2;
      const wallLenM = isH ? Math.abs(seg.x2 - seg.x1) * CELL_M : Math.abs(seg.y2 - seg.y1) * CELL_M;
      const segStartLocal = isH ? (sx1 - room.x) * CELL_M - roomW / 2 : (sy1 - room.y) * CELL_M - roomD / 2;
      const centerLocal = isH ? (sy1 - room.y) * CELL_M - roomD / 2 : (sx1 - room.x) * CELL_M - roomW / 2;
      // A freestanding interior wall has two usable faces — offer both as
      // separate candidate planes (±half the wall's real thickness from its
      // centerline, same WT3/2 as the wallSegments render block) so the
      // closest-hit-wins logic below naturally lands a dragged item on
      // whichever side the cursor is actually pointing at, instead of
      // always the one fixed side this used to hardcode.
      for (const side of [1, -1]) {
        out.push({ id: seg.id, isH, wallLenM, segStartLocal, fixedLocal: centerLocal + side * 0.075, side });
      }
    }
    return out;
  }, [room, wallSegments, roomW, roomD]);

  const ctxRef = useRef({});
  ctxRef.current = { camera, gl, controls: orbitControls, roomW, roomD, roomId, openingId: opening.id, dispatch, opening, onOpeningClick, editable, roomRects, wallSegGeoms };

  const { onDragMove, onDragUp, dragState } = useMemo(() => {
    const dragState = { moved: false, startX: 0, startY: 0, snap: null };

    function onDragMove(e) {
      const { camera, gl, roomW, roomD, editable, opening, freeEdge, freeIsWall, freeSamples, freeMidY } = ctxRef.current;
      if (!editable) return; // view mode — no dragging
      if (!dragState.moved) {
        const dx = e.clientX - dragState.startX, dy = e.clientY - dragState.startY;
        if (Math.sqrt(dx * dx + dy * dy) < DRAG_THRESHOLD_OP) return;
        dragState.moved = true;
      }
      const rect = gl.domElement.getBoundingClientRect();

      // ── DRAWN ROOM: slide along the OUTLINE ───────────────────────────────
      // The rect-plane raycast below can't place this — an outline edge is at
      // any angle and may curve, so there's no plane to hit. Instead walk the
      // outline's own sample points and take whichever sits nearest the cursor
      // ON SCREEN: that works from any camera angle (a floor-plane hit goes
      // unstable when the camera looks along the horizon) and lets the opening
      // travel round corners onto a neighbouring wall, the way dragging across
      // rect walls already does.
      // Only a DRAWN ROOM's outline-edge opening takes this exclusive path: it
      // belongs to the room and must stay on the outline. An opening on a free
      // WALL falls through to the unified path below, where its own wall is one
      // of the candidates — so it can slide along it, hop to another free wall,
      // or leave for the room's walls, and its snap carries the wall id that
      // tells the drop which of those happened.
      if (freeEdge && !freeIsWall) {
        if (!freeSamples || !freeSamples.length) return;
        let best = null, bestD = Infinity;
        for (const s of freeSamples) {
          projVec.set(s.x, freeMidY, s.z).project(camera);
          if (projVec.z > 1) continue;   // behind the camera — projection mirrors
          const sx = rect.left + (projVec.x * 0.5 + 0.5) * rect.width;
          const sy = rect.top + (-projVec.y * 0.5 + 0.5) * rect.height;
          const d = (sx - e.clientX) ** 2 + (sy - e.clientY) ** 2;
          if (d < bestD) { bestD = d; best = s; }
        }
        if (!best) return;
        const snap = { free: true, edge: best.edge, t: best.t };
        dragState.snap = snap;
        setDragSnap(snap);
        return;
      }

      ndcVec.set(
        ((e.clientX - rect.left) / rect.width)  *  2 - 1,
        -((e.clientY - rect.top)  / rect.height) *  2 + 1
      );
      rc.setFromCamera(ndcVec, camera);

      const ray = rc.ray;
      const ox = ray.origin.x, oz = ray.origin.z;
      const dx2 = ray.direction.x, dz2 = ray.direction.z;
      const { roomRects: rects, roomW: rW, roomD: rD, wallSegGeoms } = ctxRef.current;
      const hw = rW / 2, hd = rD / 2;
      const isCompound = rects && rects.length > 1;

      const hits = [];

      if (isCompound) {
        // Compound rooms: cast against actual rect boundary planes, skip interior seams.
        // A seam is where another rect directly abuts this boundary at the hit point —
        // those positions have no wall geometry, so opening should not snap there.
        const minX = Math.min(...rects.map(r => r.x));
        const minY = Math.min(...rects.map(r => r.y));
        for (const r of rects) {
          const gz_top = (r.y       - minY) * CELL_M - rD / 2;
          const gz_bot = (r.y + r.h - minY) * CELL_M - rD / 2;
          const gx_lo  = (r.x       - minX) * CELL_M - rW / 2;
          const gx_hi  = (r.x + r.w - minX) * CELL_M - rW / 2;
          if (Math.abs(dz2) > 1e-6) {
            const tT = (gz_top - oz) / dz2;
            if (tT > 0) {
              const hx = ox + tT * dx2;
              if (hx >= gx_lo - 0.01 && hx <= gx_hi + 0.01) {
                const gx = minX + (hx + hw) / CELL_M;
                const seam = rects.some(o => o !== r && o.y + o.h === r.y && gx > o.x - 0.01 && gx < o.x + o.w + 0.01);
                if (!seam) hits.push({ wall: 'top',    t: tT, pos: (hx + hw) / rW, _wallGz: gz_top });
              }
            }
            const tB = (gz_bot - oz) / dz2;
            if (tB > 0) {
              const hx = ox + tB * dx2;
              if (hx >= gx_lo - 0.01 && hx <= gx_hi + 0.01) {
                const gx = minX + (hx + hw) / CELL_M;
                const seam = rects.some(o => o !== r && o.y === r.y + r.h && gx > o.x - 0.01 && gx < o.x + o.w + 0.01);
                if (!seam) hits.push({ wall: 'bottom', t: tB, pos: (hx + hw) / rW, _wallGz: gz_bot });
              }
            }
          }
          if (Math.abs(dx2) > 1e-6) {
            const tL = (gx_lo - ox) / dx2;
            if (tL > 0) {
              const hz = oz + tL * dz2;
              if (hz >= gz_top - 0.01 && hz <= gz_bot + 0.01) {
                const gy = minY + (hz + hd) / CELL_M;
                const seam = rects.some(o => o !== r && o.x + o.w === r.x && gy > o.y - 0.01 && gy < o.y + o.h + 0.01);
                if (!seam) hits.push({ wall: 'left',   t: tL, pos: (hz + hd) / rD, _wallGx: gx_lo });
              }
            }
            const tR = (gx_hi - ox) / dx2;
            if (tR > 0) {
              const hz = oz + tR * dz2;
              if (hz >= gz_top - 0.01 && hz <= gz_bot + 0.01) {
                const gy = minY + (hz + hd) / CELL_M;
                const seam = rects.some(o => o !== r && o.x === r.x + r.w && gy > o.y - 0.01 && gy < o.y + o.h + 0.01);
                if (!seam) hits.push({ wall: 'right',  t: tR, pos: (hz + hd) / rD, _wallGx: gx_hi });
              }
            }
          }
        }
      } else {
        // Simple rooms: original bounding-box raycast, no range restriction (pos is clamped after)
        if (Math.abs(dz2) > 1e-6) {
          const tTop = (-hd - oz) / dz2;
          if (tTop > 0) hits.push({ wall: 'top',    t: tTop, pos: (ox + tTop * dx2 + hw) / rW });
          const tBot = ( hd - oz) / dz2;
          if (tBot > 0) hits.push({ wall: 'bottom', t: tBot, pos: (ox + tBot * dx2 + hw) / rW });
        }
        if (Math.abs(dx2) > 1e-6) {
          const tL = (-hw - ox) / dx2;
          if (tL > 0) hits.push({ wall: 'left',  t: tL, pos: (oz + tL * dz2 + hd) / rD });
          const tR = ( hw - ox) / dx2;
          if (tR > 0) hits.push({ wall: 'right', t: tR, pos: (oz + tR * dz2 + hd) / rD });
        }
      }

      // Interior hand-drawn wall segments — collected alongside the room's
      // own boundary above (not instead of it) so an opening can be dragged
      // freely between "around the room" and "inside the room" walls. Each
      // is a single fixed plane bounded by its own physical length; a hit
      // only counts if the ray actually crosses within (or very near) that
      // span, so a short interior wall doesn't "steal" drags meant for a
      // room-boundary wall far behind it.
      for (const wg of wallSegGeoms) {
        let tt = null, raw = null;
        if (wg.isH && Math.abs(dz2) > 1e-6) {
          tt = (wg.fixedLocal - oz) / dz2;
          if (tt > 0) raw = ox + tt * dx2;
        } else if (!wg.isH && Math.abs(dx2) > 1e-6) {
          tt = (wg.fixedLocal - ox) / dx2;
          if (tt > 0) raw = oz + tt * dz2;
        }
        if (raw == null || tt == null) continue;
        const lo = wg.segStartLocal, hi = wg.segStartLocal + wg.wallLenM;
        if (raw < lo - 0.05 || raw > hi + 0.05) continue;
        const clampedLocal = Math.max(lo + 0.02 * wg.wallLenM, Math.min(hi - 0.02 * wg.wallLenM, raw));
        const pos = wg.isH ? (clampedLocal + rW / 2) / rW : (clampedLocal + rD / 2) / rD;
        const wallLabel = wg.isH ? (wg.side === 1 ? 'top' : 'bottom') : (wg.side === 1 ? 'left' : 'right');
        hits.push({
          wall: wallLabel, t: tt, pos,
          ...(wg.isH ? { _wallGz: wg.fixedLocal } : { _wallGx: wg.fixedLocal }),
          _wsSegId: wg.id, _wsIsH: wg.isH, _wsSegStartLocal: wg.segStartLocal, _wsWallLenM: wg.wallLenM, _wsSide: wg.side,
        });
      }

      // Auto-wall openings must stay on their original room-boundary wall
      // (existing, narrower behaviour — compound-room boundaries aren't
      // part of the free cross-wall dragging above). Everything else —
      // whether it started on the room boundary or an interior wall — can
      // land on any candidate wall, whichever the cursor is actually over.
      const isAutoWall = typeof opening.id === 'string' && opening.id.startsWith('aw-');
      const candidateHits = isAutoWall ? hits.filter(h => h.wall === opening.wall && h._wsSegId == null) : hits;

      // ── Free walls are drag targets too ───────────────────────────────────
      // They're curves, not planes, so the ray test above can't produce them.
      // Score them by how near they are to the cursor ON SCREEN, and score the
      // winning ray hit the same way, so the two are compared like for like and
      // an opening can be dragged off the room's wall onto an interior free one.
      const { freeWallTargets } = ctxRef.current;
      const midY = freeMidY ?? 1;
      const screenDist = (x, y, z) => {
        projVec.set(x, y, z).project(camera);
        if (projVec.z > 1) return Infinity;   // behind the camera
        const sx = rect.left + (projVec.x * 0.5 + 0.5) * rect.width;
        const sy = rect.top + (-projVec.y * 0.5 + 0.5) * rect.height;
        return Math.hypot(sx - e.clientX, sy - e.clientY);
      };
      // The cursor must actually be NEAR the free wall for it to be in the
      // running. Without a limit the nearest-on-screen wall could win from right
      // across the room, snatching an opening that was being dragged along the
      // room's own wall — which is what made the drag feel erratic.
      const FREE_SNAP_PX = 70;
      let bestFree = null, bestFreeD = Infinity;
      if (!isAutoWall) {   // auto-wall openings stay on their own boundary wall
        for (const sPt of (freeWallTargets || [])) {
          const d = screenDist(sPt.x, midY, sPt.z);
          if (d < bestFreeD) { bestFreeD = d; bestFree = sPt; }
        }
      }

      // A ray hit lies ON the cursor ray, so projecting it back to the screen
      // always lands ~0 px from the cursor — comparing the two by screen
      // distance therefore let the room's wall win every time, which is why the
      // opening kept flicking onto another wall. Decide by proximity instead:
      // if the cursor is on a free wall, that's the target. An opening already
      // living on a free wall gets a wider radius, so ordinary jitter while
      // sliding along it can't fling it onto the room's wall behind.
      if (bestFree && bestFreeD <= (freeIsWall ? 140 : FREE_SNAP_PX)) {
        // Wall decor mounts on ONE face — whichever of the two the cursor is
        // nearer to on screen, so dragging round the wall flips it over.
        let fwSide = 1;
        if (isWallDecorType(opening.type)) {
          const dA = screenDist(bestFree.x + bestFree.nx * bestFree.half, midY, bestFree.z + bestFree.nz * bestFree.half);
          const dB = screenDist(bestFree.x - bestFree.nx * bestFree.half, midY, bestFree.z - bestFree.nz * bestFree.half);
          fwSide = dB < dA ? -1 : 1;
        }
        const snapF = { free: true, fwId: bestFree.fwId, t: bestFree.t, side: fwSide };
        dragState.snap = snapF;
        setDragSnap({ ...snapF });
        return;
      }

      if (candidateHits.length === 0) return;

      const best = candidateHits.reduce((a, b) => a.t < b.t ? a : b);
      const snap = {
        wall: best.wall,
        pos:  best._wsSegId != null ? best.pos : Math.max(0.05, Math.min(0.95, best.pos)),
        ...(best._wallGz != null ? { _wallGz: best._wallGz } : {}),
        ...(best._wallGx != null ? { _wallGx: best._wallGx } : {}),
        ...(best._wsSegId != null
          ? { _wsSegId: best._wsSegId, _wsIsH: best._wsIsH, _wsSegStartLocal: best._wsSegStartLocal, _wsWallLenM: best._wsWallLenM, _wsSide: best._wsSide }
          : {}),
      };
      dragState.snap = snap;
      setDragSnap({ ...snap });
    }

    function onDragUp() {
      document.removeEventListener('pointermove', onDragMove);
      document.removeEventListener('pointerup',   onDragUp);
      document.body.style.cursor = '';

      const { controls, dispatch, roomId, openingId, onOpeningClick, opening, editable, roomW: rW, roomD: rD } = ctxRef.current;
      if (editable && controls) controls.enabled = true;

      // In view mode OR if the pointer didn't move: treat as a click (navigate / select)
      if (!editable || !dragState.moved) {
        if (onOpeningClick) onOpeningClick(opening);
      } else if (dragState.snap) {
        const snap = dragState.snap;
        const isAutoWall = typeof opening.id === 'string' && opening.id.startsWith('aw-');

        // Landed on an interior FREE wall. Openings live on the wall itself, so
        // arriving from anywhere else means re-homing: drop it from wherever it
        // was and recreate it there with the same type/size/colour/style. This
        // is what lets a door be dragged off the room's own wall onto one.
        const common = { type: opening.type, width: opening.width, height: opening.height, sill: opening.sill, color: opening.color };
        if (opening.style) common.style = opening.style;

        if (snap.free && snap.fwId != null) {
          if (opening._fwId === snap.fwId) {
            const changes = { t: snap.t };
            if (isWallDecorType(opening.type) && snap.side != null) changes.side = snap.side;
            dispatch(updateFreeWallOpening({ wallId: snap.fwId, openingId: opening._fwOpId, changes }));
          } else {
            if (opening._fwId != null)          dispatch(removeFreeWallOpening({ wallId: opening._fwId, openingId: opening._fwOpId }));
            else if (opening._awKey)            dispatch(removeAutoWallOpening({ key: opening._awKey, openingId: opening._awOpId }));
            else if (opening._wsSegId != null)  dispatch(removeWallSegOpening({ wallId: opening._wsSegId, openingId: opening._wsOpId }));
            else                                dispatch(removeOpening({ roomId, openingId }));
            const sideProp = isWallDecorType(opening.type) && snap.side != null ? { side: snap.side } : {};
            dispatch(addFreeWallOpening({ wallId: snap.fwId, t: snap.t, ...common, ...sideProp }));
          }
        } else if (opening._fwId != null) {
          // Leaving a free wall for one of the room's own walls / a segment.
          dispatch(removeFreeWallOpening({ wallId: opening._fwId, openingId: opening._fwOpId }));
          if (snap._wsSegId != null) {
            const local = snap._wsIsH ? (-rW / 2 + snap.pos * rW) : (-rD / 2 + snap.pos * rD);
            const segT = Math.max(0.02, Math.min(0.98, (local - snap._wsSegStartLocal) / snap._wsWallLenM));
            dispatch(addWallSegOpening({ wallId: snap._wsSegId, t: segT, ...common }));
          } else {
            dispatch(addOpening({ roomId, wall: snap.wall, pos: snap.pos, ...common }));
          }
        } else if (snap.free) {
          // Drawn room: the opening rides an outline edge. wall/pos come along
          // as the coarse fallback the 2D plan reads (same shape the Builder
          // writes for a free-wall opening).
          dispatch(moveOpening({ roomId, openingId, wall: opening.wall || 'top', pos: snap.t, edge: snap.edge, t: snap.t }));
        } else if (isAutoWall) {
          // Auto-wall opening: convert snap.pos back to t and update autoWallOpenings state
          dispatch(moveAutoWallOpening({ key: opening._awKey, opId: opening._awOpId, t: opening._posToT(snap.pos) }));
        } else if (opening._wsSegId != null && snap._wsSegId === opening._wsSegId) {
          // Same interior wall as before — reposition along it, and (for a
          // wall-decor item, which actually mounts on one specific face)
          // flip to whichever of the wall's two sides the cursor ended up
          // nearest to.
          const changes = { t: opening._posToT(snap.pos) };
          if (isWallDecorType(opening.type) && snap._wsSide != null) changes.side = snap._wsSide;
          dispatch(updateWallSegOpening({ wallId: opening._wsSegId, openingId: opening._wsOpId, changes }));
        } else if (opening._wsSegId == null && snap._wsSegId == null) {
          // Room boundary to room boundary, as before.
          dispatch(moveOpening({ roomId, openingId, wall: snap.wall, pos: snap.pos }));
        } else {
          // Crossing between "homes" — the room's own boundary and a hand-
          // drawn wall segment are two entirely separate places an opening
          // can live (see addWallSegOpening/addOpening in houseSlice), so
          // there's no single "move" action; recreate it at the new home
          // with the same type/size/color/style and drop it from the old one.
          const common = { type: opening.type, width: opening.width, height: opening.height, sill: opening.sill, color: opening.color };
          if (opening.style) common.style = opening.style;

          if (opening._wsSegId != null) {
            dispatch(removeWallSegOpening({ wallId: opening._wsSegId, openingId: opening._wsOpId }));
          } else {
            dispatch(removeOpening({ roomId, openingId }));
          }

          if (snap._wsSegId != null) {
            const local = snap._wsIsH ? (-rW / 2 + snap.pos * rW) : (-rD / 2 + snap.pos * rD);
            const segT = Math.max(0.02, Math.min(0.98, (local - snap._wsSegStartLocal) / snap._wsWallLenM));
            const sideProp = isWallDecorType(opening.type) && snap._wsSide != null ? { side: snap._wsSide } : {};
            dispatch(addWallSegOpening({ wallId: snap._wsSegId, t: segT, ...common, ...sideProp }));
          } else {
            dispatch(addOpening({ roomId, wall: snap.wall, pos: snap.pos, ...common }));
          }
        }
      }

      dragState.moved = false;
      dragState.snap  = null;
      setDragSnap(null);
    }

    return { onDragMove, onDragUp, dragState };
  }, []);

  useEffect(() => () => {
    document.removeEventListener('pointermove', onDragMove);
    document.removeEventListener('pointerup',   onDragUp);
  }, [onDragMove, onDragUp]);

  function onPointerDown(e) {
    if (!ctxRef.current.onOpeningClick) return;
    e.stopPropagation();
    dragState.startX = e.nativeEvent.clientX;
    dragState.startY = e.nativeEvent.clientY;
    dragState.moved  = false;
    dragState.snap   = null;
    const { controls, editable } = ctxRef.current;
    // Only disable orbit in edit mode (view mode click should not lock navigation)
    if (editable && controls) controls.enabled = false;
    document.body.style.cursor = editable ? 'grabbing' : 'pointer';
    document.addEventListener('pointermove', onDragMove);
    document.addEventListener('pointerup',   onDragUp);
  }

  const { type } = opening;
  // Use drag snap position when dragging, otherwise use stored opening position
  const displayWall = dragSnap?.wall ?? opening.wall;
  const displayPos  = dragSnap?.pos  ?? (opening.pos ?? 0.5);

  // ── DRAWN ROOM: opening on an OUTLINE EDGE ────────────────────────────────
  // A free-form room's walls are its outline edges (`edge` + `t` along the
  // edge), which sit at any angle and may curve — "top wall, 50%" can't
  // describe them. Those openings carry wall/pos too, as a coarse fallback for
  // renderers that only know bounding-box sides, but following it here dropped
  // them onto the bbox's top wall instead of the wall they were placed on,
  // which read as the opening not showing up at all. Take the point and the
  // facing straight off the outline instead.
  // While dragging, follow the live snap instead of the stored spot.
  const freeIndex = dragSnap?.free ? dragSnap.edge : opening.edge;
  const freeT     = dragSnap?.free ? dragSnap.t    : (opening.t ?? 0.5);
  // Which curve this opening rides: an outline EDGE of a drawn room, or a free
  // interior WALL standing inside it. Both are the same shape of curve, so one
  // code path places, faces and drags either of them.
  const freeCarrier = useMemo(() => {
    // Mid-drag onto a free wall, follow THAT wall — the opening doesn't live on
    // it yet, so its own `_fwWall` is the wrong one (or absent, when the drag
    // started on the room's boundary). Without this the item stayed put while
    // being dragged and only jumped on release.
    const dragFw = (dragSnap?.free && dragSnap.fwId != null)
      ? (freeWalls || []).find(w => w.id === dragSnap.fwId) : null;
    const fw = dragFw
      ? { x1: dragFw.x1, y1: dragFw.y1, x2: dragFw.x2, y2: dragFw.y2, bulge: dragFw.bulge || 0, thickness: dragFw.thickness ?? 0.16 }
      : opening._fwWall;
    // A free-standing interior wall has two open faces, so there's no "inside"
    // to turn towards — the tangent's left is as good as its right.
    if (fw) return { a: { x: fw.x1, y: fw.y1, bulge: fw.bulge || 0 }, b: { x: fw.x2, y: fw.y2 }, flip: false, fw };
    const P = room?.pts;
    if (!P || P.length < 3 || freeIndex == null) return null;
    const a = P[freeIndex % P.length], b = P[(freeIndex + 1) % P.length];
    if (!a || !b) return null;
    return { a, b, flip: loopSignedArea(P) < 0, fw: null };
  }, [room, opening._fwWall, freeIndex, dragSnap?.free, dragSnap?.fwId, freeWalls]);

  const freeEdge = useMemo(() => {
    if (!freeCarrier) return null;
    const { a, b } = freeCarrier;
    const q = edgePointAt(a, b, freeT);
    // A group rotated by rotY sends its local +z to (sin rotY, cos rotY); that
    // has to land on the wall's INWARD normal so the door faces into the room.
    // Which side is inward follows the outline's winding, the same rule the
    // wall panels use (see flipAll in buildCompoundWallSegs).
    const rotY0 = freeCarrier.flip ? Math.PI - q.angle : -q.angle;
    // An interior free wall has TWO usable faces, so a wall-decor item records
    // which one it hangs on (side 1 / -1, the same convention the straight
    // interior walls use). Flipping the facing by 180° turns the item around AND
    // sends the offset below to the opposite face, so one formula covers both.
    const isDecor = isWallDecorType(type);
    const side = (isDecor && freeCarrier.fw) ? (opening.side ?? 1) : 1;
    const rotY = side === -1 ? rotY0 + Math.PI : rotY0;
    // A room's wall is a zero-thickness plane, but an interior free wall is a
    // real box — an item mounted on its centreline would sit half buried inside
    // it (exactly what the straight interior walls needed). Push it out to the
    // face it's mounted on; a door/window cuts through, so no offset.
    const off = (freeCarrier.fw && isDecor)
      ? ((freeCarrier.fw.thickness ?? 0.16) * CELL_M) / 2 : 0;
    return {
      gx: (q.x - room.x) * CELL_M - roomW / 2 + Math.sin(rotY) * off,
      gz: (q.y - room.y) * CELL_M - roomD / 2 + Math.cos(rotY) * off,
      rotY,
    };
  }, [freeCarrier, freeT, room, roomW, roomD, opening._fwWall, opening.side, type]);

  // Sample points along the WHOLE outline, in the same room-local metres as the
  // walls — the drag picks whichever is nearest the cursor (see onDragMove).
  // Curved edges get more samples so the opening slides smoothly round them.
  const freeSamples = useMemo(() => {
    if (!freeCarrier) return null;
    const out = [];
    const push = (a, b, edge) => {
      const N = a.bulge ? 32 : 12;
      for (let k = 0; k <= N; k++) {
        const t = Math.min(0.95, Math.max(0.05, k / N));
        const q = edgePointAt(a, b, t);
        out.push({
          edge, t,
          x: (q.x - room.x) * CELL_M - roomW / 2,
          z: (q.y - room.y) * CELL_M - roomD / 2,
        });
      }
    };
    // An interior free wall is a wall of its own — the item slides along IT, and
    // must not hop onto the room's outline. A room edge can travel the whole
    // outline, round corners onto the neighbouring wall.
    // Keyed off the CARRIER, not the opening: mid-drag onto a free wall the
    // carrier is that wall while the opening still lives elsewhere — and if that
    // elsewhere is a plain rectangular room there is no `pts` to walk at all.
    if (freeCarrier.fw) push(freeCarrier.a, freeCarrier.b, null);
    else {
      const P = room?.pts;
      if (!P || P.length < 3) return null;
      for (let i = 0; i < P.length; i++) push(P[i], P[(i + 1) % P.length], i);
    }
    return out;
  }, [freeCarrier, room, roomW, roomD]);

  // Every free wall in this room, sampled — so an opening can be dragged ONTO
  // one from the room's own walls (and back off again). Wall geometry travels
  // with each sample, since landing there re-homes the opening onto that wall.
  const freeWallTargets = useMemo(() => {
    if (!room || !freeWalls?.length) return [];
    const out = [];
    for (const w of freeWalls) {
      const sx1 = Math.min(w.x1, w.x2), sx2 = Math.max(w.x1, w.x2);
      const sy1 = Math.min(w.y1, w.y2), sy2 = Math.max(w.y1, w.y2);
      if (!(sx1 < room.x + room.w && sx2 > room.x && sy1 < room.y + room.h && sy2 > room.y)) continue;
      const a = { x: w.x1, y: w.y1, bulge: w.bulge || 0 }, b = { x: w.x2, y: w.y2 };
      const N = w.bulge ? 32 : 12;
      for (let k = 0; k <= N; k++) {
        const t = Math.min(0.95, Math.max(0.05, k / N));
        const q = edgePointAt(a, b, t);
        out.push({
          fwId: w.id, t,
          x: (q.x - room.x) * CELL_M - roomW / 2,
          z: (q.y - room.y) * CELL_M - roomD / 2,
          // Face normal + half thickness, so a decor drag can tell which of the
          // wall's two sides the cursor is on.
          nx: -Math.sin(-q.angle), nz: Math.cos(-q.angle),
          half: ((w.thickness ?? 0.16) * CELL_M) / 2,
        });
      }
    }
    return out;
  }, [room, freeWalls, roomW, roomD]);

  ctxRef.current.freeEdge = !!freeEdge;
  ctxRef.current.freeIsWall = !!freeCarrier?.fw;
  ctxRef.current.freeSamples = freeSamples;
  ctxRef.current.freeWallTargets = freeWallTargets;
  // Vertical centre of the opening — the height the drag hit-tests at, so the
  // cursor tracks the item itself rather than the floor line under it.
  ctxRef.current.freeMidY = type === 'window'
    ? (opening.sill ?? 0.9) + (opening.height ?? 0.98) / 2
    : (opening.height ?? 2.05) / 2;

  // Arch-shaped void geometry (only built when type === 'archway')
  const archVoidShape = useMemo(() => {
    if (type !== 'archway') return null;
    const dW    = opening.width  || 1.2;
    const dH    = opening.height || 2.4;
    const archR = dW / 2;
    const rectH = dH - archR;
    const s = new THREE.Shape();
    s.moveTo(-dW / 2, 0);
    s.lineTo( dW / 2, 0);
    s.lineTo( dW / 2, rectH);
    s.absarc(0, rectH, archR, 0, Math.PI, false); // right → top → left
    s.lineTo(-dW / 2, 0);
    return s;
  }, [type, opening.width, opening.height]);

  // Arch ring as solid extruded shape — clean closed ends, no open torus gaps
  const archRingShape = useMemo(() => {
    if (type !== 'archway') return null;
    const dW = opening.width || 1.2;
    const archR = dW / 2;
    const fW = 0.08;
    const s = new THREE.Shape();
    s.moveTo(archR + fW, 0);
    s.absarc(0, 0, archR + fW, 0, Math.PI, false); // outer upper arc
    s.lineTo(-(archR - fW), 0);                      // bridge to inner-left
    s.absarc(0, 0, archR - fW, Math.PI, 0, true);   // inner upper arc
    s.lineTo(archR + fW, 0);                          // close
    return s;
  }, [type, opening.width]);

  // Gothic (pointed two-centered) archway void — used by the 'gothic' archway design.
  const archGothicShape = useMemo(() => {
    if (type !== 'archway' || opening.style !== 'gothic') return null;
    const dW = opening.width || 1.2;
    const dH = opening.height || 2.4;
    const archR = dW / 2;
    const peakH = archR * 1.15;
    const rectH = dH - peakH;
    const s = new THREE.Shape();
    s.moveTo(-dW / 2, 0);
    s.lineTo(dW / 2, 0);
    s.lineTo(dW / 2, rectH);
    s.quadraticCurveTo(dW / 2, rectH + peakH * 0.72, 0, rectH + peakH);
    s.quadraticCurveTo(-dW / 2, rectH + peakH * 0.72, -dW / 2, rectH);
    s.closePath();
    return s;
  }, [type, opening.style, opening.width, opening.height]);

  // Elliptical (flatter, wider) archway void — used by the 'elliptical' archway design.
  const archEllipticalShape = useMemo(() => {
    if (type !== 'archway' || opening.style !== 'elliptical') return null;
    const dW = opening.width || 1.2;
    const dH = opening.height || 2.4;
    const archR = dW / 2;
    const ellH = archR * 0.55;
    const rectH = dH - ellH;
    const s = new THREE.Shape();
    s.moveTo(-dW / 2, 0);
    s.lineTo(dW / 2, 0);
    s.lineTo(dW / 2, rectH);
    s.absellipse(0, rectH, archR, ellH, 0, Math.PI, false, 0);
    s.lineTo(-dW / 2, rectH);
    s.closePath();
    return s;
  }, [type, opening.style, opening.width, opening.height]);

  // Arched-top window glazing outline — rectangle + semicircle, same recipe
  // as archVoidShape above but sized to the window's own width/height.
  const windowArchedShape = useMemo(() => {
    if (type !== 'window' || opening.style !== 'arched') return null;
    const wW = opening.width || 0.98;
    const wH = opening.height || 0.92;
    const archR = wW / 2;
    const straightH = wH - archR;
    const s = new THREE.Shape();
    s.moveTo(-wW / 2, 0);
    s.lineTo(wW / 2, 0);
    s.lineTo(wW / 2, straightH);
    s.absarc(0, straightH, archR, 0, Math.PI, false);
    s.lineTo(-wW / 2, straightH);
    s.closePath();
    return s;
  }, [type, opening.style, opening.width, opening.height]);

  // "Painting" items show the real uploaded photo (loaded async — see
  // useImageTexture); the seeded procedural artwork is kept only as a
  // fallback so a painting never renders blank during the brief load.
  const paintingPhotoTex = useImageTexture(type === 'painting' ? PAINTING_IMAGE_URL : null);
  const paintingArtTex = useMemo(() => {
    if (type !== 'painting') return null;
    const idStr = String(opening.id);
    let seed = 0;
    for (let i = 0; i < idStr.length; i++) seed = (seed * 31 + idStr.charCodeAt(i)) | 0;
    return makePaintingArtTexture(Math.abs(seed) || 1);
  }, [type, opening.id]);
  const paintingTex = paintingPhotoTex || paintingArtTex;

  const rt = adjacentRoom ? ROOM_TYPES.find(r => r.type === adjacentRoom.type) : null;

  // Build group transform — use drag position when dragging, stored position otherwise
  let gx, gz, rotY;
  if (freeEdge) {
    gx = freeEdge.gx; gz = freeEdge.gz; rotY = freeEdge.rotY;
  } else if (!dragSnap && opening.absX !== undefined && opening.absY !== undefined) {
    // Step-wall opening: pixel coords relative to room BB origin → centre-relative metres
    gx = opening.absX * PX_TO_M - roomW / 2;
    gz = opening.absY * PX_TO_M - roomD / 2;
    if      (displayWall === 'top')    rotY = 0;
    else if (displayWall === 'bottom') rotY = Math.PI;
    else if (displayWall === 'left')   rotY = Math.PI / 2;
    else                               rotY = -Math.PI / 2;
  } else if (displayWall === 'top') {
    gx = -roomW / 2 + displayPos * roomW;
    gz = (dragSnap?._wallGz ?? opening._wallGz) ?? -roomD / 2;
    rotY = 0;
  } else if (displayWall === 'bottom') {
    gx = -roomW / 2 + displayPos * roomW;
    gz = (dragSnap?._wallGz ?? opening._wallGz) ?? roomD / 2;
    rotY = Math.PI;
  } else if (displayWall === 'left') {
    gx = (dragSnap?._wallGx ?? opening._wallGx) ?? -roomW / 2;
    gz = -roomD / 2 + displayPos * roomD;
    rotY = Math.PI / 2;
  } else {
    gx = (dragSnap?._wallGx ?? opening._wallGx) ?? roomW / 2;
    gz = -roomD / 2 + displayPos * roomD;
    rotY = -Math.PI / 2;
  }

  const canNavigate = (type === 'door' || type === 'archway') && !!adjacentRoom && !!onNavigate && !dragSnap;
  const baseFrameColor = opening.color || '#e0d8c8';
  const frameColor     = hovered ? '#f0c860' : baseFrameColor;

  const hitProps = {
    onPointerOver: (e) => {
      e.stopPropagation(); setHovered(true);
      document.body.style.cursor = editable ? (dragSnap ? 'grabbing' : 'grab') : (canNavigate ? 'pointer' : '');
    },
    onPointerOut:  (e) => { e.stopPropagation(); setHovered(false); if (!dragSnap) document.body.style.cursor = ''; },
    onPointerDown: onPointerDown,
  };
  const selColor = dragSnap ? '#44aaff' : '#ffaa20';
  const selEmissive = dragSnap ? '#004488' : isSelected ? '#996600' : hovered ? '#c89820' : '#000000';
  const selEmissiveIntensity = dragSnap ? 0.40 : isSelected ? 0.35 : hovered ? 0.20 : 0;
  const showOutline = isSelected || !!dragSnap;

  // Shared hover/selection chrome for the 5 wall-decor items below — a
  // floating label above the item and a wireframe box around it, matching
  // the door/window/archway pattern above exactly.
  const renderHoverLabel = (icon, name, topY) => (hovered || isSelected || dragSnap) && (
    <Html center position={[0, topY, 0.12]} distanceFactor={7} zIndexRange={[10, 0]}>
      <div style={{
        background: dragSnap ? 'rgba(30,100,200,0.95)' : isSelected ? 'rgba(200,130,0,0.97)' : 'rgba(28,26,23,0.88)',
        color: '#f0e8d8', padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600,
        fontFamily: 'system-ui,sans-serif', whiteSpace: 'nowrap', pointerEvents: 'none',
        border: `1.5px solid ${dragSnap ? '#44aaff' : isSelected ? '#ffaa20' : 'rgba(255,255,255,0.14)'}`,
        letterSpacing: '0.03em', boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
      }}>
        {icon} {dragSnap ? `→ ${t('wall_' + dragSnap.wall)} ${t('wall_suffix')}` : isSelected ? t('selected_label') : name}
      </div>
    </Html>
  );
  const renderOutline = (midY, z, w, h) => showOutline && (
    <group position={[0, midY, z]} scale={[w, h, 0.18]}>
      <lineSegments geometry={_unitEdgeGeo}>
        <lineBasicMaterial color={selColor} />
      </lineSegments>
    </group>
  );

  // ── DOOR ─────────────────────────────────────────────────────────────────
  if (type === 'door') {
    // Doors are GLB models now (door1…door21). A legacy/unknown style id falls
    // back to door1 so nothing renders blank. Keeps the invisible hitbox + the
    // edit-mode label/outline for selection & drag.
    const style = isGlbDoor(opening.style) ? opening.style : GLB_DOOR_FALLBACK;
    const dW = opening.width  || 0.86;
    const dH = opening.height || 2.05;
    const fW = 0.065;            // hitbox/outline padding (former casing width)
    const fD = 0.12;             // wall thickness the opening spans
    const pZ = fD / 2;
    const pc = opening.color || '#e8e0d4';
    const hitbox = (
      <mesh position={[0, dH / 2, pZ]} {...hitProps}>
        <boxGeometry args={[dW + fW * 2, dH + fW, fD + 0.05]} />
        <meshStandardMaterial transparent opacity={0} depthWrite={false} />
      </mesh>
    );
    const label = renderHoverLabel('🚪', t('door'), dH + 0.38);
    const outline = renderOutline(dH / 2, pZ, dW + fW * 2 + 0.04, dH + fW + 0.04);
    return (
      <group position={[gx, 0, gz]} rotation={[0, rotY, 0]}>
        {hitbox}
        <GLBDoor styleId={style} dW={dW} dH={dH} color={pc} tint={opening.colored ? (opening.color || null) : null} />
        {label}{outline}
      </group>
    );
  }

  if (type === 'archway') {
    const style = opening.style || 'round';
    const dW    = opening.width  || 1.2;
    const dH    = opening.height || 2.4;
    const archR = dW / 2;
    const fW = 0.072, fD = 0.13;
    const emissive = selEmissive;
    const fc = shadeColor(frameColor, 0.04);
    const label = renderHoverLabel('🏛️', t('archway'), dH + 0.3);
    const outline = renderOutline(dH / 2, 0.08, dW + fW * 2 + 0.04, dH + fW + 0.04);
    const hitbox = (
      <mesh position={[0, dH / 2, 0.08]} {...hitProps}>
        <boxGeometry args={[dW + fW * 2, dH + fW, 0.2]} />
        <meshStandardMaterial transparent opacity={0} depthWrite={false} />
      </mesh>
    );
    const jambs = (rectH) => (
      <>
        <mesh position={[-dW / 2 - fW / 2, rectH / 2, fD / 2]}>
          <boxGeometry args={[fW, rectH, fD]} />
          <meshStandardMaterial color={fc} roughness={0.50} emissive={emissive} emissiveIntensity={selEmissiveIntensity} />
        </mesh>
        <mesh position={[dW / 2 + fW / 2, rectH / 2, fD / 2]}>
          <boxGeometry args={[fW, rectH, fD]} />
          <meshStandardMaterial color={fc} roughness={0.50} emissive={emissive} emissiveIntensity={selEmissiveIntensity} />
        </mesh>
      </>
    );

    // ── Round Arch (default) ─────────────────────────────────────────────
    if (style === 'round') {
      const rectH = dH - archR;
      return (
        <group position={[gx, 0, gz]} rotation={[0, rotY, 0]}>
          {hitbox}
          <mesh position={[0, 0, 0.014]}>
            <shapeGeometry args={[archVoidShape]} />
            {/* Genuinely see-through — was a solid near-black fill, which
                blocked any view through the archway to whatever's actually
                on the other side (matches the already-correct gothic style). */}
            <meshStandardMaterial transparent opacity={0} depthWrite={false} />
          </mesh>
          {jambs(rectH)}
          <mesh position={[0, rectH, 0]}>
            <extrudeGeometry args={[archRingShape, { depth: fD, bevelEnabled: false }]} />
            <meshStandardMaterial color={fc} roughness={0.50} emissive={emissive} emissiveIntensity={selEmissiveIntensity} side={THREE.DoubleSide} />
          </mesh>
          <mesh position={[0, rectH + archR + 0.032, fD * 0.55]}>
            <boxGeometry args={[fW * 1.4, 0.065, fD * 0.5]} />
            <meshStandardMaterial color={shadeColor(fc, -0.08)} roughness={0.44} />
          </mesh>
          {label}{outline}
        </group>
      );
    }

    // ── Gothic Arch (pointed) ────────────────────────────────────────────
    if (style === 'gothic') {
      const peakH = archR * 1.15;
      const rectH = dH - peakH;
      return (
        <group position={[gx, 0, gz]} rotation={[0, rotY, 0]}>
          {hitbox}
          <mesh position={[0, 0, 0.014]}>
            <shapeGeometry args={[archGothicShape]} />
            {/* Genuinely see-through — was a solid near-black fill. */}
            <meshStandardMaterial transparent opacity={0} depthWrite={false} />
          </mesh>
          {jambs(rectH)}
          <mesh position={[0, 0, 0]}>
            <extrudeGeometry args={[archGothicShape, { depth: fD, bevelEnabled: false }]} />
            <meshStandardMaterial color={fc} roughness={0.5} transparent opacity={0} depthWrite={false} />
          </mesh>
          {/* Trim ring traced along the two pointed curves via stacked thin segments */}
          {Array.from({ length: 10 }).map((_, i) => {
            const t = i / 9;
            const side = t < 0.5 ? -1 : 1;
            const tt = side < 0 ? t * 2 : (t - 0.5) * 2;
            const x = side * dW / 2 * (1 - tt);
            const y = rectH + peakH * (1 - Math.pow(1 - tt, 1.6));
            return (
              <mesh key={`gr${i}`} position={[x, y, fD / 2]}>
                <boxGeometry args={[0.09, 0.09, fD]} />
                <meshStandardMaterial color={fc} roughness={0.5} emissive={emissive} emissiveIntensity={selEmissiveIntensity} />
              </mesh>
            );
          })}
          <mesh position={[0, rectH + peakH + 0.02, fD * 0.55]}>
            <boxGeometry args={[fW * 1.1, 0.05, fD * 0.5]} />
            <meshStandardMaterial color={shadeColor(fc, -0.1)} roughness={0.44} />
          </mesh>
          {label}{outline}
        </group>
      );
    }

    // ── Flat Cased ────────────────────────────────────────────────────────
    if (style === 'flat') {
      return (
        <group position={[gx, 0, gz]} rotation={[0, rotY, 0]}>
          {hitbox}
          {jambs(dH)}
          <mesh position={[0, dH + fW / 2, fD / 2]}>
            <boxGeometry args={[dW + fW * 2, fW, fD]} />
            <meshStandardMaterial color={fc} roughness={0.5} emissive={emissive} emissiveIntensity={selEmissiveIntensity} />
          </mesh>
          {/* Traditional flat trim head board, slightly proud of the wall face */}
          <mesh position={[0, dH + fW + 0.02, fD * 0.4]}>
            <boxGeometry args={[dW + fW * 2 + 0.05, 0.05, fD * 0.5]} />
            <meshStandardMaterial color={shadeColor(fc, -0.05)} roughness={0.46} />
          </mesh>
          {label}{outline}
        </group>
      );
    }

    // ── Elliptical Arch ───────────────────────────────────────────────────
    if (style === 'elliptical') {
      const ellH = archR * 0.55;
      const rectH = dH - ellH;
      return (
        <group position={[gx, 0, gz]} rotation={[0, rotY, 0]}>
          {hitbox}
          <mesh position={[0, 0, 0.014]}>
            <shapeGeometry args={[archEllipticalShape]} />
            {/* Genuinely see-through — was a solid near-black fill. */}
            <meshStandardMaterial transparent opacity={0} depthWrite={false} />
          </mesh>
          {jambs(rectH)}
          {Array.from({ length: 12 }).map((_, i) => {
            const ang = (i / 11) * Math.PI;
            const x = Math.cos(ang) * archR;
            const y = rectH + Math.sin(ang) * ellH;
            return (
              <mesh key={`er${i}`} position={[x, y, fD / 2]}>
                <boxGeometry args={[0.085, 0.075, fD]} />
                <meshStandardMaterial color={fc} roughness={0.5} emissive={emissive} emissiveIntensity={selEmissiveIntensity} />
              </mesh>
            );
          })}
          <mesh position={[0, rectH + ellH + 0.015, fD * 0.55]}>
            <boxGeometry args={[fW * 1.3, 0.05, fD * 0.5]} />
            <meshStandardMaterial color={shadeColor(fc, -0.08)} roughness={0.44} />
          </mesh>
          {label}{outline}
        </group>
      );
    }

    // ── Roman Keystone (round arch + voussoir stonework) ─────────────────
    if (style === 'roman') {
      const rectH = dH - archR;
      const stoneColor = '#c8bfa8';
      return (
        <group position={[gx, 0, gz]} rotation={[0, rotY, 0]}>
          {hitbox}
          <mesh position={[0, 0, 0.014]}>
            <shapeGeometry args={[archVoidShape]} />
            {/* Genuinely see-through — was a solid near-black fill, which
                blocked any view through the archway to whatever's actually
                on the other side (matches the already-correct gothic style). */}
            <meshStandardMaterial transparent opacity={0} depthWrite={false} />
          </mesh>
          {jambs(rectH)}
          <mesh position={[0, rectH, 0]}>
            <extrudeGeometry args={[archRingShape, { depth: fD, bevelEnabled: false }]} />
            <meshStandardMaterial color={stoneColor} roughness={0.75} side={THREE.DoubleSide} />
          </mesh>
          {/* Radial voussoir seam lines */}
          {Array.from({ length: 7 }).map((_, i) => {
            const ang = (i / 6) * Math.PI;
            return (
              <mesh key={`vs${i}`} position={[Math.cos(ang) * archR, rectH + Math.sin(ang) * archR, fD / 2]} rotation={[0, 0, ang]}>
                <boxGeometry args={[0.012, fW * 1.3, fD + 0.002]} />
                <meshStandardMaterial color={shadeColor(stoneColor, -0.2)} roughness={0.8} />
              </mesh>
            );
          })}
          {/* Large central keystone */}
          <mesh position={[0, rectH + archR + 0.05, fD * 0.55]}>
            <boxGeometry args={[fW * 1.9, 0.11, fD * 0.55]} />
            <meshStandardMaterial color={shadeColor(stoneColor, -0.1)} roughness={0.7} />
          </mesh>
          {label}{outline}
        </group>
      );
    }

    // ── Modern Minimal (thin flush reveal, no arch) ──────────────────────
    if (style === 'modern') {
      const trimW = 0.03;
      return (
        <group position={[gx, 0, gz]} rotation={[0, rotY, 0]}>
          {hitbox}
          {[
            [-dW / 2 - trimW / 2, dH / 2, fD / 2, trimW, dH, fD],
            [ dW / 2 + trimW / 2, dH / 2, fD / 2, trimW, dH, fD],
            [0, dH + trimW / 2, fD / 2, dW + trimW * 2, trimW, fD],
          ].map(([x, y, z, w, h, d], i) => (
            <mesh key={`mt${i}`} position={[x, y, z]}>
              <boxGeometry args={[w, h, d]} />
              <meshStandardMaterial color={shadeColor(fc, -0.12)} roughness={0.35} emissive={emissive} emissiveIntensity={selEmissiveIntensity} />
            </mesh>
          ))}
          {label}{outline}
        </group>
      );
    }
  }

  // ── WINDOW ────────────────────────────────────────────────────────────────
  if (type === 'window') {
    // Windows are GLB models now (window1…window15). A legacy/unknown style id
    // falls back to window1. Keeps the invisible hitbox + edit label/outline.
    const style = isGlbWindow(opening.style) ? opening.style : GLB_WINDOW_FALLBACK;
    const wW    = opening.width  || 0.98;
    const wH    = opening.height || 0.92;
    const wSill = opening.sill   || 0.90;
    const midY  = wSill + wH / 2;
    const fW    = 0.052;   // hitbox/outline padding
    const fD    = 0.130;   // frame depth through the wall
    const hitbox = (
      <mesh position={[0, midY, fD / 2]} {...hitProps}>
        <boxGeometry args={[wW + fW * 2 + 0.05, wH + fW * 2 + 0.05, fD + 0.06]} />
        <meshStandardMaterial transparent opacity={0} depthWrite={false} />
      </mesh>
    );
    const label = renderHoverLabel('🪟', t('window'), wSill + wH + 0.32);
    const outline = renderOutline(midY, fD / 2, wW + fW * 2 + 0.06, wH + fW * 2 + 0.06);
    return (
      <group position={[gx, 0, gz]} rotation={[0, rotY, 0]}>
        {hitbox}
        <GLBWindow styleId={style} wW={wW} wH={wH} wSill={wSill} tint={opening.colored ? (opening.color || null) : null} />
        {label}{outline}
      </group>
    );
  }

  if (type === 'painting') {
    const style = opening.style || 'abstract';
    const pW = opening.width  || 0.80;
    const pH = opening.height || 0.60;
    const pSill = opening.sill ?? 1.30;
    const midY = pSill + pH / 2;
    const frD = 0.035;
    const label = renderHoverLabel('🖼️', t('painting'), midY + pH / 2 + 0.24);
    const hitbox = (frW) => (
      <mesh position={[0, midY, frD / 2]} {...hitProps}>
        <boxGeometry args={[pW + frW * 2 + 0.04, pH + frW * 2 + 0.04, frD + 0.05]} />
        <meshStandardMaterial transparent opacity={0} depthWrite={false} />
      </mesh>
    );
    const frameBox = (frW, color, roughness = 0.35, metalness = 0.15) => [
      [0, midY + pH / 2 + frW / 2, frD / 2, pW + frW * 2, frW, frD],
      [0, midY - pH / 2 - frW / 2, frD / 2, pW + frW * 2, frW, frD],
      [-pW / 2 - frW / 2, midY, frD / 2, frW, pH + frW * 2, frD],
      [pW / 2 + frW / 2, midY, frD / 2, frW, pH + frW * 2, frD],
    ].map(([x, y, z, w, h, d], i) => (
      <mesh key={`pf${i}`} position={[x, y, z]}>
        <boxGeometry args={[w, h, d]} />
        <meshStandardMaterial color={color} roughness={roughness} metalness={metalness}
          emissive={selEmissive} emissiveIntensity={selEmissiveIntensity} />
      </mesh>
    ));

    // ── Abstract (default mitred frame) ──────────────────────────────────
    if (style === 'abstract') {
      const frW = 0.045, matW = 0.022;
      return (
        <group position={[gx, 0, gz]} rotation={[0, rotY, 0]}>
          {hitbox(frW)}
          {frameBox(frW, frameColor)}
          <mesh position={[0, midY, frD * 0.82]}>
            <boxGeometry args={[pW + 0.006, pH + 0.006, 0.001]} />
            <meshStandardMaterial color={shadeColor(frameColor, -0.18)} roughness={0.4} />
          </mesh>
          <mesh position={[0, midY, frD * 0.85]}>
            <planeGeometry args={[pW - matW, pH - matW]} />
            <meshStandardMaterial color="#f2ede2" roughness={0.9} />
          </mesh>
          <mesh position={[0, midY, frD * 0.90]}>
            <planeGeometry args={[pW - matW * 2.2, pH - matW * 2.2]} />
            <meshStandardMaterial map={paintingTex} roughness={0.75} />
          </mesh>
          {label}{renderOutline(midY, frD / 2, pW + frW * 2 + 0.06, pH + frW * 2 + 0.06)}
        </group>
      );
    }

    // ── Ornate Baroque (wide gilded frame, no mat) ───────────────────────
    if (style === 'baroque') {
      const frW = 0.075;
      return (
        <group position={[gx, 0, gz]} rotation={[0, rotY, 0]}>
          {hitbox(frW)}
          {frameBox(frW, '#c9a24a', 0.28, 0.65)}
          {/* Inner gold bead + outer dark trim for a layered ornate look */}
          <mesh position={[0, midY, frD * 0.7]}>
            <boxGeometry args={[pW + 0.02, pH + 0.02, 0.012]} />
            <meshStandardMaterial color="#8a6a20" roughness={0.35} metalness={0.55} />
          </mesh>
          {/* Corner rosettes */}
          {[[-1, 1], [1, 1], [-1, -1], [1, -1]].map(([sx, sy], i) => (
            <mesh key={`br${i}`} position={[sx * (pW / 2 + frW / 2), midY + sy * (pH / 2 + frW / 2), frD * 0.85]}>
              <sphereGeometry args={[frW * 0.42, 10, 8]} />
              <meshStandardMaterial color="#d4b968" roughness={0.25} metalness={0.6} />
            </mesh>
          ))}
          <mesh position={[0, midY, frD * 0.90]}>
            <planeGeometry args={[pW - 0.01, pH - 0.01]} />
            <meshStandardMaterial map={paintingTex} roughness={0.7} />
          </mesh>
          {label}{renderOutline(midY, frD / 2, pW + frW * 2 + 0.06, pH + frW * 2 + 0.06)}
        </group>
      );
    }

    // ── Modern Black (ultra-thin frame) ──────────────────────────────────
    if (style === 'modern') {
      const frW = 0.018;
      return (
        <group position={[gx, 0, gz]} rotation={[0, rotY, 0]}>
          {hitbox(frW)}
          {frameBox(frW, '#161614', 0.3, 0.1)}
          <mesh position={[0, midY, frD * 0.88]}>
            <planeGeometry args={[pW - 0.006, pH - 0.006]} />
            <meshStandardMaterial map={paintingTex} roughness={0.75} />
          </mesh>
          {label}{renderOutline(midY, frD / 2, pW + frW * 2 + 0.06, pH + frW * 2 + 0.06)}
        </group>
      );
    }

    // ── Gallery Canvas (no frame, raw stretched-canvas edges) ────────────
    if (style === 'canvas') {
      const cD = 0.028; // canvas stretcher depth (thicker, protrudes more)
      return (
        <group position={[gx, 0, gz]} rotation={[0, rotY, 0]}>
          {hitbox(0.01)}
          <mesh position={[0, midY, cD / 2]}>
            <boxGeometry args={[pW, pH, cD]} />
            <meshStandardMaterial map={paintingTex} roughness={0.85} />
          </mesh>
          {/* Wrapped canvas edge — slightly darker sides for a fabric-wrap look */}
          {[
            [-pW / 2, midY, cD / 2, 0.006, pH, cD],
            [pW / 2, midY, cD / 2, 0.006, pH, cD],
            [0, midY + pH / 2, cD / 2, pW, 0.006, cD],
            [0, midY - pH / 2, cD / 2, pW, 0.006, cD],
          ].map(([x, y, z, w, h, d], i) => (
            <mesh key={`ce${i}`} position={[x, y, z]}>
              <boxGeometry args={[w, h, d]} />
              <meshStandardMaterial color="#d8d0c0" roughness={0.95} />
            </mesh>
          ))}
          {label}{renderOutline(midY, cD / 2, pW + 0.05, pH + 0.05)}
        </group>
      );
    }

    // ── Photo Collage (grid of 4 small framed prints) ────────────────────
    if (style === 'collage') {
      const gap = 0.03, frW = 0.015;
      const cols = 2, rows = 2;
      const cellW = (pW - gap * (cols - 1)) / cols;
      const cellH = (pH - gap * (rows - 1)) / rows;
      return (
        <group position={[gx, 0, gz]} rotation={[0, rotY, 0]}>
          {hitbox(0.02)}
          {Array.from({ length: cols * rows }).map((_, i) => {
            const c = i % cols, r = Math.floor(i / cols);
            const cx = -pW / 2 + cellW / 2 + c * (cellW + gap);
            const cy = midY + pH / 2 - cellH / 2 - r * (cellH + gap);
            return (
              <group key={`cg${i}`}>
                {[
                  [cx, cy + cellH / 2 + frW / 2, frD / 2, cellW + frW * 2, frW, frD],
                  [cx, cy - cellH / 2 - frW / 2, frD / 2, cellW + frW * 2, frW, frD],
                  [cx - cellW / 2 - frW / 2, cy, frD / 2, frW, cellH + frW * 2, frD],
                  [cx + cellW / 2 + frW / 2, cy, frD / 2, frW, cellH + frW * 2, frD],
                ].map(([x, y, z, w, h, d], j) => (
                  <mesh key={j} position={[x, y, z]}>
                    <boxGeometry args={[w, h, d]} />
                    <meshStandardMaterial color="#f0ece2" roughness={0.5} />
                  </mesh>
                ))}
                <mesh position={[cx, cy, frD * 0.85]}>
                  <planeGeometry args={[cellW, cellH]} />
                  <meshStandardMaterial map={paintingTex} roughness={0.75} />
                </mesh>
              </group>
            );
          })}
          {label}{renderOutline(midY, frD / 2, pW + 0.06, pH + 0.06)}
        </group>
      );
    }

    // ── Landscape Oil (wide ornate wood frame, wider aspect look) ────────
    if (style === 'landscape') {
      const frW = 0.06;
      return (
        <group position={[gx, 0, gz]} rotation={[0, rotY, 0]}>
          {hitbox(frW)}
          {frameBox(frW, shadeColor(frameColor, -0.15), 0.5, 0.1)}
          {/* Inner liner strip (classic gallery presentation) */}
          <mesh position={[0, midY, frD * 0.75]}>
            <boxGeometry args={[pW + 0.014, pH + 0.014, 0.008]} />
            <meshStandardMaterial color="#e8dcc0" roughness={0.7} />
          </mesh>
          <mesh position={[0, midY, frD * 0.90]}>
            <planeGeometry args={[pW - 0.01, pH - 0.01]} />
            <meshStandardMaterial map={paintingTex} roughness={0.8} />
          </mesh>
          {/* Small picture light above the frame */}
          <mesh position={[0, midY + pH / 2 + frW + 0.05, frD * 1.6]} rotation={[Math.PI * 0.35, 0, 0]}>
            <cylinderGeometry args={[0.012, 0.016, 0.09, 10]} />
            <meshStandardMaterial color="#3a3a3a" roughness={0.4} metalness={0.5} />
          </mesh>
          {label}{renderOutline(midY, frD / 2, pW + frW * 2 + 0.06, pH + frW * 2 + 0.06)}
        </group>
      );
    }
  }

  // ── AC UNIT (indoor split unit) ───────────────────────────────────────────
  if (type === 'ac') {
    const style = opening.style || 'split';
    const aW = opening.width  || 0.85;
    const aH = opening.height || 0.30;
    const aSill = opening.sill ?? 2.15;
    const midY = aSill + aH / 2;
    const bodyColor = frameColor;
    const grille = shadeColor(bodyColor, -0.10);
    const label = renderHoverLabel('❄️', t('ac'), midY + aH / 2 + 0.22);
    const hitbox = (aD) => (
      <mesh position={[0, midY, aD / 2]} {...hitProps}>
        <boxGeometry args={[aW + 0.04, aH + 0.04, aD + 0.05]} />
        <meshStandardMaterial transparent opacity={0} depthWrite={false} />
      </mesh>
    );

    // ── Split Unit (default) ─────────────────────────────────────────────
    if (style === 'split') {
      const aD = 0.22;
      return (
        <group position={[gx, 0, gz]} rotation={[0, rotY, 0]}>
          {hitbox(aD)}
          <mesh position={[0, midY, aD / 2]}>
            <boxGeometry args={[aW, aH, aD]} />
            <meshStandardMaterial color={bodyColor} roughness={0.28} metalness={0.05}
              emissive={selEmissive} emissiveIntensity={selEmissiveIntensity} />
          </mesh>
          <mesh position={[0, midY, aD + 0.001]}>
            <planeGeometry args={[aW - 0.02, aH - 0.02]} />
            <meshStandardMaterial color={shadeColor(bodyColor, 0.03)} roughness={0.30} />
          </mesh>
          {Array.from({ length: 5 }).map((_, i) => (
            <mesh key={`vl${i}`} position={[0, midY - aH * 0.24 + i * (aH * 0.11), aD + 0.006]}>
              <boxGeometry args={[aW * 0.86, 0.010, 0.006]} />
              <meshStandardMaterial color={grille} roughness={0.5} />
            </mesh>
          ))}
          <mesh position={[aW * 0.32, midY + aH * 0.18, aD + 0.005]}>
            <boxGeometry args={[0.052, 0.016, 0.004]} />
            <meshStandardMaterial color="#1a1a1a" roughness={0.3} />
          </mesh>
          <mesh position={[aW * 0.32 + 0.018, midY + aH * 0.18, aD + 0.008]}>
            <circleGeometry args={[0.004, 10]} />
            <meshStandardMaterial color="#4ade80" emissive="#4ade80" emissiveIntensity={1.4} />
          </mesh>
          <mesh position={[0, midY - aH / 2 - 0.006, aD * 0.65]} rotation={[Math.PI * 0.22, 0, 0]}>
            <boxGeometry args={[aW * 0.94, 0.008, aD * 0.55]} />
            <meshStandardMaterial color={grille} roughness={0.45} />
          </mesh>
          {[-1, 1].map(s => (
            <mesh key={`ec${s}`} position={[s * (aW / 2 - 0.003), midY, aD / 2]}>
              <boxGeometry args={[0.006, aH, aD]} />
              <meshStandardMaterial color={shadeColor(bodyColor, -0.10)} roughness={0.35} />
            </mesh>
          ))}
          {label}{renderOutline(midY, aD / 2, aW + 0.06, aH + 0.06)}
        </group>
      );
    }

    // ── Cassette (square, centered round diffuser) ────────────────────────
    if (style === 'cassette') {
      const aD = 0.12;
      const sq = Math.min(aW, aH * 2.2);
      return (
        <group position={[gx, 0, gz]} rotation={[0, rotY, 0]}>
          {hitbox(aD)}
          <mesh position={[0, midY, aD / 2]}>
            <boxGeometry args={[sq, sq * 0.55, aD]} />
            <meshStandardMaterial color={bodyColor} roughness={0.25} metalness={0.05}
              emissive={selEmissive} emissiveIntensity={selEmissiveIntensity} />
          </mesh>
          {/* Round central diffuser with 4-way louvers */}
          <mesh position={[0, midY, aD + 0.002]} rotation={[0, 0, 0]}>
            <circleGeometry args={[sq * 0.24, 24]} />
            <meshStandardMaterial color={grille} roughness={0.5} />
          </mesh>
          {[0, 1, 2, 3].map(i => (
            <mesh key={`cv${i}`} position={[0, midY, aD + 0.004]} rotation={[0, 0, (Math.PI / 2) * i]}>
              <planeGeometry args={[sq * 0.44, 0.012]} />
              <meshStandardMaterial color={shadeColor(grille, -0.15)} roughness={0.55} />
            </mesh>
          ))}
          <mesh position={[0, midY - sq * 0.22, aD + 0.003]}>
            <circleGeometry args={[0.006, 8]} />
            <meshStandardMaterial color="#4ade80" emissive="#4ade80" emissiveIntensity={1.2} />
          </mesh>
          {label}{renderOutline(midY, aD / 2, sq + 0.05, sq * 0.55 + 0.05)}
        </group>
      );
    }

    // ── Curved Premium ─────────────────────────────────────────────────────
    if (style === 'curved') {
      const aD = 0.24;
      return (
        <group position={[gx, 0, gz]} rotation={[0, rotY, 0]}>
          {hitbox(aD)}
          {/* Curved front approximated with 3 stepped depth segments */}
          {[0.55, 0.8, 1.0].map((f, i) => (
            <mesh key={`cc${i}`} position={[0, midY, aD * f * 0.5]}>
              <boxGeometry args={[aW - i * 0.01, aH - i * 0.01, aD * f]} />
              <meshStandardMaterial color={i === 2 ? bodyColor : shadeColor(bodyColor, -0.03 * i)} roughness={0.18} metalness={0.15}
                emissive={i === 2 ? selEmissive : '#000000'} emissiveIntensity={i === 2 ? selEmissiveIntensity : 0} />
            </mesh>
          ))}
          <mesh position={[0, midY - aH * 0.30, aD + 0.006]}>
            <boxGeometry args={[aW * 0.9, 0.03, 0.006]} />
            <meshStandardMaterial color="#161616" roughness={0.15} metalness={0.4} />
          </mesh>
          <mesh position={[0, midY + aH * 0.2, aD + 0.005]}>
            <boxGeometry args={[0.06, 0.014, 0.004]} />
            <meshStandardMaterial color="#101010" roughness={0.2} />
          </mesh>
          <mesh position={[0, midY + aH * 0.2, aD + 0.008]}>
            <circleGeometry args={[0.0035, 8]} />
            <meshStandardMaterial color="#5ac8fa" emissive="#5ac8fa" emissiveIntensity={1.3} />
          </mesh>
          {label}{renderOutline(midY, aD / 2, aW + 0.06, aH + 0.06)}
        </group>
      );
    }

    // ── Window Unit (boxy through-wall style) ─────────────────────────────
    if (style === 'window') {
      const aD = 0.38; // protrudes further, as if set into the wall
      return (
        <group position={[gx, 0, gz]} rotation={[0, rotY, 0]}>
          {hitbox(aD)}
          <mesh position={[0, midY, aD / 2]}>
            <boxGeometry args={[aW, aH * 1.6, aD]} />
            <meshStandardMaterial color={bodyColor} roughness={0.35} metalness={0.25}
              emissive={selEmissive} emissiveIntensity={selEmissiveIntensity} />
          </mesh>
          {/* Grille of horizontal slats covering most of the front */}
          {Array.from({ length: 8 }).map((_, i) => (
            <mesh key={`wv${i}`} position={[0, midY - aH * 0.6 + i * (aH * 1.2 / 7), aD + 0.006]}>
              <boxGeometry args={[aW * 0.9, 0.012, 0.008]} />
              <meshStandardMaterial color={grille} roughness={0.4} metalness={0.2} />
            </mesh>
          ))}
          {/* Control panel on the right */}
          <mesh position={[aW * 0.32, midY + aH * 0.55, aD + 0.008]}>
            <boxGeometry args={[0.16, 0.10, 0.01]} />
            <meshStandardMaterial color="#e8e4dc" roughness={0.4} />
          </mesh>
          {[-1, 0, 1].map(d => (
            <mesh key={`kn${d}`} position={[aW * 0.32 + d * 0.045, midY + aH * 0.55, aD + 0.016]}>
              <cylinderGeometry args={[0.014, 0.014, 0.012, 12]} />
              <meshStandardMaterial color="#8a8a84" roughness={0.3} metalness={0.5} />
            </mesh>
          ))}
          {label}{renderOutline(midY, aD / 2, aW + 0.06, aH * 1.6 + 0.06)}
        </group>
      );
    }

    // ── Slim High-Wall ──────────────────────────────────────────────────
    if (style === 'slim') {
      const aD = 0.13;
      return (
        <group position={[gx, 0, gz]} rotation={[0, rotY, 0]}>
          {hitbox(aD)}
          <mesh position={[0, midY, aD / 2]}>
            <boxGeometry args={[aW, aH * 0.7, aD]} />
            <meshStandardMaterial color={bodyColor} roughness={0.2} metalness={0.08}
              emissive={selEmissive} emissiveIntensity={selEmissiveIntensity} />
          </mesh>
          <mesh position={[0, midY - aH * 0.15, aD + 0.003]}>
            <boxGeometry args={[aW * 0.92, 0.02, 0.005]} />
            <meshStandardMaterial color={grille} roughness={0.45} />
          </mesh>
          <mesh position={[-aW * 0.4, midY + aH * 0.1, aD + 0.004]}>
            <circleGeometry args={[0.0035, 8]} />
            <meshStandardMaterial color="#5ac8fa" emissive="#5ac8fa" emissiveIntensity={1.2} />
          </mesh>
          {label}{renderOutline(midY, aD / 2, aW + 0.06, aH * 0.7 + 0.06)}
        </group>
      );
    }

    // ── Retro Boxy ────────────────────────────────────────────────────────
    if (style === 'retro') {
      const aD = 0.26;
      const bc = shadeColor(bodyColor, 0.02);
      return (
        <group position={[gx, 0, gz]} rotation={[0, rotY, 0]}>
          {hitbox(aD)}
          <mesh position={[0, midY, aD / 2]}>
            <boxGeometry args={[aW, aH * 1.3, aD]} />
            <meshStandardMaterial color={bc} roughness={0.5} metalness={0.05}
              emissive={selEmissive} emissiveIntensity={selEmissiveIntensity} />
          </mesh>
          {/* Wide horizontal slat grille, chunky retro proportions */}
          {Array.from({ length: 6 }).map((_, i) => (
            <mesh key={`rv${i}`} position={[0, midY - aH * 0.45 + i * (aH * 0.9 / 5), aD + 0.008]}>
              <boxGeometry args={[aW * 0.88, 0.024, 0.01]} />
              <meshStandardMaterial color={shadeColor(bc, -0.15)} roughness={0.6} />
            </mesh>
          ))}
          {/* Round analog dial */}
          <mesh position={[aW * 0.32, midY + aH * 0.42, aD + 0.008]} rotation={[Math.PI / 2, 0, 0]}>
            <cylinderGeometry args={[0.035, 0.035, 0.012, 20]} />
            <meshStandardMaterial color="#e0ddd4" roughness={0.4} />
          </mesh>
          <mesh position={[aW * 0.32 + 0.02, midY + aH * 0.42, aD + 0.015]}>
            <boxGeometry args={[0.022, 0.004, 0.004]} />
            <meshStandardMaterial color="#222" roughness={0.3} />
          </mesh>
          {label}{renderOutline(midY, aD / 2, aW + 0.06, aH * 1.3 + 0.06)}
        </group>
      );
    }
  }

  // ── WALL CLOCK ─────────────────────────────────────────────────────────────
  if (type === 'clock') {
    // Clocks are GLB models now (clock1…clock13). A legacy/unknown style id
    // falls back to clock1. Mounts flat on the wall; keeps hitbox + label/outline.
    const style = isGlbClock(opening.style) ? opening.style : GLB_CLOCK_FALLBACK;
    const cSize = opening.width || 0.35;
    const cSill = opening.sill ?? 1.55;
    const R = cSize / 2;
    const midY = cSill + R;
    const D = 0.08;
    const hitbox = (
      <mesh position={[0, midY, D / 2]} {...hitProps}>
        <boxGeometry args={[cSize + 0.05, cSize + 0.05, D + 0.05]} />
        <meshStandardMaterial transparent opacity={0} depthWrite={false} />
      </mesh>
    );
    const label = renderHoverLabel('🕐', t('clock'), midY + R + 0.22);
    return (
      <group position={[gx, 0, gz]} rotation={[0, rotY, 0]}>
        {hitbox}
        <GLBClock styleId={style} cSize={cSize} midY={midY} zBase={0} color={frameColor} tint={opening.colored ? (opening.color || null) : null} />
        {label}{renderOutline(midY, D / 2, cSize + 0.06, cSize + 0.06)}
      </group>
    );
  }

  if (type === 'outlet') {
    const style = opening.style || 'eu';
    const oW = opening.width  || 0.10;
    const oH = opening.height || 0.10;
    const oSill = opening.sill ?? 0.30;
    const midY = oSill + oH / 2;
    const plateColor = frameColor;
    const label = renderHoverLabel('🔌', t('outlet'), midY + Math.max(oH, 0.16) / 2 + 0.20);
    const hitbox = (D) => (
      <mesh position={[0, midY, D / 2]} {...hitProps}>
        <boxGeometry args={[Math.max(oW, 0.16) + 0.06, Math.max(oH, 0.16) + 0.06, D + 0.08]} />
        <meshStandardMaterial transparent opacity={0} depthWrite={false} />
      </mesh>
    );
    const plate = (D) => (
      <mesh position={[0, midY, D / 2]}>
        <boxGeometry args={[oW, oH, D]} />
        <meshStandardMaterial color={plateColor} roughness={0.35}
          emissive={selEmissive} emissiveIntensity={selEmissiveIntensity} />
      </mesh>
    );
    const screw = (D) => (
      <mesh position={[0, midY + oH * 0.36, D + 0.004]}>
        <circleGeometry args={[oW * 0.05, 10]} />
        <meshStandardMaterial color="#888880" roughness={0.4} metalness={0.5} />
      </mesh>
    );
    const outline = renderOutline(midY, 0.007, Math.max(oW, 0.16) + 0.05, Math.max(oH, 0.16) + 0.05);

    // ── EU Round-Pin (default) ───────────────────────────────────────────
    if (style === 'eu') {
      const D = 0.014;
      return (
        <group position={[gx, 0, gz]} rotation={[0, rotY, 0]}>
          {hitbox(D)}{plate(D)}
          <mesh position={[0, midY, D + 0.001]}>
            <circleGeometry args={[oW * 0.42, 24]} />
            <meshStandardMaterial color={shadeColor(plateColor, -0.12)} roughness={0.5} />
          </mesh>
          {[-1, 1].map(s => (
            <mesh key={`sk${s}`} position={[s * oW * 0.14, midY + oH * 0.02, D + 0.004]}>
              <circleGeometry args={[oW * 0.09, 16]} />
              <meshStandardMaterial color="#1a1a1a" roughness={0.6} />
            </mesh>
          ))}
          <mesh position={[0, midY - oH * 0.20, D + 0.006]} rotation={[Math.PI / 2, 0, 0]}>
            <cylinderGeometry args={[oW * 0.045, oW * 0.045, 0.006, 10]} />
            <meshStandardMaterial color="#c9a24a" roughness={0.3} metalness={0.6} />
          </mesh>
          {screw(D)}
          {label}{outline}
        </group>
      );
    }

    // ── US Decora (rectangular) ──────────────────────────────────────────
    if (style === 'us') {
      const D = 0.012;
      return (
        <group position={[gx, 0, gz]} rotation={[0, rotY, 0]}>
          {hitbox(D)}{plate(D)}
          <mesh position={[0, midY, D + 0.001]}>
            <planeGeometry args={[oW * 0.82, oH * 0.9]} />
            <meshStandardMaterial color={shadeColor(plateColor, -0.1)} roughness={0.4} />
          </mesh>
          {[1, -1].map(s => (
            <group key={s}>
              <mesh position={[0, midY + s * oH * 0.20, D + 0.004]}>
                <boxGeometry args={[0.006, oW * 0.18, 0.004]} />
                <meshStandardMaterial color="#151515" roughness={0.55} />
              </mesh>
              <mesh position={[oW * 0.09, midY + s * oH * 0.20, D + 0.004]}>
                <boxGeometry args={[0.006, oW * 0.14, 0.004]} />
                <meshStandardMaterial color="#151515" roughness={0.55} />
              </mesh>
            </group>
          ))}
          {[1, -1].map(s => (
            <mesh key={`g${s}`} position={[oW * 0.03, midY, D + 0.004]}>
              <circleGeometry args={[oW * 0.055, 14]} />
              <meshStandardMaterial color="#151515" roughness={0.55} />
            </mesh>
          ))}
          {screw(D)}
          {label}{outline}
        </group>
      );
    }

    // ── UK 3-Pin (square plate + rocker switch) ──────────────────────────
    if (style === 'uk') {
      const D = 0.016;
      return (
        <group position={[gx, 0, gz]} rotation={[0, rotY, 0]}>
          {hitbox(D)}
          <mesh position={[0, midY, D / 2]}>
            <boxGeometry args={[Math.max(oW, 0.11), Math.max(oH, 0.11), D]} />
            <meshStandardMaterial color={plateColor} roughness={0.3}
              emissive={selEmissive} emissiveIntensity={selEmissiveIntensity} />
          </mesh>
          {/* Triangular 3-pin arrangement */}
          <mesh position={[0, midY - oH * 0.14, D + 0.005]}>
            <boxGeometry args={[0.014, 0.022, 0.006]} />
            <meshStandardMaterial color="#1a1a1a" roughness={0.6} />
          </mesh>
          {[-1, 1].map(s => (
            <mesh key={s} position={[s * oW * 0.13, midY + oH * 0.10, D + 0.005]}>
              <boxGeometry args={[0.008, 0.018, 0.006]} />
              <meshStandardMaterial color="#1a1a1a" roughness={0.6} />
            </mesh>
          ))}
          {/* Rocker switch — a UK-outlet signature detail */}
          <mesh position={[oW * 0.32, midY, D + 0.006]}>
            <boxGeometry args={[0.03, 0.05, 0.01]} />
            <meshStandardMaterial color="#e8e4dc" roughness={0.35} />
          </mesh>
          <mesh position={[oW * 0.32, midY + 0.012, D + 0.011]}>
            <boxGeometry args={[0.024, 0.018, 0.006]} />
            <meshStandardMaterial color="#f5f2ea" roughness={0.3} />
          </mesh>
          {screw(D)}
          {label}{outline}
        </group>
      );
    }

    // ── USB Combo ─────────────────────────────────────────────────────────
    if (style === 'usb') {
      const D = 0.014;
      return (
        <group position={[gx, 0, gz]} rotation={[0, rotY, 0]}>
          {hitbox(D)}{plate(D)}
          <mesh position={[0, midY + oH * 0.16, D + 0.001]}>
            <circleGeometry args={[oW * 0.4, 24]} />
            <meshStandardMaterial color={shadeColor(plateColor, -0.12)} roughness={0.5} />
          </mesh>
          {[-1, 1].map(s => (
            <mesh key={`sk${s}`} position={[s * oW * 0.13, midY + oH * 0.18, D + 0.004]}>
              <circleGeometry args={[oW * 0.08, 16]} />
              <meshStandardMaterial color="#1a1a1a" roughness={0.6} />
            </mesh>
          ))}
          {/* USB-A + USB-C ports below */}
          <mesh position={[0, midY - oH * 0.24, D + 0.002]}>
            <boxGeometry args={[oW * 0.7, oH * 0.24, 0.006]} />
            <meshStandardMaterial color="#0e0e10" roughness={0.4} />
          </mesh>
          <mesh position={[-oW * 0.16, midY - oH * 0.24, D + 0.006]}>
            <boxGeometry args={[oW * 0.22, oH * 0.09, 0.004]} />
            <meshStandardMaterial color="#3a3a3c" roughness={0.4} metalness={0.3} />
          </mesh>
          <mesh position={[oW * 0.16, midY - oH * 0.24, D + 0.006]}>
            <boxGeometry args={[oW * 0.16, oH * 0.09, 0.004]} />
            <meshStandardMaterial color="#3a3a3c" roughness={0.4} metalness={0.3} />
          </mesh>
          {/* Blue "powered" indicator */}
          <mesh position={[oW * 0.34, midY - oH * 0.24, D + 0.008]}>
            <circleGeometry args={[0.004, 8]} />
            <meshStandardMaterial color="#4a9eff" emissive="#4a9eff" emissiveIntensity={1.2} />
          </mesh>
          {screw(D)}
          {label}{outline}
        </group>
      );
    }

    // ── Brass Floor ───────────────────────────────────────────────────────
    if (style === 'brass') {
      const D = 0.018;
      const brass = '#c9a24a';
      return (
        <group position={[gx, 0, gz]} rotation={[0, rotY, 0]}>
          {hitbox(D)}
          <mesh position={[0, midY, D * 0.35]} rotation={[Math.PI / 2, 0, 0]}>
            <cylinderGeometry args={[Math.max(oW, oH) * 0.55, Math.max(oW, oH) * 0.55, D * 0.7, 28]} />
            <meshStandardMaterial color={brass} roughness={0.22} metalness={0.8}
              emissive={selEmissive} emissiveIntensity={selEmissiveIntensity} />
          </mesh>
          <mesh position={[0, midY, D * 0.75]} rotation={[Math.PI / 2, 0, 0]}>
            <cylinderGeometry args={[Math.max(oW, oH) * 0.4, Math.max(oW, oH) * 0.4, 0.004, 24]} />
            <meshStandardMaterial color="#1a1a1a" roughness={0.55} />
          </mesh>
          {[-1, 1].map(s => (
            <mesh key={s} position={[s * oW * 0.12, midY, D + 0.005]}>
              <circleGeometry args={[oW * 0.08, 14]} />
              <meshStandardMaterial color="#2a2a2a" roughness={0.6} />
            </mesh>
          ))}
          {/* Hinge pip for the flip cover */}
          <mesh position={[0, midY + Math.max(oW, oH) * 0.5, D * 0.5]}>
            <cylinderGeometry args={[0.006, 0.006, oW * 0.5, 8]} />
            <meshStandardMaterial color={brass} roughness={0.25} metalness={0.75} />
          </mesh>
          {label}{outline}
        </group>
      );
    }

    // ── Industrial Metal ──────────────────────────────────────────────────
    if (style === 'industrial') {
      const D = 0.03;
      const metal = '#5a5a58';
      return (
        <group position={[gx, 0, gz]} rotation={[0, rotY, 0]}>
          {hitbox(D)}
          <mesh position={[0, midY, D / 2]}>
            <boxGeometry args={[oW * 1.3, oH * 1.3, D]} />
            <meshStandardMaterial color={metal} roughness={0.5} metalness={0.6}
              emissive={selEmissive} emissiveIntensity={selEmissiveIntensity} />
          </mesh>
          <mesh position={[0, midY, D + 0.001]}>
            <boxGeometry args={[oW * 0.85, oH * 0.85, 0.006]} />
            <meshStandardMaterial color="#2a2a28" roughness={0.6} metalness={0.3} />
          </mesh>
          {[-1, 1].map(s => (
            <mesh key={`sk${s}`} position={[s * oW * 0.15, midY, D + 0.008]}>
              <circleGeometry args={[oW * 0.1, 16]} />
              <meshStandardMaterial color="#1a1a1a" roughness={0.7} />
            </mesh>
          ))}
          {/* Exposed corner bolts */}
          {[[-1, -1], [1, -1], [-1, 1], [1, 1]].map(([sx, sy], i) => (
            <mesh key={i} position={[sx * oW * 0.58, midY + sy * oH * 0.58, D + 0.003]}>
              <cylinderGeometry args={[0.007, 0.007, 0.006, 6]} />
              <meshStandardMaterial color="#8a8a86" roughness={0.4} metalness={0.7} />
            </mesh>
          ))}
          {label}{outline}
        </group>
      );
    }
  }

  // ── RADIATOR ───────────────────────────────────────────────────────────────
  if (type === 'radiator') {
    const style = opening.style || 'panel';
    const rW = opening.width  || 1.00;
    const rH = opening.height || 0.50;
    const rSill = opening.sill ?? 0.12;
    const midY = rSill + rH / 2;
    const bodyColor = frameColor;
    const label = renderHoverLabel('♨️', t('radiator'), midY + rH / 2 + 0.24);
    const hitbox = (D) => (
      <mesh position={[0, midY, D / 2]} {...hitProps}>
        <boxGeometry args={[rW + 0.04, rH + 0.04, D + 0.05]} />
        <meshStandardMaterial transparent opacity={0} depthWrite={false} />
      </mesh>
    );
    const pipes = (D) => [-1, 1].map(s => (
      <group key={`pipe${s}`}>
        <mesh position={[s * rW * 0.36, rSill * 0.5, D * 0.5]}>
          <cylinderGeometry args={[0.014, 0.014, Math.max(0.02, rSill + 0.02), 10]} />
          <meshStandardMaterial color="#b0b0a8" roughness={0.3} metalness={0.7} />
        </mesh>
        {s < 0 && (
          <mesh position={[s * rW * 0.36, rSill * 0.85, D * 0.5]} rotation={[0, 0, Math.PI / 2]}>
            <cylinderGeometry args={[0.020, 0.020, 0.03, 10]} />
            <meshStandardMaterial color="#c9a24a" roughness={0.25} metalness={0.7} />
          </mesh>
        )}
      </group>
    ));

    // ── Panel Convector (default) ────────────────────────────────────────
    if (style === 'panel') {
      const D = 0.10, railH = 0.035;
      const finCount = Math.max(4, Math.round(rW / 0.055));
      const finW = 0.014;
      return (
        <group position={[gx, 0, gz]} rotation={[0, rotY, 0]}>
          {hitbox(D)}
          <mesh position={[0, rSill + rH - railH / 2, D / 2]}>
            <boxGeometry args={[rW, railH, D]} />
            <meshStandardMaterial color={bodyColor} roughness={0.35}
              emissive={selEmissive} emissiveIntensity={selEmissiveIntensity} />
          </mesh>
          <mesh position={[0, rSill + railH / 2, D / 2]}>
            <boxGeometry args={[rW, railH, D]} />
            <meshStandardMaterial color={bodyColor} roughness={0.35} />
          </mesh>
          {Array.from({ length: finCount }).map((_, i) => {
            const fx = -rW / 2 + finW / 2 + i * ((rW - finW) / Math.max(1, finCount - 1));
            return (
              <mesh key={`fin${i}`} position={[fx, midY, D / 2]}>
                <boxGeometry args={[finW, Math.max(0.05, rH - railH * 1.6), D]} />
                <meshStandardMaterial color={bodyColor} roughness={0.32} metalness={0.05} />
              </mesh>
            );
          })}
          {pipes(D)}
          {label}{renderOutline(midY, D / 2, rW + 0.06, rH + 0.06)}
        </group>
      );
    }

    // ── Cast Iron Column ──────────────────────────────────────────────────
    if (style === 'castiron') {
      const D = 0.16;
      const colCount = Math.max(4, Math.round(rW / 0.09));
      const colR = 0.028;
      const iron = shadeColor(bodyColor, -0.08);
      return (
        <group position={[gx, 0, gz]} rotation={[0, rotY, 0]}>
          {hitbox(D)}
          {Array.from({ length: colCount }).map((_, i) => {
            const fx = -rW / 2 + colR + i * ((rW - colR * 2) / Math.max(1, colCount - 1));
            return (
              <mesh key={`col${i}`} position={[fx, midY, D / 2]} rotation={[Math.PI / 2, 0, 0]}>
                <cylinderGeometry args={[colR, colR, D, 12]} />
                <meshStandardMaterial color={iron} roughness={0.55} metalness={0.15}
                  emissive={i === 0 ? selEmissive : '#000000'} emissiveIntensity={i === 0 ? selEmissiveIntensity : 0} />
              </mesh>
            );
          })}
          {/* Decorative top/bottom scrollwork caps */}
          <mesh position={[0, rSill + rH - 0.01, D / 2]}>
            <boxGeometry args={[rW, 0.02, D]} />
            <meshStandardMaterial color={iron} roughness={0.5} metalness={0.2} />
          </mesh>
          <mesh position={[0, rSill + 0.01, D / 2]}>
            <boxGeometry args={[rW, 0.02, D]} />
            <meshStandardMaterial color={iron} roughness={0.5} metalness={0.2} />
          </mesh>
          {pipes(D)}
          {label}{renderOutline(midY, D / 2, rW + 0.06, rH + 0.06)}
        </group>
      );
    }

    // ── Towel Rail (ladder style) ─────────────────────────────────────────
    if (style === 'towel') {
      const D = 0.09;
      const rungCount = Math.max(5, Math.round(rH / 0.08));
      const railR = 0.014;
      return (
        <group position={[gx, 0, gz]} rotation={[0, rotY, 0]}>
          {hitbox(D)}
          {/* Two vertical side rails */}
          {[-1, 1].map(s => (
            <mesh key={s} position={[s * rW * 0.42, midY, D / 2]} rotation={[0, 0, 0]}>
              <cylinderGeometry args={[railR, railR, rH, 12]} />
              <meshStandardMaterial color={bodyColor} roughness={0.25} metalness={0.4}
                emissive={selEmissive} emissiveIntensity={selEmissiveIntensity} />
            </mesh>
          ))}
          {/* Horizontal rungs */}
          {Array.from({ length: rungCount }).map((_, i) => {
            const fy = rSill + railR + i * ((rH - railR * 2) / Math.max(1, rungCount - 1));
            return (
              <mesh key={`rung${i}`} position={[0, fy, D / 2]} rotation={[0, 0, Math.PI / 2]}>
                <cylinderGeometry args={[railR * 0.8, railR * 0.8, rW * 0.86, 12]} />
                <meshStandardMaterial color={bodyColor} roughness={0.25} metalness={0.4} />
              </mesh>
            );
          })}
          {pipes(D)}
          {label}{renderOutline(midY, D / 2, rW + 0.06, rH + 0.06)}
        </group>
      );
    }

    // ── Vertical Designer (wide flat bars, tall proportions) ──────────────
    if (style === 'vertical') {
      const D = 0.07;
      const barCount = Math.max(3, Math.round(rW / 0.11));
      const barW = 0.06;
      return (
        <group position={[gx, 0, gz]} rotation={[0, rotY, 0]}>
          {hitbox(D)}
          <mesh position={[0, midY, D * 0.3]}>
            <boxGeometry args={[rW, rH, D * 0.6]} />
            <meshStandardMaterial color={shadeColor(bodyColor, -0.04)} roughness={0.3}
              emissive={selEmissive} emissiveIntensity={selEmissiveIntensity} />
          </mesh>
          {Array.from({ length: barCount }).map((_, i) => {
            const fx = -rW / 2 + barW / 2 + i * ((rW - barW) / Math.max(1, barCount - 1));
            return (
              <mesh key={`bar${i}`} position={[fx, midY, D * 0.6]}>
                <boxGeometry args={[barW * 0.7, rH - 0.03, D * 0.4]} />
                <meshStandardMaterial color={bodyColor} roughness={0.22} metalness={0.12} />
              </mesh>
            );
          })}
          {pipes(D)}
          {label}{renderOutline(midY, D / 2, rW + 0.06, rH + 0.06)}
        </group>
      );
    }

    // ── Low Skirting (slim, runs along the baseboard) ─────────────────────
    if (style === 'skirting') {
      const D = 0.055;
      return (
        <group position={[gx, 0, gz]} rotation={[0, rotY, 0]}>
          {hitbox(D)}
          <mesh position={[0, midY, D / 2]}>
            <boxGeometry args={[rW, Math.max(0.06, rH * 0.4), D]} />
            <meshStandardMaterial color={bodyColor} roughness={0.32} metalness={0.06}
              emissive={selEmissive} emissiveIntensity={selEmissiveIntensity} />
          </mesh>
          {/* Fine louvered slot pattern along the top */}
          {Array.from({ length: Math.max(6, Math.round(rW / 0.05)) }).map((_, i, arr) => (
            <mesh key={i} position={[-rW / 2 + (rW / arr.length) * (i + 0.5), midY + rH * 0.14, D + 0.003]}>
              <boxGeometry args={[rW / arr.length * 0.55, 0.008, 0.005]} />
              <meshStandardMaterial color={shadeColor(bodyColor, -0.15)} roughness={0.45} />
            </mesh>
          ))}
          {pipes(D)}
          {label}{renderOutline(midY, D / 2, rW + 0.06, Math.max(0.06, rH * 0.4) + 0.06)}
        </group>
      );
    }

    // ── Electric Flat Panel (smooth, no fins, with control dial) ─────────
    if (style === 'electric') {
      const D = 0.045;
      return (
        <group position={[gx, 0, gz]} rotation={[0, rotY, 0]}>
          {hitbox(D)}
          <mesh position={[0, midY, D / 2]}>
            <boxGeometry args={[rW, rH, D]} />
            <meshStandardMaterial color={bodyColor} roughness={0.2} metalness={0.1}
              emissive={selEmissive} emissiveIntensity={selEmissiveIntensity} />
          </mesh>
          {/* Subtle horizontal accent lines instead of fins */}
          {[0.3, 0.7].map((f, i) => (
            <mesh key={i} position={[0, rSill + rH * f, D + 0.001]}>
              <boxGeometry args={[rW * 0.94, 0.004, 0.002]} />
              <meshStandardMaterial color={shadeColor(bodyColor, -0.1)} roughness={0.3} />
            </mesh>
          ))}
          {/* Control dial + LED */}
          <mesh position={[rW * 0.38, midY, D + 0.004]} rotation={[Math.PI / 2, 0, 0]}>
            <cylinderGeometry args={[0.02, 0.02, 0.008, 16]} />
            <meshStandardMaterial color="#e8e4dc" roughness={0.3} />
          </mesh>
          <mesh position={[rW * 0.38, midY - rH * 0.24, D + 0.004]}>
            <circleGeometry args={[0.004, 8]} />
            <meshStandardMaterial color="#ff8040" emissive="#ff8040" emissiveIntensity={1.2} />
          </mesh>
          {/* Mounts flush — feet instead of visible pipes */}
          {[-1, 1].map(s => (
            <mesh key={s} position={[s * rW * 0.4, rSill - 0.005, D * 0.5]}>
              <boxGeometry args={[0.03, 0.01, D * 0.8]} />
              <meshStandardMaterial color="#8a8a86" roughness={0.4} metalness={0.5} />
            </mesh>
          ))}
          {label}{renderOutline(midY, D / 2, rW + 0.06, rH + 0.06)}
        </group>
      );
    }
  }

  return null;
}

// ─── COMPOUND ROOM HELPERS ───────────────────────────────────────────────────

// Shadow map resolution — see utils/gpuProfile for why this is capped on
// mobile. Read once at module load, as it was before; the Canvas below reads
// the live viewport for the settings that actually change with fullscreen.
const SHADOW_MAP = shadowMapSize();

const CELL_M = 1; // metres per grid cell

function traceCompoundOutline(room) {
  const rects = room.rects && room.rects.length > 1
    ? room.rects : [{ x: room.x, y: room.y, w: room.w, h: room.h }];
  const cells = new Set();
  for (const r of rects)
    for (let row = r.y; row < r.y + r.h; row++)
      for (let col = r.x; col < r.x + r.w; col++)
        cells.add(`${col},${row}`);
  const segs = [];
  for (const key of cells) {
    const [col, row] = key.split(',').map(Number);
    const x = (col - room.x) * CELL_M, z = (row - room.y) * CELL_M, C = CELL_M;
    if (!cells.has(`${col},${row - 1}`)) segs.push([x, z, x + C, z]);
    if (!cells.has(`${col + 1},${row}`)) segs.push([x + C, z, x + C, z + C]);
    if (!cells.has(`${col},${row + 1}`)) segs.push([x + C, z + C, x, z + C]);
    if (!cells.has(`${col - 1},${row}`)) segs.push([x, z + C, x, z]);
  }
  const adj = {};
  segs.forEach((s, i) => { const k = `${s[0]},${s[1]}`; (adj[k] = adj[k] || []).push(i); });
  const used = new Array(segs.length).fill(false);
  let best = null;
  for (let si = 0; si < segs.length; si++) {
    if (used[si]) continue;
    const pts = [];
    let cur = si;
    for (let g = 0; g < segs.length + 4; g++) {
      if (used[cur]) break;
      used[cur] = true;
      pts.push([segs[cur][0], segs[cur][1]]);
      const k = `${segs[cur][2]},${segs[cur][3]}`;
      const nexts = (adj[k] || []).filter(i => !used[i]);
      if (!nexts.length) break;
      cur = nexts[0];
    }
    if (!best || pts.length > best.length) best = pts;
  }
  return best; // array of [px, pz] in room-relative metres
}

// Floor uses rotation [-PI/2,0,0]: shape Y → world -Z. Needs reversed winding (CCW) so front face = +Z → +Y (up).
// Ceiling uses rotation [+PI/2,0,0]: shape Y → world +Z. Original winding (CCW) gives front face = +Z → -Y (down).
// A free-form room carries a smooth outline (`pts`, angled/curved edges) beside
// its rasterised rects — trace THAT so the floor/ceiling read as the real shape
// instead of a staircase of cells. Room-local coords, +z flipped like the rect
// path below.
// Extra floor/ceiling pieces for a MERGED free-form room: the straight rooms it
// absorbed. Rendered alongside buildFreeShape's curved piece so the 3D room shows
// the whole merged area, not just the curved half.
function buildMergeShapes(room, mirrorY = false) {
  if (!room.mergeRects?.length) return null;
  const W = room.w * CELL_M, D = room.h * CELL_M;
  const S = mirrorY ? 1 : -1;
  return room.mergeRects.map(r => {
    const x0 = (r.x - room.x) * CELL_M - W / 2, x1 = x0 + r.w * CELL_M;
    const y0 = S * ((r.y - room.y) * CELL_M - D / 2), y1 = y0 + S * r.h * CELL_M;
    const s = new THREE.Shape();
    s.moveTo(x0, y0); s.lineTo(x1, y0); s.lineTo(x1, y1); s.lineTo(x0, y1);
    s.closePath();
    return s;
  });
}

// `mirrorY` compensates for the ceiling mesh being rotated +90° about X while the
// floor is rotated −90°: feeding both the same shape lands the ceiling mirrored,
// so it no longer lines up with the room's outline.
function buildFreeShape(room, mirrorY = false) {
  const P = room.pts;
  if (!P || P.length < 3) return null;
  const W = room.w * CELL_M, D = room.h * CELL_M;
  const S = mirrorY ? 1 : -1;
  const sx = p => (p.x - room.x) * CELL_M - W / 2;
  const sy = p => S * ((p.y - room.y) * CELL_M - D / 2);
  const shape = new THREE.Shape();
  shape.moveTo(sx(P[0]), sy(P[0]));
  for (let i = 0; i < P.length; i++) {
    const a = P[i], b = P[(i + 1) % P.length];
    if (a.bulge) {
      const c = edgeControlPoint(a.x, a.y, b.x, b.y, a.bulge);
      shape.quadraticCurveTo(
        (c.cx - room.x) * CELL_M - W / 2, S * ((c.cy - room.y) * CELL_M - D / 2),
        sx(b), sy(b)
      );
    } else shape.lineTo(sx(b), sy(b));
  }
  shape.closePath();
  return shape;
}

function buildFloorShape(room) {
  const free = buildFreeShape(room);
  if (free) return free;
  const best = traceCompoundOutline(room);
  if (!best || best.length < 3) return null;
  const W = room.w * CELL_M, D = room.h * CELL_M;
  const shape = new THREE.Shape();
  [...best].reverse().forEach(([px, pz], i) => {
    const sx = px - W / 2, sy = -(pz - D / 2);
    if (i === 0) shape.moveTo(sx, sy); else shape.lineTo(sx, sy);
  });
  shape.closePath();
  return shape;
}

function buildCeilingShape(room) {
  const free = buildFreeShape(room, true);   // mirrored — see buildFreeShape
  if (free) return free;
  const best = traceCompoundOutline(room);
  if (!best || best.length < 3) return null;
  const W = room.w * CELL_M, D = room.h * CELL_M;
  const shape = new THREE.Shape();
  best.forEach(([px, pz], i) => {
    const sx = px - W / 2, sy = pz - D / 2;
    if (i === 0) shape.moveTo(sx, sy); else shape.lineTo(sx, sy);
  });
  shape.closePath();
  return shape;
}

// ─── CURVED OUTLINE EDGES ────────────────────────────────────────────────────
// A curved edge is ONE continuous mesh, not a fan of flat panels: panels showed
// a faceted silhouette, a texture seam at every joint, and — since each panel
// was its own paint face — a ~1 m paint grid that restarted mid-curve. The arc
// is described by
//   curve = { samples: [{ x, z, ang, s }], len, flip }
// in room-local metres, where `s` is arc length from the edge start, `ang` the
// (unwrapped) tangent angle, and `flip` marks the winding that needs the 180°
// facing flip — see the flipAll note in buildCompoundWallSegs.

// How far (metres) an arc runs past each of its corners, matching the overlap
// the straight panels use so neighbouring faces never show a seam between them.
const ARC_PAD = 0.06;

// Position + tangent at arc length `s`, interpolated between samples. Outside
// [0,len] it extrapolates along the end tangent — that's how the arc gets the
// small overlap past each corner that stops a hairline gap showing through.
function arcAt(curve, s) {
  const S = curve.samples;
  if (s <= 0) { const p = S[0]; return { x: p.x + Math.cos(p.ang) * s, z: p.z + Math.sin(p.ang) * s, ang: p.ang }; }
  if (s >= curve.len) {
    const p = S[S.length - 1], d = s - curve.len;
    return { x: p.x + Math.cos(p.ang) * d, z: p.z + Math.sin(p.ang) * d, ang: p.ang };
  }
  let i = 1;
  while (i < S.length - 1 && S[i].s < s) i++;
  const p = S[i - 1], q = S[i];
  const f = (s - p.s) / ((q.s - p.s) || 1);
  return { x: p.x + (q.x - p.x) * f, z: p.z + (q.z - p.z) * f, ang: p.ang + (q.ang - p.ang) * f };
}

// Inward normal (the side the wall's front face looks at) at a tangent angle.
function arcNormal(curve, ang) {
  const sgn = curve.flip ? -1 : 1;
  return { nx: -Math.sin(ang) * sgn, nz: Math.cos(ang) * sgn };
}

// How many quads a strip spanning arc fractions [u0,u1] needs to stay smooth.
function arcSegCount(curve, u0, u1) {
  return Math.max(2, Math.ceil((u1 - u0) * (curve.samples.length - 1)));
}

// A strip of wall following the arc: arc fractions [u0,u1] × heights [y0,y1],
// pushed `off` metres along the inward normal (used to float paint overlays just
// proud of the wall) and grown `pad0`/`pad1` metres past its ends along the
// tangent (corner overlap).
//
// UVs are ARC-BASED — u advances with arc LENGTH, not across the chord — so
// textures, the ~1 m paint grid and click-to-paint uv hits are evenly spaced
// around the curve. `uvAcrossArc` maps u over the WHOLE arc (the wall face, so
// its uv agrees with the strips painted on it, the overlap caps simply landing
// just outside 0..1); otherwise u runs 0..1 across the strip itself, so a
// painted cell tiles its texture exactly like a flat cell would.
//
// Vertices are room-local, so the mesh sits at the origin with no rotation.
function buildArcWallGeometry(curve, u0, u1, y0, y1, { off = 0, pad0 = 0, pad1 = 0, uvAcrossArc = false } = {}) {
  const s0 = u0 * curve.len - pad0, s1 = u1 * curve.len + pad1;
  const uA = uvAcrossArc ? 0 : s0, uB = uvAcrossArc ? curve.len : s1;
  const segs = arcSegCount(curve, u0, u1) + (pad0 ? 1 : 0) + (pad1 ? 1 : 0);
  const pos = new Float32Array((segs + 1) * 2 * 3);
  const uv = new Float32Array((segs + 1) * 2 * 2);
  for (let k = 0; k <= segs; k++) {
    const s = s0 + (s1 - s0) * (k / segs);
    const { x, z, ang } = arcAt(curve, s);
    const { nx, nz } = arcNormal(curve, ang);
    const px = x + nx * off, pz = z + nz * off;
    const f = (s - uA) / ((uB - uA) || 1);
    const o = k * 6, u = k * 4;
    pos[o] = px; pos[o + 1] = y0; pos[o + 2] = pz;
    pos[o + 3] = px; pos[o + 4] = y1; pos[o + 5] = pz;
    uv[u] = f; uv[u + 1] = 0;
    uv[u + 2] = f; uv[u + 3] = 1;
  }
  // Winding decides which way the single-sided wall faces: (bottom→next bottom→
  // next top) normals point along the un-flipped inward normal, so a flipped
  // outline needs the reverse order.
  const idx = [];
  for (let k = 0; k < segs; k++) {
    const b0 = k * 2, t0 = k * 2 + 1, b1 = k * 2 + 2, t1 = k * 2 + 3;
    if (curve.flip) idx.push(b0, t1, b1, b0, t0, t1);
    else            idx.push(b0, b1, t1, b0, t1, t0);
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  g.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
  g.setIndex(idx);
  g.computeVertexNormals();
  return g;
}

// The same strip as an outline polyline (bottom edge → up → top edge back →
// down), for the dashed selection/hover borders on a curved face.
function arcOutlinePoints(curve, u0, u1, y0, y1, off) {
  const segs = arcSegCount(curve, u0, u1);
  const s0 = u0 * curve.len, s1 = u1 * curve.len;
  const bot = [], top = [];
  for (let k = 0; k <= segs; k++) {
    const { x, z, ang } = arcAt(curve, s0 + (s1 - s0) * (k / segs));
    const { nx, nz } = arcNormal(curve, ang);
    const px = x + nx * off, pz = z + nz * off;
    bot.push([px, y0, pz]);
    top.push([px, y1, pz]);
  }
  return [...bot, ...top.reverse(), bot[0]];
}

function buildCompoundWallSegs(room) {
  // Free-form room: one wall segment per outline edge (a curved edge becomes a
  // single continuous arc mesh), so walls follow the real shape.
  if (room.pts && room.pts.length >= 3) {
    const W = room.w * CELL_M, D = room.h * CELL_M;
    const lx = p => (p.x - room.x) * CELL_M - W / 2;
    const lz = p => (p.y - room.y) * CELL_M - D / 2;
    // Which side is "inside" is decided by the outline's WINDING, not by a
    // centroid: for a non-convex outline (e.g. several arches) the centroid can
    // fall on the wrong side of a given wall, which left individual panels facing
    // out — solid from outside, see-through from inside. A plane rotated by -ang
    // has its normal on the LEFT of the edge direction, so for a CCW outline every
    // panel already faces inward; for CW every panel needs a 180° flip.
    let signedArea = 0;
    for (let i = 0; i < room.pts.length; i++) {
      const p = room.pts[i], q = room.pts[(i + 1) % room.pts.length];
      signedArea += p.x * q.y - q.x * p.y;
    }
    const flipAll = signedArea < 0;   // sign convention: <0 is the winding that needs the 180° flip

    // Trim/moulding boxes for one straight piece of wall. `pad` extends the box
    // past both ends so neighbours overlap: pieces meeting at an angle otherwise
    // stop exactly on the shared corner and leave a visible wedge gap.
    const trim = (ax, az, bx, bz, pad) => {
      const segW = Math.hypot(bx - ax, bz - az);
      if (segW < 1e-4) return null;
      const cx = (ax + bx) / 2, cz = (az + bz) / 2;
      let ang = Math.atan2(bz - az, bx - ax);
      let nx = -Math.sin(ang), nz = Math.cos(ang);
      // One consistent flip for the whole outline (see flipAll above) — a
      // per-wall decision is what broke on non-convex shapes.
      if (flipAll) { ang += Math.PI; nx = -nx; nz = -nz; }
      const segLen = segW + pad;
      return {
        cx, cz, segW: segLen, nx, nz, rot: [0, -ang, 0],
        bbPos: [cx + nx * 0.013, 0.075, cz + nz * 0.013], bbSize: [segLen, 0.15, 0.018],
        cmPos: [cx + nx * 0.009, 0, cz + nz * 0.009],     cmSize: [segLen, 0.11, 0.014],
      };
    };

    const out = [];
    room.pts.forEach((a, i) => {
      const b = room.pts[(i + 1) % room.pts.length];

      if (a.bulge) {
        // Sample the arc once; the wall itself is built from these as a single
        // continuous mesh, while baseboard/crown boxes still follow it piecewise.
        const N = 24;
        const samples = [];
        let len = 0, prevAng = null;
        for (let k = 0; k <= N; k++) {
          const p = edgePointAt(a, b, k / N);
          const x = lx(p), z = lz(p);
          if (k > 0) len += Math.hypot(x - samples[k - 1].x, z - samples[k - 1].z);
          // Unwrap the tangent so interpolating between samples never swings the
          // long way round when the arc crosses the ±π branch cut.
          let ang = p.angle;
          if (prevAng !== null) ang += Math.round((prevAng - ang) / (2 * Math.PI)) * 2 * Math.PI;
          prevAng = ang;
          samples.push({ x, z, ang, s: len });
        }
        if (len < 1e-4) return;
        const parts = [];
        for (let k = 0; k < N; k++) {
          const t = trim(samples[k].x, samples[k].z, samples[k + 1].x, samples[k + 1].z, 0.03);
          if (t) parts.push(t);
        }
        out.push({
          key: `fe_${i}`,
          curve: { samples, len, flip: flipAll },
          corners: [[samples[0].x, samples[0].z]],
          parts,
        });
        return;
      }

      const ax = lx(a), az = lz(a), bx = lx(b), bz = lz(b);
      const t = trim(ax, az, bx, bz, 0.12);
      if (!t) return;
      out.push({
        ...t,
        key: `fe_${i}`,
        free: { ax, az, bx, bz, bulge: 0, edge: i, cornerStart: true },
      });
    });
    return out;
  }
  const rects = room.rects && room.rects.length > 1
    ? room.rects : [{ x: room.x, y: room.y, w: room.w, h: room.h }];
  const cells = new Set();
  for (const r of rects)
    for (let row = r.y; row < r.y + r.h; row++)
      for (let col = r.x; col < r.x + r.w; col++)
        cells.add(`${col},${row}`);
  const W = room.w * CELL_M, D = room.h * CELL_M;
  const topF = {}, botF = {}, leftF = {}, rightF = {};
  for (const key of cells) {
    const [col, row] = key.split(',').map(Number);
    const lc = col - room.x, lr = row - room.y;
    if (!cells.has(`${col},${row - 1}`)) (topF[lr]   = topF[lr]   || []).push(lc);
    if (!cells.has(`${col},${row + 1}`)) (botF[lr]   = botF[lr]   || []).push(lc);
    if (!cells.has(`${col - 1},${row}`)) (leftF[lc]  = leftF[lc]  || []).push(lr);
    if (!cells.has(`${col + 1},${row}`)) (rightF[lc] = rightF[lc] || []).push(lr);
  }
  function mergeH(map, side) {
    const out = [];
    for (const [rStr, cols] of Object.entries(map)) {
      const row = Number(rStr);
      const sorted = [...cols].sort((a, b) => a - b);
      let s = sorted[0], l = sorted[0];
      for (let i = 1; i < sorted.length; i++) {
        if (sorted[i] === l + 1) { l = sorted[i]; }
        else { out.push({ row, sc: s, ec: l + 1, side }); s = l = sorted[i]; }
      }
      out.push({ row, sc: s, ec: l + 1, side });
    }
    return out;
  }
  function mergeV(map, side) {
    const out = [];
    for (const [cStr, rows] of Object.entries(map)) {
      const col = Number(cStr);
      const sorted = [...rows].sort((a, b) => a - b);
      let s = sorted[0], l = sorted[0];
      for (let i = 1; i < sorted.length; i++) {
        if (sorted[i] === l + 1) { l = sorted[i]; }
        else { out.push({ col, sr: s, er: l + 1, side }); s = l = sorted[i]; }
      }
      out.push({ col, sr: s, er: l + 1, side });
    }
    return out;
  }
  const walls = [];
  for (const seg of [...mergeH(topF, 'top'), ...mergeH(botF, 'bottom')]) {
    const segW  = (seg.ec - seg.sc) * CELL_M;
    const cx    = seg.sc * CELL_M - W / 2 + segW / 2;
    const cz    = seg.side === 'top' ? seg.row * CELL_M - D / 2 : (seg.row + 1) * CELL_M - D / 2;
    const inZ   = seg.side === 'top' ? 1 : -1;
    walls.push({
      cx, cz, segW, rot: seg.side === 'top' ? [0, 0, 0] : [0, Math.PI, 0],
      bbPos: [cx, 0.075, cz + inZ * 0.013], bbSize: [segW, 0.15, 0.018],
      cmPos: [cx, 0, cz + inZ * 0.009],     cmSize: [segW, 0.11, 0.014],
      key: `h_${seg.side}_${seg.row}_${seg.sc}`,
    });
  }
  for (const seg of [...mergeV(leftF, 'left'), ...mergeV(rightF, 'right')]) {
    const segD  = (seg.er - seg.sr) * CELL_M;
    const cx    = seg.side === 'left' ? seg.col * CELL_M - W / 2 : (seg.col + 1) * CELL_M - W / 2;
    const cz    = seg.sr * CELL_M - D / 2 + segD / 2;
    const inX   = seg.side === 'left' ? 1 : -1;
    walls.push({
      cx, cz, segW: segD, rot: seg.side === 'left' ? [0, Math.PI / 2, 0] : [0, -Math.PI / 2, 0],
      bbPos: [cx + inX * 0.013, 0.075, cz], bbSize: [0.018, 0.15, segD],
      cmPos: [cx + inX * 0.009, 0, cz],     cmSize: [0.014, 0.11, segD],
      key: `v_${seg.side}_${seg.col}_${seg.sr}`,
    });
  }
  return walls;
}

// ─── ROOM GEOMETRY ───────────────────────────────────────────────────────────

// ─── OPENING PANEL ───────────────────────────────────────────────────────────

const OPENING_COLOR_PRESETS = [
  '#e8e0d4', '#c4a87a', '#8b6030', '#f5f5f0',
  '#1c1a17', '#4a4a4a', '#909090', '#c4a24a',
  '#e0d8c8', '#c07040', '#6b8fa0', '#d4b896',
];

function OpeningPanel({ opening, room, adjacentRoom, onClose, onDelete, onResize, onColorChange, onResetColor, onNavigate, onCopy }) {
  const { t } = useT();
  const isGuest = useGuestMode();
  const isDecor   = isWallDecorType(opening.type);
  const typeIcon  = opening.type === 'door' ? '🚪' : opening.type === 'archway' ? '🏛️' : opening.type === 'window' ? '🪟' : (WALL_DECOR_META[opening.type]?.icon || '🔧');
  const typeLabel = ['door', 'window', 'archway', 'painting', 'ac', 'clock', 'outlet', 'radiator'].includes(opening.type) ? t(opening.type) : t('item_label');
  const [color, setColor] = useState(opening.color || '#e8e0d4');
  // Whether the user has picked a colour. false = "Default" (the item's own
  // authored GLB materials for doors/windows/clocks, or the base look otherwise).
  const [colored, setColored] = useState(!!opening.colored);

  useEffect(() => { setColor(opening.color || '#e8e0d4'); setColored(!!opening.colored); }, [opening.id]);

  function apply(c) { if (isGuest) return; setColor(c); setColored(true); onColorChange(c); }
  function applyDefault() { if (isGuest) return; setColored(false); setColor('#e8e0d4'); onResetColor && onResetColor(); }

  const iconBtn = {
    background: 'rgba(255,255,255,0.08)', border: 'none', borderRadius: 7,
    color: '#ddd', fontSize: 16, cursor: 'pointer',
    width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center',
  };
  const stepBtn = {
    background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.15)',
    borderRadius: 6, color: '#ccc', fontSize: 16, cursor: 'pointer',
    width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontWeight: 700, lineHeight: 1,
  };
  const STEP = 0.1; // 0.1 m per click

  const def3D = opening3DDefaults(opening.type);
  const dims = (opening.type === 'window' || isDecor)
    ? [['W', opening.width || def3D.width, 'width'], ['H', opening.height || def3D.height, 'height'], [t('sill'), opening.sill ?? def3D.sill, 'sill']]
    : [['W', opening.width  || def3D.width, 'width'],
       ['H', opening.height || def3D.height, 'height']];

  return (
    <div className="room3d-item-panel" style={{
      position: 'absolute', bottom: 72, right: 16,
      background: 'rgba(16,16,22,0.94)', backdropFilter: 'blur(14px)',
      borderRadius: 14, padding: '14px 16px', width: 256,
      boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
      border: '1px solid rgba(255,255,255,0.12)', zIndex: 30, userSelect: 'none',
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 }}>
        <span style={{ fontSize: 20 }}>{typeIcon}</span>
        <span style={{ color: '#fff', fontWeight: 700, fontSize: 14, flex: 1 }}>{typeLabel}</span>
        <button onClick={onCopy}  title={t('copy_ctrlc_tooltip')} style={iconBtn}>⧉</button>
        <button onClick={onDelete} title={t('delete_tooltip')} style={{ ...iconBtn, color: '#ff7070' }}>🗑</button>
        <button onClick={onClose}  title={t('close_tooltip')}  style={iconBtn}>✕</button>
      </div>

      {/* Navigate button for door/arch */}
      {adjacentRoom && onNavigate && (
        <button
          onClick={onNavigate}
          style={{
            width: '100%', marginBottom: 12, padding: '7px 0',
            background: 'rgba(255,200,80,0.15)', border: '1px solid rgba(255,200,80,0.35)',
            borderRadius: 8, color: '#ffcc60', fontWeight: 600, fontSize: 12, cursor: 'pointer',
          }}
        >
          {t('go_to_prefix')} {adjacentRoom.label}
        </button>
      )}

      {/* Color row + presets — locked for guests */}
      <div style={{ position: 'relative', marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, opacity: isGuest ? 0.35 : 1 }}>
          <input type="color" value={color} onChange={e => apply(e.target.value)} disabled={isGuest}
            style={{ width: 38, height: 30, padding: 2, cursor: isGuest ? 'default' : 'pointer', background: 'none',
              border: '1.5px solid rgba(255,255,255,0.2)', borderRadius: 7 }} />
          <span style={{ color: '#666', fontSize: 11, fontFamily: 'monospace', flex: 1 }}>{color}</span>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 5, opacity: isGuest ? 0.35 : 1 }}>
          {/* "Default" — clears the tint so the item shows its authored look
              (GLB doors/windows/clocks render their original materials). */}
          <button
            title={t('default_color')} onClick={applyDefault} disabled={isGuest}
            style={{
              width: 30, height: 30, borderRadius: 6, cursor: isGuest ? 'default' : 'pointer',
              background: 'linear-gradient(135deg, #6b6f76 0 48%, #d8dade 52% 100%)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#fff', fontSize: 14, lineHeight: 1,
              border: !colored ? '2px solid #fff' : '2px solid rgba(255,255,255,0.12)',
              boxShadow: !colored ? '0 0 0 2px rgba(255,200,80,0.5)' : 'none',
            }}
          >↺</button>
          {OPENING_COLOR_PRESETS.map(c => (
            <button key={c} onClick={() => apply(c)} disabled={isGuest} style={{
              width: 30, height: 30, borderRadius: 6, cursor: isGuest ? 'default' : 'pointer', background: c,
              border: (colored && color === c) ? '2px solid #fff' : '2px solid rgba(255,255,255,0.12)',
              boxShadow: (colored && color === c) ? '0 0 0 2px rgba(255,200,80,0.5)' : 'none',
            }} />
          ))}
        </div>
        {isGuest && (
          <div style={{
            position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
            gap: 5, fontSize: 10, fontWeight: 700, color: '#ffcc60', textAlign: 'center', padding: '0 10px',
          }}>
            🔒 {t('guest_customize_locked')}
          </div>
        )}
      </div>

      {/* Size controls */}
      <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: 10 }}>
        <div style={{ color: '#666', fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', marginBottom: 7 }}>{t('size').toUpperCase()}</div>
        {dims.map(([label, val, key]) => {
          const floor = key === 'sill' ? 0.0 : 0.1;
          return (
            <SizeField
              key={key}
              label={label}
              valueM={val}
              min={floor}
              decimals={2}
              stepM={STEP}
              stepBtnStyle={stepBtn}
              onChangeM={m => onResize({ [key]: m })}
            />
          );
        })}
      </div>

      {/* Which of the interior wall's two faces this mounts on — a
          freestanding wall has usable room on both sides, so flipping here
          is faster than dragging all the way through to the other side. */}
      {isDecor && opening._wsSegId != null && (
        <div style={{ marginTop: 12, borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: 10 }}>
          <button
            onClick={() => onResize({ side: (opening._wsSide ?? 1) === 1 ? -1 : 1 })}
            style={{
              width: '100%', padding: '7px 0', borderRadius: 8, cursor: 'pointer',
              background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.14)',
              color: '#ccc', fontWeight: 600, fontSize: 12,
            }}
          >
            ⇄ {t('flip_to_other_side')}
          </button>
        </div>
      )}

      {/* Design picker — 6 realistic 3D designs per type */}
      {OPENING_STYLES[opening.type] && (
        <div style={{ marginTop: 12, borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: 10, position: 'relative' }}>
          <div style={{ color: '#666', fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', marginBottom: 7 }}>{t('design_label').toUpperCase()}</div>
          <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', opacity: isGuest ? 0.35 : 1 }}>
            {OPENING_STYLES[opening.type].map(s => {
              const active = (opening.style || OPENING_STYLES[opening.type][0].id) === s.id;
              // Prefer a translation; fall back to the style's own label when no
              // i18n key exists (the GLB door1…door21 styles have labels, not keys).
              const k = `opstyle_${opening.type}_${s.id}`;
              const tr = t(k);
              const label = tr === k ? s.label : tr;
              return (
                <button key={s.id} onClick={() => { if (!isGuest) onResize({ style: s.id }); }} disabled={isGuest} style={{
                  flex: '1 1 28%', minWidth: 62, padding: '4px 5px', borderRadius: 7, cursor: isGuest ? 'default' : 'pointer',
                  border: active ? '1.5px solid rgba(255,200,80,0.8)' : '1.5px solid rgba(255,255,255,0.12)',
                  background: active ? 'rgba(255,200,80,0.15)' : 'rgba(255,255,255,0.05)',
                  color: active ? '#ffcc50' : '#aaa',
                  fontSize: 10, fontWeight: active ? 700 : 400, transition: 'all 0.15s',
                }}>{label}</button>
              );
            })}
          </div>
          {isGuest && (
            <div style={{
              position: 'absolute', inset: '17px 0 0 0', display: 'flex', alignItems: 'center', justifyContent: 'center',
              gap: 5, fontSize: 10, fontWeight: 700, color: '#ffcc60', textAlign: 'center', padding: '0 10px',
            }}>
              🔒 {t('guest_customize_locked')}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── ADD OPENING MODAL ────────────────────────────────────────────────────────

// Centered on whichever wall is picked — the old draggable "position along
// wall" slider was removed, so every opening now lands mid-wall by default.
const CENTER_POS = 0.5;

function AddOpeningModal({ room, onAdd, onClose, allowedTypes, title = 'Add Opening', wallSegments = [], freeWalls = [] }) {
  const { t } = useT();
  const ALL_TYPE_OPTIONS = [
    { value: 'door',    label: t('door'),    icon: '🚪' },
    { value: 'window',  label: t('window'),  icon: '🪟' },
    { value: 'archway', label: t('archway'), icon: '🏛️' },
    ...WALL_DECOR_TYPES.map(dt => ({ value: dt, label: t(dt), icon: WALL_DECOR_META[dt].icon })),
  ];
  const typeOptions = allowedTypes
    ? ALL_TYPE_OPTIONS.filter(o => allowedTypes.includes(o.value))
    : ALL_TYPE_OPTIONS;

  const [type, setType] = useState(typeOptions[0]?.value || 'door');

  // Compound (stepped) rooms don't have a single "top/bottom/left/right" —
  // the bounding box edge often isn't a real wall at all. So instead of
  // guessing which real wall a "top, 40%" choice was meant to land on, list
  // the room's actual boundary segments directly; picking one is unambiguous.
  const rects = getRoomRects(room);
  const isCompound = rects.length > 1;
  const realSegments = useMemo(() => {
    if (!isCompound) return [];
    return computeRoomAutoWalls([room]).map(w => {
      const isH = w.y1 === w.y2;
      const length = isH ? w.x2 - w.x1 : w.y2 - w.y1;
      const side = isH
        ? (rects.some(r => r.y === w.y1) ? 'top' : 'bottom')
        : (rects.some(r => r.x === w.x1) ? 'left' : 'right');
      return { ...w, isH, length, side };
    }).sort((a, b) => b.length - a.length);
    // eslint-disable-next-line
  }, [room]);

  // A DRAWN room's walls ARE its outline edges — any angle, possibly curved —
  // so list those. Its rasterised rects would otherwise send it down the
  // compound path and offer a staircase of 1-cell segments, none of which is a
  // wall you can actually see in the room.
  const freePts = room?.pts?.length >= 3 ? room.pts : null;
  const freeEdges = useMemo(() => {
    if (!freePts) return [];
    return freePts.map((a, i) => ({
      index: i,
      length: edgeLength(a, freePts[(i + 1) % freePts.length]),
      curved: !!a.bulge,
    }));
  }, [freePts]);

  const wallOptions = [
    { value: 'top',    label: t('wall_top') },
    { value: 'bottom', label: t('wall_bottom') },
    { value: 'left',   label: t('wall_left') },
    { value: 'right',  label: t('wall_right') },
  ];
  const sideLabel = { top: t('wall_top'), bottom: t('wall_bottom'), left: t('wall_left'), right: t('wall_right') };

  // Every wall this can go on. The room's own walls came in three flavours
  // already; INTERIOR walls standing inside the room — hand-drawn straight ones
  // and free ones — were missing entirely, so from the 3D view there was no way
  // to put a door, window or wall item on one. Same overlap test the room's 3D
  // render uses to decide which interior walls belong to this room.
  const overlapsRoom = (w) => {
    const sx1 = Math.min(w.x1, w.x2), sx2 = Math.max(w.x1, w.x2);
    const sy1 = Math.min(w.y1, w.y2), sy2 = Math.max(w.y1, w.y2);
    return sx1 < room.x + room.w && sx2 > room.x && sy1 < room.y + room.h && sy2 > room.y;
  };
  // First free spot along a wall, so two openings don't stack on each other.
  const freeSpot = (used) => {
    for (const cand of [CENTER_POS, 0.25, 0.75, 0.15, 0.85]) {
      if (!used.some(u => Math.abs(u - cand) < 0.12)) return cand;
    }
    return CENTER_POS;
  };

  const choices = useMemo(() => {
    const out = [];
    if (freePts) {
      freeEdges.forEach(e => out.push({
        key: `e${e.index}`,
        label: `${t('wall_label')} ${e.index + 1}${e.curved ? ' ⌒' : ''}`,
        len: e.length,
        make: () => ({ edge: e.index, t: freeSpot((room.openings || []).filter(o => o.edge === e.index).map(o => o.t ?? 0.5)) }),
      }));
    } else if (isCompound) {
      realSegments.forEach((s, i) => out.push({
        key: `s${i}`,
        label: `${sideLabel[s.side]} ${t('wall_suffix')}`,
        len: s.length,
        make: () => {
          const worldVal = (s.isH ? s.x1 : s.y1) + CENTER_POS * s.length;
          return {
            wall: s.side, pos: CENTER_POS,
            absX: ((s.isH ? worldVal : s.x1) - room.x) * CELL,
            absY: ((s.isH ? s.y1 : worldVal) - room.y) * CELL,
          };
        },
      }));
    } else {
      wallOptions.forEach(o => out.push({
        key: o.value, label: o.label, len: null,
        make: () => ({ wall: o.value, pos: CENTER_POS }),
      }));
    }
    for (const s of (wallSegments || []).filter(overlapsRoom)) {
      out.push({
        key: `ws${s.id}`,
        label: t('interior_wall_label'),
        len: Math.hypot(s.x2 - s.x1, s.y2 - s.y1),
        twoSided: true,
        make: (side) => ({ wallId: s.id, t: freeSpot((s.openings || []).map(o => o.t ?? 0.5)), side }),
      });
    }
    for (const w of (freeWalls || []).filter(overlapsRoom)) {
      out.push({
        key: `fw${w.id}`,
        label: `${t('interior_wall_label')} ⌒`,
        len: edgeLength({ x: w.x1, y: w.y1, bulge: w.bulge || 0 }, { x: w.x2, y: w.y2 }),
        twoSided: true,
        make: (side) => ({ freeWallId: w.id, t: freeSpot((w.openings || []).map(o => o.t ?? 0.5)), side }),
      });
    }
    return out;
    // eslint-disable-next-line
  }, [room, freePts, freeEdges, isCompound, realSegments, wallSegments, freeWalls]);

  const [pick, setPick] = useState(0);
  const [side, setSide] = useState(1);   // which face, on a two-sided interior wall
  const chosen = choices[Math.min(pick, choices.length - 1)];
  // An interior wall stands free, so both its faces are usable — a wall item
  // has to say which one it hangs on. A room wall only has an inside.
  const askSide = !!chosen?.twoSided && WALL_DECOR_TYPES.includes(type);

  function handleConfirm() {
    if (!chosen) return;
    onAdd({ type, ...chosen.make(askSide ? side : undefined) });
    onClose();
  }

  return (
    <div className="i3d-modal-overlay" onClick={onClose}>
      <div className="i3d-modal" onClick={e => e.stopPropagation()}>
        <div className="i3d-modal-head">
          <span className="i3d-modal-title">{title}</span>
          <button className="i3d-iconbtn" onClick={onClose} title="Close">✕</button>
        </div>

        <div className="i3d-list">
          {/* Type */}
          <div className="i3d-section-label">{t('type_label')}</div>
          {typeOptions.map(o => (
            <button
              key={o.value}
              className={`i3d-row${type === o.value ? ' i3d-row--active' : ''}`}
              onClick={() => setType(o.value)}
            >
              <span className="i3d-row-ic"><ItemIcon type={o.value} /></span>
              <span className="i3d-row-body"><span className="i3d-row-label">{o.label}</span></span>
              {type === o.value && (
                <span className="i3d-row-aff" style={{ color: '#ffcf72' }}>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M3 8.5L6.5 12 13 4.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </span>
              )}
            </button>
          ))}

          {/* Wall */}
          <div className="i3d-section-label">{t('wall_label')}</div>
          {choices.map((c, i) => (
            <button key={c.key} className={`i3d-row${chosen?.key === c.key ? ' i3d-row--active' : ''}`} onClick={() => setPick(i)}>
              <span className="i3d-row-body"><span className="i3d-row-label">{c.label}</span></span>
              {c.len != null && <span className="i3d-row-sub" style={{ marginLeft: 'auto' }}>{c.len.toFixed(1)}m</span>}
            </button>
          ))}

          {askSide && (<>
            <div className="i3d-section-label">{t('wall_side_label')}</div>
            {[{ v: 1, l: t('wall_side_a') }, { v: -1, l: t('wall_side_b') }].map(o => (
              <button key={o.v} className={`i3d-row${side === o.v ? ' i3d-row--active' : ''}`} onClick={() => setSide(o.v)}>
                <span className="i3d-row-body"><span className="i3d-row-label">{o.l}</span></span>
              </button>
            ))}
          </>)}
        </div>

        {/* Confirm */}
        <div className="i3d-modal-foot">
          <button className="i3d-confirm" onClick={handleConfirm} disabled={!chosen}>
            {t('add_prefix')} {typeOptions.find(o => o.value === type)?.label}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── ADD ITEM MODAL ──────────────────────────────────────────────────────────

// Room-based grouping for the furniture picker. A type may appear under
// several rooms — items that suit any space (lamp, plant, rug, chair) also
// live under "Anywhere" so they're always one tap away.
const FURNITURE_CATEGORIES = [
  { id: 'living',   types: ['sofa', 'armchair', 'tv', 'shelf', 'rug', 'plant', 'lamp'] },
  { id: 'kitchen',  types: ['table', 'chair', 'sink'] },
  { id: 'bedroom',  types: ['bed', 'wardrobe', 'tv', 'lamp'] },
  { id: 'bathroom', types: ['bathtub', 'toilet', 'sink'] },
  { id: 'office',   types: ['desk', 'chair', 'shelf', 'lamp'] },
  { id: 'anywhere', types: ['lamp', 'plant', 'rug', 'chair'] },
];

// One model tile: lazily renders a real 3D thumbnail of its GLB (generated
// off-screen and cached, see modelThumbnails.js) once it scrolls into view,
// falling back to the type's line icon while loading / if it has no GLB.
function ModelTile({ type, styleId, label, onPick }) {
  const url = GLB_MODELS[styleId];
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  const [thumb, setThumb] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || !url) return;
    const root = el.closest('.i3d-grid');
    const io = new IntersectionObserver((entries) => {
      if (entries.some(e => e.isIntersecting)) { setVisible(true); io.disconnect(); }
    }, { root, rootMargin: '150px' });
    io.observe(el);
    return () => io.disconnect();
  }, [url]);

  useEffect(() => {
    if (!visible || !url) return;
    let alive = true;
    getModelThumbnail(url).then(d => { if (!alive) return; if (d) setThumb(d); else setFailed(true); });
    return () => { alive = false; };
  }, [visible, url]);

  // Show a spinner while a real 3D thumbnail is still being generated; only
  // fall back to the line icon if there's no model or it failed to render.
  const loading = url && !thumb && !failed;

  return (
    <button ref={ref} className="i3d-tile" onClick={onPick}>
      <span className={`i3d-tile-ic${thumb ? ' i3d-tile-ic--img' : ''}`}>
        {thumb
          ? <img src={thumb} alt={label} className="i3d-tile-img" />
          : loading
            ? <span className="i3d-tile-spinner" />
            : <ItemIcon type={type} size={30} />}
      </span>
      <span className="i3d-tile-label">{label}</span>
    </button>
  );
}

function AddItemModal({ room, onAdd, onClose }) {
  const { t } = useT();
  const [catId, setCatId] = useState(null);   // null → category list; else a room id
  const [typeId, setTypeId] = useState(null); // set → showing that type's model grid

  const catLabel = id => t('cat_' + id);
  const activeCat = FURNITURE_CATEGORIES.find(c => c.id === catId);
  const items = activeCat
    ? activeCat.types.map(tp => FURNITURE_TYPES.find(f => f.type === tp)).filter(Boolean)
    : [];
  const typeFt = typeId ? FURNITURE_TYPES.find(f => f.type === typeId) : null;
  const models = typeId ? (FURNITURE_STYLES[typeId] || []) : [];

  // Header title + back target depend on the current depth (category → type → models).
  const title = typeFt ? typeFt.label : (catId ? catLabel(catId) : t('add_furniture_modal_title'));
  const goBack = typeId ? () => setTypeId(null) : () => setCatId(null);

  return (
    <div className="i3d-modal-overlay" onClick={onClose}>
      <div className="i3d-modal" onClick={e => e.stopPropagation()}>
        <div className="i3d-modal-head">
          {(catId || typeId) && (
            <button className="i3d-iconbtn" onClick={goBack} title={t('back')}>
              <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
                <path d="M10 3L5 8l5 5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          )}
          <span className="i3d-modal-title">{title}</span>
          <button className="i3d-iconbtn" onClick={onClose} title="Close">✕</button>
        </div>

        {typeId ? (
          /* ── Level 3: model grid for the chosen type ── */
          <div className="i3d-grid">
            {models.map((m, i) => (
              <ModelTile
                key={m.id}
                type={typeId}
                styleId={m.id}
                label={`${t('model_label')} ${i + 1}`}
                onPick={() => { onAdd(typeId, m.id); onClose(); }}
              />
            ))}
          </div>
        ) : (
          <div className="i3d-list">
            {!catId ? (
              <>
                <div className="i3d-hint">{t('cat_pick_hint')}</div>
                {FURNITURE_CATEGORIES.map(c => (
                  <button key={c.id} className="i3d-row" onClick={() => setCatId(c.id)}>
                    <span className="i3d-row-ic"><ItemIcon type={c.id} /></span>
                    <span className="i3d-row-body">
                      <span className="i3d-row-label">{catLabel(c.id)}</span>
                      <span className="i3d-row-sub">{c.types.length} {t('cat_items')}</span>
                    </span>
                    <span className="i3d-row-aff">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M6 3l5 5-5 5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </span>
                  </button>
                ))}
              </>
            ) : (
              /* ── Level 2: item types → open that type's models ── */
              items.map(ft => {
                const count = (FURNITURE_STYLES[ft.type] || []).length;
                return (
                  <button key={ft.type} className="i3d-row" onClick={() => setTypeId(ft.type)}>
                    <span className="i3d-row-ic"><ItemIcon type={ft.type} /></span>
                    <span className="i3d-row-body">
                      <span className="i3d-row-label">{itemLabel(t, 'furn_', ft)}</span>
                      <span className="i3d-row-sub">{count} {t('model_count')}</span>
                    </span>
                    <span className="i3d-row-aff">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M6 3l5 5-5 5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </span>
                  </button>
                );
              })
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function AddLightingModal({ onAdd, onClose }) {
  const { t } = useT();
  return (
    <div className="i3d-modal-overlay" onClick={onClose}>
      <div className="i3d-modal" onClick={e => e.stopPropagation()}>
        <div className="i3d-modal-head">
          <span className="i3d-modal-title">{t('add_lighting_modal_title')}</span>
          <button className="i3d-iconbtn" onClick={onClose} title="Close">✕</button>
        </div>

        <div className="i3d-list">
          {LIGHTING_TYPES.map(lt => (
            <button key={lt.type} className="i3d-row" onClick={() => { onAdd(lt.type); onClose(); }}>
              <span className="i3d-row-ic"><ItemIcon type={lt.type} /></span>
              <span className="i3d-row-body">
                <span className="i3d-row-label">{itemLabel(t, 'light_', lt)}</span>
              </span>
              <span className="i3d-row-aff">
                <svg width="17" height="17" viewBox="0 0 17 17" fill="none">
                  <path d="M8.5 4v9M4 8.5h9" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"/>
                </svg>
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── HOVER MANAGER ───────────────────────────────────────────────────────────
// Uses DOM-level pointermove + THREE.Box3 ray intersection so hover is
// 100% reliable — bypasses R3F's event system entirely.

function HoverManager({ roomItems, room, roomW, roomD, onHoverChange, active }) {
  const { camera, gl } = useThree();
  const hoverRef    = useRef(null);
  const ndcVec      = useMemo(() => new THREE.Vector2(), []);
  const raycaster   = useMemo(() => new THREE.Raycaster(), []);
  const cameraRef   = useRef(camera);
  cameraRef.current = camera;

  const boxes = useMemo(() => {
    if (!room) return [];
    return roomItems.map(item => {
      const rot = item.rotation || 0;
      const dw  = rot % 180 === 0 ? item.w : item.h;
      const dh  = rot % 180 === 0 ? item.h : item.w;
      const cx  = ((item.x + dw / 2) - room.x * CELL) * PX_TO_M - roomW / 2;
      const cz  = ((item.y + dh / 2) - room.y * CELL) * PX_TO_M - roomD / 2;
      const hw  = (dw * PX_TO_M) / 2;
      const hd  = (dh * PX_TO_M) / 2;
      return {
        id: item.id,
        box: new THREE.Box3(
          new THREE.Vector3(cx - hw, -0.5, cz - hd),
          new THREE.Vector3(cx + hw,  5.0, cz + hd)
        ),
      };
    });
  }, [roomItems, room, roomW, roomD]);

  const boxesRef    = useRef(boxes);
  boxesRef.current  = boxes;

  useEffect(() => {
    const canvas = gl.domElement;

    function onMove(e) {
      const rect = canvas.getBoundingClientRect();
      ndcVec.set(
        ((e.clientX - rect.left) / rect.width)  * 2 - 1,
        -((e.clientY - rect.top)  / rect.height) * 2 + 1
      );
      raycaster.setFromCamera(ndcVec, cameraRef.current);

      let hitId = null;
      for (const { id, box } of boxesRef.current) {
        if (raycaster.ray.intersectsBox(box)) {
          hitId = id;
          break;
        }
      }

      if (hitId !== hoverRef.current) {
        hoverRef.current = hitId;
        onHoverChange(active ? hitId : null);
        canvas.style.cursor = (hitId && active) ? 'grab' : '';
      }
    }

    function onLeave() {
      if (hoverRef.current !== null) {
        hoverRef.current = null;
        onHoverChange(null);
        canvas.style.cursor = '';
      }
    }

    canvas.addEventListener('pointermove', onMove);
    canvas.addEventListener('pointerleave', onLeave);
    return () => {
      canvas.removeEventListener('pointermove', onMove);
      canvas.removeEventListener('pointerleave', onLeave);
    };
  }, [gl, onHoverChange]);

  return null;
}

// ─── ROOM SCENE ───────────────────────────────────────────────────────────────

// Overlay meshes (accent patches, selection/hover highlights) sit just in front
// of the wall — make the raycaster ignore them so a click always resolves the
// full-wall UV (and thus the right grid cell) rather than the small overlay's.
const overlayNoRaycast = () => null;

// Multiply two sRGB hex colours channel-by-channel. Walls are unlit
// (meshBasicMaterial) so they don't darken with the scene at night — instead we
// multiply their colour/texture by a per-time-of-day tint (white = unchanged by
// day, a dark cool tint at night) so they read night/evening-appropriate too.
function mulHex(base, tint) {
  const norm = (h) => {
    h = String(h || '').replace('#', '');
    if (h.length === 3) h = h.split('').map((c) => c + c).join('');
    return h.length === 6 ? h : 'ffffff';
  };
  const A = norm(base), B = norm(tint);
  const ch = (i) => Math.round(parseInt(A.slice(i, i + 2), 16) * parseInt(B.slice(i, i + 2), 16) / 255);
  const hx = (n) => n.toString(16).padStart(2, '0');
  return '#' + hx(ch(0)) + hx(ch(2)) + hx(ch(4));
}
// Grid cell size for per-area wall painting (~1 metre).
const WALL_CELL_M = 1.0;

function RoomScene({ room, roomW, roomD, roomH, floorMat, wallMat, ceilMat, wallFaceMats, wallPatchMats, floorPatchMats, cellPreviewMat, selectTarget, selectedSurface, selectedCells, onAreaSelect, roomItems, lightingItems, openingsToRender, onNavigate, onFurnitureClick, onLightingClick, selectedItemId, selectedLightingId, hoveredId, selectedOpeningId, onOpeningClick, openingsEditable, wallSegments, freeWalls, timeOfDay }) {
  // Which surface (if any) is armed for per-area painting this turn.
  const wallSelect  = selectTarget === 'wall';
  const floorSelect = selectTarget === 'floor';
  // A recoloured preset (__colorOverride) can also carry a hand-picked finish —
  // apply it as a roughness/metalness override on top of the pattern texture
  // the preset's own generator drew; __custom materials already bake their
  // finish in via makeCustomFinishTexture, so leave those untouched here.
  const withFinishOverride = (mat, data) =>
    (data && mat?.finish && mat.__colorOverride) ? { ...data, ...finishToPBR(mat.finish) } : data;

  const floorData = useMemo(() => withFinishOverride(floorMat, makeFloorTexture(floorMat)), [floorMat?.id, floorMat?.baseColor, floorMat?.finish]);
  const wallData  = useMemo(() => withFinishOverride(wallMat, makeWallTexture(wallMat)),  [wallMat?.id,  wallMat?.color,     wallMat?.finish]);
  const ceilData  = useMemo(() => withFinishOverride(ceilMat, makeCeilingTexture(ceilMat)), [ceilMat?.id, ceilMat?.color,    ceilMat?.finish]);

  // World-space walls furniture measures its distance to (room perimeter +
  // interior freestanding walls). Computed once here, shared by every piece.
  const dimWalls = useMemo(() => buildDimWalls(room, roomW, roomD, wallSegments), [room, roomW, roomD, wallSegments]);

  // Live map of each furniture piece's top-surface height (metres). Filled by
  // the pieces themselves; read when a stackable item is dropped so it knows
  // how high to sit on whatever it landed on.
  const heightsRef = useRef({});

  const floorColor = floorMat?.baseColor || '#d4c4a8';
  const wallColor  = wallMat?.color  || '#f0e8d8';
  const ceilColor  = ceilMat?.color  || '#f8f6f2';
  // Per-time-of-day darkening tint for the (unlit) walls — see mulHex above.
  const wallTint = (TIME_OF_DAY_PRESETS[timeOfDay] || {}).wallTint || '#ffffff';

  const floorRough = floorData?.roughness ?? 0.85;
  const floorMetal = floorData?.metalness ?? 0;
  const ceilRough  = ceilData?.roughness  ?? 0.92;

  // wall repeat scales
  const wRy    = wallData?.repeat?.[1] ?? 0.5;
  const cRepX  = Math.max(1, Math.round(roomW * (ceilData?.repeat?.[0] ?? 0.5)));
  const cRepZ  = Math.max(1, Math.round(roomD * (ceilData?.repeat?.[0] ?? 0.5)));

  const cloneWallTex = (repX, repY = wRy) => {
    if (!wallData?.texture) return null;
    // Custom materials have no `repeat` key — use the texture directly to avoid clone/upload issues
    if (!wallData.repeat) return wallData.texture;
    const t = wallData.texture.clone();
    t.repeat.set(repX, repY);
    t.needsUpdate = true;
    return t;
  };
  const cloneCeilTex = () => {
    if (!ceilData?.texture) return null;
    const t = ceilData.texture.clone();
    t.repeat.set(cRepX, cRepZ);
    t.needsUpdate = true;
    return t;
  };

  const wallTexKey = wallData?.texture ? 'tex' : 'col';

  // A room's 4 walls face 4 different directions, so under a single
  // directional sun a lit material gives them each a different brightness
  // and colour cast — one wall near-facing the sun, its neighbour barely
  // grazed by it — even though they're all the same chosen wall colour.
  // An emissive boost only softened that; the user wants zero variation,
  // so walls are fully unlit — colour/texture render exactly as picked, the
  // same everywhere, independent of light angle (the one trade-off: they no
  // longer show contact shadows from nearby furniture).
  // repY defaults to a full-height wall; a piece shorter than the room (the
  // sill/header slices around an opening) passes its own share so the pattern
  // keeps the same density instead of being squeezed into the smaller piece.
  const makeWallMat = (repX, repY) => (
    <meshBasicMaterial
      key={`wm-${wallTexKey}-${wallColor}-${wallTint}`}
      color={wallData?.texture ? wallTint : mulHex(wallColor, wallTint)}
      map={cloneWallTex(repX, repY)}
    />
  );

  // baseboard colour matches wall (slightly lighter)
  const bbColor = wallColor;

  // ── PER-WALL (accent-wall) OVERRIDES ──────────────────────────────────────
  // Full-face overrides (legacy — kept so already-saved designs still render).
  const faceInfo = useMemo(() => {
    const wf = wallFaceMats || {};
    const out = {};
    for (const k in wf) { const m = wf[k]; if (m) out[k] = { data: withFinishOverride(m, makeWallTexture(m)), color: m.color || '#f0e8d8' }; }
    return out;
  }, [wallFaceMats]);

  // Per-area (metre-grid) overrides, resolved to texture/colour once per cell.
  const patchInfo = useMemo(() => {
    const wp = wallPatchMats || {};
    const out = {};
    for (const f in wp) {
      out[f] = {};
      for (const k in wp[f]) { const m = wp[f][k]; if (m) out[f][k] = { data: withFinishOverride(m, makeWallTexture(m)), color: m.color || '#f0e8d8' }; }
    }
    return out;
  }, [wallPatchMats]);

  // Hover cell under the cursor while in select mode ({ faceId, key }).
  const [hoverCell, setHoverCell] = useState(null);

  // Staged material previewed on the selected WALL cells (before Apply).
  const previewInfo = useMemo(
    () => (cellPreviewMat && selectTarget !== 'floor' ? { data: withFinishOverride(cellPreviewMat, makeWallTexture(cellPreviewMat)), color: cellPreviewMat.color || '#f0e8d8' } : null),
    [cellPreviewMat, selectTarget]
  );

  // Per-area FLOOR overrides, resolved to texture data once per cell.
  const floorPatchInfo = useMemo(() => {
    const fp = floorPatchMats || {};
    const out = {};
    for (const k in fp) { const m = fp[k]; if (m) out[k] = { data: withFinishOverride(m, makeFloorTexture(m)), color: m.baseColor || m.color || '#d4c4a8' }; }
    return out;
  }, [floorPatchMats]);
  // Staged material previewed on the selected FLOOR cells (before Apply).
  const floorPreviewInfo = useMemo(
    () => (cellPreviewMat && selectTarget === 'floor' ? { data: withFinishOverride(cellPreviewMat, makeFloorTexture(cellPreviewMat)), color: cellPreviewMat.baseColor || cellPreviewMat.color || '#d4c4a8' } : null),
    [cellPreviewMat, selectTarget]
  );

  // The ~1m grid for a surface of the given width/height (walls: height = room
  // height; floor: height = room depth).
  const faceGrid = (width, height = roomH) => {
    const cols = Math.max(1, Math.round(width / WALL_CELL_M));
    const rows = Math.max(1, Math.round(height / WALL_CELL_M));
    return { cols, rows, cellW: width / cols, cellH: height / rows };
  };
  const cellKeyFromUV = (uv, width, height = roomH) => {
    const { cols, rows } = faceGrid(width, height);
    const c = Math.min(cols - 1, Math.max(0, Math.floor(uv.x * cols)));
    const r = Math.min(rows - 1, Math.max(0, Math.floor(uv.y * rows)));
    return `${c}-${r}`;
  };
  // Local-space centre (x,y) of a cell within a surface of the given size.
  const cellCenter = (key, width, height = roomH) => {
    const { cols, rows, cellW, cellH } = faceGrid(width, height);
    const [c, r] = key.split('-').map(Number);
    const cc = Math.min(cols - 1, Math.max(0, c));
    const rr = Math.min(rows - 1, Math.max(0, r));
    return { x: -width / 2 + (cc + 0.5) * cellW, y: -height / 2 + (rr + 0.5) * cellH, cellW, cellH };
  };

  // Material JSX for one wall face — full-face override if set, else the room
  // default. Per-area patches are drawn as overlay meshes on top (see below).
  const wallMatForFace = (faceId, width) => {
    const info = faceInfo[faceId];
    const data = info ? info.data : wallData;
    const color = info ? info.color : wallColor;
    const repX = Math.max(1, Math.round(width * (data?.repeat?.[0] ?? 0.4)));
    const repY = data?.repeat?.[1] ?? 0.5;
    let map = null;
    if (data?.texture) {
      if (!data.repeat) map = data.texture;
      else { const t = data.texture.clone(); t.repeat.set(repX, repY); t.needsUpdate = true; map = t; }
    }
    return (
      <meshBasicMaterial
        key={`wf-${faceId}-${info ? (info.color + (data?.texture ? 't' : '')) : (wallTexKey + wallColor)}-${wallTint}`}
        color={data?.texture ? wallTint : mulHex(color, wallTint)}
        map={map}
      />
    );
  };

  // Material for a single painted cell (sized to the cell, density kept ~equal
  // to the whole wall so the pattern matches).
  const cellMaterial = (info, cellW, cellH) => {
    const data = info.data, color = info.color;
    const repX = Math.max(1, Math.round(cellW * (data?.repeat?.[0] ?? 0.4)));
    const repY = Math.max(0.15, (data?.repeat?.[1] ?? 0.5) * (cellH / roomH));
    let map = null;
    if (data?.texture) {
      if (!data.repeat) map = data.texture;
      else { const t = data.texture.clone(); t.repeat.set(repX, repY); t.needsUpdate = true; map = t; }
    }
    return <meshBasicMaterial color={data?.texture ? wallTint : mulHex(color, wallTint)} map={map} />;
  };

  // Floor cell material — lit (meshStandardMaterial), matching the main floor.
  // The floor texture bakes its FULL-room tile count into `repeat`, so a cell
  // must show just its slice of that (repeat × cell/room) offset to the cell's
  // world position — giving the same tile size as the whole floor and keeping
  // adjacent same-material cells seamless.
  const floorCellMat = (info, x, y, cellW, cellH) => {
    const data = info.data;
    const r = data?.roughness ?? 0.85;
    let map = null;
    if (data?.texture) {
      const t = data.texture.clone();
      const bx = data.texture.repeat.x || 1, by = data.texture.repeat.y || 1;
      t.repeat.set(bx * cellW / roomW, by * cellH / roomD);
      t.offset.set(bx * (x - cellW / 2 + roomW / 2) / roomW, by * (y - cellH / 2 + roomD / 2) / roomD);
      t.needsUpdate = true; map = t;
    }
    return (
      <meshStandardMaterial
        map={map}
        color={data?.texture ? '#f8f4ee' : info.color}
        roughness={Math.max(0.05, r * 0.9)}
        metalness={data?.metalness ?? 0}
        envMapIntensity={r < 0.12 ? 3.0 : r < 0.30 ? 2.0 : r < 0.55 ? 1.2 : 0.5}
      />
    );
  };

  // One dashed-rectangle outline (a cell border), non-raycastable.
  const dashRect = (key, x, y, cellW, cellH, color, lineWidth, dashSize, gapSize, z) => {
    const hw = (cellW * 0.97) / 2, hh = (cellH * 0.97) / 2;
    return (
      <Line
        key={key}
        points={[[-hw, -hh, 0], [hw, -hh, 0], [hw, hh, 0], [-hw, hh, 0], [-hw, -hh, 0]]}
        position={[x, y, z]}
        color={color} lineWidth={lineWidth}
        dashed dashSize={dashSize} gapSize={gapSize}
        transparent depthWrite={false} raycast={overlayNoRaycast}
      />
    );
  };

  // Selected (red) + hovered (blue) cell outlines for one surface.
  const selHoverOutlines = (faceId, width, height, armed) => {
    const sel = (selectedSurface === faceId && Array.isArray(selectedCells)) ? selectedCells : [];
    const hov = (armed && hoverCell && hoverCell.faceId === faceId) ? hoverCell.key : null;
    return (
      <>
        {sel.map(key => { const { x, y, cellW, cellH } = cellCenter(key, width, height); return dashRect(`s-${key}`, x, y, cellW, cellH, '#ef4444', 3, 0.12, 0.06, 0.015); })}
        {hov && !sel.includes(hov) && (() => { const { x, y, cellW, cellH } = cellCenter(hov, width, height); return dashRect('hov', x, y, cellW, cellH, '#3b82f6', 2, 0.09, 0.06, 0.013); })()}
      </>
    );
  };

  // Click-to-pick a ~1m cell + live hover, for any surface. Non-armed callers
  // simply don't spread this (or don't render the hit-surface).
  const areaCellProps = (faceId, width, height = roomH) => (onAreaSelect
    ? {
        onClick: (e) => { e.stopPropagation(); if (e.uv) onAreaSelect(faceId, cellKeyFromUV(e.uv, width, height)); },
        onPointerMove: (e) => {
          e.stopPropagation();
          if (!e.uv) return;
          const key = cellKeyFromUV(e.uv, width, height);
          setHoverCell(prev => (prev && prev.faceId === faceId && prev.key === key) ? prev : { faceId, key });
        },
        onPointerOut: () => setHoverCell(prev => (prev && prev.faceId === faceId) ? null : prev),
      }
    : {});
  // Wall boundary meshes attach the handlers only while wall-select is armed.
  const wallFaceProps = (faceId, width) => (wallSelect ? areaCellProps(faceId, width, roomH) : {});

  // Painted patches + preview + selection/hover for one WALL face. All overlays
  // are non-raycastable so they never steal the wall's click/hover.
  const faceOverlays = (faceId, width) => {
    const patches = patchInfo[faceId] || {};
    const sel = (selectedSurface === faceId && Array.isArray(selectedCells)) ? selectedCells : [];
    return (
      <>
        {Object.keys(patches).map(key => {
          const { x, y, cellW, cellH } = cellCenter(key, width);
          return (
            <mesh key={`p-${key}`} position={[x, y, 0.010]} raycast={overlayNoRaycast}>
              <planeGeometry args={[cellW, cellH]} />
              {cellMaterial(patches[key], cellW, cellH)}
            </mesh>
          );
        })}
        {previewInfo && sel.map(key => {
          const { x, y, cellW, cellH } = cellCenter(key, width);
          return (
            <mesh key={`pv-${key}`} position={[x, y, 0.012]} raycast={overlayNoRaycast}>
              <planeGeometry args={[cellW, cellH]} />
              {cellMaterial(previewInfo, cellW, cellH)}
            </mesh>
          );
        })}
        {selHoverOutlines(faceId, width, roomH, wallSelect)}
      </>
    );
  };

  // Painted patches + preview + selection/hover for the FLOOR. Rendered inside
  // a group already rotated flat, so local (x,y) maps to (world x, -z) and local
  // +z lifts overlays just above the floor.
  const floorOverlays = () => {
    const fid = 'floor';
    const patches = floorPatchInfo || {};
    const sel = (selectedSurface === fid && Array.isArray(selectedCells)) ? selectedCells : [];
    return (
      <>
        {Object.keys(patches).map(key => {
          const { x, y, cellW, cellH } = cellCenter(key, roomW, roomD);
          return (
            <mesh key={`fp-${key}`} position={[x, y, 0.006]} raycast={overlayNoRaycast}>
              <planeGeometry args={[cellW, cellH]} />
              {floorCellMat(patches[key], x, y, cellW, cellH)}
            </mesh>
          );
        })}
        {floorPreviewInfo && sel.map(key => {
          const { x, y, cellW, cellH } = cellCenter(key, roomW, roomD);
          return (
            <mesh key={`fpv-${key}`} position={[x, y, 0.008]} raycast={overlayNoRaycast}>
              <planeGeometry args={[cellW, cellH]} />
              {floorCellMat(floorPreviewInfo, x, y, cellW, cellH)}
            </mesh>
          );
        })}
        {selHoverOutlines(fid, roomW, roomD, floorSelect)}
      </>
    );
  };

  // A free-form room (smooth `pts` outline) also renders through the compound
  // path — its floor/ceiling shape and per-edge walls come from that outline.
  const isCompound    = (room?.rects && room.rects.length > 1) || (room?.pts && room.pts.length >= 3);
  const floorShape    = useMemo(() => isCompound ? buildFloorShape(room)    : null, [room?.id, isCompound]);
  const ceilingShape  = useMemo(() => isCompound ? buildCeilingShape(room)  : null, [room?.id, isCompound]);
  const compoundWalls = useMemo(() => isCompound ? buildCompoundWallSegs(room) : null, [room?.id, isCompound]);

  // ── CURVED FACES ──────────────────────────────────────────────────────────
  // One continuous arc mesh per curved outline edge. Geometry is built (and
  // disposed) in a memo rather than per render, since it can't come from a
  // reusable <planeGeometry>.
  const curvedFaces = useMemo(() => (compoundWalls || []).filter(w => w.curve), [compoundWalls]);
  const curvedWallGeos = useMemo(() => {
    const m = new Map();
    for (const w of curvedFaces)
      m.set(w.key, buildArcWallGeometry(w.curve, 0, 1, 0, roomH, { pad0: ARC_PAD, pad1: ARC_PAD, uvAcrossArc: true }));
    return m;
  }, [curvedFaces, roomH]);
  useEffect(() => () => curvedWallGeos.forEach(g => g.dispose()), [curvedWallGeos]);

  // Painted patches + the staged preview, as curved STRIPS hugging the arc: the
  // cell's column becomes an arc-fraction range and its row a height range, so a
  // painted area wraps the curve instead of chording across it.
  const curvedPatchGeos = useMemo(() => {
    const made = [];
    const byFace = new Map();
    for (const w of curvedFaces) {
      const { cols, rows, cellW, cellH } = faceGrid(w.curve.len);
      const patches = patchInfo[w.key] || {};
      const sel = (selectedSurface === w.key && Array.isArray(selectedCells)) ? selectedCells : [];
      // A cell key can outlive the outline it was painted on (edit the curve and
      // the grid resizes), so ignore any that no longer land on this face.
      const strip = (key, off) => {
        const [c, r] = key.split('-').map(Number);
        if (!(c >= 0 && c < cols && r >= 0 && r < rows)) return null;
        // The end cells carry the same corner overlap as the wall beneath them,
        // so painting the first/last one doesn't leave a bare sliver at the corner.
        const geo = buildArcWallGeometry(w.curve, c / cols, (c + 1) / cols, r * cellH, (r + 1) * cellH, {
          off, pad0: c === 0 ? ARC_PAD : 0, pad1: c === cols - 1 ? ARC_PAD : 0,
        });
        made.push(geo);
        return { geo, cellW, cellH };
      };
      const entry = { patches: [], preview: [], sel: [], cols, rows, cellW, cellH };
      for (const key of Object.keys(patches)) {
        const s = strip(key, 0.010);
        if (s) entry.patches.push({ key, info: patches[key], ...s });
      }
      if (previewInfo) for (const key of sel) {
        const s = strip(key, 0.012);
        if (s) entry.preview.push({ key, info: previewInfo, ...s });
      }
      entry.sel = sel;
      byFace.set(w.key, entry);
    }
    byFace.dispose = () => made.forEach(g => g.dispose());
    return byFace;
  }, [curvedFaces, roomH, patchInfo, previewInfo, selectedSurface, selectedCells]);
  useEffect(() => () => curvedPatchGeos.dispose(), [curvedPatchGeos]);

  // Dashed selection/hover borders for a curved face — the flat dashRect would
  // cut straight across the arc, so trace the cell's outline along it instead.
  const curvedOutline = (w, key, color, lineWidth, dashSize, off) => {
    const g = curvedPatchGeos.get(w.key);
    if (!g) return null;
    const [c, r] = key.split('-').map(Number);
    if (!(c >= 0 && c < g.cols && r >= 0 && r < g.rows)) return null;
    // Inset ~1.5% (as dashRect does) so the border reads inside its own cell.
    const uPad = 0.015 / g.cols, yPad = g.cellH * 0.015;
    return (
      <Line
        key={`${color}-${key}`}
        points={arcOutlinePoints(w.curve, c / g.cols + uPad, (c + 1) / g.cols - uPad, r * g.cellH + yPad, (r + 1) * g.cellH - yPad, off)}
        color={color} lineWidth={lineWidth}
        dashed dashSize={dashSize} gapSize={0.06}
        transparent depthWrite={false} raycast={overlayNoRaycast}
      />
    );
  };

  // Everything drawn ON a curved face: paint patches, staged preview, borders.
  const curvedFaceOverlays = (w) => {
    const g = curvedPatchGeos.get(w.key);
    if (!g) return null;
    const hov = (wallSelect && hoverCell && hoverCell.faceId === w.key) ? hoverCell.key : null;
    return (
      <>
        {g.patches.map(p => (
          <mesh key={`p-${p.key}`} geometry={p.geo} raycast={overlayNoRaycast}>
            {cellMaterial(p.info, p.cellW, p.cellH)}
          </mesh>
        ))}
        {g.preview.map(p => (
          <mesh key={`pv-${p.key}`} geometry={p.geo} raycast={overlayNoRaycast}>
            {cellMaterial(p.info, p.cellW, p.cellH)}
          </mesh>
        ))}
        {g.sel.map(key => curvedOutline(w, key, '#ef4444', 3, 0.12, 0.015))}
        {hov && !g.sel.includes(hov) && curvedOutline(w, hov, '#3b82f6', 2, 0.09, 0.013)}
      </>
    );
  };
  // Merged free-form room: the absorbed straight parts (floor pieces + their
  // outer walls), so the 3D view shows the WHOLE merged room.
  const mergeShapes = useMemo(() => buildMergeShapes(room), [room?.id, room?.mergeRects]);
  const mergeCeilShapes = useMemo(() => buildMergeShapes(room, true), [room?.id, room?.mergeRects]);
  const mergeWalls = useMemo(() => {
    if (!room?.mergeRects?.length) return null;
    const W = room.w * CELL_M, D = room.h * CELL_M;
    const out = [];
    room.mergeRects.forEach((r, ri) => {
      const x0 = (r.x - room.x) * CELL_M - W / 2, x1 = x0 + r.w * CELL_M;
      const z0 = (r.y - room.y) * CELL_M - D / 2, z1 = z0 + r.h * CELL_M;
      const inCurve = (px, pz) => {
        // Skip the face that abuts the curved part — that's an interior seam.
        const P = room.pts; if (!P) return false;
        let inside = false;
        const gx = px + W / 2 + room.x * CELL_M, gz = pz + D / 2 + room.y * CELL_M;
        for (let i = 0, j = P.length - 1; i < P.length; j = i++) {
          const xi = P[i].x * CELL_M, yi = P[i].y * CELL_M, xj = P[j].x * CELL_M, yj = P[j].y * CELL_M;
          if ((yi > gz) !== (yj > gz) && gx < ((xj - xi) * (gz - yi)) / ((yj - yi) || 1e-9) + xi) inside = !inside;
        }
        return inside;
      };
      // Probe further out (0.3 m) and at three points along each face: a shared
      // wall between the merged part and the curved part is INTERIOR and must not
      // be drawn, otherwise it stands as a stray wall inside the room. A single
      // 5 cm probe sat too close to the boundary to classify reliably.
      const O = 0.3;
      const faces = [
        { cx: (x0 + x1) / 2, cz: z0, len: x1 - x0, rot: 0,           px: [x0, (x0 + x1) / 2, x1], pz: [z0 - O, z0 - O, z0 - O] },
        { cx: (x0 + x1) / 2, cz: z1, len: x1 - x0, rot: 0,           px: [x0, (x0 + x1) / 2, x1], pz: [z1 + O, z1 + O, z1 + O] },
        { cx: x0, cz: (z0 + z1) / 2, len: z1 - z0, rot: Math.PI / 2, px: [x0 - O, x0 - O, x0 - O], pz: [z0, (z0 + z1) / 2, z1] },
        { cx: x1, cz: (z0 + z1) / 2, len: z1 - z0, rot: Math.PI / 2, px: [x1 + O, x1 + O, x1 + O], pz: [z0, (z0 + z1) / 2, z1] },
      ];
      faces.forEach((f, fi) => {
        // Interior if the majority of probes fall inside the curved outline.
        let hits = 0;
        for (let s = 0; s < 3; s++) if (inCurve(f.px[s], f.pz[s])) hits++;
        if (hits < 2) out.push({ cx: f.cx, cz: f.cz, len: f.len, rot: f.rot, key: `mw-${ri}-${fi}` });
      });
    });
    return out;
  }, [room?.id, room?.mergeRects, room?.pts, room?.w, room?.h]);

  return (
    <group>
      {/* ── FLOOR ── */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, 0]} receiveShadow>
        {isCompound && floorShape ? <shapeGeometry args={[floorShape]} /> : <planeGeometry args={[roomW, roomD]} />}
        <meshStandardMaterial
          map={floorData?.texture}
          color={floorData?.texture ? '#f8f4ee' : floorColor}
          roughness={Math.max(0.05, floorRough * 0.9)}
          metalness={floorMetal}
          envMapIntensity={floorRough < 0.12 ? 3.0 : floorRough < 0.30 ? 2.0 : floorRough < 0.55 ? 1.2 : 0.5}
        />
      </mesh>

      {/* ── FLOOR area painting ── overlays (always) + hit-plane (while armed).
          Same rotated frame as the floor mesh, so cells map onto the floor. */}
      <group rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, 0]}>
        {floorSelect && (
          <mesh position={[0, 0, 0.004]} {...areaCellProps('floor', roomW, roomD)}>
            <planeGeometry args={[roomW, roomD]} />
            <meshBasicMaterial transparent opacity={0} depthWrite={false} />
          </mesh>
        )}
        {floorOverlays()}
      </group>

      {/* ── CEILING ── */}
      <mesh rotation={[Math.PI / 2, 0, 0]} position={[0, roomH, 0]}>
        {isCompound && ceilingShape ? <shapeGeometry args={[ceilingShape]} /> : <planeGeometry args={[roomW, roomD]} />}
        <meshStandardMaterial
          key={`cm-${ceilMat?.id || 'none'}-${ceilMat?.color || ''}`}
          color={ceilData?.texture ? '#ffffff' : ceilColor}
          map={cloneCeilTex()}
          roughness={ceilRough}
          metalness={ceilData?.metalness ?? 0}
          envMapIntensity={0}
          side={THREE.FrontSide}
        />
      </mesh>


      {/* ── WALLS ── */}
      {isCompound && compoundWalls ? compoundWalls.map(w => {
        // A curved edge is one continuous arc mesh; its paint grid runs over the
        // ARC LENGTH, and the geometry's uv is arc-based so a click lands on the
        // cell it looks like it landed on.
        if (w.curve) {
          const geo = curvedWallGeos.get(w.key);
          if (!geo) return null;
          return (
            <mesh key={w.key} geometry={geo} receiveShadow {...wallFaceProps(w.key, w.curve.len)}>
              {wallMatForFace(w.key, w.curve.len)}
              {curvedFaceOverlays(w)}
            </mesh>
          );
        }
        // Paint grid uses the wall's TRUE width. Free-form panels carry 0.12 m of
        // overlap padding for seam-free corners; including it would shift every
        // area cell and make the picked cell not match what was clicked.
        const paintW = w.free ? Math.max(0.1, w.segW - 0.12) : w.segW;
        return (
          <mesh key={w.key} position={[w.cx, roomH / 2, w.cz]} rotation={w.rot} receiveShadow {...wallFaceProps(w.key, paintW)}>
            <planeGeometry args={[w.segW, roomH]} />
            {wallMatForFace(w.key, paintW)}
            {faceOverlays(w.key, paintW)}
          </mesh>
        );
      }) : (<>
        <mesh position={[0, roomH / 2, -roomD / 2]} receiveShadow {...wallFaceProps('back', roomW)}>
          <planeGeometry args={[roomW, roomH]} />
          {wallMatForFace('back', roomW)}
          {faceOverlays('back', roomW)}
        </mesh>
        <mesh position={[0, roomH / 2, roomD / 2]} rotation={[0, Math.PI, 0]} receiveShadow {...wallFaceProps('front', roomW)}>
          <planeGeometry args={[roomW, roomH]} />
          {wallMatForFace('front', roomW)}
          {faceOverlays('front', roomW)}
        </mesh>
        <mesh position={[-roomW / 2, roomH / 2, 0]} rotation={[0, Math.PI / 2, 0]} receiveShadow {...wallFaceProps('left', roomD)}>
          <planeGeometry args={[roomD, roomH]} />
          {wallMatForFace('left', roomD)}
          {faceOverlays('left', roomD)}
        </mesh>
        <mesh position={[roomW / 2, roomH / 2, 0]} rotation={[0, -Math.PI / 2, 0]} receiveShadow {...wallFaceProps('right', roomD)}>
          <planeGeometry args={[roomD, roomH]} />
          {wallMatForFace('right', roomD)}
          {faceOverlays('right', roomD)}
        </mesh>
      </>)}

      {/* ── CORNER SEAMS ── */}
      {/* A thin, floor-to-ceiling line at every corner where two walls meet.
          Walls are now a single flat unlit colour with zero per-wall shading
          (see makeWallMat above) so there's no lighting cue left telling
          users where one wall ends and the next begins — this restores that
          boundary explicitly, and (being unlit itself) stays visible the
          same way regardless of time of day. */}
      {(isCompound && compoundWalls
        ? compoundWalls.flatMap(w => {
            // A curved edge posts only its real corner (where the arc starts) —
            // there are no sub-seams along it to mark.
            if (w.curve) return w.corners.map(([x, z], i) => ({ p: [x, roomH / 2, z], k: `cs-${w.key}-${i}` }));
            const half = w.segW / 2;
            let ends;
            if (w.free) {
              // Free-form wall: ends lie along its own direction, at any angle.
              if (!w.free.cornerStart) return [];
              // segW carries the 0.12 overlap padding — back it out so the post
              // sits on the true corner rather than 6 cm past it.
              const trueHalf = Math.max(0, (w.segW - 0.12) / 2);
              const ang = -w.rot[1];
              const dx = Math.cos(ang) * trueHalf, dz = Math.sin(ang) * trueHalf;
              ends = [[w.cx - dx, w.cz - dz]];
            } else {
              const isVert = Math.abs(Math.abs(w.rot[1]) - Math.PI / 2) < 0.01;
              ends = isVert
                ? [[w.cx, w.cz - half], [w.cx, w.cz + half]]
                : [[w.cx - half, w.cz], [w.cx + half, w.cz]];
            }
            return ends.map(([x, z], i) => ({ p: [x, roomH / 2, z], k: `cs-${w.key}-${i}` }));
          })
        : [
            { p: [-roomW / 2, roomH / 2, -roomD / 2], k: 'cs0' },
            { p: [ roomW / 2, roomH / 2, -roomD / 2], k: 'cs1' },
            { p: [-roomW / 2, roomH / 2,  roomD / 2], k: 'cs2' },
            { p: [ roomW / 2, roomH / 2,  roomD / 2], k: 'cs3' },
          ]
      ).map(({ p, k }) => (
        <mesh key={k} position={p}>
          <boxGeometry args={[0.024, roomH, 0.024]} />
          <meshBasicMaterial color={mulHex(shadeColor(bbColor, -0.15), wallTint)} />
        </mesh>
      ))}

      {/* ── BASEBOARDS ── */}
      {/* A curved edge's wall is one mesh, but its trim still follows the arc as
          a chain of short boxes (`parts`). */}
      {(isCompound && compoundWalls ? compoundWalls.flatMap(w => (w.parts || [w]).map((s, si) => ({ p: s.bbPos, g: s.bbSize, k: `bb-${w.key}-${si}`, r: (w.curve || w.free) ? s.rot : undefined }))) : [
        { p: [0,              0.075, -roomD/2+0.013], g: [roomW,  0.15, 0.018], k: 'bb0' },
        { p: [0,              0.075,  roomD/2-0.013], g: [roomW,  0.15, 0.018], k: 'bb1' },
        { p: [-roomW/2+0.013, 0.075, 0],              g: [0.018,  0.15, roomD], k: 'bb2' },
        { p: [ roomW/2-0.013, 0.075, 0],              g: [0.018,  0.15, roomD], k: 'bb3' },
      ]).map(({ p, g, k, r }) => (
        <mesh key={k} position={p} rotation={r}>
          <boxGeometry args={g} />
          <meshBasicMaterial color={mulHex(shadeColor(bbColor, 0.08), wallTint)} />
        </mesh>
      ))}
      {/* ── CROWN MOULDING ── */}
      {(isCompound && compoundWalls ? compoundWalls.flatMap(w => (w.parts || [w]).map((s, si) => ({ p: [s.cmPos[0], roomH - 0.056, s.cmPos[2]], g: s.cmSize, k: `cm-${w.key}-${si}`, r: (w.curve || w.free) ? s.rot : undefined }))) : [
        { p: [0,              roomH - 0.056, -roomD/2+0.009], g: [roomW,  0.11, 0.014], k: 'cm0' },
        { p: [0,              roomH - 0.056,  roomD/2-0.009], g: [roomW,  0.11, 0.014], k: 'cm1' },
        { p: [-roomW/2+0.009, roomH - 0.056, 0],              g: [0.014,  0.11, roomD], k: 'cm2' },
        { p: [ roomW/2-0.009, roomH - 0.056, 0],              g: [0.014,  0.11, roomD], k: 'cm3' },
      ]).map(({ p, g, k, r }) => (
        <mesh key={k} position={p} rotation={r}>
          <boxGeometry args={g} />
          <meshBasicMaterial color={mulHex(shadeColor(bbColor, 0.08), wallTint)} />
        </mesh>
      ))}

      {/* ── FURNITURE ── */}
      {roomItems.map(item => (
        <DraggableFurniture
          key={item.id}
          item={item}
          room={room}
          roomW={roomW}
          roomD={roomD}
          onFurnitureClick={onFurnitureClick}
          isSelected={selectedItemId === item.id}
          isHovered={hoveredId === item.id}
          timeOfDay={timeOfDay}
          dimWalls={dimWalls}
          heightsRef={heightsRef}
        />
      ))}

      {/* ── LIGHTING (ceiling-mounted fixtures) ── */}
      {(lightingItems || []).map(item => (
        <DraggableLighting
          key={item.id}
          item={item}
          room={room}
          roomW={roomW}
          roomD={roomD}
          roomH={roomH}
          onLightingClick={onLightingClick}
          isSelected={selectedLightingId === item.id}
          timeOfDay={timeOfDay}
        />
      ))}

      {/* ── DOORS & WINDOWS ── */}
      {openingsToRender.map(({ opening, adjacentRoom }) => (
        <Opening3D
          key={opening.id}
          opening={opening}
          room={room}
          roomW={roomW} roomD={roomD} roomH={roomH}
          adjacentRoom={adjacentRoom}
          onNavigate={onNavigate}
          onOpeningClick={onOpeningClick}
          isSelected={selectedOpeningId === opening.id}
          roomId={room?.id}
          editable={openingsEditable}
          roomRects={isCompound ? room.rects : null}
          wallSegments={wallSegments}
          freeWalls={freeWalls}
          timeOfDay={timeOfDay}
        />
      ))}

      {/* ── STANDALONE WALL SEGMENTS (from Draw Walls mode) ── */}
      {room && (wallSegments || [])
        .filter(seg => {
          const rx1 = room.x, rx2 = room.x + room.w;
          const ry1 = room.y, ry2 = room.y + room.h;
          const sx1 = Math.min(seg.x1, seg.x2), sx2 = Math.max(seg.x1, seg.x2);
          const sy1 = Math.min(seg.y1, seg.y2), sy2 = Math.max(seg.y1, seg.y2);
          return sx1 < rx2 && sx2 > rx1 && sy1 < ry2 && sy2 > ry1;
        })
        .flatMap(seg => {
          const isH = seg.y1 === seg.y2;
          const WT3 = 0.15;
          const wallLenM = isH ? Math.abs(seg.x2 - seg.x1) * CELL_M : Math.abs(seg.y2 - seg.y1) * CELL_M;
          const ox3 = (Math.min(seg.x1, seg.x2) - room.x) * CELL_M - roomW / 2;
          const oz3 = (Math.min(seg.y1, seg.y2) - room.y) * CELL_M - roomD / 2;
          const cx3 = isH ? ox3 + wallLenM / 2 : ox3;
          const cz3 = isH ? oz3 : oz3 + wallLenM / 2;

          // Split wall around openings — only door/window/archway actually
          // punch through the wall. Wall-decor items (painting, AC unit,
          // clock, outlet, radiator) are just mounted on the surface, same
          // as furniture, so they must never carve a hole here — Opening3D
          // renders them as their own mesh sitting proud of a fully solid
          // wall piece.
          const openings = (seg.openings || []).map(o => ({ ...o, pos: o.t }));
          const structuralOpenings = openings.filter(op => !isWallDecorType(op.type));
          const breaks = [0, wallLenM];
          for (const op of structuralOpenings) {
            const oW = op.type === 'door' ? (op.width || 0.88) : op.type === 'archway' ? (op.width || 1.2) : (op.width || 0.98);
            breaks.push(Math.max(0, op.pos * wallLenM - oW / 2));
            breaks.push(Math.min(wallLenM, op.pos * wallLenM + oW / 2));
          }
          breaks.sort((a, b) => a - b);

          const pieces = [];
          for (let i = 0; i < breaks.length - 1; i++) {
            const s = breaks[i], e = breaks[i + 1];
            if (e - s < 0.02) continue;
            const mid = (s + e) / 2;
            // Which opening (if any) this slice falls inside — door/archway/
            // window all punch a hole for their own height, with the wall
            // filled back in above (and, for windows, below the sill too).
            // Previously only windows got this treatment; door/archway fell
            // through with no piece at all, leaving a floor-to-ceiling gap
            // instead of a proper doorway (looked like the wall was severed
            // into two disconnected panels).
            const activeOp = structuralOpenings.find(op => {
              const oW = op.type === 'door' ? (op.width || 0.88) : op.type === 'archway' ? (op.width || 1.2) : (op.width || 0.98);
              return mid >= op.pos * wallLenM - oW / 2 - 0.001 && mid <= op.pos * wallLenM + oW / 2 + 0.001;
            });
            const repX = Math.max(1, Math.round((e - s) * (wallData?.repeat?.[0] ?? 0.4)));
            if (!activeOp) {
              pieces.push(
                <mesh key={`${seg.id}_${i}`}
                  position={isH ? [ox3 + mid, roomH / 2, cz3] : [cx3, roomH / 2, oz3 + mid]}
                  castShadow receiveShadow>
                  <boxGeometry args={isH ? [e - s, roomH, WT3] : [WT3, roomH, e - s]} />
                  {makeWallMat(repX)}
                </mesh>
              );
            } else {
              const sill = activeOp.type === 'window' ? (activeOp.sill ?? 0.9) : 0;
              const opH  = activeOp.type === 'door'    ? (activeOp.height ?? 2.06)
                         : activeOp.type === 'archway' ? (activeOp.height ?? 2.4)
                         : (activeOp.height ?? 0.88);
              if (sill > 0.01) pieces.push(
                <mesh key={`${seg.id}_${i}b`}
                  position={isH ? [ox3 + mid, sill / 2, cz3] : [cx3, sill / 2, oz3 + mid]}
                  castShadow receiveShadow>
                  <boxGeometry args={isH ? [e - s, sill, WT3] : [WT3, sill, e - s]} />
                  {makeWallMat(repX)}
                </mesh>
              );
              const top = sill + opH;
              // No castShadow here — this header sits well above head height,
              // and with a low sun angle (morning/evening) its shadow can
              // stretch a long way across the floor right at the doorway,
              // reading as a phantom door-shaped shape even though the door
              // itself is (and always renders as) fully closed.
              if (roomH - top > 0.01) pieces.push(
                <mesh key={`${seg.id}_${i}t`}
                  position={isH ? [ox3 + mid, top + (roomH - top) / 2, cz3] : [cx3, top + (roomH - top) / 2, oz3 + mid]}
                  receiveShadow>
                  <boxGeometry args={isH ? [e - s, roomH - top, WT3] : [WT3, roomH - top, e - s]} />
                  {makeWallMat(repX)}
                </mesh>
              );
            }
          }

          // Seam line wherever this interior wall's own end actually meets
          // the room's own boundary wall — same purpose as the room-corner
          // seams above, just for the "around wall" ↔ "inside wall" join,
          // which those didn't cover since a hand-drawn wall segment is a
          // wholly separate piece of geometry from the room's own 4 walls.
          // A free-floating end (not touching the boundary) gets nothing —
          // there's no seam to mark, just an open end of the wall.
          const endpoints3D = isH
            ? [[ox3, oz3], [ox3 + wallLenM, oz3]]
            : [[ox3, oz3], [ox3, oz3 + wallLenM]];
          const endpointsGrid = isH
            ? [[Math.min(seg.x1, seg.x2), seg.y1], [Math.max(seg.x1, seg.x2), seg.y1]]
            : [[seg.x1, Math.min(seg.y1, seg.y2)], [seg.x1, Math.max(seg.y1, seg.y2)]];
          const EPS = 0.02;
          // A compound (stepped/L-shaped) room's real perimeter isn't just
          // its bounding box — it can also have inward "step" edges wherever
          // one sub-rect is narrower than another. Checking only room.x/y/w/h
          // missed a wall segment attached to one of those step edges (looked
          // "not touching" and got no seam even though it visibly meets the
          // boundary there), so this checks every edge of every sub-rect.
          const seamRects = room.rects && room.rects.length > 1
            ? room.rects : [{ x: room.x, y: room.y, w: room.w, h: room.h }];
          endpointsGrid.forEach(([gx, gy], i) => {
            const touchesRoomBoundary = seamRects.some(r => {
              const withinX = gx >= r.x - EPS && gx <= r.x + r.w + EPS;
              const withinY = gy >= r.y - EPS && gy <= r.y + r.h + EPS;
              const onVerticalEdge   = withinY && (Math.abs(gx - r.x) < EPS || Math.abs(gx - (r.x + r.w)) < EPS);
              const onHorizontalEdge = withinX && (Math.abs(gy - r.y) < EPS || Math.abs(gy - (r.y + r.h)) < EPS);
              return onVerticalEdge || onHorizontalEdge;
            });
            // Two hand-drawn walls can also meet each other directly (a T or
            // L of interior partitions) without either end touching the
            // room's own boundary at all — same missing-seam problem, just
            // wall-to-wall instead of wall-to-boundary, so check for another
            // segment sharing this exact endpoint too.
            const touchesOtherSegment = (wallSegments || []).some(other => {
              if (other.id === seg.id) return false;
              const otherEnds = [[other.x1, other.y1], [other.x2, other.y2]];
              return otherEnds.some(([ox, oy]) => Math.abs(ox - gx) < EPS && Math.abs(oy - gy) < EPS);
            });
            if (!touchesRoomBoundary && !touchesOtherSegment) return;
            const [x3, z3] = endpoints3D[i];
            // Only needs to be wider than the wall SEGMENT's own real
            // thickness (WT3 = 0.15 above) along the axis the wall is thick
            // in, or it renders buried inside that segment's own geometry —
            // the same bug the wall-decor depth fix solved for clocks/
            // outlets earlier. Along the wall's length there's nothing to
            // clear (the wall simply doesn't extend past its own end), so
            // that axis can stay thin — a visibly thin line rather than a
            // thick square post.
            const THIN = 0.03, THICK = 0.18;
            pieces.push(
              <mesh key={`cs-ws-${seg.id}-${i}`} position={[x3, roomH / 2, z3]}>
                <boxGeometry args={isH ? [THIN, roomH, THICK] : [THICK, roomH, THIN]} />
                <meshBasicMaterial color={mulHex(shadeColor(bbColor, -0.15), wallTint)} />
              </mesh>
            );
          });

          // ── Interior-wall area painting ──────────────────────────────────
          // An invisible hit-plane + the shared paint/selection overlays on
          // EACH side of this partition, so the side you click gets painted
          // (its own faceId) while the other side keeps the default wall.
          {
            const IWEPS = 0.004;
            const sides = isH
              ? [
                  { fid: `iw-${seg.id}-a`, pos: [cx3, roomH / 2, cz3 + WT3 / 2 + IWEPS], rot: [0, 0, 0] },
                  { fid: `iw-${seg.id}-b`, pos: [cx3, roomH / 2, cz3 - WT3 / 2 - IWEPS], rot: [0, Math.PI, 0] },
                ]
              : [
                  { fid: `iw-${seg.id}-a`, pos: [cx3 + WT3 / 2 + IWEPS, roomH / 2, cz3], rot: [0, Math.PI / 2, 0] },
                  { fid: `iw-${seg.id}-b`, pos: [cx3 - WT3 / 2 - IWEPS, roomH / 2, cz3], rot: [0, -Math.PI / 2, 0] },
                ];
            sides.forEach(sd => {
              pieces.push(
                <group key={sd.fid} position={sd.pos} rotation={sd.rot}>
                  {/* Invisible hit-surface — only exists (and catches clicks/
                      hover) while area-select is armed, so it never blocks
                      furniture/opening editing the rest of the time. */}
                  {wallSelect && (
                    <mesh {...wallFaceProps(sd.fid, wallLenM)}>
                      <planeGeometry args={[wallLenM, roomH]} />
                      <meshBasicMaterial transparent opacity={0} depthWrite={false} />
                    </mesh>
                  )}
                  {faceOverlays(sd.fid, wallLenM)}
                </group>
              );
            });
          }

          return pieces;
        })
      }

      {/* ── MERGED free-form room: absorbed straight parts (floor + walls) ── */}
      {mergeShapes && mergeShapes.map((s, i) => (
        <group key={`ms-${i}`}>
          <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.002, 0]} receiveShadow>
            <shapeGeometry args={[s]} />
            {/* FrontSide: ShapeGeometry normals are +Z, which the −90°/+90° X
                rotations aim up (floor) and down (ceiling) — so each is visible
                only from inside the room, and invisible from outside. */}
            <meshStandardMaterial map={floorData?.texture} color={floorData?.texture ? '#f8f4ee' : floorColor}
              roughness={Math.max(0.05, floorRough * 0.9)} metalness={floorMetal} side={THREE.FrontSide} />
          </mesh>
          <mesh rotation={[Math.PI / 2, 0, 0]} position={[0, roomH, 0]}>
            <shapeGeometry args={[(mergeCeilShapes || mergeShapes)[i]]} />
            <meshStandardMaterial color={ceilData?.texture ? '#ffffff' : ceilColor} map={ceilData?.texture}
              roughness={ceilRough} side={THREE.FrontSide} />
          </mesh>
        </group>
      ))}
      {mergeWalls && mergeWalls.map(w => (
        <mesh key={w.key} position={[w.cx, roomH / 2, w.cz]} rotation={[0, w.rot, 0]} receiveShadow>
          <planeGeometry args={[Math.abs(w.len), roomH]} />
          {/* DoubleSide: these panels are axis-aligned box faces whose winding
              isn't tracked, so render both faces rather than risk an invisible
              wall from inside the room. */}
          <meshBasicMaterial color={mulHex(wallColor, wallTint)} side={THREE.DoubleSide} />
        </mesh>
      ))}

      {/* ── FREE-FORM WALLS (any angle + curve) overlapping this room ── */}
      {(freeWalls || []).filter(w => {
        const sx1 = Math.min(w.x1, w.x2), sx2 = Math.max(w.x1, w.x2);
        const sy1 = Math.min(w.y1, w.y2), sy2 = Math.max(w.y1, w.y2);
        return sx1 < room.x + room.w && sx2 > room.x && sy1 < room.y + room.h && sy2 > room.y;
      }).map(w => {
        const toLocal = (fx, fy) => [(fx - room.x) * CELL_M - roomW / 2, (fy - room.y) * CELL_M - roomD / 2];
        const [ax, az] = toLocal(w.x1, w.y1);
        const [bx, bz] = toLocal(w.x2, w.y2);
        const bulge = (w.bulge || 0) * CELL_M;
        const thick = (w.thickness || 0.16) * CELL_M;
        const dx = bx - ax, dz = bz - az;
        const len = Math.hypot(dx, dz) || 1;
        const nx = dz / len, nz = -dx / len;
        const cx = (ax + bx) / 2 + nx * bulge * 2;
        const cz = (az + bz) / 2 + nz * bulge * 2;
        const N = Math.abs(bulge) > 0.02 ? 20 : 1;
        const pts = [];
        for (let i = 0; i <= N; i++) { const t = i / N, mt = 1 - t; pts.push([mt * mt * ax + 2 * mt * t * cx + t * t * bx, mt * mt * az + 2 * mt * t * cz + t * t * bz]); }
        // Total sampled length, so each piece knows its t-range and can be cut
        // where an opening sits — a door leaves a header above, a window leaves
        // a sill below and a header above.
        // Only a door/window/archway punches through — a wall-decor item
        // (painting/AC/clock/outlet/radiator) mounts on the FACE. Cutting for those
        // tore a chunk out of the wall under every picture.
        const ops = (w.openings || []).filter(o => !isWallDecorType(o.type));
        // An interior free wall wears the ROOM's wall material — the same texture
        // and tint its own walls use — instead of a flat colour, which left it a
        // bare white slab in a papered room. Repeat is scaled off the wall's own
        // length so the pattern runs at the same density as the room's walls.
        const wallLenM = Math.hypot(bx - ax, bz - az);
        // Every piece is its own box with 0..1 UVs, so the repeat has to be that
        // PIECE's share — passing the whole wall's count squeezed the full
        // pattern into each 20cm slice, which is why the stripes came out tiny.
        // NOT clamped to a whole repeat: a 0.25 m slice legitimately shows 0.1 of
        // the pattern. Flooring it at 1 (as a full wall does, to avoid a seam)
        // crammed a whole repeat into every slice — the microscopic stripes.
        const pieceRepX = (len) => Math.max(0.01, len * (wallData?.repeat?.[0] ?? 0.4));
        const pieceRepY = (h) => Math.max(0.05, (wallData?.repeat?.[1] ?? 0.5) * (h / roomH));
        // An opening's `t` is the CURVE parameter (what edgePointAt takes and
        // what the door model is placed with) — NOT a fraction of the wall's
        // length. On a bowed wall those differ, so cutting the hole by treating
        // t as an arc fraction put the gap somewhere the door wasn't. Map t
        // through the same sample table to get its position in arc length, then
        // cut a real width/2 either side of it.
        const cum = [0];
        for (let i = 0; i < pts.length - 1; i++) {
          cum.push(cum[i] + Math.hypot(pts[i + 1][0] - pts[i][0], pts[i + 1][1] - pts[i][1]));
        }
        const NSEG = pts.length - 1;
        const arcAtT = (tBez) => {
          const f = Math.max(0, Math.min(1, tBez)) * NSEG;
          const i = Math.min(NSEG - 1, Math.floor(f));
          return cum[i] + (cum[i + 1] - cum[i]) * (f - i);
        };
        const totalLen = cum[NSEG];
        // Point at a given arc length along the wall.
        const ptAtS = (s) => {
          const c = Math.max(0, Math.min(totalLen, s));
          let i = 1;
          while (i < cum.length - 1 && cum[i] < c) i++;
          const f = (c - cum[i - 1]) / ((cum[i] - cum[i - 1]) || 1);
          return [pts[i - 1][0] + (pts[i][0] - pts[i - 1][0]) * f, pts[i - 1][1] + (pts[i][1] - pts[i - 1][1]) * f];
        };
        const meshes = [];
        // Wall between two arc lengths, at a given height band. Split at every
        // sample boundary so a bowed wall stays bowed.
        const emitSpan = (s0, s1, yc, h, key) => {
          if (h <= 0.02 || s1 - s0 <= 0.005) return;
          const bounds = [s0];
          for (let i = 1; i < NSEG; i++) if (cum[i] > s0 + 1e-4 && cum[i] < s1 - 1e-4) bounds.push(cum[i]);
          bounds.push(s1);
          for (let k = 0; k < bounds.length - 1; k++) {
            const a0 = ptAtS(bounds[k]), a1 = ptAtS(bounds[k + 1]);
            const len = Math.hypot(a1[0] - a0[0], a1[1] - a0[1]);
            if (len < 1e-4) continue;
            meshes.push(
              <mesh key={`${key}-${k}`} castShadow receiveShadow raycast={overlayNoRaycast}
                position={[(a0[0] + a1[0]) / 2, yc, (a0[1] + a1[1]) / 2]}
                rotation={[0, -Math.atan2(a1[1] - a0[1], a1[0] - a0[0]), 0]}>
                <boxGeometry args={[len + 0.02, h, thick]} />
                {makeWallMat(pieceRepX(len), pieceRepY(h))}
              </mesh>
            );
          }
        };
        // Cut EXACTLY the opening's own width. Testing whether a tessellated
        // piece's midpoint fell inside the opening snapped the hole out to whole
        // pieces — and a straight wall is a single piece, so one door erased the
        // entire wall. Spans are computed in arc length instead, independent of
        // how finely the wall happens to be sampled.
        const ranges = ops
          .map(o => {
            const c = arcAtT(o.t ?? 0.5), hw = (o.width || 0.9) / 2;
            return { lo: Math.max(0, c - hw), hi: Math.min(totalLen, c + hw), op: o };
          })
          .sort((a, b) => a.lo - b.lo);
        let cursor = 0;
        ranges.forEach((r, ri) => {
          if (r.lo > cursor) emitSpan(cursor, r.lo, roomH / 2, roomH, `s${ri}`);
          const sill = r.op.type === 'window' ? (r.op.sill ?? 0.9) : 0;
          const opH = r.op.height ?? (r.op.type === 'door' ? 2.06 : r.op.type === 'archway' ? 2.4 : 0.88);
          const top = sill + opH;
          const lo = Math.max(r.lo, cursor);
          if (sill > 0.02)          emitSpan(lo, r.hi, sill / 2, sill, `b${ri}`);
          if (top < roomH - 0.02)   emitSpan(lo, r.hi, (top + roomH) / 2, roomH - top, `t${ri}`);
          cursor = Math.max(cursor, r.hi);
        });
        if (cursor < totalLen) emitSpan(cursor, totalLen, roomH / 2, roomH, 'e');
        return <group key={`fw-${w.id}`}>{meshes}</group>;
      })}
    </group>
  );
}

function CameraRig({ roomW, roomD, roomH, moveRef }) {
  const controlsRef = useRef();

  useFrame((_, delta) => {
    const m = moveRef.current;
    if (!controlsRef.current) return;
    if (!m.fwd && !m.back && !m.left && !m.right) return;

    const controls  = controlsRef.current;
    const camera    = controls.object;
    const dt        = Math.min(delta, 0.05);
    const SPEED     = 3.5 * dt;
    const TURN      = 1.6 * dt;

    // Horizontal forward direction (ignore vertical tilt)
    const fwd = new THREE.Vector3();
    camera.getWorldDirection(fwd);
    fwd.y = 0;
    if (fwd.lengthSq() < 0.0001) fwd.set(0, 0, -1);
    fwd.normalize();

    if (m.fwd || m.back) {
      const dist = (m.fwd ? 1 : -1) * SPEED;
      const half = { x: roomW / 2 - 0.35, z: roomD / 2 - 0.35 };
      const nx = Math.max(-half.x, Math.min(half.x, camera.position.x + fwd.x * dist));
      const nz = Math.max(-half.z, Math.min(half.z, camera.position.z + fwd.z * dist));
      const dx = nx - camera.position.x;
      const dz = nz - camera.position.z;
      camera.position.x += dx;
      camera.position.z += dz;
      controls.target.x  += dx;
      controls.target.z  += dz;
    }

    if (m.left || m.right) {
      const angle    = (m.left ? 1 : -1) * TURN;
      const toTarget = new THREE.Vector3().subVectors(controls.target, camera.position);
      toTarget.applyAxisAngle(new THREE.Vector3(0, 1, 0), angle);
      controls.target.copy(camera.position).add(toTarget);
    }

    controls.update();
  });

  const camX = roomW * 0.3;
  const camZ = roomD * 0.3;
  return (
    <>
      <PerspectiveCamera makeDefault fov={72} position={[camX, 1.65, camZ]} near={0.05} far={100} />
      <OrbitControls
        ref={controlsRef}
        makeDefault
        target={[0, roomH * 0.45, 0]}
        minPolarAngle={0.05}
        maxPolarAngle={Math.PI * 0.9}
        minDistance={0.3}
        maxDistance={Math.max(roomW, roomD) * 0.95}
        enableDamping
        dampingFactor={0.05}
        rotateSpeed={0.7}
        zoomSpeed={0.8}
      />
    </>
  );
}

// ─── TIME OF DAY ──────────────────────────────────────────────────────────────
// Drives the ambient/sun lighting, the drei Environment HDRI (for reflections),
// tone-mapping exposure, bloom, background clear colour, and — via `timeOfDay`
// threaded down to Opening3D/LightingMesh3D — the window-view sky and whether
// lighting fixtures cast real shadows. azimuth/elevation (radians) position
// the directional "sun" relative to the room; its target defaults to the
// world origin, which is already the room's centre in this scene's coordinate
// system, so no explicit target object is needed.
export const TIME_OF_DAY_PRESETS = {
  morning: {
    label: 'Morning',
    // Shifted some fill (ambient) into the key light (sun) so furniture casts
    // clearly-visible shadows on the floor while lit areas stay ~as bright as
    // before. Walls are unlit (meshBasicMaterial) so this leaves them untouched.
    ambientIntensity: 1.2, ambientColor: '#fff6ea',
    sunIntensity: 2.1, sunColor: '#fff3d6', azimuth: -0.7, elevation: 0.62,
    envPreset: 'dawn', envIntensity: 0.35,
    exposure: 1.05, bg: '#bcd4e6',
    bloomThreshold: 0.92, bloomIntensity: 0.10, bloomSmoothing: 0.7,
    wallTint: '#ffffff', // full brightness by day
  },
  evening: {
    label: 'Evening',
    ambientIntensity: 0.78, ambientColor: '#ffd2a0',
    sunIntensity: 1.7, sunColor: '#ff9c52', azimuth: 0.9, elevation: 0.22,
    envPreset: 'sunset', envIntensity: 0.42,
    exposure: 0.98, bg: '#5a3a3c',
    bloomThreshold: 0.82, bloomIntensity: 0.20, bloomSmoothing: 0.6,
    wallTint: '#e8d6bd', // subtle warm dim at sunset
  },
  night: {
    label: 'Night',
    ambientIntensity: 0.22, ambientColor: '#3a4a70',
    sunIntensity: 0.10, sunColor: '#94aad8', azimuth: 0.35, elevation: 0.75,
    envPreset: 'night', envIntensity: 0.15,
    exposure: 0.82, bg: '#020408',
    bloomThreshold: 0.55, bloomIntensity: 0.38, bloomSmoothing: 0.55,
    wallTint: '#3a4560', // dark cool — walls read as night, not daylit
  },
};

// ─── MAIN EXPORT ─────────────────────────────────────────────────────────────

export default function ThreeDRoom({ room, design, onWebSave, onEditModeChange, readOnly = false, selectTarget = null, selectedSurface = null, selectedCells = null, cellPreview = null, onAreaSelect }) {
  const { t } = useT();
  const isGuest = useGuestMode();
  const dispatch      = useDispatch();
  const allFurniture  = useSelector(s => s.house.furniture);
  const allLighting   = useSelector(s => s.house.lighting || []);
  const allRooms      = useSelector(s => s.house.rooms);
  const wallSegments      = useSelector(s => s.house.wallSegments || []);
  const freeWalls         = useSelector(s => s.house.freeWalls || []);
  const autoWallOpenings  = useSelector(s => s.house.autoWallOpenings || {});
  const builderHistory      = useSelector(s => s.house.builderHistory);
  const builderHistoryIndex = useSelector(s => s.house.builderHistoryIndex);
  const nextFurnitureId = useSelector(s => s.house.nextFurnitureId);
  const nextLightingId  = useSelector(s => s.house.nextLightingId);
  const nextOpeningId   = useSelector(s => s.house.nextOpeningId);
  const [editMode, setEditMode] = useState(false);
  // Lets the parent (RoomEditor) pause its continuous debounced autosave
  // while the user is actively editing in the 3D view — every drag/resize/
  // add would otherwise restart that timer and rack up a save request every
  // ~1.5s. Saving is instead explicit: exitEdit() below calls onWebSave()
  // directly the moment the user clicks "Done", so at most one request per
  // edit session instead of one per debounce window.
  useEffect(() => { if (onEditModeChange) onEditModeChange(editMode); }, [editMode, onEditModeChange]);

  const [selectedItemId, setSelectedItemId] = useState(null);
  const [selectedOpeningId, setSelectedOpeningId] = useState(null);
  const [selectedLightingId, setSelectedLightingId] = useState(null);
  const [hoveredId, setHoveredId] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showAddOpeningModal, setShowAddOpeningModal] = useState(false);
  const [showAddWallItemModal, setShowAddWallItemModal] = useState(false);
  const [showAddLightingModal, setShowAddLightingModal] = useState(false);
  const containerRef = useRef(null);
  // Local clipboard for the copy/paste feature — { kind: 'furniture'|'opening'|'lighting', data }
  const clipboardRef = useRef(null);
  // Snapshot of the undo-history position taken when edit mode is entered —
  // "Cancel" jumps straight back to it, discarding everything done since.
  const editStartHistoryIndexRef = useRef(null);
  const [realFullscreen, setRealFullscreen] = useState(false);
  // iOS Safari (and every other iOS browser, since they're all WebKit) has
  // no usable Fullscreen API — requestFullscreen is simply absent on the
  // element there, so the real-API path below silently no-ops. This is a
  // CSS-only "fake fullscreen" fallback (position:fixed over the whole
  // viewport) used whenever the real API isn't available or rejects.
  const [fakeFullscreen, setFakeFullscreen] = useState(false);
  const isFullscreen = realFullscreen || fakeFullscreen;
  // iOS Safari's viewport-unit quirks (100vh/100vw shifting as browser
  // chrome shows/hides, no Screen Orientation lock) made the simulated
  // landscape rotation unreliable there — iOS now only gets the fake-
  // fullscreen fill, no forced rotation; other platforms are unaffected.
  const isIOS = typeof navigator !== 'undefined' &&
    (/iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1));
  const [isPortrait, setIsPortrait] = useState(() => typeof window !== 'undefined' && window.innerWidth < window.innerHeight);
  useEffect(() => {
    function updateOrientation() { setIsPortrait(window.innerWidth < window.innerHeight); }
    window.addEventListener('resize', updateOrientation);
    window.addEventListener('orientationchange', updateOrientation);
    return () => {
      window.removeEventListener('resize', updateOrientation);
      window.removeEventListener('orientationchange', updateOrientation);
    };
  }, []);
  const [timeOfDay, setTimeOfDay] = useState('morning');
  const tod = TIME_OF_DAY_PRESETS[timeOfDay];

  // Render quality. "auto" defers entirely to the device detection in
  // gpuProfile, so anyone who never touches the control sees no change.
  // Every setting here can be applied to a LIVE canvas, so switching quality
  // does not remount the Canvas — the camera and the room stay put.
  const quality = useQuality();
  const q = qualitySettings(quality);
  const [qualityOpen, setQualityOpen] = useState(false);

  // ── THE IMAGE THE AI RENDER WORKS FROM ─────────────────────────────────────
  //
  // The AI render is image-to-image: it redraws the screenshot of this canvas.
  // How much detail that screenshot carries decides how well it holds your wall
  // and floor materials — so it must NOT depend on the quality dropdown, which
  // is about how smoothly the room moves while you use it.
  //
  // Left alone, Low quality renders at a device pixel ratio of 1 and the AI
  // would receive a quarter of the pixels (measured: 366×236 instead of
  // 732×472 on a phone), which is enough for the model to start inventing
  // materials.
  //
  // So the canvas is put back to full resolution for the instant the capture
  // happens, then returned to whatever the user picked. It is a React state
  // change, not a manual re-render — r3f resizes and redraws the scene itself,
  // through the normal path including post-processing, so the captured frame is
  // exactly what is on screen, only sharper.
  const [captureDpr, setCaptureDpr] = useState(null);
  const captureForAI = useCallback(async () => {
    const live = containerRef.current?.querySelector('canvas');
    if (!live) return null;

    // Render the capture as LARGE as the upload allows, not merely at the
    // screen's own pixel ratio.
    //
    // canvasToUploadBlob only ever downscales, and its ceiling is 1536px on the
    // long side. A desktop at devicePixelRatio 1 was therefore handing the model
    // a 1064px-wide image when it could have sent 1536 — about half the pixels,
    // for nothing. This scales the drawing buffer to land near that ceiling so
    // the model gets the most detail the pipeline can carry.
    //
    // Capped at 3: the buffer is momentary, but on a phone this is exactly the
    // allocation that was crashing tabs, so it does not get to grow without
    // limit.
    const UPLOAD_CEILING = 1536;
    const longEdgeCss = Math.max(live.clientWidth || 1, live.clientHeight || 1);
    const wanted = UPLOAD_CEILING / longEdgeCss;
    const full = Math.min(Math.max(wanted, window.devicePixelRatio || 1, 1), 3);
    setCaptureDpr(full);
    // Three frames: one for React to commit, one for r3f to resize the drawing
    // buffer, one for it to draw the scene at the new size.
    await new Promise((resolve) => {
      requestAnimationFrame(() => requestAnimationFrame(() => requestAnimationFrame(resolve)));
    });

    // Copy into a plain 2D canvas before restoring the resolution, so the
    // pixels are safely ours no matter what the live canvas does next.
    const snapshot = document.createElement('canvas');
    snapshot.width = live.width;
    snapshot.height = live.height;
    snapshot.getContext('2d').drawImage(live, 0, 0);

    setCaptureDpr(null);
    return snapshot;
  }, []);


  // Branded loader. It appears in exactly two cases and NEVER for editing/adding
  // a single item:
  //   • FIRST ENTRY into the 3D view, and
  //   • ROOM SWITCH.
  // In BOTH it stays up until the room has ACTUALLY loaded — the first frame has
  // painted AND every GLB item used in the room has arrived — so the user never
  // sees a half-populated room with items popping in. A generous safety cap
  // guarantees it can never stay stuck if an asset truly hangs.
  const [sceneReady, setSceneReady] = useState(false);

  // The quality note appears once the room is actually on screen and on every
  // change, then clears itself after five seconds — long enough to read, short
  // enough that it is not permanently parked over the room.
  //
  // Gated on sceneReady deliberately: the models can take twenty seconds to
  // load, and a timer started at mount would run out while the user is still
  // looking at a loading screen, so the note would be gone before it could be
  // read. Keyed on `quality` too, so each change brings its note back.
  const [qualityNote, setQualityNote] = useState(false);
  useEffect(() => {
    if (!sceneReady) return undefined;
    setQualityNote(true);
    const id = setTimeout(() => setQualityNote(false), 5000);
    return () => clearTimeout(id);
  }, [quality, sceneReady]);
  const [glbPending, setGlbPending] = useState(0);
  const glbPendingRef = useRef(0);
  const glbLoad = useMemo(() => ({
    start: () => { glbPendingRef.current += 1; setGlbPending(glbPendingRef.current); },
    done:  () => { glbPendingRef.current = Math.max(0, glbPendingRef.current - 1); setGlbPending(glbPendingRef.current); },
  }), []);

  const [loaderOn, setLoaderOn] = useState(true);
  // True while covering a room load (first entry or a switch). Editing an item
  // afterwards briefly bumps glbPending but never re-enters a phase, so the
  // loader stays hidden for edits.
  const loadingPhaseRef = useRef(true);

  // Enter a loading phase on first mount and on every room switch: reset the
  // paint signal, show the loader, and arm a safety cap so a hung/dead asset
  // can't pin it open forever (each GLB item also self-completes after 25s).
  useEffect(() => {
    loadingPhaseRef.current = true;
    setSceneReady(false);
    setLoaderOn(true);
    const safety = setTimeout(() => {
      if (loadingPhaseRef.current) { loadingPhaseRef.current = false; setLoaderOn(false); }
    }, 35000);
    return () => clearTimeout(safety);
  }, [room?.id]);

  // Hide once the scene has painted AND every GLB in the room has finished
  // loading. The 400ms settle guards the mount race: glbPending can dip to 0 for
  // a frame before all items have registered, so we only lift the loader once it
  // has HELD at 0 — if another item registers within the window the timer is
  // cancelled and we keep waiting.
  useEffect(() => {
    if (!loadingPhaseRef.current) return undefined;
    if (!sceneReady || glbPending !== 0) return undefined;
    const id = setTimeout(() => {
      if (loadingPhaseRef.current && glbPendingRef.current === 0) {
        loadingPhaseRef.current = false;
        setLoaderOn(false);
      }
    }, 400);
    return () => clearTimeout(id);
  }, [sceneReady, glbPending]);

  function enterEdit() {
    editStartHistoryIndexRef.current = builderHistoryIndex;
    setEditMode(true);
  }
  function exitEdit()  {
    setEditMode(false);
    setSelectedItemId(null);
    setSelectedOpeningId(null);
    setSelectedLightingId(null);
    setShowAddModal(false);
    if (onWebSave) onWebSave();
    setShowAddOpeningModal(false);
    setShowAddWallItemModal(false);
    setShowAddLightingModal(false);
  }
  function cancelEdit() {
    if (editStartHistoryIndexRef.current !== null) {
      dispatch(jumpToBuilderHistory(editStartHistoryIndexRef.current));
    }
    setEditMode(false);
    setSelectedItemId(null);
    setSelectedOpeningId(null);
    setSelectedLightingId(null);
    setShowAddModal(false);
    setShowAddOpeningModal(false);
    setShowAddWallItemModal(false);
    setShowAddLightingModal(false);
  }

  // Fullscreen — makes just this 3D view (canvas + its own UI) go fullscreen,
  // not the whole page. Tracks the browser's own fullscreen state (not just
  // our button click) so it also updates correctly if the user exits via Esc
  // or their browser/OS chrome instead of our button.
  function toggleFullscreen() {
    if (fakeFullscreen) { setFakeFullscreen(false); return; }
    if (document.fullscreenElement) { document.exitFullscreen?.(); return; }
    if (containerRef.current?.requestFullscreen) {
      containerRef.current.requestFullscreen().catch(() => setFakeFullscreen(true));
    } else {
      // No Fullscreen API at all — iOS Safari (and every other iOS browser).
      setFakeFullscreen(true);
    }
  }
  useEffect(() => {
    const onFsChange = () => setRealFullscreen(document.fullscreenElement === containerRef.current);
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);

  // Download now opens the chooser (original vs AI render) rather than saving
  // straight away. The capture stays a FUNCTION so the screenshot is taken at
  // click time against the live canvas — a WebGL drawing buffer can be cleared
  // between frames, so a data URL grabbed when the modal opened could be blank
  // by the time the user picks an option.
  const [dlOpen, setDlOpen] = useState(false);
  const dlFilename = `${(room?.label || 'room').toLowerCase().replace(/\s+/g, '-')}-3d.png`;
  const captureRoom = useCallback(() => {
    const canvas = containerRef.current?.querySelector('canvas');
    // Composite onto a 2D canvas + stamp the logo bottom-right (a WebGL canvas
    // can't be drawn on directly). preserveDrawingBuffer is on, so the render
    // is still readable here.
    return canvas ? watermarkCanvasToDataUrl(canvas) : null;
  }, []);
  const handleDownloadChoice = useCallback((r) => {
    if (r.type === 'save') saveOrShareImage(r.dataUrl, r.filename, room?.label || 'Room');
  }, [room?.label]);
  function handleScreenshot3D() { setDlOpen(true); }

  const selectedItem = allFurniture.find(f => f.id === selectedItemId) ?? null;
  const selectedLighting = allLighting.find(l => l.id === selectedLightingId) ?? null;

  const wallHeight = useSelector(s => s.house.wallHeight ?? 2.8);
  const W = (room?.w || 3) * CELL_M;
  const D = (room?.h || 3) * CELL_M;
  const H = wallHeight;

  // Sun position from the current time-of-day's azimuth/elevation, scaled to
  // clear the room regardless of its size — target defaults to the world
  // origin, which is already this scene's room-centre convention.
  const sunReach = Math.max(W, D) * 1.8 + 6;
  const sunPos = [
    Math.cos(tod.azimuth) * Math.cos(tod.elevation) * sunReach,
    Math.sin(tod.elevation) * sunReach + H,
    Math.sin(tod.azimuth) * Math.cos(tod.elevation) * sunReach,
  ];
  // The shadow camera's orthographic frustum (shadow-camera-left/right/top/
  // bottom below) has to cover the room as PROJECTED from the sun's actual
  // angle, not just the room's own floor footprint — at a shallow elevation
  // (morning/evening) a wall's shadow stretches roughly height/tan(elevation)
  // beyond the wall itself. The previous max(W,D)*0.9+2 only accounted for
  // footprint size, so on a large room at a low sun angle the frustum edge
  // fell mid-floor: shadows simply stopped dead in a straight diagonal line
  // exactly where the frustum's own boundary was, instead of extending (or
  // naturally fading) across the whole visible floor.
  const floorHalfDiag = Math.sqrt(W * W + D * D) / 2;
  const shadowStretch = H / Math.max(0.12, Math.tan(tod.elevation));
  const shadowSpan = floorHalfDiag + Math.min(shadowStretch, floorHalfDiag * 3) + 2;

  // Furniture that belongs to this room (uses actual rects for compound rooms)
  const roomItems = useMemo(() => {
    if (!room) return [];
    const isCompound = room.rects && room.rects.length > 1;
    return allFurniture.filter(item => {
      const rot = item.rotation || 0;
      const dw = rot % 180 === 0 ? item.w : item.h;
      const dh = rot % 180 === 0 ? item.h : item.w;
      const cx = item.x + dw / 2;
      const cy = item.y + dh / 2;
      if (isCompound) {
        return room.rects.some(r => {
          const rl = r.x * CELL, rt = r.y * CELL;
          const rr = (r.x + r.w) * CELL, rb = (r.y + r.h) * CELL;
          return cx >= rl && cx <= rr && cy >= rt && cy <= rb;
        });
      }
      const left   = room.x * CELL, top    = room.y * CELL;
      const right  = (room.x + room.w) * CELL, bottom = (room.y + room.h) * CELL;
      return cx >= left && cx <= right && cy >= top && cy <= bottom;
    });
  }, [allFurniture, room]);

  // Lighting fixtures that belong to this room — same membership test as furniture
  const roomLighting = useMemo(() => {
    if (!room) return [];
    const isCompound = room.rects && room.rects.length > 1;
    return allLighting.filter(item => {
      const rot = item.rotation || 0;
      const dw = rot % 180 === 0 ? item.w : item.h;
      const dh = rot % 180 === 0 ? item.h : item.w;
      const cx = item.x + dw / 2;
      const cy = item.y + dh / 2;
      if (isCompound) {
        return room.rects.some(r => {
          const rl = r.x * CELL, rt = r.y * CELL;
          const rr = (r.x + r.w) * CELL, rb = (r.y + r.h) * CELL;
          return cx >= rl && cx <= rr && cy >= rt && cy <= rb;
        });
      }
      const left   = room.x * CELL, top    = room.y * CELL;
      const right  = (room.x + room.w) * CELL, bottom = (room.y + room.h) * CELL;
      return cx >= left && cx <= right && cy >= top && cy <= bottom;
    });
  }, [allLighting, room]);

  // All openings to show: this room's own openings + auto-wall openings on this room's boundary
  const openingsToRender = useMemo(() => {
    if (!room) return [];
    const result = [];

    // Own openings (placed via room editor)
    for (const op of (room.openings || [])) {
      result.push({ opening: op, adjacentRoom: findAdjacentRoom(allRooms, room, op) });
    }

    // For compound rooms use individual rects for boundary matching; simple rooms use their own bbox as one rect
    const roomRects = (room.rects && room.rects.length > 1)
      ? room.rects
      : [{ x: room.x, y: room.y, w: room.w, h: room.h }];
    const roomW_m = room.w * CELL_M;
    const roomD_m = room.h * CELL_M;

    // Auto-wall openings placed in Builder
    for (const [key, ops] of Object.entries(autoWallOpenings)) {
      if (!ops || !ops.length) continue;
      const parts = key.split(':');

      if (parts[0] === 'h') {
        const Y = Number(parts[1]), X1 = Number(parts[2]), X2 = Number(parts[3]);
        const autoLen = X2 - X1;

        // Collect which rects have this horizontal wall as bottom / top boundary
        const botRects = roomRects.filter(r => r.y + r.h === Y && X1 < r.x + r.w && X2 > r.x);
        const topRects = roomRects.filter(r => r.y       === Y && X1 < r.x + r.w && X2 > r.x);
        if (!botRects.length && !topRects.length) continue;

        for (const op of ops) {
          const worldX = X1 + op.t * autoLen;

          // Find the specific rect (and side) that owns this worldX position
          let side = null, matchRect = null;
          for (const r of botRects) {
            if (worldX >= r.x - 0.01 && worldX <= r.x + r.w + 0.01) { side = 'bottom'; matchRect = r; break; }
          }
          if (!side) {
            for (const r of topRects) {
              if (worldX >= r.x - 0.01 && worldX <= r.x + r.w + 0.01) { side = 'top'; matchRect = r; break; }
            }
          }
          if (!side) continue;

          // Doors and arches on shared walls must be visible from both rooms;
          // only skip an opening bound to a different room if it is a window.
          if (op.roomId != null && op.roomId !== room.id && op.type !== 'door' && op.type !== 'archway') continue;

          if (!op.roomId || (op.roomId !== room.id)) {
            if (!op.roomId && op.type !== 'door' && op.type !== 'archway') {
              let phantom = false;
              if (side === 'top') {
                phantom = allRooms.some(other =>
                  other.id !== room.id &&
                  other.y + other.h === Y &&
                  worldX > other.x - 0.01 && worldX < other.x + other.w + 0.01 &&
                  other.x >= room.x - 0.01 && other.x + other.w <= room.x + room.w + 0.01 &&
                  other.w < room.w * 0.5
                );
              } else if (side === 'bottom') {
                phantom = allRooms.some(other =>
                  other.id !== room.id &&
                  other.y === Y &&
                  worldX > other.x - 0.01 && worldX < other.x + other.w + 0.01 &&
                  other.x >= room.x - 0.01 && other.x + other.w <= room.x + room.w + 0.01 &&
                  other.w < room.w * 0.5
                );
              }
              if (phantom) continue;
            }
          }

          const wallEdgeY = matchRect.y + (side === 'bottom' ? matchRect.h : 0);
          const wallGz = (wallEdgeY - room.y) * CELL_M - roomD_m / 2;
          const pos = (worldX - room.x) / room.w;
          const _posToT = (snapPos) => Math.max(0.02, Math.min(0.98, (room.x + snapPos * room.w - X1) / autoLen));
          const isDupeH = result.some(e => !e.opening._awKey && e.opening.wall === side && Math.abs(e.opening.pos - pos) < 0.08);
          if (!isDupeH) result.push({
            opening: { ...op, id: `aw-${key}-${op.id}`, wall: side, pos, _wallGz: wallGz,
                       _awKey: key, _awOpId: op.id, _posToT },
            // Use the exact matched-rect edge (not the room's bounding-box edge) so
            // adjacency resolves correctly for compound (merged) rooms with step walls.
            adjacentRoom: findAdjacentRoom(allRooms, room, {
              wall: side, pos,
              absX: (worldX - room.x) * CELL,
              absY: (wallEdgeY - room.y) * CELL,
            }),
          });
        }

      } else if (parts[0] === 'v') {
        const X = Number(parts[1]), Y1 = Number(parts[2]), Y2 = Number(parts[3]);
        const autoLen = Y2 - Y1;

        const rightRects = roomRects.filter(r => r.x + r.w === X && Y1 < r.y + r.h && Y2 > r.y);
        const leftRects  = roomRects.filter(r => r.x       === X && Y1 < r.y + r.h && Y2 > r.y);
        if (!rightRects.length && !leftRects.length) continue;

        for (const op of ops) {
          const worldY = Y1 + op.t * autoLen;

          let side = null, matchRect = null;
          for (const r of rightRects) {
            if (worldY >= r.y - 0.01 && worldY <= r.y + r.h + 0.01) { side = 'right'; matchRect = r; break; }
          }
          if (!side) {
            for (const r of leftRects) {
              if (worldY >= r.y - 0.01 && worldY <= r.y + r.h + 0.01) { side = 'left'; matchRect = r; break; }
            }
          }
          if (!side) continue;

          if (op.roomId != null && op.roomId !== room.id && op.type !== 'door' && op.type !== 'archway') continue;

          if (!op.roomId || (op.roomId !== room.id)) {
            if (!op.roomId && op.type !== 'door' && op.type !== 'archway') {
              let phantom = false;
              if (side === 'left') {
                phantom = allRooms.some(other =>
                  other.id !== room.id &&
                  other.x + other.w === X &&
                  worldY > other.y - 0.01 && worldY < other.y + other.h + 0.01 &&
                  other.y >= room.y - 0.01 && other.y + other.h <= room.y + room.h + 0.01 &&
                  other.h < room.h * 0.5
                );
              } else if (side === 'right') {
                phantom = allRooms.some(other =>
                  other.id !== room.id &&
                  other.x === X &&
                  worldY > other.y - 0.01 && worldY < other.y + other.h + 0.01 &&
                  other.y >= room.y - 0.01 && other.y + other.h <= room.y + room.h + 0.01 &&
                  other.h < room.h * 0.5
                );
              }
              if (phantom) continue;
            }
          }

          const wallEdgeX = matchRect.x + (side === 'right' ? matchRect.w : 0);
          const wallGx = (wallEdgeX - room.x) * CELL_M - roomW_m / 2;
          const pos = (worldY - room.y) / room.h;
          const _posToT = (snapPos) => Math.max(0.02, Math.min(0.98, (room.y + snapPos * room.h - Y1) / autoLen));
          const isDupeV = result.some(e => !e.opening._awKey && e.opening.wall === side && Math.abs(e.opening.pos - pos) < 0.08);
          if (!isDupeV) result.push({
            opening: { ...op, id: `aw-${key}-${op.id}`, wall: side, pos, _wallGx: wallGx,
                       _awKey: key, _awOpId: op.id, _posToT },
            // Use the exact matched-rect edge (not the room's bounding-box edge) so
            // adjacency resolves correctly for compound (merged) rooms with step walls.
            adjacentRoom: findAdjacentRoom(allRooms, room, {
              wall: side, pos,
              absX: (wallEdgeX - room.x) * CELL,
              absY: (worldY - room.y) * CELL,
            }),
          });
        }
      }
    }

    // Openings on interior hand-drawn walls ("Draw Walls" mode — wallSegments).
    // These are a third, independent home for openings (distinct from
    // room.openings and autoWallOpenings) and previously had zero presence
    // here — they rendered as static geometry in RoomScene only, with no
    // click/select/drag support at all. _wallGz/_wallGx pin the opening to
    // this specific segment's fixed line (same mechanism the auto-wall
    // entries above use for compound-room boundaries); _posToT converts a
    // dragged "fraction of room" position back to "fraction of this
    // segment", clamped to the segment's own physical length so the item
    // can't slide off the end of a short interior wall into open space.
    {
      const rx1 = room.x, rx2 = room.x + room.w, ry1 = room.y, ry2 = room.y + room.h;
      for (const seg of wallSegments) {
        if (!seg.openings || !seg.openings.length) continue;
        const sx1 = Math.min(seg.x1, seg.x2), sx2 = Math.max(seg.x1, seg.x2);
        const sy1 = Math.min(seg.y1, seg.y2), sy2 = Math.max(seg.y1, seg.y2);
        if (!(sx1 < rx2 && sx2 > rx1 && sy1 < ry2 && sy2 > ry1)) continue;

        const isH = seg.y1 === seg.y2;
        const wallLenM = isH ? (sx2 - sx1) * CELL_M : (sy2 - sy1) * CELL_M;
        if (wallLenM <= 0) continue;
        const segStartLocal = isH ? (sx1 - room.x) * CELL_M - roomW_m / 2 : (sy1 - room.y) * CELL_M - roomD_m / 2;
        const fixedLocal = isH ? (sy1 - room.y) * CELL_M - roomD_m / 2 : (sx1 - room.x) * CELL_M - roomW_m / 2;
        const localMin = segStartLocal + 0.02 * wallLenM;
        const localMax = segStartLocal + 0.98 * wallLenM;
        // Every door/window/wall-decor mesh positions itself with a small
        // positive local-Z offset from its wall reference point, tuned for
        // room-boundary walls — which render as zero-thickness planes, so
        // any positive offset already clears the surface. A wall SEGMENT is
        // a real 0.15m-thick box (WT3 in the render block above) centered on
        // this same reference point, so anything mounted with less than half
        // that thickness of clearance (a wall clock's ~0.016m, an outlet's
        // ~0.007m) was rendering literally embedded inside the solid wall —
        // invisible, even though perfectly selectable/draggable. Pushing the
        // reference point out to the wall's actual outer face fixes every
        // decor type at once instead of hand-tuning each one's own depth.
        const WALL_SEG_HALF_THICKNESS = 0.075; // must match WT3/2 above

        for (const op of seg.openings) {
          const midLocal = segStartLocal + op.t * wallLenM;
          const pos = isH ? (midLocal + roomW_m / 2) / roomW_m : (midLocal + roomD_m / 2) / roomD_m;
          const _posToT = (snapPos) => {
            const local = isH ? (-roomW_m / 2 + snapPos * roomW_m) : (-roomD_m / 2 + snapPos * roomD_m);
            return Math.max(0.02, Math.min(0.98, (local - segStartLocal) / wallLenM));
          };
          // A door/window/archway cuts through the wall — visible (and
          // identical) from either face, so which "side" it's tagged with
          // is arbitrary. A wall-decor item (painting/AC/clock/outlet/
          // radiator) actually mounts on ONE specific face, and a
          // freestanding interior wall has usable room on both — side=1
          // reuses the 'top'/'left' convention (+offset) already used
          // everywhere else in this file; side=-1 mirrors it to 'bottom'/
          // 'right' (-offset), landing the item on the opposite face.
          const side = isWallDecorType(op.type) ? (op.side ?? 1) : 1;
          const wallLabel = isH ? (side === 1 ? 'top' : 'bottom') : (side === 1 ? 'left' : 'right');
          const sidedFixedLocal = fixedLocal + side * WALL_SEG_HALF_THICKNESS;
          result.push({
            opening: {
              ...op, id: `ws-${seg.id}-${op.id}`, wall: wallLabel, pos,
              ...(isH ? { _wallGz: sidedFixedLocal } : { _wallGx: sidedFixedLocal }),
              _wsSegId: seg.id, _wsOpId: op.id, _wsIsH: isH, _wsSide: side,
              _wsLocalMin: localMin, _wsLocalMax: localMax, _posToT,
            },
            adjacentRoom: null,
          });
        }
      }
    }

    // Openings on interior FREE walls (any angle / curved) — a fourth home for
    // openings. The wall itself already gets cut around them in RoomScene, but
    // without an entry here there was no door/window model in the gap and no way
    // to select, move, resize or delete it from the 3D view. `_fwWall` carries
    // the wall's own geometry so Opening3D can sit the item on the arc.
    for (const w of (freeWalls || [])) {
      if (!w.openings || !w.openings.length) continue;
      const sx1 = Math.min(w.x1, w.x2), sx2 = Math.max(w.x1, w.x2);
      const sy1 = Math.min(w.y1, w.y2), sy2 = Math.max(w.y1, w.y2);
      if (!(sx1 < room.x + room.w && sx2 > room.x && sy1 < room.y + room.h && sy2 > room.y)) continue;
      for (const op of w.openings) {
        result.push({
          opening: {
            ...op,
            id: `fw-${w.id}-${op.id}`,
            wall: 'top', pos: op.t ?? 0.5,          // coarse fallback; _fwWall wins
            _fwId: w.id, _fwOpId: op.id,
            _fwWall: { x1: w.x1, y1: w.y1, x2: w.x2, y2: w.y2, bulge: w.bulge || 0, thickness: w.thickness ?? 0.16 },
          },
          adjacentRoom: null,
        });
      }
    }

    return result;
  }, [allRooms, room, autoWallOpenings, wallSegments, freeWalls]);

  const selectedOpeningEntry = openingsToRender.find(({ opening }) => opening.id === selectedOpeningId) ?? null;
  const selectedOpening = selectedOpeningEntry?.opening ?? null;
  const selectedOpeningAdjacentRoom = selectedOpeningEntry?.adjacentRoom ?? null;

  const handleNavigate = (roomId) => dispatch(selectRoom(roomId));

  function handleAddOpening({ type, wall, pos, absX, absY, wallId, freeWallId, t: segT, edge, side }) {
    if (!room) return;
    // Interior FREE wall (any angle / curved): its own openings list, positioned
    // by t along the wall.
    if (freeWallId != null) {
      dispatch(addFreeWallOpening({ wallId: freeWallId, type, t: segT, side }));
      return;
    }
    // Interior hand-drawn wall segment: goes into that wall's own openings
    // list, not the room's — a completely different placement path.
    if (wallId != null) {
      dispatch(addWallSegOpening({ wallId, type, t: segT, side }));
      return;
    }
    // DRAWN room: the opening lives on an outline edge (`edge` + `t`). wall/pos
    // ride along as the coarse fallback the 2D plan and the house view use.
    if (edge != null) {
      dispatch(addOpening({ roomId: room.id, type, wall: 'top', pos: segT, edge, t: segT }));
      return;
    }
    // Compound rooms: the modal already picked a real wall segment and
    // computed the exact position. Simple rooms: resolve the naive
    // top/bottom/left/right + % (a no-op for a single-rect room).
    const placement = absX !== undefined
      ? { wall, pos, absX, absY }
      : resolveOpeningPlacement(room, wall, pos);
    dispatch(addOpening({ roomId: room.id, type, ...placement }));
  }

  function handleOpeningClick(opening) {
    setSelectedItemId(null);
    setSelectedOpeningId(opening.id);
  }

  function handleOpeningClickView(opening) {
    // In view mode: navigate to adjacent room if the opening connects one —
    // only doors/archways are actual passages; windows and wall-decor items
    // (painting/AC/clock/outlet/radiator) never trigger navigation, even if
    // they happen to sit on a wall shared with another room.
    if (opening.type !== 'door' && opening.type !== 'archway') return;
    const entry = openingsToRender.find(e => e.opening.id === opening.id);
    if (entry?.adjacentRoom) handleNavigate(entry.adjacentRoom.id);
  }

  function handleFurnitureClick(item) {
    setSelectedOpeningId(null);
    setSelectedLightingId(null);
    setSelectedItemId(item.id);
  }

  function handleLightingClick(item) {
    setSelectedOpeningId(null);
    setSelectedItemId(null);
    setSelectedLightingId(item.id);
  }

  function handlePointerMissed() {
    setSelectedItemId(null);
    setSelectedOpeningId(null);
    setSelectedLightingId(null);
  }

  function handleDeleteOpening() {
    if (!selectedOpening || !room) return;
    if (selectedOpening._awKey) {
      dispatch(removeAutoWallOpening({ key: selectedOpening._awKey, openingId: selectedOpening._awOpId }));
    } else if (selectedOpening._fwId != null) {
      dispatch(removeFreeWallOpening({ wallId: selectedOpening._fwId, openingId: selectedOpening._fwOpId }));
    } else if (selectedOpening._wsSegId != null) {
      dispatch(removeWallSegOpening({ wallId: selectedOpening._wsSegId, openingId: selectedOpening._wsOpId }));
    } else {
      dispatch(removeOpening({ roomId: room.id, openingId: selectedOpening.id }));
    }
    setSelectedOpeningId(null);
  }

  function handleResizeOpening(dims) {
    if (!selectedOpening || !room) return;
    if (selectedOpening._awKey) {
      dispatch(updateAutoWallOpening({ key: selectedOpening._awKey, openingId: selectedOpening._awOpId, changes: dims }));
    } else if (selectedOpening._fwId != null) {
      dispatch(updateFreeWallOpening({ wallId: selectedOpening._fwId, openingId: selectedOpening._fwOpId, changes: dims }));
    } else if (selectedOpening._wsSegId != null) {
      dispatch(updateWallSegOpening({ wallId: selectedOpening._wsSegId, openingId: selectedOpening._wsOpId, changes: dims }));
    } else {
      dispatch(resizeOpening({ roomId: room.id, openingId: selectedOpening.id, ...dims }));
    }
  }

  function handleRecolorOpening(color) {
    if (!selectedOpening || !room) return;
    // `colored: true` records the explicit pick (GLB items only tint once set).
    if (selectedOpening._awKey) {
      dispatch(updateAutoWallOpening({ key: selectedOpening._awKey, openingId: selectedOpening._awOpId, changes: { color, colored: true } }));
    } else if (selectedOpening._fwId != null) {
      dispatch(updateFreeWallOpening({ wallId: selectedOpening._fwId, openingId: selectedOpening._fwOpId, changes: { color, colored: true } }));
    } else if (selectedOpening._wsSegId != null) {
      dispatch(updateWallSegOpening({ wallId: selectedOpening._wsSegId, openingId: selectedOpening._wsOpId, changes: { color, colored: true } }));
    } else {
      dispatch(recolorOpening({ roomId: room.id, openingId: selectedOpening.id, color }));
    }
  }

  function handleResetOpeningColor() {
    if (!selectedOpening || !room) return;
    // Back to the item's authored look (clears colour + the `colored` flag).
    if (selectedOpening._awKey) {
      dispatch(updateAutoWallOpening({ key: selectedOpening._awKey, openingId: selectedOpening._awOpId, changes: { color: undefined, colored: false } }));
    } else if (selectedOpening._fwId != null) {
      dispatch(updateFreeWallOpening({ wallId: selectedOpening._fwId, openingId: selectedOpening._fwOpId, changes: { color: undefined, colored: false } }));
    } else if (selectedOpening._wsSegId != null) {
      dispatch(updateWallSegOpening({ wallId: selectedOpening._wsSegId, openingId: selectedOpening._wsOpId, changes: { color: undefined, colored: false } }));
    } else {
      dispatch(resetOpeningColor({ roomId: room.id, openingId: selectedOpening.id }));
    }
  }

  function handleAddFurniture(type, style) {
    if (!room) return;
    const ft = FURNITURE_TYPES.find(f => f.type === type);
    if (!ft) return;
    const newId = nextFurnitureId; // placeFurniture assigns this id (see reducer)
    dispatch(placeFurniture({
      type,
      ...(style ? { style } : {}),
      x: Math.round(room.x * CELL + (room.w * CELL - ft.w) / 2),
      y: Math.round(room.y * CELL + (room.h * CELL - ft.h) / 2),
    }));
    // Auto-select the new piece so its distance-to-wall dimensions show at once
    setSelectedItemId(newId);
  }

  function handleAddLighting(type) {
    if (!room) return;
    const lt = LIGHTING_TYPES.find(l => l.type === type);
    if (!lt) return;
    dispatch(placeLighting({
      type,
      x: Math.round(room.x * CELL + (room.w * CELL - lt.w) / 2),
      y: Math.round(room.y * CELL + (room.h * CELL - lt.h) / 2),
    }));
  }

  // ── COPY / PASTE ─────────────────────────────────────────────────────────
  // Copies whichever item is currently selected (furniture OR an opening/wall
  // item) into a local clipboard, preserving every parameter — type, size,
  // rotation, color. Paste re-creates it nearby (offset so it doesn't land
  // exactly on top of the original) and selects the new copy.
  function handleCopySelected() {
    if (selectedItem) {
      clipboardRef.current = {
        kind: 'furniture',
        data: {
          type: selectedItem.type, w: selectedItem.w, h: selectedItem.h, h3: selectedItem.h3,
          rotation: selectedItem.rotation || 0, color: selectedItem.color, colored: selectedItem.colored, style: selectedItem.style,
          x: selectedItem.x, y: selectedItem.y,
        },
      };
    } else if (selectedOpening) {
      clipboardRef.current = {
        kind: 'opening',
        data: {
          type: selectedOpening.type, width: selectedOpening.width, height: selectedOpening.height,
          sill: selectedOpening.sill, color: selectedOpening.color, style: selectedOpening.style,
          wall: selectedOpening.wall, pos: selectedOpening.pos,
          absX: selectedOpening.absX, absY: selectedOpening.absY,
          // Which outline edge it sits on, for a DRAWN room — without this the
          // copy has only the coarse wall/pos fallback to go on and lands on a
          // bounding-box side instead of the wall it was copied from.
          edge: selectedOpening.edge, t: selectedOpening.t,
        },
      };
    } else if (selectedLighting) {
      clipboardRef.current = {
        kind: 'lighting',
        data: {
          type: selectedLighting.type, w: selectedLighting.w, h: selectedLighting.h,
          rotation: selectedLighting.rotation || 0, color: selectedLighting.color, style: selectedLighting.style,
          x: selectedLighting.x, y: selectedLighting.y,
        },
      };
    }
  }

  function handlePasteClipboard() {
    const c = clipboardRef.current;
    if (!c || !room) return;

    if (c.kind === 'furniture') {
      const OFFSET = 20; // px — visibly offset from the original so it's not hidden underneath it
      const newId = nextFurnitureId;
      dispatch(placeFurniture({
        type: c.data.type, w: c.data.w, h: c.data.h, h3: c.data.h3, rotation: c.data.rotation,
        color: c.data.color, colored: c.data.colored, style: c.data.style,
        x: c.data.x + OFFSET, y: c.data.y + OFFSET,
      }));
      setSelectedOpeningId(null);
      setSelectedItemId(newId);
    } else if (c.kind === 'opening') {
      const { type, width, height, sill, color, style, wall, pos, absX, absY, edge, t } = c.data;
      const newId = nextOpeningId;
      const newPos = Math.max(0.05, Math.min(0.95, (pos ?? 0.5) + 0.08));
      let placement;
      if (edge != null) {
        // DRAWN room: the copy belongs on the SAME outline edge, nudged along it
        // so it doesn't land exactly on the original.
        const newT = Math.max(0.05, Math.min(0.95, (t ?? 0.5) + 0.08));
        placement = { wall: wall || 'top', pos: newT, edge, t: newT };
      } else if (absX !== undefined && absY !== undefined) {
        // Compound-room precise placement — shift half a grid cell along the wall.
        const isH = wall === 'top' || wall === 'bottom';
        const SHIFT = CELL * 0.5;
        placement = isH ? { wall, pos: newPos, absX: absX + SHIFT, absY } : { wall, pos: newPos, absX, absY: absY + SHIFT };
      } else {
        placement = resolveOpeningPlacement(room, wall, newPos);
      }
      dispatch(addOpening({ roomId: room.id, type, width, height, sill, color, style, ...placement }));
      setSelectedItemId(null);
      setSelectedOpeningId(newId);
    } else if (c.kind === 'lighting') {
      const OFFSET = 20;
      const newId = nextLightingId;
      dispatch(placeLighting({
        type: c.data.type, w: c.data.w, h: c.data.h, rotation: c.data.rotation,
        color: c.data.color, style: c.data.style,
        x: c.data.x + OFFSET, y: c.data.y + OFFSET,
      }));
      setSelectedOpeningId(null);
      setSelectedItemId(null);
      setSelectedLightingId(newId);
    }
  }

  const floorMat = resolveMat(design?.floor,   FLOOR_MATERIALS);
  const wallMat  = resolveMat(design?.wall,    WALL_MATERIALS);
  const ceilMat  = resolveMat(design?.ceiling, CEILING_MATERIALS);
  // Per-wall (accent-wall) overrides, resolved to full material objects.
  const wallFaceMats = useMemo(() => {
    const wf = design?.wallFaces || {};
    const out = {};
    for (const k in wf) out[k] = resolveMat(wf[k], WALL_MATERIALS);
    return out;
  }, [design?.wallFaces]);
  // Per-area (metre-grid) overrides, resolved to full material objects per cell.
  const wallPatchMats = useMemo(() => {
    const wp = design?.wallPatches || {};
    const out = {};
    for (const f in wp) { out[f] = {}; for (const k in wp[f]) out[f][k] = resolveMat(wp[f][k], WALL_MATERIALS); }
    return out;
  }, [design?.wallPatches]);
  // Per-area floor overrides, resolved to full material objects per cell.
  const floorPatchMats = useMemo(() => {
    const fp = design?.floorPatches || {};
    const out = {};
    for (const k in fp) out[k] = resolveMat(fp[k], FLOOR_MATERIALS);
    return out;
  }, [design?.floorPatches]);
  // Material staged for the selected cells (live preview before Apply) —
  // resolved against the list matching whichever surface is being painted.
  const cellPreviewMat = useMemo(
    () => (cellPreview != null ? resolveMat(cellPreview, selectTarget === 'floor' ? FLOOR_MATERIALS : WALL_MATERIALS) : null),
    [cellPreview, selectTarget]
  );

  const moveRef = useRef({ fwd: false, back: false, left: false, right: false });

  // Keyboard support — arrow keys and WASD drive the same moveRef
  useEffect(() => {
    const KEY_MAP = {
      ArrowUp: 'fwd',  KeyW: 'fwd',
      ArrowDown: 'back', KeyS: 'back',
      ArrowLeft: 'left', KeyA: 'left',
      ArrowRight: 'right', KeyD: 'right',
    };
    const down = (e) => {
      const k = KEY_MAP[e.code];
      if (k) { e.preventDefault(); moveRef.current[k] = true; }
    };
    const up = (e) => {
      const k = KEY_MAP[e.code];
      if (k) moveRef.current[k] = false;
    };
    window.addEventListener('keydown', down);
    window.addEventListener('keyup',   up);
    return () => {
      window.removeEventListener('keydown', down);
      window.removeEventListener('keyup',   up);
    };
  }, []);

  // Undo/redo — Ctrl+Z / Ctrl+Shift+Z / Ctrl+Y. builderHistory is a single
  // shared Redux history covering the 2D Builder AND this 3D editor (every
  // furniture/opening reducer already pushes to it), so this just wires up
  // the same shortcuts + a visible history bar for edits made here.
  useEffect(() => {
    if (!editMode) return;
    const onKey = (e) => {
      const t = e.target;
      const isEditableEl = t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable);
      if (isEditableEl) return;

      const mod = e.ctrlKey || e.metaKey;
      const key = e.key.toLowerCase();
      const isUndo = mod && key === 'z' && !e.shiftKey;
      const isRedo = mod && (key === 'y' || (key === 'z' && e.shiftKey));
      if (isUndo || isRedo) {
        e.preventDefault();
        dispatch(isRedo ? builderHistoryNext() : builderHistoryBack());
        return;
      }

      if (mod && key === 'c') {
        e.preventDefault();
        handleCopySelected();
        return;
      }
      if (mod && key === 'v') {
        e.preventDefault();
        handlePasteClipboard();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [editMode, dispatch, selectedItem, selectedOpening, selectedLighting, room, nextFurnitureId, nextLightingId, nextOpeningId]);

  // ── WALK ARROWS, MULTI-TOUCH SAFE ─────────────────────────────────────────
  //
  // The old version tracked no pointer id and had no cancel handler, which is
  // why holding an arrow and turning the camera with a second finger went
  // haywire:
  //
  //   · ANY pointerup or pointerleave on the arrow cleared the "moving" flag,
  //     so a second finger touching and lifting anywhere over it stopped a walk
  //     the first finger was still holding.
  //   · iOS fires pointercancel when it takes a touch over for a system
  //     gesture, and nothing listened for it — so the flag stayed TRUE after
  //     the finger was gone and the view kept walking on its own. That is the
  //     "unpredictable movement": not a camera bug, a key that never released.
  //
  // Now one arrow belongs to exactly one pointer: the first one down claims it,
  // later pointers are ignored, and only the owner can release it — including
  // via cancel and lost-capture, the two paths that used to strand it.
  const movePointerRef = useRef({});

  // Released from the WINDOW, not the button.
  //
  // The obvious handlers are all wrong here, each for its own reason:
  //
  //   onLostPointerCapture — fires when a SECOND touch begins, because the
  //     browser drops the implicit capture. Losing capture is not the finger
  //     lifting, and treating it as such is what cancelled the walk the moment
  //     you put a second finger on the view to turn the camera.
  //   onPointerLeave — fires while the finger is still down if capture is gone.
  //   onPointerUp on the button — never arrives at all once capture is lost and
  //     the finger lifts somewhere else, leaving the key stuck on.
  //
  // A window listener keyed to the owning pointerId has none of those failure
  // modes: it hears the end of THAT pointer wherever it happens, and ignores
  // every other finger.
  const bindRelease = (key, pointerId) => {
    const done = (e) => {
      if (e.pointerId !== pointerId) return;
      window.removeEventListener('pointerup', done);
      window.removeEventListener('pointercancel', done);
      if (movePointerRef.current[key] !== pointerId) return;
      delete movePointerRef.current[key];
      moveRef.current[key] = false;
    };
    window.addEventListener('pointerup', done);
    window.addEventListener('pointercancel', done);
  };

  const bind = (key) => ({
    onPointerDown: (e) => {
      if (movePointerRef.current[key] != null) return;         // already held
      // Keep it off the canvas underneath, so OrbitControls counts only the
      // fingers actually on the view and a walk never reads as a pinch.
      e.stopPropagation();
      console.log();
      movePointerRef.current[key] = e.pointerId;
      moveRef.current[key] = true;
      bindRelease(key, e.pointerId);
    },
  });

  return (
    <div
      ref={containerRef}
      className={[
        // Stable hook for the view's own styling — outside fullscreen this
        // element had no class at all, so there was nothing to hang the iOS
        // long-press guards on.
        'room3d-wrap',
        fakeFullscreen && 'room3d-fake-fullscreen',
        isFullscreen && isPortrait && !isIOS && 'room3d-fullscreen--rotated',
      ].filter(Boolean).join(' ')}
      style={fakeFullscreen ? undefined : { position: 'relative', width: '100%', height: '100%' }}
    >
      <Loader3D visible={loaderOn} label={t('loading_3d')} />

      {/* Undo/redo history bar — edit mode only */}
      {editMode && (
        <div
          style={{
            position: 'absolute', top: 12, left: 16, zIndex: 10,
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '6px 10px', borderRadius: 10,
            border: '1px solid rgba(255,255,255,0.10)', background: 'rgba(22,24,36,0.88)',
            backdropFilter: 'blur(10px)', boxShadow: '0 4px 20px rgba(0,0,0,0.35)',
          }}
        >
          <button
            onClick={() => dispatch(builderHistoryBack())}
            disabled={builderHistoryIndex === 0}
            title={t('undo_ctrlz_tooltip')}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: 28, height: 28, borderRadius: 6,
              border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.04)',
              color: builderHistoryIndex === 0 ? 'rgba(138,141,158,0.4)' : '#8a8d9e',
              cursor: builderHistoryIndex === 0 ? 'default' : 'pointer',
            }}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M9 2.5L4.5 7L9 11.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          {builderHistory.length > 1 && (
            <span style={{ fontSize: 9, fontWeight: 500, color: '#d4903a', background: 'rgba(212,144,58,0.12)', borderRadius: 8, padding: '1px 6px' }}>
              {builderHistoryIndex}/{builderHistory.length - 1}
            </span>
          )}
          <button
            onClick={() => dispatch(builderHistoryNext())}
            disabled={builderHistoryIndex >= builderHistory.length - 1}
            title={t('redo_ctrly_tooltip')}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: 28, height: 28, borderRadius: 6,
              border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.04)',
              color: builderHistoryIndex >= builderHistory.length - 1 ? 'rgba(138,141,158,0.4)' : '#8a8d9e',
              cursor: builderHistoryIndex >= builderHistory.length - 1 ? 'default' : 'pointer',
            }}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M5 2.5L9.5 7L5 11.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
      )}

      {/* Bottom-right stack: render quality sits directly ABOVE the
          time-of-day chips. One flex column owns both, so they stay aligned and
          correctly spaced without either needing to know the other's height. */}
      <div style={{
        position: 'absolute', bottom: 16, right: 16, zIndex: 10,
        display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-end',
      }}>

        {/* Render quality.

            The note explains the cost of "high" — it turns on everything that
            was exhausting phone GPUs. It shows on entry and on every change,
            then clears itself after five seconds: long enough to read, short
            enough not to sit on top of the room permanently. */}
        {qualityNote && (quality === 'high' || quality === 'auto') && (
          <div style={{
            width: 176, padding: '6px 9px', borderRadius: 9,
            background: 'rgba(16,16,22,0.86)', backdropFilter: 'blur(10px)',
            border: `1px solid ${quality === 'high' ? 'rgba(255,207,112,0.3)' : 'rgba(255,255,255,0.1)'}`,
            color: quality === 'high' ? '#ffcf70' : '#b0b0b0',
            fontSize: 9.5, lineHeight: 1.4, fontWeight: 600, textAlign: 'left',
            animation: 'room3d-q-fade 0.25s ease',
          }}>
            {quality === 'high' ? t('quality_high_note') : t('quality_auto_note')}
          </div>
        )}

        {/* A custom menu rather than a native <select>: the browser's own option
            list renders as unstyled black-on-white here, which looks broken
            against the dark controls it sits among. */}
        <div style={{ position: 'relative' }}>
          {qualityOpen && (
            <>
              {/* Click anywhere else to dismiss. */}
              <div
                onClick={() => setQualityOpen(false)}
                style={{ position: 'fixed', inset: 0, zIndex: 20 }}
              />
              <div style={{
                position: 'absolute', bottom: 'calc(100% + 6px)', right: 0, zIndex: 21,
                minWidth: 132, padding: 4, borderRadius: 10,
                background: 'rgba(22,22,28,0.97)', backdropFilter: 'blur(14px)',
                border: '1px solid rgba(255,255,255,0.14)',
                boxShadow: '0 8px 28px rgba(0,0,0,0.55)',
                animation: 'room3d-q-fade 0.16s ease',
              }}>
                {QUALITY_LEVELS.map((level) => {
                  const active = quality === level;
                  return (
                    <button
                      key={level}
                      onClick={() => { setQuality(level); setQualityOpen(false); }}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 7, width: '100%',
                        padding: '7px 9px', borderRadius: 7, border: 'none',
                        background: active ? 'rgba(255,200,80,0.92)' : 'transparent',
                        color: active ? '#1a1400' : '#ddd',
                        fontSize: 11, fontWeight: 700, cursor: 'pointer',
                        textAlign: 'left', transition: 'background 0.12s',
                      }}
                      onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; }}
                      onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = 'transparent'; }}
                    >
                      {/* Bars that grow with the level — the setting reads at a
                          glance without depending on the label. */}
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ flexShrink: 0 }}>
                        {[0, 1, 2].map((i) => {
                          const filled = level === 'auto'
                            ? true
                            : i < ({ low: 1, medium: 2, high: 3 })[level];
                          return (
                            <rect
                              key={i} x={1 + i * 4} y={9 - (i + 1) * 2.4} width="2.4" height={(i + 1) * 2.4}
                              rx="0.7"
                              fill={filled ? (active ? '#1a1400' : '#eee') : 'transparent'}
                              stroke={filled ? 'none' : (active ? 'rgba(26,20,0,0.4)' : 'rgba(255,255,255,0.3)')}
                              strokeWidth="0.8"
                            />
                          );
                        })}
                      </svg>
                      <span style={{ flex: 1 }}>{t(`quality_${level}`)}</span>
                      {active && <span style={{ fontSize: 11 }}>✓</span>}
                    </button>
                  );
                })}
              </div>
            </>
          )}

          <button
            onClick={() => setQualityOpen((o) => !o)}
            title={t('quality_label')}
            aria-label={t('quality_label')}
            aria-expanded={qualityOpen}
            style={{
              display: 'flex', alignItems: 'center', gap: 5, padding: '5px 8px',
              borderRadius: 10, cursor: 'pointer',
              background: 'rgba(16,16,22,0.82)', backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255,255,255,0.12)',
              boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
              color: '#eee', fontSize: 10, fontWeight: 700,
            }}
          >
            {/* Sliders — "settings you adjust", not anything about the room. */}
            <svg width="12" height="12" viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0 }}>
              <path d="M2 4h10M2 10h10" stroke="#bbb" strokeWidth="1.3" strokeLinecap="round" />
              <circle cx="5.2" cy="4" r="1.7" fill="#16161a" stroke="#eee" strokeWidth="1.3" />
              <circle cx="9" cy="10" r="1.7" fill="#16161a" stroke="#eee" strokeWidth="1.3" />
            </svg>
            <span>{t(`quality_${quality}`)}</span>
            <svg width="8" height="8" viewBox="0 0 8 8" fill="none" style={{
              flexShrink: 0, opacity: 0.65,
              transform: qualityOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s',
            }}>
              <path d="M1 5L4 2l3 3" stroke="#eee" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>
        {/* Time of day — morning / evening / night. Drives sun angle & colour,
            ambient level, the Environment HDRI, bloom, and (via timeOfDay
            threaded down) the window-view sky and whether fixtures cast
            shadows. */}
        <div style={{
          display: 'flex', gap: 4, padding: 4, borderRadius: 12,
          background: 'rgba(16,16,22,0.82)', backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255,255,255,0.12)', boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
        }}>
          {Object.entries(TIME_OF_DAY_PRESETS).map(([key, preset]) => {
            const locked = isGuest && key !== 'morning';
            const label = t(`time_${key}`);
            return (
              <button
                key={key}
                onClick={() => { if (!locked) setTimeOfDay(key); }}
                title={locked ? t('guest_time_locked') : label}
                style={{
                  position: 'relative',
                  display: 'flex', alignItems: 'center', gap: 5,
                  padding: timeOfDay === key ? '6px 12px' : '6px 8px',
                  borderRadius: 9, border: 'none', cursor: locked ? 'default' : 'pointer',
                  background: timeOfDay === key ? 'rgba(255,200,80,0.92)' : 'transparent',
                  color: timeOfDay === key ? '#1a1400' : '#ccc',
                  fontWeight: 700, fontSize: 12, transition: 'all 0.15s',
                  opacity: locked ? 0.45 : 1,
                }}
              >
                <ItemIcon type={key} size={16} />
                {timeOfDay === key && <span>{label}</span>}
                {locked && <span style={{ fontSize: 10 }}>🔒</span>}
              </button>
            );
          })}
        </div>
      </div>

    <Canvas
      // PCFSoftShadowMap is deprecated in this version of three and silently
      // falls back to PCFShadowMap anyway — asking for it only logs a warning
      // on every mount. Requesting what we actually get.
      shadows={{ type: THREE.PCFShadowMap }}
      // Cap the render resolution at 2× so high-DPI phones/retina screens don't
      // render at 3×+ (which quadruples the pixels to shade for no visible gain)
      // — a big GPU/perf win on mobile with no perceptible quality loss.
      // captureDpr overrides the quality setting for the moment an AI render is
      // taken — see captureForAI.
      dpr={captureDpr ?? q.dpr}
      // antialias is off on mobile — MSAA allocates a full-size multisample
      // buffer that grows again in fullscreen, and at phone pixel density it
      // smooths edges nobody can see. See utils/gpuProfile.
      //
      // preserveDrawingBuffer STAYS ON. It also costs memory, but every
      // screenshot path reads this canvas after the frame was drawn — the
      // download and the AI render — and turning it off handed them a cleared
      // buffer. Its saving was small next to the multisampling fix; leave it be.
      gl={{
        antialias: useMSAA(),
        toneMapping: THREE.ACESFilmicToneMapping,
        toneMappingExposure: tod.exposure,
        preserveDrawingBuffer: true,
      }}
      style={{ width: '100%', height: '100%', borderRadius: 8 }}
      onPointerMissed={handlePointerMissed}
      onCreated={({ gl }) => {
        // SURVIVING A LOST CONTEXT.
        //
        // Mobile Safari drops the WebGL context when it needs the memory — and
        // without preventDefault() here the loss is PERMANENT: the browser does
        // not even attempt to restore it, the canvas dies, and what the user
        // sees is the page reloading itself mid-edit.
        //
        // Calling preventDefault is what asks for a restore. It does not
        // guarantee one, so the real fix is using less memory (see the shadow
        // map size above) — but it turns a fatal loss into a recoverable one.
        const canvas = gl.domElement;
        canvas.addEventListener('webglcontextlost', (e) => {
          e.preventDefault();
          console.warn('[3d] WebGL context lost — asked the browser to restore it');
        }, false);
        canvas.addEventListener('webglcontextrestored', () => {
          console.warn('[3d] WebGL context restored');
        }, false);
      }}
    >
      <SceneReadySignal key={room?.id} onReady={() => setSceneReady(true)} />
      <CameraRig key={room?.id} roomW={W} roomD={D} roomH={H} moveRef={moveRef} />

      {/* Clear colour behind/around the room — matches the current sky tone
          so anything the camera sees past the room's walls reads as outdoor
          atmosphere rather than a flat engine-default grey. */}
      <color attach="background" args={[tod.bg]} />

      <ambientLight intensity={tod.ambientIntensity} color={tod.ambientColor} />
      {/* "Sun" (or, at night, faint moonlight) — position swings with azimuth/
          elevation per time of day for realistic shadow direction/length; the
          shadow camera frustum is sized to the room so shadows never clip. */}
      <directionalLight
        // Rebuilt when the shadow-map size changes — the depth texture is
        // allocated once when the light is created, so a changed size does
        // not take effect without this.
        key={`sun-${q.shadowMap}`}
        position={sunPos}
        intensity={tod.sunIntensity}
        color={tod.sunColor}
        castShadow={q.shadows}
        shadow-mapSize-width={q.shadowMap}
        shadow-mapSize-height={q.shadowMap}
        shadow-bias={-0.0006}
        // normalBias offsets the shadow lookup along each surface's normal —
        // it cleans up the self-shadow "acne" that GLB details (a chair's arms
        // shadowing its seat, a sofa's cushions) would otherwise show, so those
        // fine shadows read cleanly. A small value keeps contact shadows tight
        // (furniture stays grounded, not floating).
        shadow-normalBias={0.02}
        // Tighter blur than before → GLB shadows keep their SHAPE (detail)
        // instead of smearing into a soft blob, while still not razor-hard.
        shadow-radius={3.5}
        shadow-camera-left={-shadowSpan}
        shadow-camera-right={shadowSpan}
        shadow-camera-top={shadowSpan}
        shadow-camera-bottom={-shadowSpan}
        shadow-camera-near={0.5}
        shadow-camera-far={sunReach * 2.2}
      />
      {/* Reflection-only IBL — every envMapIntensity above (gloss/metallic
          finishes especially) needs this to have anything to reflect, or
          roughness/metalness changes are invisible regardless of value.
          Preset + intensity swing with time of day (dawn/sunset/night HDRIs)
          without relighting everything else, which stays ambient+sun driven. */}
      <SafeEnvironment key={tod.envPreset} preset={tod.envPreset} background={false} environmentIntensity={tod.envIntensity} />

      {/* multisampling MUST be passed explicitly — the default is 8, which
          allocates a full-drawing-buffer render target 8 samples deep (~42MB on
          a phone, growing again in fullscreen) and was the biggest single
          reason mobile browsers killed this tab. 0 on mobile keeps the bloom,
          drops the 8× buffer. */}
      <EffectComposer multisampling={q.samples}>
        <Bloom luminanceThreshold={tod.bloomThreshold} luminanceSmoothing={tod.bloomSmoothing} intensity={tod.bloomIntensity} radius={0.6} />
      </EffectComposer>

      <HoverManager
        roomItems={roomItems}
        room={room}
        roomW={W}
        roomD={D}
        onHoverChange={setHoveredId}
        active={editMode}
      />
      {/* Provided inside the Canvas so the GLB furniture/doors below can register
          their load state with the room loader (R3F won't bridge outer context). */}
      <GlbLoadingContext.Provider value={glbLoad}>
        <RoomScene
          room={room}
          roomW={W} roomD={D} roomH={H}
          floorMat={floorMat} wallMat={wallMat} ceilMat={ceilMat}
          wallFaceMats={wallFaceMats} wallPatchMats={wallPatchMats} floorPatchMats={floorPatchMats} cellPreviewMat={cellPreviewMat} selectTarget={selectTarget} selectedSurface={selectedSurface} selectedCells={selectedCells} onAreaSelect={onAreaSelect}
          roomItems={roomItems}
          lightingItems={roomLighting}
          openingsToRender={openingsToRender}
          onNavigate={handleNavigate}
          onFurnitureClick={editMode ? handleFurnitureClick : null}
          onLightingClick={editMode ? handleLightingClick : null}
          selectedItemId={editMode ? selectedItemId : null}
          selectedLightingId={editMode ? selectedLightingId : null}
          hoveredId={editMode ? hoveredId : null}
          selectedOpeningId={editMode ? selectedOpeningId : null}
          onOpeningClick={editMode ? handleOpeningClick : handleOpeningClickView}
          openingsEditable={editMode}
          wallSegments={wallSegments}
          freeWalls={freeWalls}
          timeOfDay={timeOfDay}
        />
      </GlbLoadingContext.Provider>
    </Canvas>

    {/* Top-right mode toggle + edit actions */}
    <div className="room3d-edit-toolbar" style={{
      position: 'absolute', top: 12, left: 12, right: 12, zIndex: 10,
      display: 'flex', flexWrap: 'wrap', gap: 6, justifyContent: 'flex-end',
    }}>
      <button
        className="room3d-tbar-btn room3d-tbar-btn--icon-only"
        onClick={toggleFullscreen}
        title={isFullscreen ? t('exit_fullscreen_tooltip') : t('fullscreen_tooltip')}
      >
        {isFullscreen ? (
          <svg className="room3d-tbar-icon" width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M5.5 1.5V4A1.5 1.5 0 0 1 4 5.5H1.5M8.5 1.5V4A1.5 1.5 0 0 0 10 5.5H12.5M5.5 12.5V10A1.5 1.5 0 0 0 4 8.5H1.5M8.5 12.5V10A1.5 1.5 0 0 1 10 8.5H12.5"
              stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        ) : (
          <svg className="room3d-tbar-icon" width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M1.5 5V2.5A1 1 0 0 1 2.5 1.5H5M9 1.5H11.5A1 1 0 0 1 12.5 2.5V5M12.5 9V11.5A1 1 0 0 1 11.5 12.5H9M5 12.5H2.5A1 1 0 0 1 1.5 11.5V9"
              stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        )}
      </button>
      {editMode ? (
        <>
          <button
            className="room3d-tbar-btn"
            onClick={() => { setShowAddModal(true); setShowAddOpeningModal(false); setShowAddWallItemModal(false); setShowAddLightingModal(false); }}
            title={t('add_furniture_btn')}
          >
            <svg className="room3d-tbar-icon room3d-tbar-icon--furniture" width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M2.5 6V4.75C2.5 3.78 3.28 3 4.25 3H9.75C10.72 3 11.5 3.78 11.5 4.75V6" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
              <rect x="1.5" y="6" width="11" height="4" rx="1" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/>
              <path d="M2.2 10V11.3M11.8 10V11.3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
            </svg>
            <span className="room3d-tbar-label">{t('add_furniture_btn')}</span>
          </button>
          <button
            className="room3d-tbar-btn"
            onClick={() => { setShowAddOpeningModal(true); setShowAddModal(false); setShowAddWallItemModal(false); setShowAddLightingModal(false); }}
            title={t('add_opening_btn')}
          >
            <svg className="room3d-tbar-icon room3d-tbar-icon--opening" width="14" height="14" viewBox="0 0 14 14" fill="none">
              <rect x="3" y="1.5" width="8" height="11" rx="0.8" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/>
              <circle cx="8.7" cy="7" r="0.7" fill="currentColor"/>
            </svg>
            <span className="room3d-tbar-label">{t('add_opening_btn')}</span>
          </button>
          <button
            className="room3d-tbar-btn"
            onClick={() => { setShowAddWallItemModal(true); setShowAddModal(false); setShowAddOpeningModal(false); setShowAddLightingModal(false); }}
            title={t('add_wallitem_btn')}
          >
            <svg className="room3d-tbar-icon room3d-tbar-icon--wallitem" width="14" height="14" viewBox="0 0 14 14" fill="none">
              <rect x="2" y="2" width="10" height="10" rx="1" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/>
              <path d="M2 9.5L5 6.5L7 8.5L9.5 5.5L12 8" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
              <circle cx="5" cy="4.7" r="0.8" fill="currentColor"/>
            </svg>
            <span className="room3d-tbar-label">{t('add_wallitem_btn')}</span>
          </button>
          <button
            className="room3d-tbar-btn"
            onClick={() => { setShowAddLightingModal(true); setShowAddModal(false); setShowAddOpeningModal(false); setShowAddWallItemModal(false); }}
            title={t('add_lighting_btn')}
          >
            <svg className="room3d-tbar-icon room3d-tbar-icon--lighting" width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M7 1.5C4.79 1.5 3 3.29 3 5.5C3 6.98 3.8 8.27 5 8.96V10.25C5 10.66 5.34 11 5.75 11H8.25C8.66 11 9 10.66 9 10.25V8.96C10.2 8.27 11 6.98 11 5.5C11 3.29 9.21 1.5 7 1.5Z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round"/>
              <path d="M5.75 12.5H8.25" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
            </svg>
            <span className="room3d-tbar-label">{t('add_lighting_btn')}</span>
          </button>
          <div className="room3d-tbar-divider" />
          <button className="room3d-tbar-btn room3d-tbar-btn--danger" onClick={cancelEdit}>
            <svg className="room3d-tbar-icon" width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path d="M2.5 2.5L9.5 9.5M9.5 2.5L2.5 9.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
            </svg>
            {t('cancel')}
          </button>
          <button className="room3d-tbar-btn room3d-tbar-btn--primary" onClick={exitEdit}>
            <svg className="room3d-tbar-icon" width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path d="M2 6.5L4.5 9L10 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            {t('done_btn')}
          </button>
        </>
      ) : (
        <>
          {/* Read-only (shared) viewers never see the Edit button, so edit
              mode can't be entered — they can still walk around, change the
              time of day, go fullscreen and screenshot. */}
          {!readOnly && (
            <button className="room3d-tbar-btn room3d-tbar-btn--accent" onClick={enterEdit}>
              <svg className="room3d-tbar-icon room3d-tbar-icon--edit" width="13" height="13" viewBox="0 0 14 14" fill="none">
                <path d="M9.62 2.13L11.87 4.38M2 12L2.4 9.9C2.45 9.63 2.58 9.38 2.78 9.19L8.9 3.06C9.29 2.67 9.93 2.67 10.32 3.06L10.94 3.68C11.33 4.07 11.33 4.71 10.94 5.1L4.81 11.22C4.62 11.42 4.37 11.55 4.1 11.6L2 12Z" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              {t('edit_btn')}
            </button>
          )}
          <button
            className="room3d-tbar-btn room3d-tbar-btn--icon-only room3d-shot"
            onClick={() => { if (!isGuest) handleScreenshot3D(); }}
            title={isGuest ? t('guest_screenshot_locked') : t('screenshot')}
            style={{ cursor: isGuest ? 'default' : 'pointer', opacity: isGuest ? 0.45 : 1, position: 'relative' }}
          >
            <svg className="room3d-tbar-icon" width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M1 4.5C1 3.67 1.67 3 2.5 3H4L5 1.5H9L10 3H11.5C12.33 3 13 3.67 13 4.5V10.5C13 11.33 12.33 12 11.5 12H2.5C1.67 12 1 11.33 1 10.5V4.5Z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round"/>
              <circle cx="7" cy="7.5" r="2" stroke="currentColor" strokeWidth="1.2"/>
            </svg>
            {isGuest && <span style={{ fontSize: 9, position: 'absolute', bottom: -2, right: -2 }}>🔒</span>}
          </button>
        </>
      )}
    </div>

    {/* Edit-mode-only: furniture panel, opening panel, modals */}
    {editMode && selectedItem && (
      <FurniturePanel
        item={selectedItem}
        onClose={() => setSelectedItemId(null)}
        onColorChange={color => dispatch(recolorFurniture({ id: selectedItem.id, color }))}
        onResetColor={() => dispatch(resetFurnitureColor({ id: selectedItem.id }))}
        onRotate={() => dispatch(rotateFurniture(selectedItem.id))}
        onDelete={() => { dispatch(removeFurniture(selectedItem.id)); setSelectedItemId(null); }}
        onResize={(w, h, h3) => { dispatch(resizeFurniture({ id: selectedItem.id, w, h, h3 })); dispatch(commitBuilderHistory()); }}
        onStyleChange={style => dispatch(changeFurnitureStyle({ id: selectedItem.id, style }))}
        onCopy={handleCopySelected}
      />
    )}

    {editMode && selectedOpening && (
      <OpeningPanel
        opening={selectedOpening}
        room={room}
        adjacentRoom={selectedOpeningAdjacentRoom}
        onClose={() => setSelectedOpeningId(null)}
        onDelete={handleDeleteOpening}
        onResize={handleResizeOpening}
        onColorChange={handleRecolorOpening}
        onResetColor={handleResetOpeningColor}
        onNavigate={selectedOpeningAdjacentRoom ? () => handleNavigate(selectedOpeningAdjacentRoom.id) : null}
        onCopy={handleCopySelected}
      />
    )}

    {editMode && selectedLighting && (
      <LightingPanel
        item={selectedLighting}
        onClose={() => setSelectedLightingId(null)}
        onColorChange={color => dispatch(recolorLighting({ id: selectedLighting.id, color }))}
        onResetColor={() => dispatch(resetLightingColor({ id: selectedLighting.id }))}
        onRotate={() => dispatch(rotateLighting(selectedLighting.id))}
        onDelete={() => { dispatch(removeLighting(selectedLighting.id)); setSelectedLightingId(null); }}
        onResize={(w, h) => dispatch(resizeLighting({ id: selectedLighting.id, w, h }))}
        onStyleChange={style => dispatch(changeLightingStyle({ id: selectedLighting.id, style }))}
        onCopy={handleCopySelected}
      />
    )}

    {/* Download chooser — original vs AI render. Not gated on editMode: the
        download button is available in view mode too. */}
    <DownloadModal
      open={dlOpen}
      onClose={() => setDlOpen(false)}
      capture={captureRoom}
      canvasRef={captureForAI}
      filename={dlFilename}
      onNotify={handleDownloadChoice}
      // So the AI render keeps the mood it was captured in (see LIGHTING_BY_MOOD).
      timeOfDay={timeOfDay}
      // Wall colours for the AI prompt — deliberately almost always EMPTY.
      //
      // Only two kinds qualify: a colour the user mixed themselves, and the few
      // presets vivid enough that the model normalises them (a chartreuse wall
      // came back pale sage). Everything else is left out entirely, because
      // everything else already renders correctly and every earlier attempt to
      // describe more of the room made it worse.
      //
      // TEXTURED AND PATTERNED MATERIALS ARE NEVER SENT. Their colour value is
      // not what they are — describing grey concrete as "grey" made the model
      // paint a flat grey wall and throw the texture away.
      surfaces={(() => {
        const chroma = (hex) => {
          if (typeof hex !== 'string' || !/^#?[0-9a-f]{6}$/i.test(hex.trim())) return 0;
          const n = parseInt(hex.replace('#', ''), 16);
          const r = ((n >> 16) & 255) / 255, g = ((n >> 8) & 255) / 255, b = (n & 255) / 255;
          return Math.max(r, g, b) - Math.min(r, g, b);
        };
        const qualifies = (m) => {
          if (!m || !m.color) return false;
          if (m.category === 'texture' || m.category === 'pattern') return false;
          if (m.__custom || m.category === 'custom') return chroma(m.color) >= 0.25;
          return m.category === 'colour' && chroma(m.color) >= 0.45;
        };
        const mats = [
          wallMat,
          ...Object.values(wallFaceMats),
          ...Object.values(wallPatchMats).flatMap((f) => Object.values(f || {})),
        ].filter(Boolean);
        return { walls: [...new Set(mats.filter(qualifies).map((m) => m.color))].slice(0, 3) };
      })()}
      // THE ACTUAL SURFACE COLOURS.
      //
      // Telling the model to "keep the colours" repeatedly failed: pale yellow
      // walls came back sage green, cream came back blue-grey. It was not
      // redecorating — the texture, tiling and layout survived intact — it was
      // falling back on what interior photographs usually look like, and sage
      // green walls are far commoner in that training data than pale yellow ones.
      //
      // An abstract instruction cannot outweigh that prior, but a concrete one
      // can: naming the colour the wall actually is. We are the only ones who
      // know it — it is right here in the room's design — so send it.
    />

    {editMode && showAddModal && (
      <AddItemModal
        room={room}
        onAdd={handleAddFurniture}
        onClose={() => setShowAddModal(false)}
      />
    )}

    {editMode && showAddOpeningModal && (
      <AddOpeningModal
        room={room}
        wallSegments={wallSegments}
        freeWalls={freeWalls}
        onAdd={handleAddOpening}
        onClose={() => setShowAddOpeningModal(false)}
        allowedTypes={['door', 'window', 'archway']}
        title={t('add_opening_modal_title')}
      />
    )}

    {editMode && showAddWallItemModal && (
      <AddOpeningModal
        room={room}
        wallSegments={wallSegments}
        freeWalls={freeWalls}
        onAdd={handleAddOpening}
        onClose={() => setShowAddWallItemModal(false)}
        allowedTypes={WALL_DECOR_TYPES}
        title={t('add_wallitem_modal_title')}
      />
    )}

    {editMode && showAddLightingModal && (
      <AddLightingModal
        onAdd={handleAddLighting}
        onClose={() => setShowAddLightingModal(false)}
      />
    )}

    {/* D-pad */}
    <div className={`room3d-dpad${isFullscreen ? ' room3d-dpad--fullscreen' : ''}`} onContextMenu={e => e.preventDefault()}>
      <button className="dpad-btn dpad-up"    {...bind('fwd')}>▲</button>
      <button className="dpad-btn dpad-left"  {...bind('left')}>◀</button>
      <button className="dpad-btn dpad-center" disabled>●</button>
      <button className="dpad-btn dpad-right" {...bind('right')}>▶</button>
      <button className="dpad-btn dpad-down"  {...bind('back')}>▼</button>
    </div>
    </div>
  );
}
